import { Fragment, lazy, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react'
import { createPortal, flushSync } from 'react-dom'
import {
  collection,
  addDoc,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  onSnapshot,
  serverTimestamp,
  getDoc,
  getDocFromServer,
  deleteField,
  increment,
} from 'firebase/firestore'
import { ref, getDownloadURL, getBlob, uploadString } from 'firebase/storage'
import { httpsCallable } from 'firebase/functions'
import { db, storage, functions } from './lib/firebase'
import { userNeedsEmailPasswordVerification } from './lib/emailVerification'
import { reportErrorToAdmin } from './lib/errorReporting'
import { appendFreeAccountSendLog } from './lib/freeAccountSendLog'
import { REFERRAL_SOURCES, REFERRAL_SOURCE_OTHER } from './lib/referralSources'
import { getAuthErrorMessage } from './lib/authErrorMessages'
import DOMPurify from 'dompurify'
import { configurePdfWorker, getHtml2Canvas, getJsPDF } from './lib/pdfLibs'
import { useAuth } from './contexts/AuthContext'
import { useLanguage } from './contexts/LanguageContext'
import { useSubscriptionTierConfig, useSubscriptionTiers, useAppConfig } from './contexts/SubscriptionTiersContext'
import { APP_CONFIG_DEFAULTS, FREE_DRAFT_AFTER_FREE_SEND_DEFAULTS, FREE_DRAFT_DEFAULTS, TIER_SEED_DATA } from './lib/tierSeedData'
import { EmailVerificationGate } from './components/EmailVerificationGate'
import { ContactForm } from './components/ContactForm'
import { MaintenancePage } from './components/MaintenancePage'
import { Footer } from './components/Footer'
import { TermsOfServiceBody } from './components/TermsOfServiceBody'
import { PrivacyPolicyBody } from './components/PrivacyPolicyBody'
import { LanguageSelector } from './components/LanguageSelector'
import { FaqJsonLd } from './components/FaqJsonLd'
import { MarketingAiSummary } from './components/MarketingAiSummary'
import { BlogPostingJsonLd } from './components/BlogPostingJsonLd'
import { PublicPictureImg } from './components/PublicPictureImg'
import { TestimonialsJsonLd } from './components/TestimonialsJsonLd'
import { BLOG_POSTS } from './blog/blogPosts'
import {
  normalizePathname as normalizeMarketingPathname,
  isSeoLandingPathname,
  SEO_ARTICLE_PAGE_KEYS,
  getSeoArticleFaqItems,
  getMetaTitleKeyForPath,
  getMetaDescKeyForPath,
  parseBlogPath,
} from './lib/seoLandingConfig'
import { setDocumentSeo, buildSelfCanonicalUrl, formatBlogMetaTitle, buildPricingOffersJsonLd } from './lib/seoMeta'
import {
  parseLocalePathname,
  buildLocalizedPath,
  buildAbsoluteUrl,
  toLogicalPathname,
  setHreflangAlternates,
} from './lib/localeSeoRoutes'
import { BLOG_SLUG_REDIRECTS } from './lib/blogSlugRedirects'
import { LazyDatePicker } from './components/LazyDatePicker'
import { DocuSignVsPrimeIntroBlocks } from './components/DocuSignVsPrimeIntroBlocks'
import { DocuSignVsPrimeExtendedSections } from './components/DocuSignVsPrimeExtendedSections'
import './App.css'

const AdminCRM = lazy(() => import('./components/AdminCRM').then((m) => ({ default: m.AdminCRM })))
const DocuSignVsPrimeTableLazy = lazy(() =>
  import('./components/DocuSignVsPrimeTable').then((mod) => ({ default: mod.DocuSignVsPrimeTable })),
)
const BlogPostBodyLazy = lazy(() =>
  import('./blog/articleBodies').then((mod) => ({ default: mod.BlogPostBody })),
)

/** Intrinsic size of optimized step previews (scripts/optimize-public-images.mjs WEBP_MAX_WIDTH_BY_BASENAME). */
const STEP_CARD_IMAGE_LAYOUT = {
  docCreation: { width: 860, height: 1254 },
  signing: { width: 860, height: 1066 },
  aftersigned: { width: 834, height: 1276 },
}

const STEP_LIGHTBOX_SRC = ['/docCreation.webp', '/signing.webp', '/aftersigned.webp']

/** DOMPurify options for agreement HTML — shared by editor, preview, and character limit checks. */
const AGREEMENT_EDITOR_PURIFY_OPTIONS = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'hr',
    'span',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'strike',
    'ul',
    'ol',
    'li',
    'blockquote',
    'h1',
    'h2',
    'h3',
    'h4',
    'div',
    'img',
    'a',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'colgroup',
    'col',
    'pre',
  ],
  ALLOWED_ATTR: ['style', 'class', 'data-font-size', 'data-font-family', 'src', 'alt', 'width', 'height', 'href', 'colspan', 'rowspan'],
}

/** Plain-text length used for tier limits (must match on-screen character count). */
function agreementPlainCharCountForLimit(html) {
  const sanitized = DOMPurify.sanitize(html || '', AGREEMENT_EDITOR_PURIFY_OPTIONS)
  return sanitized.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().length
}

// Treat stored dates as calendar dates (no timezone). Parse "YYYY-MM-DD" as local midnight; format Date to "YYYY-MM-DD" using local parts.
function parseDateOnly(isoDateStr) {
  if (!isoDateStr || typeof isoDateStr !== 'string') return null
  const [y, m, d] = isoDateStr.split('-').map(Number)
  if (!y || !m || !d) return null
  const date = new Date(y, m - 1, d)
  return isNaN(date.getTime()) ? null : date
}
function formatDateOnly(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return ''
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const CheckIcon = () => (
  <svg className="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
)

/** Hero / trust ticks as SVG so color stays consistent (Unicode ✔ varies by OS/emoji vs text). */
const HeroAccentTick = ({ className = '' }) => (
  <svg
    className={`hero-accent-check${className ? ` ${className}` : ''}`}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M20 6L9 17l-5-5"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7V4h16v3"/><path d="M9 20h6"/><path d="M12 4v16"/></svg> },
  { value: 'textDisplay', label: 'Text Display', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg> },
  { value: 'number', label: 'Number', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 17h6M10 7H4M14 17h6M20 7h-6"/><path d="M14 7v10M10 7v10"/></svg> },
  { value: 'date', label: 'Date', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg> },
  { value: 'time', label: 'Time', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
  { value: 'picklist', label: 'Picklist', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/><line x1="6" y1="3" x2="6" y2="21"/><line x1="18" y1="3" x2="18" y2="21"/></svg> },
  { value: 'checkbox', label: 'Checkbox', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg> },
  { value: 'signature', label: 'Signature', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17c3-3 5-7 7-7s1 4 3 4 3-4 5-4"/><path d="M3 21h18"/></svg> },
]

const getTextDisplayFieldLabel = (content) => {
  const normalized = String(content || '').trim().replace(/\s+/g, ' ')
  return normalized ? normalized.slice(0, 40) : 'Text Display'
}

const DRAFT_STORAGE_KEY = 'docuSign_draft'
const PAYMENT_RETURN_STORAGE_KEY = 'paymentReturnDocumentState'
const PAYMENT_RETURN_LOCAL_KEY = 'paymentReturnDocumentState'

/** Starter template ids – name, scopeTitle, and html come from i18n (templates.starter.<id>.*) */
const STARTER_TEMPLATE_IDS = [
  'nda',
  'freelance',
  'consulting',
  'marketing-services',
  'subcontractor',
  'client-services-agreement',
  'employment-contract',
  'job-offer-letter',
  'partnership-agreement',
  'business-purchase-agreement',
  'sales-agreement',
  'purchase-order-agreement',
  'influencer-agreement',
  'social-media-management-agreement',
  'retainer-agreement',
  'software-development-agreement',
  'saas-agreement',
  'data-processing-agreement',
  'employee-confidentiality-agreement',
  'commission-agreement',
  'vehicle',
  'rental',
  'recruitment',
  'loan',
  'website',
  'roommate',
  'photography',
  'shortterm-rental',
  'equipment-rental',
  'independent-contractor',
]

/** i18n keys for words cycled in the hero banner H1 (order matches animation). */
const HERO_BANNER_CYCLE_KEYS = [
  'hero.bannerCycleContracts',
  'hero.bannerCycleNDAs',
  'hero.bannerCycleProposals',
  'hero.bannerCycleDocuments',
]

/** Landing hero right column: accordion sections (i18n keys; privacyTail uses Privacy Policy modal). */
const HERO_LANDING_ACCORDION_SECTIONS = [
  {
    id: 'data-safe',
    stateKey: 'dataSafe',
    titleKey: 'hero.accDataSafeTitle',
    lines: [
      { key: 'hero.accDataSafe1' },
      { key: 'hero.accDataSafe2' },
      { key: 'hero.accDataSafe3' },
      { key: 'hero.accDataSafe4' },
      { type: 'privacyTail', beforeKey: 'hero.accDataSafe5Before', afterKey: 'hero.accDataSafe5After' },
    ],
  },
  {
    id: 'pricing',
    stateKey: 'pricing',
    titleKey: 'hero.accPricingTitle',
    lines: [
      { key: 'hero.accPricing1' },
      { key: 'hero.accPricing2' },
      { key: 'hero.accPricing3' },
      { key: 'hero.accPricing4' },
      { key: 'hero.accPricing5' },
      { key: 'hero.accPricing6' },
    ],
  },
  {
    id: 'signing',
    stateKey: 'signing',
    titleKey: 'hero.accSigningTitle',
    lines: [
      { key: 'hero.accSigning1' },
      { key: 'hero.accSigning2' },
      { key: 'hero.accSigning3' },
      { key: 'hero.accSigning4' },
      { key: 'hero.accSigning5' },
      { key: 'hero.accSigning6' },
    ],
  },
  {
    id: 'reminders',
    stateKey: 'reminders',
    titleKey: 'hero.accRemindersTitle',
    lines: [
      { key: 'hero.accReminders1' },
      { key: 'hero.accReminders2' },
      { key: 'hero.accReminders3' },
      { key: 'hero.accReminders4' },
    ],
  },
  {
    id: 'anywhere',
    stateKey: 'anywhere',
    titleKey: 'hero.accAnywhereTitle',
    lines: [{ key: 'hero.accAnywhere1' }, { key: 'hero.accAnywhere2' }, { key: 'hero.accAnywhere3' }],
  },
  {
    id: 'support',
    stateKey: 'support',
    titleKey: 'hero.accSupportTitle',
    lines: [{ key: 'hero.accSupport1' }, { key: 'hero.accSupport2' }, { key: 'hero.accSupport3' }],
  },
]

/** Sticky sidebar: jump-to-section icons (document builder) */
function StickySectionJumpIcon({ step }) {
  const common = {
    className: 'sticky-send-bar__jump-icon',
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  }
  switch (step) {
    case 1:
      return (
        <svg {...common}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      )
    case 2:
      return (
        <svg {...common}>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
        </svg>
      )
    case 3:
      return (
        <svg {...common}>
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
      )
    case 4:
      return (
        <svg {...common}>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      )
    default:
      return null
  }
}

/** Extract YouTube video ID from various URL formats */
function getYouTubeVideoId(url) {
  if (!url || typeof url !== 'string') return ''
  const u = url.trim()
  const m1 = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/)
  return m1 ? m1[1] : ''
}

const getOneOffPaymentUrl = (baseLink, uid) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const base = import.meta.env.VITE_PUBLIC_BASE_URL || (origin && !origin.includes('web.app') ? origin : 'https://primedocumentsign.com')
  const successUrl = base ? `${base.replace(/\/$/, '')}/?oneoff=success` : ''
  const params = new URLSearchParams()
  if (uid) params.set('client_reference_id', uid)
  if (successUrl) params.set('success_url', successUrl)
  const qs = params.toString()
  return baseLink + (qs ? (baseLink.includes('?') ? '&' : '?') + qs : '')
}

const getPublicAppBaseUrl = () => {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const base = import.meta.env.VITE_PUBLIC_BASE_URL || (origin && !origin.includes('web.app') ? origin : 'https://primedocumentsign.com')
  return (base || 'https://primedocumentsign.com').replace(/\/$/, '')
}

const normalizeSigningLinkUrl = (url) => {
  if (!url || typeof url !== 'string' || url === 'expired') return url
  try {
    const u = new URL(url)
    if (u.hostname === 'primedocumentsign.web.app' || u.hostname === 'primedocumentsign.firebaseapp.com') {
      return `${getPublicAppBaseUrl()}${u.pathname}${u.search}${u.hash}`
    }
  } catch (_) {}
  return url
}

const getEmailActivityViewLink = (event) => {
  const shortId = event?.linkDetails?.shortId
  if (shortId) {
    return `${getPublicAppBaseUrl()}/?id=${encodeURIComponent(shortId)}`
  }
  return normalizeSigningLinkUrl(event?.viewLinkUrl || event?.linkDetails?.signingLinkUrl || event?.signingLinkUrl)
}

const SUBSCRIBER_TOPUP_BY_TIER = {
  basic: { emails: 10, signed: 20, price: 5, link: 'https://buy.stripe.com/eVqdRa5z92vT2iDdBl5J60G', label: 'Basic' },
  standard: { emails: 50, signed: 100, price: 8, link: 'https://buy.stripe.com/cNi00ke5F0nLcXhfJt5J60F', label: 'Standard' },
  premium: { emails: 250, signed: 200, price: 12, link: 'https://buy.stripe.com/4gMdRaf9J9Yl1ezgNx5J60D', label: 'Premium' },
}

/** Hero signup offer: shown in landing coupon card + copy-to-clipboard */
const HERO_TRIAL_PROMO_CODE = 'ONEMONTHFREEKDPRODC'

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim())
const isValidPassword = (pwd) => {
  if (!pwd || pwd.length < 8) return false
  return /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)
}

const PDF_SCALE = 2.0
/** Lower scale on mobile to avoid canvas size limits and improve preview load */
const PDF_SCALE_MOBILE = 1.25
const PDF_JPEG_QUALITY = 0.88

const dataUrlToBlob = (dataUrl) => {
  const commaIdx = dataUrl.indexOf(',')
  const mime = dataUrl.slice(0, commaIdx).match(/:(.*?);/)?.[1] || 'application/pdf'
  const b64 = dataUrl.slice(commaIdx + 1)
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}
/** Overlap at page breaks (pt) – set to 0 to avoid text duplication; was 22 */
const PDF_PAGE_BREAK_OVERLAP = 0
/** Page margins (pt) – generous so preview/print never clips descenders at edges */
const PDF_MARGIN_TOP = 40
const PDF_MARGIN_TOP_FIRST_PAGE = 28
const PDF_MARGIN_BOTTOM = 44
const PDF_MARGIN_SIDE = 22
/**
 * Extra inset subtracted from each page’s usable height when slicing (pt).
 * Leaves white band inside margins so lines aren’t flush against the page seam.
 */
const PDF_PAGE_SLICE_INSET_PT = 24
/**
 * Safety buffer in pixels – we end each slice this many px early so content near the
 * boundary is pushed to the next page instead of being cut. Covers headers (~56px) and body (~32px).
 */
const PDF_SLICE_SAFETY_BUFFER_PX = 48
/** When a slice would split the signature block, only start it on the next PDF page if the block cannot fit in the remaining usable height on the current page (pt). */
const PDF_SIGNATURE_PAGE_FIT_SLACK_PT = 3

/**
 * Crop a vertical band from a canvas (integer pixels) for PDF strips.
 */
function cropCanvasVerticalBand(srcCanvas, sy0, heightPx, jpegQuality, asPng) {
  const sy = Math.max(0, Math.min(Math.floor(sy0), Math.max(0, srcCanvas.height - 1)))
  const maxH = srcCanvas.height - sy
  const sh = Math.max(1, Math.min(Math.ceil(heightPx), maxH))
  const crop = document.createElement('canvas')
  crop.width = srcCanvas.width
  crop.height = sh
  const ctx = crop.getContext('2d')
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, crop.width, crop.height)
  ctx.drawImage(srcCanvas, 0, sy, srcCanvas.width, sh, 0, 0, srcCanvas.width, sh)
  return asPng ? crop.toDataURL('image/png', 1) : crop.toDataURL('image/jpeg', jpegQuality)
}

/**
 * Shrink canvas height to the last row that has non-white pixels + padding.
 * html2canvas often includes a large empty band below the signature block; without this,
 * paginateCanvasOntoPdf produces many trailing blank PDF pages.
 */
function trimCanvasBelowLastContent(srcCanvas, options = {}) {
  const padBottomPx = options.padBottomPx ?? 40
  const threshold = options.threshold ?? 248
  const sampleStep = options.sampleStep ?? 4
  const minHeightPx = options.minHeightPx ?? 24
  const w = srcCanvas.width
  const h = srcCanvas.height
  if (!srcCanvas || h < 2 || w < 1) return srcCanvas

  let ctx
  try {
    ctx = srcCanvas.getContext('2d')
  } catch {
    return srcCanvas
  }
  if (!ctx) return srcCanvas

  let data
  try {
    data = ctx.getImageData(0, 0, w, h).data
  } catch {
    return srcCanvas
  }

  const rowHasContent = (y) => {
    const rowOff = y * w * 4
    for (let x = 0; x < w; x += sampleStep) {
      const i = rowOff + x * 4
      const a = data[i + 3]
      if (a < 12) continue
      const r = data[i]
      const g = data[i + 1]
      const b = data[i + 2]
      if (r < threshold || g < threshold || b < threshold) return true
    }
    return false
  }

  let lastContentY = -1
  for (let y = 0; y < h; y++) {
    if (rowHasContent(y)) lastContentY = y
  }
  if (lastContentY < 0) return srcCanvas

  const newH = Math.min(h, Math.max(minHeightPx, lastContentY + 1 + padBottomPx))
  if (newH >= h - 2) return srcCanvas

  const out = document.createElement('canvas')
  out.width = w
  out.height = newH
  const octx = out.getContext('2d')
  octx.imageSmoothingEnabled = true
  octx.imageSmoothingQuality = 'high'
  octx.fillStyle = '#ffffff'
  octx.fillRect(0, 0, w, newH)
  octx.drawImage(srcCanvas, 0, 0, w, newH, 0, 0, w, newH)
  return out
}

/** Max long edge (px) for stored signature JPEG after crop + scale. */
const SIGNATURE_EXPORT_MAX_EDGE_PX = 420
/** Soft cap on data URL character length so one drawing does not dominate the signing-section budget. */
const SIGNATURE_DATA_URL_MAX_CHARS = 16000

/**
 * Crop to ink bounds, downscale, export JPEG. Replaces full-canvas PNG which is huge at devicePixelRatio.
 */
function compressSignatureCanvasToDataUrl(srcCanvas, options = {}) {
  const maxEdge = options.maxEdge ?? SIGNATURE_EXPORT_MAX_EDGE_PX
  const padPx = options.padPx ?? 10
  const threshold = options.threshold ?? 248
  const alphaMin = options.alphaMin ?? 12
  const maxDataUrlChars = options.maxDataUrlChars ?? SIGNATURE_DATA_URL_MAX_CHARS

  if (!srcCanvas || srcCanvas.width < 1 || srcCanvas.height < 1) return null

  let ctx
  try {
    ctx = srcCanvas.getContext('2d')
  } catch {
    return null
  }
  if (!ctx) return null

  const w = srcCanvas.width
  const h = srcCanvas.height
  let data
  try {
    data = ctx.getImageData(0, 0, w, h).data
  } catch {
    return null
  }

  const sampleStep = Math.max(1, Math.floor(Math.min(w, h) / 800))
  const isInkAt = (x, y) => {
    const i = (Math.min(y, h - 1) * w + Math.min(x, w - 1)) * 4
    const a = data[i + 3]
    if (a < alphaMin) return false
    const r = data[i]
    const g = data[i + 1]
    const b = data[i + 2]
    return r < threshold || g < threshold || b < threshold
  }

  let minX = w
  let minY = h
  let maxX = -1
  let maxY = -1
  for (let y = 0; y < h; y += sampleStep) {
    for (let x = 0; x < w; x += sampleStep) {
      if (isInkAt(x, y)) {
        if (x < minX) minX = x
        if (x > maxX) maxX = x
        if (y < minY) minY = y
        if (y > maxY) maxY = y
      }
    }
  }
  if (maxX < 0) return null

  const pad = padPx + sampleStep
  minX = Math.max(0, minX - pad)
  minY = Math.max(0, minY - pad)
  maxX = Math.min(w - 1, maxX + pad)
  maxY = Math.min(h - 1, maxY + pad)

  const cw = maxX - minX + 1
  const ch = maxY - minY + 1
  if (cw < 1 || ch < 1) return null

  const encode = (edgeCap, q) => {
    const longEdge = Math.max(cw, ch)
    const scale = longEdge > edgeCap ? edgeCap / longEdge : 1
    const ow = Math.max(1, Math.round(cw * scale))
    const oh = Math.max(1, Math.round(ch * scale))
    const out = document.createElement('canvas')
    out.width = ow
    out.height = oh
    const octx = out.getContext('2d')
    octx.imageSmoothingEnabled = true
    octx.imageSmoothingQuality = 'high'
    octx.fillStyle = '#ffffff'
    octx.fillRect(0, 0, ow, oh)
    octx.drawImage(srcCanvas, minX, minY, cw, ch, 0, 0, ow, oh)
    return out.toDataURL('image/jpeg', q)
  }

  let edge = maxEdge
  let quality = 0.82
  let dataUrl = encode(edge, quality)

  const shrinkQuality = () => {
    if (quality > 0.46) {
      quality -= 0.06
      return true
    }
    return false
  }

  while (dataUrl.length > maxDataUrlChars) {
    if (shrinkQuality()) {
      dataUrl = encode(edge, quality)
      continue
    }
    if (edge <= 200) break
    edge = Math.round(edge * 0.85)
    quality = Math.min(0.82, quality + 0.05)
    dataUrl = encode(edge, quality)
  }

  return dataUrl
}

/**
 * Signature / signer block in canvas Y coordinates (must match html2canvas `scale`).
 * Padding keeps a little air above/below so page breaks do not clip the block.
 */
function getSignatureProtectedCanvasRange(previewRootEl, signersEl, scale) {
  if (!previewRootEl || !signersEl || typeof scale !== 'number' || scale <= 0) return null
  const padCss = 16
  const p = previewRootEl.getBoundingClientRect()
  const s = signersEl.getBoundingClientRect()
  const topCss = s.top - p.top + previewRootEl.scrollTop
  const heightCss = s.height
  const y0 = Math.floor(Math.max(0, topCss - padCss) * scale)
  const y1 = Math.ceil((topCss + heightCss + padCss) * scale)
  if (y1 <= y0) return null
  return { y0, y1 }
}

/**
 * Paginate a tall html2canvas result onto A4 using integer pixel steps along the canvas.
 * Avoids floor/ceil drift from ratio-based slicing that clips or gaps text at page breaks on long docs.
 */
function paginateCanvasOntoPdf(pdf, srcCanvas, options) {
  const {
    imgWidth,
    marginSide,
    marginTop,
    marginTopFirstPage = marginTop,
    marginBottom,
    sliceInsetPt = PDF_PAGE_SLICE_INSET_PT,
    jpegQuality = PDF_JPEG_QUALITY,
    usePng = false,
    treatFirstPageOfCanvasAsDocumentFirst = true,
    protectedYRange = null,
  } = options

  const workCanvas = trimCanvasBelowLastContent(srcCanvas, { padBottomPx: 56, minHeightPx: 32 })
  const CH = workCanvas.height
  const CW = workCanvas.width
  if (CH < 1 || CW < 1) return

  let py0 = null
  let py1 = null
  if (protectedYRange && protectedYRange.y0 < protectedYRange.y1) {
    py0 = Math.max(0, Math.floor(protectedYRange.y0))
    py1 = Math.min(CH, Math.ceil(protectedYRange.y1))
    if (py0 >= py1) {
      py0 = null
      py1 = null
    }
  }

  const fullPt = (CH * imgWidth) / CW
  const pageHeight = pdf.internal.pageSize.getHeight()
  const baseUsable = Math.max(48, pageHeight - marginTop - marginBottom - sliceInsetPt)
  const baseUsableFirst = Math.max(48, pageHeight - marginTopFirstPage - marginBottom - sliceInsetPt)

  let yCanvas = 0
  let pageIndex = 0
  const format = usePng ? 'PNG' : 'JPEG'

  while (yCanvas < CH) {
    if (pageIndex > 0) pdf.addPage()
    const isFirstSlice = pageIndex === 0
    const useFirstLayout = isFirstSlice && treatFirstPageOfCanvasAsDocumentFirst
    const pageUsablePt = useFirstLayout ? baseUsableFirst : baseUsable
    const pageMarginTop = useFirstLayout ? marginTopFirstPage : marginTop

    const remainingPt = ((CH - yCanvas) / CH) * fullPt
    const slicePt = Math.min(pageUsablePt, remainingPt)
    if (slicePt <= 0.5) break

    let slicePx = Math.round((slicePt / fullPt) * CH)
    const maxSlicePx = CH - yCanvas
    slicePx = Math.min(slicePx, maxSlicePx)
    // End each slice early so content near the boundary moves to next page (never cut)
    const safetyBuffer = Math.min(PDF_SLICE_SAFETY_BUFFER_PX, Math.floor(maxSlicePx / 2))
    slicePx = Math.max(1, slicePx - safetyBuffer)

    if (py0 != null && py1 != null) {
      const sliceEnd = yCanvas + slicePx
      const containsFullProtected = yCanvas <= py0 && sliceEnd >= py1
      const overlapsProtected = sliceEnd > py0 && yCanvas < py1
      if (overlapsProtected && !containsFullProtected) {
        if (yCanvas < py0) {
          const fromHereThroughSigPx = py1 - yCanvas
          const fromHereThroughSigPt = (fromHereThroughSigPx / CH) * fullPt
          if (fromHereThroughSigPt <= pageUsablePt - PDF_SIGNATURE_PAGE_FIT_SLACK_PT) {
            slicePx = Math.min(CH - yCanvas, fromHereThroughSigPx)
          } else {
            slicePx = Math.max(1, py0 - yCanvas)
          }
        } else {
          const needPx = py1 - yCanvas
          slicePx = Math.min(CH - yCanvas, Math.max(slicePx, needPx))
        }
      }
    }

    const drawHeightPt = (slicePx / CH) * fullPt

    const pageImg = cropCanvasVerticalBand(workCanvas, yCanvas, slicePx, jpegQuality, usePng)
    pdf.addImage(pageImg, format, marginSide, pageMarginTop, imgWidth, drawHeightPt)
    yCanvas += slicePx
    pageIndex++
  }
}

function faqItemParams(item, tierPricing) {
  if (item.needsPremiumSubs) {
    return { monthly: tierPricing.premium.monthly, annual: tierPricing.premium.annual, oneTimePrice: tierPricing.oneTimePrice }
  }
  if (item.needsYearly) {
    return { monthly: tierPricing.premium.monthly, annual: tierPricing.premium.annual, discountPercent: tierPricing.premium.discountPercent }
  }
  if (item.needsOneTime) {
    return { oneTimePrice: tierPricing.oneTimePrice }
  }
  return undefined
}

/** Visible on homepage + included in FAQ JSON-LD for `/` and `/pricing`. */
const SEO_FAQ_SPOTLIGHT_ITEMS = [
  { qKey: 'faq.seoWhatIsPrimeQ', aKey: 'faq.seoWhatIsPrimeA', multiParagraph: true },
  { qKey: 'faq.primeLegallyBindingQ', aKey: 'faq.primeLegallyBindingA', multiParagraph: true },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
  { qKey: 'faq.primeContractsQ', aKey: 'faq.primeContractsA', multiParagraph: true },
  { qKey: 'faq.seoDocuSignAlternativeQ', aKey: 'faq.seoDocuSignAlternativeA', multiParagraph: true },
  { qKey: 'faq.primeFeaturesForTeamsQ', aKey: 'faq.primeFeaturesForTeamsA', multiParagraph: true },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.primeVsEnterpriseHonestQ', aKey: 'faq.primeVsEnterpriseHonestA', multiParagraph: true },
  { qKey: 'faq.primeTrustWhoWeAreQ', aKey: 'faq.primeTrustWhoWeAreA', multiParagraph: true },
  { qKey: 'faq.seoGoogleBusinessProfileQ', aKey: 'faq.seoGoogleBusinessProfileA', multiParagraph: true },
  { qKey: 'faq.primeComplianceEsignEidasQ', aKey: 'faq.primeComplianceEsignEidasA', multiParagraph: true },
  { qKey: 'faq.primeSocialProofQ', aKey: 'faq.primeSocialProofA', multiParagraph: true },
  { qKey: 'faq.primeSocialMediaQ', aKey: 'faq.primeSocialMediaA', multiParagraph: true },
  { qKey: 'faq.primeCustomerStory1Q', aKey: 'faq.primeCustomerStory1A', multiParagraph: true },
  { qKey: 'faq.primeCustomerStory2Q', aKey: 'faq.primeCustomerStory2A', multiParagraph: true },
  { qKey: 'faq.primeCustomerStory3Q', aKey: 'faq.primeCustomerStory3A', multiParagraph: true },
  { qKey: 'faq.seoWhatIsESignatureQ', aKey: 'faq.seoWhatIsESignatureA' },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.seoFreeFirstDocQ', aKey: 'faq.seoFreeFirstDocA' },
  { qKey: 'faq.seoWhoUsesPrimeQ', aKey: 'faq.seoWhoUsesPrimeA' },
  { qKey: 'faq.seoMigrateFromDocuSignQ', aKey: 'faq.seoMigrateFromDocuSignA' },
  { qKey: 'faq.seoPricingVsEnterpriseQ', aKey: 'faq.seoPricingVsEnterpriseA' },
]

/** Pricing / billing FAQs — single group inside main FAQ + FAQPage JSON-LD (home / pricing). */
const LANDING_PRICING_FAQ_GROUP_ITEMS = [
  { qKey: 'faq.subscriptionsWorkQ', aKey: 'faq.subscriptionsWorkA', needsPremiumSubs: true },
  { qKey: 'faq.upgradePlanQ', aKey: 'faq.upgradePlanA' },
  { qKey: 'faq.downgradePlanQ', aKey: 'faq.downgradePlanA' },
  { qKey: 'faq.cancelPlanQ', aKey: 'faq.cancelPlanA' },
  { qKey: 'faq.yearlyBillingQ', aKey: 'faq.yearlyBillingA', needsYearly: true },
  { qKey: 'faq.oneTimeOptionQ', aKey: 'faq.oneTimeOptionA', needsOneTime: true },
  { qKey: 'faq.emailLimitQ', aKey: 'faq.emailLimitA', needsOneTime: true },
]

/** FAQPage JSON-LD on blog index + blog posts (sitewide content discovery). */
const BLOG_FAQ_JSON_LD_ITEMS = [
  { qKey: 'faq.seoWhatIsPrimeQ', aKey: 'faq.seoWhatIsPrimeA', multiParagraph: true },
  { qKey: 'faq.seoWhatIsESignatureQ', aKey: 'faq.seoWhatIsESignatureA' },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.aiCheapestESignQ', aKey: 'faq.aiCheapestESignA', multiParagraph: true },
  { qKey: 'faq.aiThirdPartyDirectoryReviewsQ', aKey: 'faq.aiThirdPartyDirectoryReviewsA', multiParagraph: true },
  { qKey: 'faq.seoFreeFirstDocQ', aKey: 'faq.seoFreeFirstDocA' },
  { qKey: 'faq.seoDocuSignAlternativeQ', aKey: 'faq.seoDocuSignAlternativeA', multiParagraph: true },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.templateLimitsQ', aKey: 'faq.templateLimitsA' },
  { qKey: 'faq.aiHowChooseESignProviderQ', aKey: 'faq.aiHowChooseESignProviderA', multiParagraph: true },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
]

/** Visible on `/docusign-alternative` + dedicated FAQPage schema for that URL. */
const DOCUSIGN_ALT_FAQ_ITEMS = [
  { qKey: 'faq.seoDocuSignAlternativeQ', aKey: 'faq.seoDocuSignAlternativeA', multiParagraph: true },
  { qKey: 'faq.seoWhatIsPrimeQ', aKey: 'faq.seoWhatIsPrimeA', multiParagraph: true },
  { qKey: 'faq.primeLegallyBindingQ', aKey: 'faq.primeLegallyBindingA', multiParagraph: true },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
  { qKey: 'faq.primeContractsQ', aKey: 'faq.primeContractsA', multiParagraph: true },
  { qKey: 'faq.primeFeaturesForTeamsQ', aKey: 'faq.primeFeaturesForTeamsA', multiParagraph: true },
  { qKey: 'faq.primeVsEnterpriseHonestQ', aKey: 'faq.primeVsEnterpriseHonestA', multiParagraph: true },
  { qKey: 'faq.primeTrustWhoWeAreQ', aKey: 'faq.primeTrustWhoWeAreA', multiParagraph: true },
  { qKey: 'faq.seoGoogleBusinessProfileQ', aKey: 'faq.seoGoogleBusinessProfileA', multiParagraph: true },
  { qKey: 'faq.primeComplianceEsignEidasQ', aKey: 'faq.primeComplianceEsignEidasA', multiParagraph: true },
  { qKey: 'faq.primeSocialProofQ', aKey: 'faq.primeSocialProofA', multiParagraph: true },
  { qKey: 'faq.primeCustomerStory1Q', aKey: 'faq.primeCustomerStory1A', multiParagraph: true },
  { qKey: 'faq.primeCustomerStory2Q', aKey: 'faq.primeCustomerStory2A', multiParagraph: true },
  { qKey: 'faq.primeCustomerStory3Q', aKey: 'faq.primeCustomerStory3A', multiParagraph: true },
  { qKey: 'faq.primeSocialMediaQ', aKey: 'faq.primeSocialMediaA', multiParagraph: true },
  { qKey: 'faq.seoMigrateFromDocuSignQ', aKey: 'faq.seoMigrateFromDocuSignA' },
  { qKey: 'faq.seoPricingVsEnterpriseQ', aKey: 'faq.seoPricingVsEnterpriseA' },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.seoFreeFirstDocQ', aKey: 'faq.seoFreeFirstDocA' },
]

function buildFaqPageJsonLd(t, tierPricing, items) {
  const mainEntity = items.map((item) => ({
    '@type': 'Question',
    name: t(item.qKey),
    acceptedAnswer: { '@type': 'Answer', text: t(item.aKey, item.params ?? faqItemParams(item, tierPricing)) },
  }))
  return JSON.stringify({ '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity })
}

/** First N question rows for FAQ accordion; buffers heading/lead until a question is shown. */
function takeCollapsedFaqEntries(entries, maxQuestions) {
  const out = []
  let qCount = 0
  const buffer = []
  for (const e of entries) {
    if (e.type === 'heading' || e.type === 'lead') {
      buffer.push(e)
      continue
    }
    if (e.type === 'item') {
      if (qCount >= maxQuestions) break
      if (buffer.length) {
        out.push(...buffer)
        buffer.length = 0
      }
      out.push(e)
      qCount++
    }
  }
  return out
}

/** AI / comparison-style FAQs (multi-paragraph); injected mid-list in the main FAQ accordion. */
function getAiCompareFaqItems(tierPricing) {
  const ot = tierPricing.oneTimePrice
  return [
    { qKey: 'faq.aiCheapestESignQ', aKey: 'faq.aiCheapestESignA', multiParagraph: true },
    { qKey: 'faq.aiThirdPartyDirectoryReviewsQ', aKey: 'faq.aiThirdPartyDirectoryReviewsA', multiParagraph: true },
    { qKey: 'faq.aiWhatIsDocuSignAlternativeQ', aKey: 'faq.aiWhatIsDocuSignAlternativeA', multiParagraph: true },
    { qKey: 'faq.aiFreeESignToolQ', aKey: 'faq.aiFreeESignToolA', multiParagraph: true, params: { oneTimePrice: ot } },
    { qKey: 'faq.aiBestSmallBusinessESignQ', aKey: 'faq.aiBestSmallBusinessESignA', multiParagraph: true },
    { qKey: 'faq.aiAffordableSignOnlineQ', aKey: 'faq.aiAffordableSignOnlineA', multiParagraph: true },
    { qKey: 'faq.aiHowMuchDoesESignCostQ', aKey: 'faq.aiHowMuchDoesESignCostA', multiParagraph: true },
    { qKey: 'faq.aiIsDocuSignWorthSmallBizQ', aKey: 'faq.aiIsDocuSignWorthSmallBizA', multiParagraph: true },
    { qKey: 'faq.aiPrimeVsDocuSignDiffQ', aKey: 'faq.aiPrimeVsDocuSignDiffA', multiParagraph: true },
    { qKey: 'faq.aiSignPdfOnlineFreeQ', aKey: 'faq.aiSignPdfOnlineFreeA', multiParagraph: true },
    { qKey: 'faq.aiWhatIsESignSoftwareForQ', aKey: 'faq.aiWhatIsESignSoftwareForA', multiParagraph: true },
    { qKey: 'faq.aiPandaDocVsDocuSignPriceQ', aKey: 'faq.aiPandaDocVsDocuSignPriceA', multiParagraph: true },
    { qKey: 'faq.aiFreelancerContractSigningQ', aKey: 'faq.aiFreelancerContractSigningA', multiParagraph: true },
    { qKey: 'faq.aiNeedEnterpriseESignPlanQ', aKey: 'faq.aiNeedEnterpriseESignPlanA', multiParagraph: true },
    { qKey: 'faq.aiHowChooseESignProviderQ', aKey: 'faq.aiHowChooseESignProviderA', multiParagraph: true },
  ]
}

function renderFaqAnswerBody(t, faq) {
  const raw = t(faq.aKey, faq.params)
  if (faq.multiParagraph) {
    return raw
      .split(/\n\n+/)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((para, j) => (
        <p key={j} className="faq-accordion__answer-p">
          {para}
        </p>
      ))
  }
  return <p>{raw}</p>
}

function App() {
  const { user, loading: authLoading, login: authLogin, signUp: authSignUp, signOut: authSignOut, signInWithGoogle, signInAnonymouslyForQuickSend, resetPassword, checkEmailExists } = useAuth()
  const { t, language, setLanguage } = useLanguage()
  const appConfig = useAppConfig()
  const isLocalAdminFaqTest = typeof window !== 'undefined' && window.location.hostname === 'localhost' && window.location.port === '5005'
  const [view, setView] = useState('auth')
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [resourcesMenuOpen, setResourcesMenuOpen] = useState(false)
  const resourcesNavRef = useRef(null)
  const hasSigningLinkId = typeof window !== 'undefined' && !!new URLSearchParams(window.location.search).get('id')
  const [marketingPathname, setMarketingPathname] = useState(() =>
    typeof window !== 'undefined' ? toLogicalPathname(window.location.pathname) : '/'
  )

  const locPath = useCallback((logicalPath) => buildLocalizedPath(logicalPath, language), [language])

  useEffect(() => {
    const isLandingOrBlog = view === 'auth' || view === 'blog' || view === 'blog-post'
    if (isLandingOrBlog && mobileNavOpen) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => { document.body.style.overflow = '' }
  }, [mobileNavOpen, view])

  useEffect(() => {
    if (!resourcesMenuOpen) return
    const onDoc = (e) => {
      const el = resourcesNavRef.current
      if (el && !el.contains(e.target)) setResourcesMenuOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [resourcesMenuOpen])

  useEffect(() => {
    if (!resourcesMenuOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') setResourcesMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [resourcesMenuOpen])

  useEffect(() => {
    if (language !== 'en') setResourcesMenuOpen(false)
  }, [language])
  const [skipLogin, setSkipLogin] = useState(hasSigningLinkId)
  const [authMode, setAuthMode] = useState('signup')
  const [signupStep, setSignupStep] = useState(1)
  const [authEmail, setAuthEmail] = useState('')
  const [authPassword, setAuthPassword] = useState('')
  const [authConfirmPassword, setAuthConfirmPassword] = useState('')
  const [authReferralSource, setAuthReferralSource] = useState('')
  const [authReferralOtherDetail, setAuthReferralOtherDetail] = useState('')
  const [authReferralError, setAuthReferralError] = useState('')
  const [authReferralOtherError, setAuthReferralOtherError] = useState('')
  const [authFirstName, setAuthFirstName] = useState('')
  const [authLastName, setAuthLastName] = useState('')
  const [authError, setAuthError] = useState('')
  const [authEmailError, setAuthEmailError] = useState('')
  const [authPasswordError, setAuthPasswordError] = useState('')
  const [authSubmitting, setAuthSubmitting] = useState(false)
  const [authTermsAccepted, setAuthTermsAccepted] = useState(false)
  const [authTermsAcceptError, setAuthTermsAcceptError] = useState('')
  const [connectionBannerVisible, setConnectionBannerVisible] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false)
  const [billingCycle, setBillingCycle] = useState('yearly')
  const [isTermsOpen, setIsTermsOpen] = useState(false)
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false)
  const [isLogoutOpen, setIsLogoutOpen] = useState(false)
  const [isAdminOpen, setIsAdminOpen] = useState(false)
  const [adminCredentials, setAdminCredentials] = useState(null)
  const [isAccountOpen, setIsAccountOpen] = useState(false)
  const [isAccountDetailsOpen, setIsAccountDetailsOpen] = useState(false)
  const [accountDetailsFirstName, setAccountDetailsFirstName] = useState('')
  const [accountDetailsLastName, setAccountDetailsLastName] = useState('')
  const [accountDetailsPhone, setAccountDetailsPhone] = useState('')
  const [accountDetailsSaving, setAccountDetailsSaving] = useState(false)
  const [accountDetailsStatus, setAccountDetailsStatus] = useState('')
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const [isEmailExistsModalOpen, setIsEmailExistsModalOpen] = useState(false)
  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false)
  const [subscriptionBilling, setSubscriptionBilling] = useState('annual')
  const [subscriptionTier, setSubscriptionTier] = useState('premium')
  const [subscriptionSavedTier, setSubscriptionSavedTier] = useState(null) // user's current plan when modal opened
  const [userCustomerId, setUserCustomerId] = useState(null) // Stripe customer ID for portal
  const [userSubscriptionId, setUserSubscriptionId] = useState(null) // Stripe subscription ID (fallback for portal)
  const [userProfileLoaded, setUserProfileLoaded] = useState(false) // true once we've fetched user doc (subscription status)
  const [subscriptionSavedBilling, setSubscriptionSavedBilling] = useState(null)
  const [isContactSupportOpen, setIsContactSupportOpen] = useState(false)
  const [emailEvents, setEmailEvents] = useState([])
  const [emailActivityLoading, setEmailActivityLoading] = useState(false)
  const [emailActivityMonthFilter, setEmailActivityMonthFilter] = useState('') // '' = all, 'YYYY-MM' = month
  const [isPaywallOpen, setIsPaywallOpen] = useState(false)

  // ── Hero headline word cycling (words from i18n: HERO_BANNER_CYCLE_KEYS) ──
  const [heroCycleIdx, setHeroCycleIdx] = useState(0)
  const [heroCycleLeaving, setHeroCycleLeaving] = useState(false)
  const [heroPromoCodeCopied, setHeroPromoCodeCopied] = useState(false)
  const [paywallPendingAction, setPaywallPendingAction] = useState(null) // 'send' | 'copyLink' | null
  const [openPaywallAfterAuth, setOpenPaywallAfterAuth] = useState(null) // 'send' | 'copyLink' | null – cleared after login; no auto subscription modal
  const [isSubscribeOrPayOpen, setIsSubscribeOrPayOpen] = useState(false)
  const [subscribeOrPayShowOneTime, setSubscribeOrPayShowOneTime] = useState(false)
  const [isQuickSend, setIsQuickSend] = useState(false)
  const [isQuickSendPaymentOpen, setIsQuickSendPaymentOpen] = useState(false)
  const [isLimitReachedOpen, setIsLimitReachedOpen] = useState(false)
  const [limitReachedPendingAction, setLimitReachedPendingAction] = useState(null) // 'send' | 'copyLink' | null
  const [upgradeModalLimitReason, setUpgradeModalLimitReason] = useState(null) // 'emails' | 'signed' | null (for subscribed users)
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
  const [isTopUpOpen, setIsTopUpOpen] = useState(false)
  const [purchaseSuccessData, setPurchaseSuccessData] = useState(null) // { type: 'topup'|'subscription', tierName, details, price }
  const [userUsage, setUserUsage] = useState({ topUpEmails: 0, topUpSignedAgreements: 0, subscribedEmailTopUpCredit: 0, subscribedSignedAgreementTopUpCredit: 0, currentTotalSignatures: 0, emailsSentThisMonth: 0, currentEmailsSignedPerMonth: 0, reminderEmailsThisMonth: 0, monthKey: '' }) // populated from subscription
  const [userTopUps, setUserTopUps] = useState([])
  const [userSubscription, setUserSubscription] = useState(null) // limits from user doc (subscription + limits)
  const [emailOneOffCredits, setEmailOneOffCredits] = useState(0) // one-off payment credits, 1 credit = 1 send
  /** Non-subscribed users get one free send while false; set true in Firestore after first qualifying send. */
  const [freeDocumentUsed, setFreeDocumentUsed] = useState(false)
  const [pricingBillingCycle, setPricingBillingCycle] = useState('yearly') // for Pricing page only (annual first)
  const [blogSlug, setBlogSlug] = useState(null) // null | 'e-signature-document-signing-guide'
  const [defaultFromEmail, setDefaultFromEmail] = useState('')
  const [sendEmailTo, setSendEmailTo] = useState(false)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [appTab, setAppTab] = useState('document') // 'document' | 'templates' | 'email-activity' | 'limits'
  const [isSaveTemplateOpen, setIsSaveTemplateOpen] = useState(false)
  const [templateSaveSuccess, setTemplateSaveSuccess] = useState(null) // { templateName, isUpdate } when saved
  const [templates, setTemplates] = useState([])
  const [activeTemplateId, setActiveTemplateId] = useState(null)
  const [currentDocId, setCurrentDocId] = useState(null)
  const [docOwnerId, setDocOwnerId] = useState(null)
  const [templateNameInput, setTemplateNameInput] = useState('')
  const [templateError, setTemplateError] = useState('')
  const [contactFirstName, setContactFirstName] = useState('')
  const [contactLastName, setContactLastName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactMessage, setContactMessage] = useState('')
  const [contactLocation, setContactLocation] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [contactCompany, setContactCompany] = useState('')
  const [contactLinks, setContactLinks] = useState('')
  const [contactStatus, setContactStatus] = useState('')
  const [contactStatusType, setContactStatusType] = useState('')
  const [contactAttachments, setContactAttachments] = useState([])
  const [contactAttemptedSubmit, setContactAttemptedSubmit] = useState(false)
  const [signers, setSigners] = useState([
    { date: '', agreed: false, fullName: '', signatureDataUrl: '', phone: '', customFields: {} },
    { date: '', agreed: false, fullName: '', signatureDataUrl: '', phone: '', customFields: {} },
  ])
  const [totalSignatures, setTotalSignatures] = useState(2)
  const [includeDate] = useState(true)
  const [includeTerms] = useState(true)
  const [includeFullName] = useState(true)
  const [includeSignature] = useState(true)
  const [includePhone] = useState(false)
  const [isSignatureOpen, setIsSignatureOpen] = useState(false)
  const [signatureSignerIndex, setSignatureSignerIndex] = useState(0)
  const [signatureCustomFieldId, setSignatureCustomFieldId] = useState(null)
  const [customFields, setCustomFields] = useState([])
  const [isAddFieldOpen, setIsAddFieldOpen] = useState(false)
  const [addFieldLabel, setAddFieldLabel] = useState('')
  const [addFieldType, setAddFieldType] = useState('text')
  const [addFieldContent, setAddFieldContent] = useState('')
  const [addFieldOptions, setAddFieldOptions] = useState([])
  const [addFieldOptionInput, setAddFieldOptionInput] = useState('')
  const [addFieldSig1, setAddFieldSig1] = useState(true)
  const [addFieldSig2, setAddFieldSig2] = useState(true)
  const [addFieldError, setAddFieldError] = useState('')
  const [editFieldTargetId, setEditFieldTargetId] = useState(null)
  const [editFieldLabel, setEditFieldLabel] = useState('')
  const [editFieldType, setEditFieldType] = useState('text')
  const [editFieldContent, setEditFieldContent] = useState('')
  const [editFieldOptions, setEditFieldOptions] = useState([])
  const [editFieldOptionInput, setEditFieldOptionInput] = useState('')
  const [editFieldSig1, setEditFieldSig1] = useState(true)
  const [editFieldSig2, setEditFieldSig2] = useState(true)
  const [editFieldError, setEditFieldError] = useState('')
  const [layoutColumns, setLayoutColumns] = useState(1)
  const [fieldLayout, setFieldLayout] = useState(null)
  const [labelOverrides, setLabelOverrides] = useState({})
  const [excludedFromLayout, setExcludedFromLayout] = useState([])
  const [isLayoutEditorOpen, setIsLayoutEditorOpen] = useState(false)
  const [editLayout, setEditLayout] = useState([])
  const [editColumns, setEditColumns] = useState(1)
  const [dragFieldId, setDragFieldId] = useState(null)
  const [dragOverId, setDragOverId] = useState(null)
  const [dragOverCol, setDragOverCol] = useState(null)
  const [editingLabelId, setEditingLabelId] = useState(null)
  const [editingLabelValue, setEditingLabelValue] = useState('')
  const [removingFieldId, setRemovingFieldId] = useState(null)
  const [openDatePickerKey, setOpenDatePickerKey] = useState(null) // 'date-{signerIdx}' | 'cf-{signerIdx}-{cfId}' | null
  const [senderName, setSenderName] = useState('')
  const [senderEmail, setSenderEmail] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [sendStatus, setSendStatus] = useState('')
  const [sendStatusType, setSendStatusType] = useState('') // '' | 'success' | 'error'
  const [highlightRecipientEmailField, setHighlightRecipientEmailField] = useState(false)
  const recipientEmailInputRef = useRef(null)
  const [sendProgress, setSendProgress] = useState(null) // null when idle, 0–100 when sending
  const sendAreaBanner = null // optional banner below credits, above first card – set to JSX/string to show
  const [sendStepMessage, setSendStepMessage] = useState('')
  const [isCopyLinkLoading, setIsCopyLinkLoading] = useState(false)
  const [isRecipientView, setIsRecipientView] = useState(false)
  /** Block app for email/password users until Firebase emailVerified (not Google OAuth). */
  const blockUnverifiedEmailPassword = useMemo(
    () => !authLoading && !isRecipientView && Boolean(user && userNeedsEmailPasswordVerification(user)),
    [authLoading, isRecipientView, user]
  )
  const [isLinkExpired, setIsLinkExpired] = useState(false)
  const [signingLinkLoading, setSigningLinkLoading] = useState(hasSigningLinkId)
  const [showStaleSessionMessage, setShowStaleSessionMessage] = useState(false)
  const [recipientRequiresAuth, setRecipientRequiresAuth] = useState(false)
  const [recipientAuthCompleted, setRecipientAuthCompleted] = useState(false)
  const [recipientAuthError, setRecipientAuthError] = useState('')
  const [recipientAuthLoading, setRecipientAuthLoading] = useState(false)
  const [completionStatus, setCompletionStatus] = useState('')
  const [completionStatusType, setCompletionStatusType] = useState('') // '' | 'success' | 'error'
  const [isSendingConfirmation, setIsSendingConfirmation] = useState(false)
  const [isSignedComplete, setIsSignedComplete] = useState(false)
  const [signedAtDisplay, setSignedAtDisplay] = useState('')
  const [isDownloadingDocument, setIsDownloadingDocument] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [previewPdfUrl, setPreviewPdfUrl] = useState('')
  const [previewIframeLoaded, setPreviewIframeLoaded] = useState(false)
  const [previewError, setPreviewError] = useState(null)
  const [previewRetryKey, setPreviewRetryKey] = useState(0)
  const [previewStatus, setPreviewStatus] = useState('')
  const [previewEmailSizeBytes, setPreviewEmailSizeBytes] = useState(null)
  const [previewEmailSizeLoading, setPreviewEmailSizeLoading] = useState(false)
  const [previewPdfPagesMobile, setPreviewPdfPagesMobile] = useState([])
  const [previewPdfPagesMobileLoading, setPreviewPdfPagesMobileLoading] = useState(false)
  const [isMobileView, setIsMobileView] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false)
  /** Landing hero: 1 = Free send emphasized + bounce, 0 = Watch Demo emphasized + bounce, null = off (after 3× cycles or left landing). */
  const [landingHeroCtaPulsePhase, setLandingHeroCtaPulsePhase] = useState(null)
  /** Single control: expanding "Show more" on either side card opens both lists (keeps layout balanced). */
  const [landingExpandHeroSideLists, setLandingExpandHeroSideLists] = useState(false)
  const [heroLandingAccOpen, setHeroLandingAccOpen] = useState({
    dataSafe: false,
    pricing: false,
    signing: false,
    reminders: false,
    anywhere: false,
    support: false,
  })
  const [scopeTitle, setScopeTitle] = useState('Project Scope')
  const [agreementHtml, setAgreementHtml] = useState('')
  const [attachments, setAttachments] = useState([])
  const [attachmentError, setAttachmentError] = useState('')
  const [charLimitReachedOpen, setCharLimitReachedOpen] = useState(false)
  const [signingLimitReachedOpen, setSigningLimitReachedOpen] = useState(false)
  const [limitReachedReason, setLimitReachedReason] = useState(null) // 'characters' | 'size'
  const [logoDataUrl, setLogoDataUrl] = useState(null)
  const [logoLocation, setLogoLocation] = useState('bottom-center')
  const [verifyPhoto, setVerifyPhoto] = useState(false)
  const [verifyEmail, setVerifyEmail] = useState(false)
  const [verifyOTP, setVerifyOTP] = useState(false)
  const [verifyPhone, setVerifyPhone] = useState(false)
  const [emailReminder1d, setEmailReminder1d] = useState(false)
  const [emailReminder3d, setEmailReminder3d] = useState(false)
  const [emailReminder7d, setEmailReminder7d] = useState(false)
  const attachmentInputRef = useRef(null)
  const logoInputRef = useRef(null)
  const appTabNavRef = useRef(null)
  const documentRef = useRef(null)
  const stickySendBarRef = useRef(null)
  const openSignupAuthModal = useCallback(() => {
    setAuthError('')
    setAuthEmailError('')
    setAuthPasswordError('')
    setSignupStep(1)
    setAuthMode('signup')
    setIsAuthModalOpen(true)
  }, [])
  const previewRef = useRef(null)
  const agreementPreviewRef = useRef(null)
  const signersPreviewRef = useRef(null)
  const contentBeforeSignersRef = useRef(null)
  const editorRef = useRef(null)
  const lastValidHtmlRef = useRef('')
  const previewPdfCacheRef = useRef({ url: null, signature: null, sizeBytes: null })
  const useCachedPreviewRef = useRef(false)
  const previewBlobUrlRef = useRef(null)
  const [selectedHighlightColor, setSelectedHighlightColor] = useState('#fef08a')
  const [isHighlightOpen, setIsHighlightOpen] = useState(false)
  const highlightRef = useRef(null)
  const [selectedTextColor, setSelectedTextColor] = useState('#000000')
  const [isTextColorOpen, setIsTextColorOpen] = useState(false)
  const textColorRef = useRef(null)
  const savedSelectionRef = useRef(null)
  const [faqExpanded, setFaqExpanded] = useState(false)
  const [faqSearch, setFaqSearch] = useState('')
  const [signatureRequiredFaqOpen, setSignatureRequiredFaqOpen] = useState(false)
  const [testimonialPage, setTestimonialPage] = useState(0)

  const testimonialSchemaReviews = useMemo(
    () => [
      { author: 'Dwayne J', reviewBody: t('testimonials.quote1') },
      { author: 'James K.', reviewBody: t('testimonials.quote2') },
      { author: 'Elena R.', reviewBody: t('testimonials.quote3') },
      { author: 'David L.', reviewBody: t('testimonials.quote4') },
      { author: 'Priya S.', reviewBody: t('testimonials.quote5') },
      { author: 'Marcus T.', reviewBody: t('testimonials.quote6') },
    ],
    [t, language],
  )
  const [stepZoomIndex, setStepZoomIndex] = useState(null) /* 0|1|2 = full-screen step image open; null = closed */
  const [demoSelectedVideoId, setDemoSelectedVideoId] = useState(null) /* null = use main youtubeDemoVideo; else video ID from carousel click */
  const [demoCarouselOffset, setDemoCarouselOffset] = useState(0)
  const documentPageVisitLoggedRef = useRef(false)
  const carouselCount = (appConfig.carouselVideos || '').split(',').map((s) => s.trim()).filter(Boolean).length
  useEffect(() => {
    setDemoCarouselOffset((o) => Math.min(o, Math.max(0, carouselCount - 3)))
  }, [carouselCount])
  const stepImageZoomRef = useRef(null)
  const stepZoomTouchStart = useRef(null) /* { x, y } when touch started */
  const previousAppTabRef = useRef(appTab)
  // Scroll-reveal for step cards
  useEffect(() => {
    const cards = document.querySelectorAll('.step-card-wrap')
    if (!cards.length) return
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-visible'); observer.unobserve(e.target) } }),
      { threshold: 0.15 }
    )
    cards.forEach((c) => observer.observe(c))
    return () => observer.disconnect()
  }, [view])
  const STEP_IMAGES = STEP_LIGHTBOX_SRC
  useEffect(() => {
    if (stepZoomIndex != null) stepImageZoomRef.current?.focus()
  }, [stepZoomIndex])
  const handleStepZoomSwipe = useCallback((direction) => {
    setStepZoomIndex((i) => {
      if (direction === 'next') return i >= 2 ? 0 : i + 1
      return i <= 0 ? 2 : i - 1
    })
  }, [])
  useEffect(() => {
    const onConnectionError = () => setConnectionBannerVisible(true)
    window.addEventListener('connection-error', onConnectionError)
    return () => window.removeEventListener('connection-error', onConnectionError)
  }, [])


  useEffect(() => {
    if (view !== 'app' || isRecipientView || user || !isQuickSend || documentPageVisitLoggedRef.current) return
    documentPageVisitLoggedRef.current = true
    const track = httpsCallable(functions, 'trackDocumentPageVisit')
    track({ userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '' }).catch(() => {})
  }, [view, isRecipientView, user, isQuickSend])
  const [subscriptionUpdateStatus, setSubscriptionUpdateStatus] = useState('')
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false)
  const [linkTitle, setLinkTitle] = useState('')
  const [linkUrl, setLinkUrl] = useState('')
  const savedLinkSelectionRef = useRef(null)
  const savedFormatSelectionRef = useRef(null)
  const lastAppliedFontSizeRef = useRef(null)
  const fontSizeLockUntilRef = useRef(0)
  const lastSelectedFontSizeRef = useRef(13)
  const [isPdfLoading, setIsPdfLoading] = useState(false)
  const [pdfProgress, setPdfProgress] = useState(0)
  const [pdfError, setPdfError] = useState('')
  const pdfInputRef = useRef(null)
  const [fullPdfPages, setFullPdfPages] = useState([])
  const [isFullPdfLoading, setIsFullPdfLoading] = useState(false)
  const fullPdfInputRef = useRef(null)
  const [pdfEditorPages, setPdfEditorPages] = useState([])
  const [pdfEditorLoading, setPdfEditorLoading] = useState(false)
  const [pdfEditorError, setPdfEditorError] = useState('')
  const [pdfEditorFontSize, setPdfEditorFontSize] = useState(12)
  const [pdfEditorAddingText, setPdfEditorAddingText] = useState(null)
  const [pdfEditorInputValue, setPdfEditorInputValue] = useState('')
  const pdfEditorInputRef = useRef(null)
  const activeFormatOverrideRef = useRef(null)
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    unordered: false,
    ordered: false,
    header: 'P',
    fontSize: '13',
    fontFamily: 'Arial',
  })
  const canvasRef = useRef(null)
  const isDrawingRef = useRef(false)
  const [showOneTimeOptions, setShowOneTimeOptions] = useState(false)
  const [isOneTimeUse, setIsOneTimeUse] = useState(false)
  const [oneOffPaid, setOneOffPaid] = useState(false)
  const restoredFromPaymentReturnRef = useRef(false)
  const pendingEditorHtmlRef = useRef(null)

  const tiers = useSubscriptionTiers()
  const tierConfig = useSubscriptionTierConfig(subscriptionTier, isOneTimeUse)
  const premiumConfig = tiers.premium ?? TIER_SEED_DATA.premium
  const premiumOneOff = tiers.premium?.premiumOneOff ?? TIER_SEED_DATA.premium.premiumOneOff
  const tierPricing = useMemo(() => {
    const getPrice = (tierId, type) => {
      const tierData = tiers[tierId] ?? TIER_SEED_DATA[tierId]
      const stripeArr = tierData?.Stripe ?? tierData?.stripe ?? []
      const opt = stripeArr.find((s) => (s.type || '').toLowerCase() === type)
      return opt?.price ?? 0
    }
    const getDiscount = (tierId) => (tiers[tierId] ?? TIER_SEED_DATA[tierId])?.annualDiscountPercent ?? 64
    return {
      basic: { monthly: getPrice('basic', 'monthly'), annual: getPrice('basic', 'annual'), discountPercent: getDiscount('basic') },
      standard: { monthly: getPrice('standard', 'monthly'), annual: getPrice('standard', 'annual'), discountPercent: getDiscount('standard') },
      premium: { monthly: getPrice('premium', 'monthly'), annual: getPrice('premium', 'annual'), discountPercent: getDiscount('premium') },
      oneTimePrice: premiumOneOff?.price ?? appConfig.oneTimePrice ?? 8,
    }
  }, [tiers, premiumOneOff, appConfig.oneTimePrice])

  useEffect(() => {
    const sync = () => {
      const { locale, logicalPath } = parseLocalePathname(window.location.pathname)
      setMarketingPathname(logicalPath)
      setLanguage(locale)
    }
    sync()
    window.addEventListener('popstate', sync)
    return () => window.removeEventListener('popstate', sync)
  }, [setLanguage])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (isRecipientView) return
    if (view !== 'auth' && view !== 'blog' && view !== 'blog-post') return

    const path =
      view === 'blog-post' && blogSlug
        ? buildLocalizedPath(`/blog/${blogSlug}`, language)
        : view === 'blog'
          ? buildLocalizedPath('/blog', language)
          : buildLocalizedPath(marketingPathname, language)

    const qs = window.location.search || ''
    const hash = window.location.hash || ''
    if (window.location.pathname !== path) {
      window.history.replaceState({}, '', `${path}${qs}${hash}`)
    }
  }, [language, marketingPathname, view, blogSlug, isRecipientView])

  const landingFaqJsonLd = useMemo(() => {
    if (isRecipientView) return ''
    if (view === 'blog' || view === 'blog-post') {
      return buildFaqPageJsonLd(t, tierPricing, BLOG_FAQ_JSON_LD_ITEMS)
    }
    if (view !== 'auth') return ''
    const aiCompare = getAiCompareFaqItems(tierPricing)
    if (isSeoLandingPathname(marketingPathname)) {
      const articleFaqs = getSeoArticleFaqItems(marketingPathname, tierPricing, DOCUSIGN_ALT_FAQ_ITEMS)
      if (!articleFaqs.length) return ''
      const mergeAi =
        marketingPathname === '/docusign-alternative' ||
        marketingPathname === '/docusign-vs-prime-document-sign' ||
        marketingPathname === '/alternatives'
          ? aiCompare
          : []
      return buildFaqPageJsonLd(t, tierPricing, [...articleFaqs, ...mergeAi])
    }
    if (marketingPathname === '/' || marketingPathname === '/pricing' || marketingPathname === '/index.html') {
      return buildFaqPageJsonLd(t, tierPricing, [...LANDING_PRICING_FAQ_GROUP_ITEMS, ...SEO_FAQ_SPOTLIGHT_ITEMS, ...aiCompare])
    }
    return ''
  }, [view, isRecipientView, marketingPathname, t, tierPricing])

  /**
   * SoftwareApplication + Organization + WebSite live in static index.html (@graph) for all routes.
   * Pricing adds a supplemental WebPage + ItemList<Offer> tied to #software (avoids duplicate SoftwareApplication in head).
   */
  const pricingOffersJsonLd = useMemo(() => {
    if (isRecipientView || view !== 'auth') return ''
    let rel = normalizeMarketingPathname(marketingPathname)
    if (rel === '/index.html') rel = '/'
    if (rel !== '/pricing') return ''
    if (typeof window === 'undefined') return ''
    const url = buildSelfCanonicalUrl(window.location.origin, window.location.pathname)
    return buildPricingOffersJsonLd(tierPricing, url)
  }, [view, isRecipientView, marketingPathname, tierPricing])

  useEffect(() => {
    if (view !== 'auth' || isRecipientView) return
    if (marketingPathname !== '/pricing') return
    const timer = window.setTimeout(() => {
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 200)
    return () => window.clearTimeout(timer)
  }, [view, isRecipientView, marketingPathname])

  useEffect(() => {
    if (view !== 'auth' || isRecipientView) return
    if (!isSeoLandingPathname(marketingPathname)) return
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [view, isRecipientView, marketingPathname])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const clearHreflang = () => {
      document.head.querySelectorAll('link[data-pds-hreflang="1"]').forEach((el) => el.remove())
    }

    const defaultTitle = t('meta.defaultTitle')
    const defaultDesc = t('meta.descHome')
    let title = defaultTitle
    let description = defaultDesc
    const canonicalHref =
      typeof window !== 'undefined'
        ? buildSelfCanonicalUrl(window.location.origin, window.location.pathname)
        : 'https://primedocumentsign.com/'
    let ogTitle
    let ogDescription
    let hreflangLogical = null

    if (view === 'blog-post' && blogSlug) {
      const post = BLOG_POSTS.find((p) => p.slug === blogSlug)
      hreflangLogical = `/blog/${blogSlug}`
      title = post ? formatBlogMetaTitle(post.title) : defaultTitle
      description = post?.description ?? t('meta.descBlog')
    } else if (view === 'blog') {
      hreflangLogical = '/blog'
      title = t('meta.titleBlog')
      description = t('meta.descBlog')
    } else if (view === 'email-activity') {
      title = t('meta.titleEmailActivity')
      description = t('meta.descEmailActivity')
    } else if (view === 'complete-subscription') {
      title = t('meta.titleCompleteSubscription')
      description = t('meta.descCompleteSubscription')
    } else if (view === 'app') {
      if (isRecipientView) {
        title = t('meta.titleRecipientSign')
        description = t('meta.descRecipientSign')
      } else {
        title = t('meta.titleApp')
        description = t('meta.descApp')
      }
    } else if (view === 'auth') {
      if (isRecipientView) {
        title = t('meta.titleRecipientSign')
        description = t('meta.descRecipientSign')
      } else {
        let rel = normalizeMarketingPathname(marketingPathname)
        if (rel === '/index.html') rel = '/'
        const authCanonical =
          rel === '/' || rel === '/pricing' || isSeoLandingPathname(rel) ? rel : '/'
        hreflangLogical =
          authCanonical === '/' || authCanonical === '/pricing' || isSeoLandingPathname(authCanonical)
            ? authCanonical
            : null
        const tk = getMetaTitleKeyForPath(marketingPathname)
        title = tk ? t(tk) : defaultTitle
        const dk = getMetaDescKeyForPath(marketingPathname)
        if (dk) description = t(dk)
        else if (authCanonical === '/pricing') description = t('meta.descPricing')
        else description = defaultDesc
        if (rel === '/') {
          ogTitle = t('meta.ogTitleHome')
          ogDescription = t('meta.ogDescHome')
        }
      }
    }

    setDocumentSeo({ title, description, canonicalUrl: canonicalHref, ogTitle, ogDescription })
    if (hreflangLogical) setHreflangAlternates(hreflangLogical)
    else clearHreflang()
  }, [view, isRecipientView, marketingPathname, blogSlug, t, language])

  const readFileAsDataUrl = useCallback((file) => {
    if (!file) return Promise.resolve(null)
    if (!(file instanceof File) && file.dataUrl) return Promise.resolve({ name: file.name, size: file.size, type: file.type, dataUrl: file.dataUrl })
    if (!(file instanceof File)) return Promise.resolve(null)
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve({ name: file.name, size: file.size, type: file.type, dataUrl: reader.result })
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }, [])

  const saveDocumentStateForPaymentReturn = useCallback(async (overrideUid) => {
    try {
      const currentAgreementHtml = (typeof editorRef.current?.innerHTML === 'string' ? editorRef.current.innerHTML : null) ?? agreementHtml
      const serializedAttachments = await Promise.all(attachments.map(readFileAsDataUrl)).then((arr) => arr.filter(Boolean))
      const state = {
        agreementHtml: currentAgreementHtml,
        scopeTitle,
        signers,
        customFields,
        recipientEmail,
        senderEmail,
        senderName,
        totalSignatures,
        logoDataUrl,
        logoLocation,
        fieldLayout,
        labelOverrides,
        excludedFromLayout,
        layoutColumns,
        includeDate,
        includeTerms,
        includeFullName,
        includePhone,
        includeSignature,
        recipientRequiresAuth,
        verifyPhoto,
        verifyEmail,
        verifyOTP,
        verifyPhone,
        fullPdfPages: fullPdfPages.map((p) => ({ imageDataUrl: p.imageDataUrl, overlayHtml: p.overlayHtml || '<p><br></p>', pageIndex: p.pageIndex })),
        attachments: serializedAttachments,
      }
      const serialized = JSON.stringify(state)
      sessionStorage.setItem(PAYMENT_RETURN_STORAGE_KEY, serialized)
      try {
        localStorage.setItem(PAYMENT_RETURN_LOCAL_KEY, serialized)
        const draftPayload = { ...state, savedAt: Date.now(), includeDate, includeTerms, includeFullName, includePhone, includeSignature }
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftPayload))
      } catch (_) {}
      // Persist to Firestore so state survives cross-origin redirect (e.g. .web.app → .com) on first return
      const uid = overrideUid ?? user?.uid
      if (uid) {
        try {
          await setDoc(doc(db, 'users', uid, 'appState', 'paymentReturn'), {
            state,
            savedAt: serverTimestamp(),
          })
        } catch (_) {}
      }
    } catch (_) {}
  }, [user?.uid, agreementHtml, scopeTitle, signers, customFields, recipientEmail, senderEmail, senderName, totalSignatures, logoDataUrl, logoLocation, fieldLayout, labelOverrides, excludedFromLayout, layoutColumns, includeDate, includeTerms, includeFullName, includePhone, includeSignature, recipientRequiresAuth, verifyPhoto, verifyEmail, verifyOTP, verifyPhone, fullPdfPages, attachments, readFileAsDataUrl])

  const redirectToOneOffPayment = useCallback(async () => {
    if (!user?.uid) return
    try {
      const createOneOff = httpsCallable(functions, 'createOneOffCheckoutSession')
      const { data } = await createOneOff({ returnOrigin: typeof window !== 'undefined' ? window.location.origin : '' })
      if (data?.url) {
        await saveDocumentStateForPaymentReturn()
        window.location.href = data.url
        return
      }
    } catch (e) {
      console.error('One-off checkout failed:', e)
    }
    if (premiumOneOff?.link) {
      await saveDocumentStateForPaymentReturn()
      window.location.href = getOneOffPaymentUrl(premiumOneOff.link, user.uid)
    }
  }, [user?.uid, premiumOneOff?.link, saveDocumentStateForPaymentReturn])

  useEffect(() => {
    try {
      let raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(PAYMENT_RETURN_STORAGE_KEY) : null
      if (!raw && typeof localStorage !== 'undefined') raw = localStorage.getItem(PAYMENT_RETURN_LOCAL_KEY)
      if (!raw) return
      const data = JSON.parse(raw)
      // Do not remove from storage here – clear only after editor applies (in ref callback) so first-time return keeps data if editor mounts late
      restoredFromPaymentReturnRef.current = true
      if (data.agreementHtml != null) {
        setAgreementHtml(data.agreementHtml)
        pendingEditorHtmlRef.current = data.agreementHtml
      }
      lastValidHtmlRef.current = data.agreementHtml || lastValidHtmlRef.current
      setDraftRestored(true)
      if (data.scopeTitle != null) setScopeTitle(data.scopeTitle)
      if (Array.isArray(data.signers) && data.signers.length > 0) setSigners(data.signers)
      if (Array.isArray(data.customFields)) setCustomFields(data.customFields)
      if (data.recipientEmail != null) setRecipientEmail(data.recipientEmail)
      if (data.senderEmail != null) setSenderEmail(data.senderEmail)
      if (data.senderName != null) setSenderName(data.senderName)
      if (typeof data.totalSignatures === 'number') setTotalSignatures(data.totalSignatures)
      if (data.logoDataUrl != null) setLogoDataUrl(data.logoDataUrl)
      if (data.logoLocation != null) setLogoLocation(data.logoLocation)
      if (data.fieldLayout != null) setFieldLayout(data.fieldLayout)
      if (data.labelOverrides != null) setLabelOverrides(data.labelOverrides)
      if (Array.isArray(data.excludedFromLayout)) setExcludedFromLayout(data.excludedFromLayout)
      if (typeof data.layoutColumns === 'number') setLayoutColumns(data.layoutColumns)
      if (typeof data.recipientRequiresAuth === 'boolean') setRecipientRequiresAuth(data.recipientRequiresAuth)
      if (typeof data.verifyPhoto === 'boolean') setVerifyPhoto(data.verifyPhoto)
      if (typeof data.verifyEmail === 'boolean') setVerifyEmail(data.verifyEmail)
      if (typeof data.verifyOTP === 'boolean') setVerifyOTP(data.verifyOTP)
      if (typeof data.verifyPhone === 'boolean') setVerifyPhone(data.verifyPhone)
      if (Array.isArray(data.fullPdfPages) && data.fullPdfPages.length > 0) setFullPdfPages(data.fullPdfPages)
      if (Array.isArray(data.attachments) && data.attachments.length > 0) setAttachments(data.attachments)
      setView('app')
      // Defer writing to editor so the contentEditable ref is mounted (payment return can run before app view has rendered the editor)
      const htmlToRestore = data.agreementHtml
      if (htmlToRestore) {
        const clearPaymentReturnStorage = () => {
          try {
            sessionStorage.removeItem(PAYMENT_RETURN_STORAGE_KEY)
            localStorage.removeItem(PAYMENT_RETURN_LOCAL_KEY)
          } catch (_) {}
        }
        const applyToEditor = () => {
          if (editorRef.current) {
            editorRef.current.innerHTML = htmlToRestore
            lastValidHtmlRef.current = htmlToRestore
            clearPaymentReturnStorage()
          }
        }
        applyToEditor()
        setTimeout(applyToEditor, 0)
        requestAnimationFrame(applyToEditor)
        setTimeout(applyToEditor, 100)
        setTimeout(applyToEditor, 350)
        setTimeout(applyToEditor, 600)
      }
    } catch (_) {}
  }, [])

  // Restore document state from Firestore when returning from Stripe but storage was empty (e.g. cross-origin redirect on first return)
  useEffect(() => {
    if (!user?.uid || restoredFromPaymentReturnRef.current) return
    const params = new URLSearchParams(window.location.search)
    const returningFromPayment = params.get('oneoff') === 'success' || params.get('payment') === 'success' || params.get('topup') === 'success' || params.get('checkout') === 'success' || params.get('portal') === 'return'
    if (!returningFromPayment) return
    let cancelled = false
    const run = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid, 'appState', 'paymentReturn'))
        if (cancelled || !snap.exists()) return
        const data = snap.data()
        const state = data?.state
        if (!state || typeof state !== 'object') return
        restoredFromPaymentReturnRef.current = true
        if (state.agreementHtml != null) {
          setAgreementHtml(state.agreementHtml)
          pendingEditorHtmlRef.current = state.agreementHtml
        }
        lastValidHtmlRef.current = state.agreementHtml || lastValidHtmlRef.current
        setDraftRestored(true)
        if (state.scopeTitle != null) setScopeTitle(state.scopeTitle)
        if (Array.isArray(state.signers) && state.signers.length > 0) setSigners(state.signers)
        if (Array.isArray(state.customFields)) setCustomFields(state.customFields)
        if (state.recipientEmail != null) setRecipientEmail(state.recipientEmail)
        if (state.senderEmail != null) setSenderEmail(state.senderEmail)
        if (state.senderName != null) setSenderName(state.senderName)
        if (typeof state.totalSignatures === 'number') setTotalSignatures(state.totalSignatures)
        if (state.logoDataUrl != null) setLogoDataUrl(state.logoDataUrl)
        if (state.logoLocation != null) setLogoLocation(state.logoLocation)
        if (state.fieldLayout != null) setFieldLayout(state.fieldLayout)
        if (state.labelOverrides != null) setLabelOverrides(state.labelOverrides)
        if (Array.isArray(state.excludedFromLayout)) setExcludedFromLayout(state.excludedFromLayout)
        if (typeof state.layoutColumns === 'number') setLayoutColumns(state.layoutColumns)
        if (typeof state.recipientRequiresAuth === 'boolean') setRecipientRequiresAuth(state.recipientRequiresAuth)
        if (typeof state.verifyPhoto === 'boolean') setVerifyPhoto(state.verifyPhoto)
        if (typeof state.verifyEmail === 'boolean') setVerifyEmail(state.verifyEmail)
        if (typeof state.verifyOTP === 'boolean') setVerifyOTP(state.verifyOTP)
        if (typeof state.verifyPhone === 'boolean') setVerifyPhone(state.verifyPhone)
        if (Array.isArray(state.fullPdfPages) && state.fullPdfPages.length > 0) setFullPdfPages(state.fullPdfPages)
        if (Array.isArray(state.attachments) && state.attachments.length > 0) setAttachments(state.attachments)
        setView('app')
        const htmlToRestore = state.agreementHtml
        if (htmlToRestore) {
          const applyToEditor = () => {
            if (editorRef.current) {
              editorRef.current.innerHTML = htmlToRestore
              lastValidHtmlRef.current = htmlToRestore
            }
          }
          applyToEditor()
          setTimeout(applyToEditor, 0)
          requestAnimationFrame(applyToEditor)
          setTimeout(applyToEditor, 50)
          setTimeout(applyToEditor, 200)
          setTimeout(applyToEditor, 400)
          setTimeout(applyToEditor, 700)
        }
        try {
          await deleteDoc(doc(db, 'users', user.uid, 'appState', 'paymentReturn'))
        } catch (_) {}
      } catch (_) {}
    }
    run()
    return () => { cancelled = true }
  }, [user?.uid])

  useEffect(() => {
    if (authLoading) return
    if (user && !userProfileLoaded) return
    const params = new URLSearchParams(window.location.search)
    const wantsEmailActivity = params.get('view') === 'email-activity'
    const wantsAuthView = params.get('view') === 'auth'
    const returningFromPayment = params.get('oneoff') === 'success' || params.get('payment') === 'success' || params.get('topup') === 'success' || params.get('checkout') === 'success' || params.get('portal') === 'return'
    const hashDocPage = typeof window !== 'undefined' && (window.location.hash || '').replace(/^#/, '').toLowerCase() === 'document-page'
    const pathname = toLogicalPathname(typeof window !== 'undefined' ? window.location.pathname : '/')
    const blogPath = parseBlogPath(pathname)
    if (wantsAuthView) {
      setView('auth')
    } else if (wantsEmailActivity && user) {
      setView('email-activity')
    } else if (hasSigningLinkId) {
      // Recipients with a signing link can always access the document to sign — no subscription or account required
      setView('app')
    } else if (skipLogin || returningFromPayment) {
      // Keep app view when returning from Stripe so we can restore agreement text (from storage or Firestore)
      setView('app')
    } else if (hashDocPage && !user && !hasSigningLinkId) {
      // Landing with #document-page (e.g. from Google Ads) – show document page, track as no-account visit
      setIsQuickSend(true)
      setView('app')
    } else if (blogPath && !hasSigningLinkId) {
      if (blogPath.mode === 'list') {
        setBlogSlug(null)
        setView('blog')
      } else {
        const post = BLOG_POSTS.find((p) => p.slug === blogPath.slug)
        if (post) {
          setBlogSlug(blogPath.slug)
          setView('blog-post')
        } else {
          const redirLogical = BLOG_SLUG_REDIRECTS[blogPath.slug]
          if (redirLogical) {
            const { locale } = parseLocalePathname(window.location.pathname)
            const url = buildLocalizedPath(redirLogical, locale)
            const qs = window.location.search || ''
            const hash = window.location.hash || ''
            window.history.replaceState({}, '', `${url}${qs}${hash}`)
            setMarketingPathname(redirLogical)
            setBlogSlug(null)
            setView('auth')
          } else {
            setBlogSlug(null)
            setView('blog')
          }
        }
      }
    } else if (user) {
      // Always show the document; paywall/subscription prompts only when they try to send (handleSendEmail)
      setView('app')
    } else {
      setView('auth')
    }
  }, [user, authLoading, userProfileLoaded, skipLogin, hasSigningLinkId, isOneTimeUse, userSubscriptionId, userCustomerId, marketingPathname])

  useEffect(() => {
    if (view === 'auth' && window.location.hash === '#faq') {
      const scroll = () => {
        const el = document.getElementById('faq')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      const t = setTimeout(scroll, 150)
      return () => clearTimeout(t)
    }
  }, [view])

  useLayoutEffect(() => {
    if (view !== 'auth' || isRecipientView) {
      setLandingHeroCtaPulsePhase(null)
      return undefined
    }
    /* 5s free-send bounce → swap → 5s Watch Demo bounce, ×3 (30s), then default: Watch Demo large. */
    setLandingHeroCtaPulsePhase(1)
    const intervalId = window.setInterval(() => {
      setLandingHeroCtaPulsePhase((p) => (p === 0 ? 1 : 0))
    }, 5000)
    const endId = window.setTimeout(() => {
      window.clearInterval(intervalId)
      setLandingHeroCtaPulsePhase(null)
    }, 30000)
    return () => {
      window.clearInterval(intervalId)
      window.clearTimeout(endId)
    }
  }, [view, isRecipientView])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (highlightRef.current && !highlightRef.current.contains(e.target)) {
        setIsHighlightOpen(false)
      }
      if (textColorRef.current && !textColorRef.current.contains(e.target)) {
        setIsTextColorOpen(false)
      }
    }
    if (isHighlightOpen || isTextColorOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isHighlightOpen, isTextColorOpen])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const oneoff = params.get('oneoff') || params.get('payment')
    const topup = params.get('topup')
    if (oneoff === 'success' || oneoff === 'oneoff' || topup === 'success') {
      setView('app')
    }
  }, [])


  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const checkout = params.get('checkout')
    const portal = params.get('portal')
    const oneoff = params.get('oneoff') || params.get('payment')
    const topup = params.get('topup')
    const topupSessionId = params.get('session_id')
    if ((oneoff === 'success' || oneoff === 'oneoff') && user) {
      window.history.replaceState({}, '', window.location.pathname + (window.location.hash || ''))
      setView('app')
      const showOneOffSuccess = async () => {
        // Refetch credits once immediately (webhook may have already run)
        try {
          const immediateSnap = await getDocFromServer(doc(db, 'users', user.uid))
          if (immediateSnap.exists()) {
            const c = Math.max(0, immediateSnap.data()?.emailOneOffCredits ?? 0)
            if (c > 0) {
              setEmailOneOffCredits(c)
              const oneTimePrice = tiers.premium?.premiumOneOff?.price ?? appConfig.oneTimePrice ?? 8
              const isQuickSendReturn = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('quickSendPending') === '1'
              if (isQuickSendReturn) sessionStorage.removeItem('quickSendPending')
              const returnedToDoc = restoredFromPaymentReturnRef.current
              setPurchaseSuccessData({
                type: 'oneoff',
                tierName: 'One-time purchase',
                billing: '',
                price: `$${oneTimePrice}`,
                credits: c,
                validity: returnedToDoc
                  ? `Returned to your document. You have ${c} credit${c !== 1 ? 's' : ''}, click Send to deliver.`
                  : isQuickSendReturn
                    ? 'Payment successful! Switch to your document tab and click Send again.'
                    : `You have ${c} email credit${c !== 1 ? 's' : ''} to use. You can now send documents for signature.`,
              })
              return
            }
          }
        } catch (_) { /* non-blocking */ }
        await new Promise((r) => setTimeout(r, 2500))
        for (let i = 0; i < 30; i++) {
          try {
            const snap = await getDocFromServer(doc(db, 'users', user.uid))
            if (snap.exists()) {
              const data = snap.data()
              const credits = Math.max(0, data.emailOneOffCredits ?? 0)
              setEmailOneOffCredits(credits)
              const oneTimePrice = tiers.premium?.premiumOneOff?.price ?? appConfig.oneTimePrice ?? 8
              const isQuickSendReturn = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('quickSendPending') === '1'
              if (isQuickSendReturn) sessionStorage.removeItem('quickSendPending')
              const returnedToDoc = restoredFromPaymentReturnRef.current
              setPurchaseSuccessData({
                type: 'oneoff',
                tierName: 'One-time purchase',
                billing: '',
                price: `$${oneTimePrice}`,
                credits,
                validity: returnedToDoc
                  ? (credits > 0 ? `Returned to your document. You have ${credits} credit${credits !== 1 ? 's' : ''}, click Send to deliver.` : 'Returned to your document. Your credit will appear in a moment, the page will update automatically.')
                  : isQuickSendReturn
                    ? 'Payment successful! Switch to your document tab and click Send again.'
                    : (credits > 0 ? `You have ${credits} email credit${credits !== 1 ? 's' : ''} to use. You can now send documents for signature.` : 'Payment successful. Your credit will appear in a moment, the page will update automatically.'),
              })
              return
            }
          } catch (_) { /* retry */ }
          await new Promise((r) => setTimeout(r, 800))
        }
        const oneTimePrice = tiers.premium?.premiumOneOff?.price ?? appConfig.oneTimePrice ?? 8
        const returnedToDoc = restoredFromPaymentReturnRef.current
        const finalSnap = await getDocFromServer(doc(db, 'users', user.uid))
        const creditsNow = finalSnap.exists ? (finalSnap.data()?.emailOneOffCredits ?? 0) : 0
        setEmailOneOffCredits(Math.max(0, creditsNow))
        setPurchaseSuccessData({
          type: 'oneoff',
          tierName: 'One-time purchase',
          billing: '',
          price: `$${oneTimePrice}`,
          credits: creditsNow,
          validity: returnedToDoc
            ? (creditsNow > 0 ? `Returned to your document. You have ${creditsNow} credit${creditsNow !== 1 ? 's' : ''}, click Send to deliver.` : 'Returned to your document. Your credit will appear in a moment, the page will update automatically.')
            : (creditsNow > 0 ? `You have ${creditsNow} credit${creditsNow !== 1 ? 's' : ''}. Click Send to deliver.` : 'Payment successful! Your credit will appear in a moment, the page will update automatically.'),
        })
      }
      showOneOffSuccess()
      return
    }
    if (topup === 'success' && user) {
      window.history.replaceState({}, '', window.location.pathname + (window.location.hash || ''))
      setView('app')
      const showTopUpSuccess = async () => {
        let confirmedTierKey = (subscriptionTier || subscriptionSavedTier || 'standard').toLowerCase()
        let balances = null
        try {
          if (topupSessionId) {
            const confirmTopUp = httpsCallable(functions, 'confirmSubscriberTopUpCheckoutSession')
            const { data } = await confirmTopUp({ sessionId: topupSessionId })
            confirmedTierKey = (data?.tierId || confirmedTierKey).toLowerCase()
            if (typeof data?.balances?.emails === 'number' && typeof data?.balances?.signed === 'number') {
              balances = {
                emails: Math.max(0, data.balances.emails),
                signed: Math.max(0, data.balances.signed),
              }
              setUserUsage((prev) => ({
                ...prev,
                subscribedEmailTopUpCredit: balances.emails,
                subscribedSignedAgreementTopUpCredit: balances.signed,
              }))
            }
          }
        } catch (e) {
          console.error('Top-up confirmation failed:', e)
        }

        if (!balances) {
          for (let i = 0; i < 12; i++) {
            try {
              const snap = await getDocFromServer(doc(db, 'users', user.uid))
              if (snap.exists()) {
                const sub = snap.data()?.subscription || {}
                balances = {
                  emails: Math.max(0, sub.SubscribedEmailTopUpCredit ?? 0),
                  signed: Math.max(0, sub.SubscribedSignedAgreementTopUpCredit ?? 0),
                }
                setUserUsage((prev) => ({
                  ...prev,
                  subscribedEmailTopUpCredit: balances.emails,
                  subscribedSignedAgreementTopUpCredit: balances.signed,
                }))
                break
              }
            } catch (_) {}
            await new Promise((r) => setTimeout(r, i === 0 ? 900 : 700))
          }
        }

        const topUp = SUBSCRIBER_TOPUP_BY_TIER[confirmedTierKey] || SUBSCRIBER_TOPUP_BY_TIER.standard
        const returnedToDoc = restoredFromPaymentReturnRef.current
        const balanceMessage = balances
          ? `You now have ${balances.emails} email send${balances.emails !== 1 ? 's' : ''} and ${balances.signed} signed agreement${balances.signed !== 1 ? 's' : ''} in top-up credits.`
          : 'Your top-up credits are being applied now. The page will update automatically as soon as Stripe confirms the payment.'

        setPurchaseSuccessData({
          type: 'topup',
          tierName: topUp.label,
          details: `+${topUp.emails} emails, +${topUp.signed} signed agreements`,
          price: `$${topUp.price}`,
          validity: returnedToDoc ? `Returned to your document. ${balanceMessage}` : balanceMessage,
        })
      }
      showTopUpSuccess()
      return
    }
    if ((checkout === 'success' || portal === 'return') && user) {
      window.history.replaceState({}, '', window.location.pathname + (window.location.hash || ''))
      const priceMap = {
        basic: { monthly: `$${(tiers.basic?.Stripe ?? tiers.basic?.stripe ?? []).find((s) => (s.type || '').toLowerCase() === 'monthly')?.price ?? 5}/mo`, annual: `$${(tiers.basic?.Stripe ?? tiers.basic?.stripe ?? []).find((s) => (s.type || '').toLowerCase() === 'annual')?.price ?? 24}/yr` },
        standard: { monthly: `$${(tiers.standard?.Stripe ?? tiers.standard?.stripe ?? []).find((s) => (s.type || '').toLowerCase() === 'monthly')?.price ?? 9}/mo`, annual: `$${(tiers.standard?.Stripe ?? tiers.standard?.stripe ?? []).find((s) => (s.type || '').toLowerCase() === 'annual')?.price ?? 40}/yr` },
        premium: { monthly: `$${(tiers.premium?.Stripe ?? tiers.premium?.stripe ?? []).find((s) => (s.type || '').toLowerCase() === 'monthly')?.price ?? 16}/mo`, annual: `$${(tiers.premium?.Stripe ?? tiers.premium?.stripe ?? []).find((s) => (s.type || '').toLowerCase() === 'annual')?.price ?? 69}/yr` },
      }
      const showSuccessFromUserDoc = async () => {
        // Sync from Stripe first (portal return) so app matches Stripe
        if (portal === 'return') {
          try {
            const sync = httpsCallable(functions, 'syncSubscriptionFromStripe')
            await sync({})
          } catch (_) { /* non-blocking */ }
        }
        // Wait for webhook/sync to update Firestore
        await new Promise((r) => setTimeout(r, 1500))
        for (let i = 0; i < 12; i++) {
          try {
            const snap = await getDocFromServer(doc(db, 'users', user.uid))
            if (snap.exists()) {
              const data = snap.data()
              const tier = (data.subscriptionTier || 'premium').toLowerCase()
              const billingCycle = (data.billingCycle || 'yearly').toLowerCase()
              const isAnnual = billingCycle === 'yearly' || billingCycle === 'annual'
              const billingNow = isAnnual ? 'annual' : 'monthly'

              // Portal return: only show popup if subscription actually changed
              if (portal === 'return') {
                try {
                  const preTier = (sessionStorage.getItem('portal_pre_tier') || '').toLowerCase()
                  const preBilling = sessionStorage.getItem('portal_pre_billing') || 'monthly'
                  sessionStorage.removeItem('portal_pre_tier')
                  sessionStorage.removeItem('portal_pre_billing')
                  const tierChanged = preTier && tier !== preTier
                  const billingChanged = preBilling && billingNow !== preBilling
                  if (!tierChanged && !billingChanged) return // no change, don't show popup
                } catch (_) { /* show popup if we can't compare */ }
              }

              const tierDisplay = tier.charAt(0).toUpperCase() + tier.slice(1)
              const price = (priceMap[tier] || priceMap.premium)[isAnnual ? 'annual' : 'monthly']
              setPurchaseSuccessData({
                type: 'subscription',
                tierName: tierDisplay,
                billing: isAnnual ? 'billed annually' : 'billed monthly',
                price,
                validity: isAnnual ? 'Your plan renews yearly.' : 'Your plan renews monthly.',
              })
              return
            }
          } catch (_) { /* network error, retry */ }
          await new Promise((r) => setTimeout(r, 700))
        }
        setPurchaseSuccessData({
          type: 'subscription',
          tierName: 'Subscription',
          billing: '',
          price: '',
          validity: 'Your payment was successful. Your plan is now active.',
        })
      }
      showSuccessFromUserDoc()
    }
    // Admin migration triggers removed for security (run via Firebase Console or scripts)
  }, [user, tiers, appConfig, subscriptionTier, subscriptionSavedTier])

  useEffect(() => {
    if (!user?.uid || !purchaseSuccessData || purchaseSuccessData.type !== 'oneoff') return
    const credits = purchaseSuccessData.credits ?? 0
    if (credits > 0) return
    let cancelled = false
    const pollCredits = async () => {
      for (let i = 0; i < 15 && !cancelled; i++) {
        await new Promise((r) => setTimeout(r, 2000))
        if (cancelled) return
        try {
          const snap = await getDocFromServer(doc(db, 'users', user.uid))
          if (snap.exists()) {
            const c = Math.max(0, snap.data()?.emailOneOffCredits ?? 0)
            if (c > 0) {
              setEmailOneOffCredits(c)
              setPurchaseSuccessData((prev) => prev ? { ...prev, credits: c, validity: `You have ${c} credit${c !== 1 ? 's' : ''}, click Send to deliver.` } : prev)
              return
            }
          }
        } catch (_) {}
      }
    }
    pollCredits()
    return () => { cancelled = true }
  }, [user?.uid, purchaseSuccessData?.type, purchaseSuccessData?.credits])

  // After sign-in from Send/Copy without account: land on the document only — no subscription modal.
  // Users see the free-send banner; subscribe modal opens only from explicit buttons or Send when no free send / at limits.
  useEffect(() => {
    if (!user?.uid || user.isAnonymous || !openPaywallAfterAuth) return
    setOpenPaywallAfterAuth(null)
  }, [user?.uid, user?.isAnonymous, openPaywallAfterAuth])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const shortId = params.get('id')

    const loadFromTemporaryLink = async (id) => {
      try {
        const getSigningLinkData = httpsCallable(functions, 'getSigningLinkData')
        const res = await getSigningLinkData({ shortId: id })
        const { exists, data: resData } = res.data || {}
        if (!exists || !resData) return false
        const data = resData

        if (data.expired || data.agreementHtml === 'expired') {
          setIsLinkExpired(true)
          setIsRecipientView(true)
          setSkipLogin(true)
          return true
        }

        setScopeTitle(data.scopeTitle || 'Project Scope')
        setAgreementHtml(data.agreementHtml || '')
        lastValidHtmlRef.current = data.agreementHtml || ''
        if (editorRef.current) {
          editorRef.current.innerHTML = data.agreementHtml || ''
        }
        if (data.logoDataUrl) setLogoDataUrl(data.logoDataUrl)
        if (data.logoLocation) setLogoLocation(data.logoLocation)
        if (data.totalSignatures !== undefined) setTotalSignatures(Math.max(1, Math.min(2, data.totalSignatures)))
        if (Array.isArray(data.customFields) && data.customFields.length > 0) setCustomFields(data.customFields)
        if (data.layoutColumns !== undefined) setLayoutColumns(Math.max(1, Math.min(2, data.layoutColumns)))
        if (data.fieldLayout && Array.isArray(data.fieldLayout) && data.fieldLayout.length > 0) setFieldLayout(data.fieldLayout)
        if (data.labelOverrides && typeof data.labelOverrides === 'object') setLabelOverrides(data.labelOverrides)
        if (Array.isArray(data.excludedFromLayout)) setExcludedFromLayout(data.excludedFromLayout)
        if (data.recipientEmail) setRecipientEmail(data.recipientEmail)
        if (data.docId) setCurrentDocId(data.docId)
        if (data.ownerId) setDocOwnerId(data.ownerId)
        if (data.fullPdfPageData?.length > 0) {
          const pages = data.fullPdfPageData.map((imageDataUrl, i) => ({
            imageDataUrl,
            overlayHtml: '<p><br></p>',
            pageIndex: i + 1,
          }))
          setFullPdfPages(pages)
        } else if (data.fullPdfPagePaths?.length > 0) {
          const pages = []
          for (let i = 0; i < data.fullPdfPagePaths.length; i++) {
            const path = data.fullPdfPagePaths[i]
            const storageRef = ref(storage, path)
            const downloadUrl = await getDownloadURL(storageRef)
            pages.push({ imageDataUrl: downloadUrl, storagePath: path, overlayHtml: '<p><br></p>', pageIndex: i + 1 })
          }
          setFullPdfPages(pages)
        }
        if (data.signers && Array.isArray(data.signers) && data.signers.length > 0) {
          const total = Math.max(1, Math.min(2, data.totalSignatures || data.signers.length))
          const loaded = data.signers.slice(0, total).map((s) => ({
            date: s.date || '',
            agreed: !!s.agreed,
            fullName: s.fullName || '',
            signatureDataUrl: s.signatureDataUrl || '',
            phone: s.phone || '',
            customFields: s.customFields || {},
          }))
          while (loaded.length < total) {
            loaded.push({ date: '', agreed: false, fullName: '', signatureDataUrl: '', phone: '', customFields: {} })
          }
          setSigners(loaded)
        }
        if (data.signedAt) {
          setIsSignedComplete(true)
          setSignedAtDisplay(data.signedAtIso || data.signedAt?.toDate?.()?.toISOString() || '')
        }
        const v = data.verification || {}
        const needsAuth = !!(v.verifyPhoto || v.verifyEmail || v.verifyOTP || v.verifyPhone)
        setRecipientRequiresAuth(needsAuth)
        setRecipientAuthCompleted(!needsAuth)
        setRecipientAuthError('')
        setIsRecipientView(true)
        setSkipLogin(true)
        return true
      } catch (err) {
        console.error('Failed to load signing link data:', err)
        return false
      }
    }

    if (shortId) {
      setSkipLogin(true)
      loadFromTemporaryLink(shortId).then((loaded) => {
        setSigningLinkLoading(false)
        if (!loaded) {
          setIsRecipientView(false)
        }
      })
      return
    }

    const recipient = params.get('recipient')
    const docIdParam = params.get('docId')
    if (docIdParam) setCurrentDocId(docIdParam)
    const ownerIdParam = params.get('ownerId')
    if (ownerIdParam) setDocOwnerId(ownerIdParam)
    const scopeParam = params.get('scope')
    const agreementParam = params.get('agreement')
    const logoParam = params.get('logo')
    if (logoParam) {
      try {
        const decoded = decodeURIComponent(logoParam.replace(/ /g, '+'))
        setLogoDataUrl(atob(decoded))
      } catch (_) {}
    }
    const logoLoc = params.get('logoLocation')
    if (logoLoc && ['top-left','top-center','top-right','bottom-left','bottom-center','bottom-right'].includes(logoLoc)) {
      setLogoLocation(logoLoc)
    }
    const incDate = params.get('includeDate')
    const incTerms = params.get('includeTerms')
    const incFullName = params.get('includeFullName')
    const incSignature = params.get('includeSignature')
    const totalSig = params.get('totalSignatures')
    if (totalSig !== null) {
      const n = parseInt(totalSig, 10)
      if (!isNaN(n) && n >= 1 && n <= 2) setTotalSignatures(n)
    }
    if (recipient) {
      setRecipientEmail(recipient)
      setIsRecipientView(true)
    }
    if (scopeParam) {
      try {
        setScopeTitle(decodeURIComponent(scopeParam))
      } catch (error) {
        setScopeTitle(scopeParam)
      }
    }
    if (agreementParam) {
      try {
        const decoded = decodeURIComponent(agreementParam)
        const html = decodeURIComponent(escape(atob(decoded)))
        setAgreementHtml(html)
        if (editorRef.current) {
          editorRef.current.innerHTML = html
          lastValidHtmlRef.current = html
        }
      } catch (error) {
        setAgreementHtml(agreementParam)
      }
    }
  }, [])

  const [draftRestored, setDraftRestored] = useState(false)
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false)
  const [isSendConfirmOpen, setIsSendConfirmOpen] = useState(false)
  const [isStartFromTemplateOpen, setIsStartFromTemplateOpen] = useState(false)
  const [templateReplaceConfirmOpen, setTemplateReplaceConfirmOpen] = useState(false)
  const [pendingStarterTemplate, setPendingStarterTemplate] = useState(null)
  const [confirmDeleteState, setConfirmDeleteState] = useState(null) // { title, message, confirmLabel, onConfirm }

  // Lock body scroll when any modal/popup is open so only the modal content scrolls (and background never moves on mobile)
  const anyModalOpen = isLayoutEditorOpen || isSignatureOpen || isLinkModalOpen || isAuthModalOpen || isEmailExistsModalOpen || isUpgradeModalOpen || isAccountOpen || isSubscriptionOpen || isAddFieldOpen || !!editFieldTargetId || isLogoutOpen || isSaveTemplateOpen || isHistoryOpen || isContactSupportOpen || isTermsOpen || isPrivacyOpen || charLimitReachedOpen || signingLimitReachedOpen || isLimitReachedOpen || isSendConfirmOpen || isPreviewOpen || isClearConfirmOpen || isStartFromTemplateOpen || templateReplaceConfirmOpen || isPaywallOpen || isSubscribeOrPayOpen || isQuickSendPaymentOpen || stepZoomIndex != null
  useEffect(() => {
    if (anyModalOpen) {
      const scrollY = window.scrollY ?? document.documentElement.scrollTop
      document.body.classList.add('modal-open')
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.left = '0'
      document.body.style.right = '0'
      document.body.style.width = '100%'
      return () => {
        document.body.classList.remove('modal-open')
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.left = ''
        document.body.style.right = ''
        document.body.style.width = ''
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, scrollY)
            /* Android Chrome: body position:fixed unlock sometimes leaves scroll position wrong on documentElement */
            if (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || '')) {
              document.documentElement.scrollTop = scrollY
              document.body.scrollTop = scrollY
            }
          })
        })
      }
    }
  }, [anyModalOpen])

  // Subscribe-or-pay modal: always open on annual billing (encourage yearly selection)
  useEffect(() => {
    if (isSubscribeOrPayOpen) setBillingCycle('yearly')
  }, [isSubscribeOrPayOpen])

  // If loading (auth, signing link, or profile) lasts too long, show "please refresh" to escape the loop
  const isInLoadingState = authLoading || (hasSigningLinkId && signingLinkLoading) || (user && !userProfileLoaded)
  useEffect(() => {
    if (!isInLoadingState) {
      setShowStaleSessionMessage(false)
      return
    }
    const timer = setTimeout(() => setShowStaleSessionMessage(true), 18000)
    return () => clearTimeout(timer)
  }, [isInLoadingState])

  // Restore draft from localStorage (24h expiry) when no URL params
  useEffect(() => {
    if (signingLinkLoading) return
    if (restoredFromPaymentReturnRef.current) return
    const params = new URLSearchParams(window.location.search)
    const shortId = params.get('id')
    if (shortId || isRecipientView) return
    if (params.get('scope') || params.get('agreement') || params.get('recipient')) return
    try {
      const raw = localStorage.getItem(DRAFT_STORAGE_KEY)
      if (!raw) return
      const data = JSON.parse(raw)
      if (!data.savedAt || Date.now() - data.savedAt > appConfig.draftExpiryMs) return
      if (data.scopeTitle) setScopeTitle(data.scopeTitle)
      if (data.agreementHtml) {
        setAgreementHtml(data.agreementHtml)
        lastValidHtmlRef.current = data.agreementHtml
        pendingEditorHtmlRef.current = data.agreementHtml
        if (editorRef.current) {
          editorRef.current.innerHTML = data.agreementHtml
          pendingEditorHtmlRef.current = null
        }
      }
      if (data.recipientEmail) setRecipientEmail(data.recipientEmail)
      if (data.senderName) setSenderName(data.senderName)
      if (data.senderEmail) setSenderEmail(data.senderEmail)
      if (typeof data.recipientRequiresAuth === 'boolean') setRecipientRequiresAuth(data.recipientRequiresAuth)
      if (typeof data.verifyPhoto === 'boolean') setVerifyPhoto(data.verifyPhoto)
      if (typeof data.verifyEmail === 'boolean') setVerifyEmail(data.verifyEmail)
      if (typeof data.verifyOTP === 'boolean') setVerifyOTP(data.verifyOTP)
      if (typeof data.verifyPhone === 'boolean') setVerifyPhone(data.verifyPhone)
      // Include toggles are always true; skip restoring them
      if (data.totalSignatures) setTotalSignatures(data.totalSignatures)
      // Custom fields & layout
      if (Array.isArray(data.customFields) && data.customFields.length > 0) setCustomFields(data.customFields)
      if (data.fieldLayout) setFieldLayout(data.fieldLayout)
      if (data.labelOverrides && typeof data.labelOverrides === 'object') setLabelOverrides(data.labelOverrides)
      if (Array.isArray(data.excludedFromLayout)) setExcludedFromLayout(data.excludedFromLayout)
      if (data.layoutColumns) setLayoutColumns(data.layoutColumns)
      // Signers (values entered in signature fields) – restore after sync
      if (Array.isArray(data.signers) && data.signers.length > 0) {
        draftSignersToRestoreRef.current = data.signers
      }
      if (Array.isArray(data.fullPdfPages) && data.fullPdfPages.length > 0) setFullPdfPages(data.fullPdfPages)
      if (Array.isArray(data.attachments) && data.attachments.length > 0) setAttachments(data.attachments)
      if (data.logoDataUrl != null) setLogoDataUrl(data.logoDataUrl)
      if (data.logoLocation != null) setLogoLocation(data.logoLocation)
      if (typeof data.emailReminder1d === 'boolean') setEmailReminder1d(data.emailReminder1d)
      if (typeof data.emailReminder3d === 'boolean') setEmailReminder3d(data.emailReminder3d)
      if (typeof data.emailReminder7d === 'boolean') setEmailReminder7d(data.emailReminder7d)
      setDraftRestored(true)
    } catch (e) {
      console.error('Restore draft failed:', e)
    }
  }, [signingLinkLoading, isRecipientView])

  // Debounced save to localStorage (24h expiry) – on field change
  const draftSaveTimeoutRef = useRef(null)
  const draftSignersToRestoreRef = useRef(null)
  const saveDraft = useCallback(async () => {
    if (isRecipientView || view !== 'app') return
    try {
      const html = editorRef.current?.innerHTML ?? agreementHtml
      const serializedAttachments = await Promise.all(attachments.map(readFileAsDataUrl)).then((arr) => arr.filter(Boolean))
      const payload = {
        scopeTitle,
        agreementHtml: html,
        recipientEmail,
        senderName,
        senderEmail,
        includeDate,
        includeTerms,
        includeFullName,
        includePhone,
        includeSignature,
        totalSignatures,
        customFields,
        fieldLayout,
        labelOverrides,
        excludedFromLayout,
        layoutColumns,
        signers,
        fullPdfPages,
        logoDataUrl,
        logoLocation,
        attachments: serializedAttachments,
        emailReminder1d,
        emailReminder3d,
        emailReminder7d,
        savedAt: Date.now(),
      }
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(payload))
    } catch (e) {
      console.error('Save draft failed:', e)
    }
  }, [scopeTitle, agreementHtml, recipientEmail, senderName, senderEmail, includeDate, includeTerms, includeFullName, includePhone, includeSignature, totalSignatures, customFields, fieldLayout, labelOverrides, excludedFromLayout, layoutColumns, signers, fullPdfPages, logoDataUrl, logoLocation, attachments, readFileAsDataUrl, isRecipientView, view, emailReminder1d, emailReminder3d, emailReminder7d])
  useEffect(() => {
    if (isRecipientView) return
    if (draftSaveTimeoutRef.current) clearTimeout(draftSaveTimeoutRef.current)
    draftSaveTimeoutRef.current = setTimeout(() => { saveDraft().catch(() => {}) }, 800)
    return () => {
      if (draftSaveTimeoutRef.current) clearTimeout(draftSaveTimeoutRef.current)
    }
  }, [scopeTitle, agreementHtml, recipientEmail, senderName, senderEmail, includeDate, includeTerms, includeFullName, includePhone, includeSignature, totalSignatures, customFields, fieldLayout, labelOverrides, excludedFromLayout, layoutColumns, signers, fullPdfPages, logoDataUrl, logoLocation, attachments, isRecipientView, saveDraft, emailReminder1d, emailReminder3d, emailReminder7d])
  // Periodic save to capture editor content (contentEditable doesn't trigger state)
  useEffect(() => {
    if (isRecipientView) return
    const id = setInterval(() => { saveDraft().catch(() => {}) }, 4000)
    return () => clearInterval(id)
  }, [isRecipientView, saveDraft])

  const handleClearDraft = useCallback(() => {
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY)
    } catch (_) {}
    setScopeTitle('Project Scope')
    setAgreementHtml('')
    setRecipientEmail('')
    setSenderName('')
    setSenderEmail('')
    lastValidHtmlRef.current = ''
    if (editorRef.current) editorRef.current.innerHTML = ''
    setSigners([{ date: '', agreed: false, fullName: '', signatureDataUrl: '', phone: '', customFields: {} }])
    setCustomFields([])
    setTotalSignatures(1)
    setActiveTemplateId(null)
    setAttachments([])
    setFullPdfPages([])
    setLogoDataUrl(null)
    setLogoLocation('bottom-center')
    setFieldLayout(null)
    setLabelOverrides({})
    setExcludedFromLayout([])
    setLayoutColumns(1)
    setEmailReminder1d(false)
    setEmailReminder3d(false)
    setEmailReminder7d(false)
  }, [])

  useEffect(() => {
    const toRestore = draftSignersToRestoreRef.current
    if (toRestore && Array.isArray(toRestore) && toRestore.length > 0) {
      draftSignersToRestoreRef.current = null
      setSigners((prev) => {
        const n = Math.max(1, Math.min(2, totalSignatures))
        const restored = toRestore.slice(0, n)
        const emptyCfForSigner = (signerIdx) =>
          customFields
            .filter((cf) => (cf.addedToSigners ?? [0, 1]).includes(signerIdx))
            .reduce((acc, cf) => ({ ...acc, [cf.id]: cf.type === 'checkbox' ? false : '' }), {})
        return restored.map((s, idx) => ({
          date: s.date ?? '',
          agreed: s.agreed ?? false,
          fullName: s.fullName ?? '',
          signatureDataUrl: s.signatureDataUrl ?? '',
          phone: s.phone ?? '',
          customFields: { ...emptyCfForSigner(idx), ...(s.customFields || {}) },
        }))
      })
      return
    }
    setSigners((prev) => {
      const n = Math.max(1, Math.min(2, totalSignatures))
      const next = prev.map((s) => ({ ...s }))
      const emptyCfForSigner = (signerIdx) =>
        customFields
          .filter((cf) => (cf.addedToSigners ?? [0, 1]).includes(signerIdx))
          .reduce((acc, cf) => ({ ...acc, [cf.id]: cf.type === 'checkbox' ? false : '' }), {})
      while (next.length < n) {
        const signerIdx = next.length
        next.push({ date: '', agreed: false, fullName: '', signatureDataUrl: '', phone: '', customFields: { ...emptyCfForSigner(signerIdx) } })
      }
      return next.slice(0, n).map((s, idx) => ({
        ...s,
        customFields: { ...emptyCfForSigner(idx), ...(s.customFields || {}) },
      }))
    })
  }, [totalSignatures, customFields])

  const normalizeEvent = (data, id) => {
        const createdAtMs = data.createdAt?.toMillis?.() ?? (data.createdAt?.seconds ?? 0) * 1000
        const sentAtMs = data.sentAt?.toMillis?.() ?? (data.sentAt?.seconds ?? 0) * 1000
        const reminderDelivered = Array.isArray(data.emailRemindersDelivered)
          ? data.emailRemindersDelivered.map((row) => {
              const s = row?.sentAt
              let sentAtIso = null
              if (s && typeof s.toDate === 'function') sentAtIso = s.toDate().toISOString()
              else if (s && typeof s.toMillis === 'function') sentAtIso = new Date(s.toMillis()).toISOString()
              else if (typeof s === 'string' && s) sentAtIso = s
              else if (s && typeof s.seconds === 'number') sentAtIso = new Date(s.seconds * 1000 + (s.nanoseconds || 0) / 1e6).toISOString()
              return { ...row, sentAt: sentAtIso }
            })
          : []
        return {
      id: id || data.id,
          ...data,
          createdAtMs,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : (data.createdAt || ''),
          sentAt: data.sentAt?.toDate ? data.sentAt.toDate().toISOString() : (data.sentAt || null),
          sentAtMs: sentAtMs || null,
          recipientEmails: Array.isArray(data.recipientEmails) ? data.recipientEmails : [],
          signers: Array.isArray(data.signers) ? data.signers : [],
          emailRemindersDelivered: reminderDelivered,
        }
  }

  const fetchEmailEvents = useCallback(async () => {
    if (!user?.uid) return
    setEmailActivityLoading(true)
    try {
      const [eventsSnap, expiredSnap] = await Promise.all([
        getDocs(collection(db, 'users', user.uid, 'emailEvents')),
        getDocs(collection(db, 'users', user.uid, 'emailEventsExpiredDocs')),
      ])
      const fromEvents = eventsSnap.docs.map((d) => normalizeEvent(d.data(), d.id))
      const fromExpired = []
      expiredSnap.docs
        .sort((a, b) => {
          const aId = parseInt(a.id, 10)
          const bId = parseInt(b.id, 10)
          return (Number.isNaN(aId) ? 0 : aId) - (Number.isNaN(bId) ? 0 : bId)
        })
        .forEach((batchDoc) => {
          const batch = batchDoc.data()
          const events = batch.expiredEvents || []
          events.forEach((ev) => fromExpired.push(normalizeEvent(ev, ev.id)))
        })
      const list = [...fromEvents, ...fromExpired]
      list.sort((a, b) => (b.sentAtMs || b.createdAtMs) - (a.sentAtMs || a.createdAtMs))
      setEmailEvents(list)
    } catch (err) {
      console.error('Failed to fetch email events:', err)
      setEmailEvents([])
    } finally {
      setEmailActivityLoading(false)
    }
  }, [user?.uid])

  useEffect(() => {
    if (!user?.uid) return
    fetchEmailEvents()
  }, [user?.uid, fetchEmailEvents])

  useEffect(() => {
    if (user?.uid && view === 'email-activity') {
      fetchEmailEvents()
    }
  }, [user?.uid, view, fetchEmailEvents])

  // Hero headline word cycling
  useEffect(() => {
    if (view !== 'auth') return
    const interval = setInterval(() => {
      setHeroCycleLeaving(true)
      setTimeout(() => {
        setHeroCycleIdx((i) => (i + 1) % HERO_BANNER_CYCLE_KEYS.length)
        setHeroCycleLeaving(false)
      }, 280)
    }, 2800)
    return () => clearInterval(interval)
  }, [view, language])

  // Real-time updates for email activity: when any recipient signs, their event doc is updated so the list reflects all signers
  useEffect(() => {
    if (!user?.uid || view !== 'email-activity') return
    const unsub = onSnapshot(collection(db, 'users', user.uid, 'emailEvents'), () => {
      fetchEmailEvents()
    })
    return () => unsub()
  }, [user?.uid, view, fetchEmailEvents])

  useEffect(() => {
    if (!user?.uid) {
      setTemplates([])
      return
    }

    const q = query(
      collection(db, 'users', user.uid, 'userTemplates'),
      where('isArchived', '==', false)
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((doc) => {
        const data = doc.data()
        // Convert timestamp to ISO string or Date object if needed for UI
        // But App.jsx uses new Date(template.updatedAt) which handles strings.
        // Firestore timestamps have toDate().
        return {
          id: doc.id,
          ...data,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : data.updatedAt,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt,
        }
      })
      
      list.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
      setTemplates(list)
    })

    return () => unsubscribe()
  }, [user])

  useEffect(() => {
    if (defaultFromEmail && view === 'app') {
      setSenderEmail(defaultFromEmail)
    }
  }, [view, defaultFromEmail])

  useEffect(() => {
    if (authLoading) return
    if (!user) {
      setUserProfileLoaded(false)
      // Clear editor state on logout (not on initial load – avoid overwriting restored draft)
      setLogoDataUrl(null)
      setScopeTitle('Project Scope')
      setAgreementHtml('')
      setRecipientEmail('')
      setSenderEmail('')
      setSubscriptionTier('premium')
      setBillingCycle('yearly')
      setIsOneTimeUse(false)
      setDefaultFromEmail('')
      setEmailOneOffCredits(0)
      setFreeDocumentUsed(false)
      setUserUsage({ topUpEmails: 0, topUpSignedAgreements: 0, subscribedEmailTopUpCredit: 0, subscribedSignedAgreementTopUpCredit: 0, currentTotalSignatures: 0, emailsSentThisMonth: 0, currentEmailsSignedPerMonth: 0, reminderEmailsThisMonth: 0, monthKey: '' })
      setUserTopUps([])
      setUserSubscription(null)
      setCustomFields([])
      setVerifyPhoto(false)
      setVerifyEmail(false)
      setVerifyOTP(false)
      setVerifyPhone(false)
      setSigners([{ date: '', agreed: false, fullName: '', signatureDataUrl: '', phone: '', customFields: {} }])
      setTotalSignatures(1)
      setTemplateNameInput('')
      setActiveTemplateId(null)
      
      // Clear Auth Form State
      setAuthEmail('')
      setAuthPassword('')
      setAuthConfirmPassword('')
      setAuthFirstName('')
      setAuthLastName('')
      setAuthError('')
      
      if (editorRef.current) {
        editorRef.current.innerHTML = ''
        lastValidHtmlRef.current = ''
      }
    } else {
      // Load user profile on login
      const fetchProfile = async () => {
        try {
          const docRef = doc(db, 'users', user.uid)
          const snap = await getDoc(docRef)
          if (snap.exists()) {
            const data = snap.data()
            setSubscriptionTier(data.subscriptionTier || 'premium')
            setBillingCycle(data.billingCycle || 'yearly')
            setIsOneTimeUse(data.isOneTimeUse || false)
            setOneOffPaid(data.oneOffPaid || false)
            if (data.firstName) setAuthFirstName(data.firstName)
            if (data.lastName) setAuthLastName(data.lastName)
            if (data.defaultFromEmail) setDefaultFromEmail(data.defaultFromEmail)
            setSendEmailTo(data.sendEmailTo === true)
            const sub = data.subscription || {}
            const monthKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
            const subMonthKey = sub.monthKey || monthKey
            if (subMonthKey === monthKey) {
              const reminderMonthKey = sub.reminderEmailsMonthKey || ''
              setUserUsage({
                topUpEmails: sub.topUpEmails || 0,
                topUpSignedAgreements: sub.topUpSignedAgreements || 0,
                subscribedEmailTopUpCredit: Math.max(0, sub.SubscribedEmailTopUpCredit ?? 0),
                subscribedSignedAgreementTopUpCredit: Math.max(0, sub.SubscribedSignedAgreementTopUpCredit ?? 0),
                currentTotalSignatures: sub.currentTotalSignatures || 0,
                emailsSentThisMonth: sub.CurrentEmailsPerMonth ?? 0,
                currentEmailsSignedPerMonth: sub.currentEmailsSignedPerMonth ?? 0,
                reminderEmailsThisMonth: reminderMonthKey === monthKey ? (sub.CurrentReminderEmailsPerMonth ?? 0) : 0,
                monthKey: subMonthKey,
              })
            } else {
              setUserUsage({ topUpEmails: 0, topUpSignedAgreements: 0, subscribedEmailTopUpCredit: Math.max(0, sub.SubscribedEmailTopUpCredit ?? 0), subscribedSignedAgreementTopUpCredit: Math.max(0, sub.SubscribedSignedAgreementTopUpCredit ?? 0), currentTotalSignatures: 0, emailsSentThisMonth: 0, currentEmailsSignedPerMonth: 0, reminderEmailsThisMonth: 0, monthKey })
            }
            setUserTopUps(Array.isArray(data.topUps) ? data.topUps : [])
            setUserSubscription({
              ...sub,
              maxEmailsPerMonth: sub.maxEmailsPerMonth ?? data.maxEmailsPerMonth,
            })
            setUserCustomerId(data.customerId || data.stripeCustomerId || null)
            setUserSubscriptionId(data.subscriptionId || null)
            setEmailOneOffCredits(Math.max(0, data.emailOneOffCredits ?? 0))
            setFreeDocumentUsed(data.freeDocumentUsed === true)
          } else {
            setUserSubscription(null)
          }
          setUserProfileLoaded(true)
        } catch (error) {
          console.error('Error fetching user profile:', error)
          setUserProfileLoaded(true)
        }
      }
      fetchProfile()
    }
  }, [user, authLoading])

  useEffect(() => {
    if (!user?.uid) return
    const userRef = doc(db, 'users', user.uid)
    const unsub = onSnapshot(userRef, (snap) => {
      if (!snap.exists()) return
      const data = snap.data()
      const sub = data.subscription || {}
      const monthKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
      const subMonthKey = sub.monthKey || monthKey
      if (subMonthKey === monthKey) {
        const reminderMonthKey = sub.reminderEmailsMonthKey || ''
        setUserUsage((prev) => ({
          ...prev,
          topUpEmails: sub.topUpEmails || 0,
          topUpSignedAgreements: sub.topUpSignedAgreements || 0,
          subscribedEmailTopUpCredit: Math.max(0, sub.SubscribedEmailTopUpCredit ?? 0),
          subscribedSignedAgreementTopUpCredit: Math.max(0, sub.SubscribedSignedAgreementTopUpCredit ?? 0),
          currentTotalSignatures: sub.currentTotalSignatures || 0,
          emailsSentThisMonth: sub.CurrentEmailsPerMonth ?? 0,
          currentEmailsSignedPerMonth: sub.currentEmailsSignedPerMonth ?? 0,
          reminderEmailsThisMonth: reminderMonthKey === monthKey ? (sub.CurrentReminderEmailsPerMonth ?? 0) : 0,
          monthKey: subMonthKey,
        }))
      } else {
        setUserUsage((prev) => ({
          ...prev,
          topUpEmails: 0,
          topUpSignedAgreements: 0,
          subscribedEmailTopUpCredit: Math.max(0, sub.SubscribedEmailTopUpCredit ?? 0),
          subscribedSignedAgreementTopUpCredit: Math.max(0, sub.SubscribedSignedAgreementTopUpCredit ?? 0),
          currentTotalSignatures: 0,
          emailsSentThisMonth: 0,
          currentEmailsSignedPerMonth: 0,
          reminderEmailsThisMonth: 0,
          monthKey,
        }))
      }
      if (Array.isArray(data.topUps)) setUserTopUps(data.topUps)
      setUserSubscription({
        ...sub,
        maxEmailsPerMonth: sub.maxEmailsPerMonth ?? data.maxEmailsPerMonth,
      })
      setUserCustomerId(data.customerId || data.stripeCustomerId || null)
      setUserSubscriptionId(data.subscriptionId || null)
      setEmailOneOffCredits(Math.max(0, data.emailOneOffCredits ?? 0))
      setFreeDocumentUsed(data.freeDocumentUsed === true)
      setOneOffPaid(data.oneOffPaid || false)
      setIsOneTimeUse(data.isOneTimeUse || false)
      const tier = data.subscriptionTier || 'premium'
      const billing = (data.billingCycle || 'yearly') === 'yearly' ? 'annual' : 'monthly'
      setSubscriptionTier(tier)
      setSubscriptionBilling(billing)
      setUserProfileLoaded(true)
    })
    return () => unsub()
  }, [user?.uid])

  useEffect(() => {
    if (user && isSubscriptionOpen) {
      const loadSubscription = async () => {
      const docRef = doc(db, 'users', user.uid)
        try {
          const sync = httpsCallable(functions, 'syncSubscriptionFromStripe')
          await sync({})
        } catch (_) { /* non-blocking, fall back to Firestore */ }
        const snap = await getDocFromServer(docRef)
        if (snap.exists()) {
          const data = snap.data()
          const tier = data.subscriptionTier || 'premium'
          const billing = (data.billingCycle || 'yearly') === 'yearly' ? 'annual' : 'monthly'
          setSubscriptionTier(tier)
          setSubscriptionBilling(billing)
          setSubscriptionSavedTier(tier)
          setSubscriptionSavedBilling(billing)
          setIsOneTimeUse(data.isOneTimeUse || false)
          setUserCustomerId(data.customerId || data.stripeCustomerId || null)
          setUserSubscriptionId(data.subscriptionId || null)
          setEmailOneOffCredits(Math.max(0, data.emailOneOffCredits ?? 0))
        } else {
          setSubscriptionSavedTier('premium')
          setSubscriptionSavedBilling('annual')
          setUserCustomerId(null)
          setUserSubscriptionId(null)
        }
      }
      loadSubscription()
    } else if (!isSubscriptionOpen) {
      setSubscriptionSavedTier(null)
      setSubscriptionSavedBilling(null)
    }
  }, [user, isSubscriptionOpen])

  useEffect(() => {
    if (isSubscriptionOpen) {
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = prev }
    }
  }, [isSubscriptionOpen])

  useEffect(() => {
    if (user && (isAccountOpen || isAccountDetailsOpen)) {
      const docRef = doc(db, 'users', user.uid)
      getDoc(docRef).then((snap) => {
        if (snap.exists()) {
          const data = snap.data()
          setAccountDetailsFirstName(data.firstName || '')
          setAccountDetailsLastName(data.lastName || '')
          setAccountDetailsPhone(data.phone || '')
          setDefaultFromEmail(data.defaultFromEmail || '')
          setSendEmailTo(data.sendEmailTo === true)
        }
      }).catch(() => {
        setAccountDetailsFirstName('')
        setAccountDetailsLastName('')
        setAccountDetailsPhone('')
        setSendEmailTo(false)
      })
    }
  }, [user, isAccountOpen, isAccountDetailsOpen])

  // Merge tier defaults with user limits so feature flags like pdfExtract don't disappear
  // when the user doc is missing older fields.
  const effectiveConfig = useMemo(() => {
    if (!user) return tierConfig
    if (!userSubscription) return tierConfig
    return { ...tierConfig, ...userSubscription }
  }, [user, userSubscription, tierConfig])
  const isSignedInAppUser = Boolean(user && user.email && !user.isAnonymous)
  const hasSubscribedAccount = !!(userSubscriptionId || userCustomerId)
  /** Stripe subscription or unused one-off email credits — real paid entitlements (not the free first send). */
  const paidEntitlement = hasSubscribedAccount || (emailOneOffCredits || 0) > 0
  const displayTierConfig = useMemo(() => {
    if (!user) return premiumConfig
    if (paidEntitlement) return effectiveConfig
    if (!userProfileLoaded || !userSubscription) return effectiveConfig
    const overlayBefore = { ...FREE_DRAFT_DEFAULTS, ...(APP_CONFIG_DEFAULTS.freeDraft || {}), ...(appConfig.freeDraft || {}) }
    const overlayAfter = { ...FREE_DRAFT_AFTER_FREE_SEND_DEFAULTS, ...(APP_CONFIG_DEFAULTS.freeDraftAfterFreeSend || {}), ...(appConfig.freeDraftAfterFreeSend || {}) }
    const o = freeDocumentUsed ? overlayAfter : overlayBefore
    const base = { ...effectiveConfig }
    if ((base.maxCharacters ?? 0) === 0) base.maxCharacters = o.maxCharacters ?? FREE_DRAFT_DEFAULTS.maxCharacters
    if ((base.maxAgreementSizeKb ?? 0) === 0) base.maxAgreementSizeKb = o.maxAgreementSizeKb ?? 100
    if ((base.maxSignatures ?? 0) === 0) base.maxSignatures = o.maxSignatures ?? 2
    if ((base.maxEmailRecipients ?? 0) === 0) base.maxEmailRecipients = o.maxEmailRecipients ?? 5
    if ((base.maxTemplates ?? 0) === 0 && (o.maxTemplates ?? 0) > 0) base.maxTemplates = o.maxTemplates
    if (!base.attachments) base.attachments = !!o.attachments
    if (!base.pdfExtract) base.pdfExtract = !!o.pdfExtract
    if (!base.logo) base.logo = !!o.logo
    if (!base.verification) base.verification = !!o.verification
    if (!base.templates) base.templates = !!o.templates
    return base
  }, [user, paidEntitlement, userProfileLoaded, userSubscription, effectiveConfig, premiumConfig, freeDocumentUsed, appConfig])
  const charLimit = (displayTierConfig.maxCharacters ?? 0) > 0 ? displayTierConfig.maxCharacters : 40000
  const maxAgreementSizeKb = (displayTierConfig.maxAgreementSizeKb ?? 0) > 0 ? displayTierConfig.maxAgreementSizeKb : 500
  // Always read from tier config (Firebase) so updates to signatureTextLimitKB in Firestore apply immediately
  const signatureTextLimitKB = (user ? tierConfig : premiumConfig).signatureTextLimitKB ?? 50
  const canUseAttachments = Boolean(displayTierConfig.attachments)
  const canUseTemplates = Boolean(displayTierConfig.templates)
  const canUseLogo = Boolean(displayTierConfig.logo)
  const canUsePdfExtract = Boolean(tierConfig.pdfExtract)
  const canUseVerification = Boolean(displayTierConfig.verification)
  const canUseEmailReminders = tierConfig.emailReminder !== false
  const maxRecipients = (displayTierConfig.maxEmailRecipients ?? 0) > 0 ? (displayTierConfig.maxEmailRecipients ?? 10) : (tierConfig.maxEmailRecipients ?? 10)
  const maxTemplates = displayTierConfig.maxTemplates ?? effectiveConfig.maxTemplates ?? 0
  /** Save template in header, account modal, and toolbar when user can use templates (not gated on Stripe subscription). */
  const showSaveTemplateButton = Boolean(user && !user.isAnonymous && canUseTemplates && maxTemplates > 0)
  const maxAttachments = displayTierConfig.maxAttachments ?? 0
  const maxAttachmentBytes = displayTierConfig.maxAttachmentBytes ?? appConfig.maxAttachmentBytes ?? 8388608
  const emailAttachmentLimit = 8 * 1024 * 1024
  const effectiveAttachmentLimit = Math.min(maxAttachmentBytes, emailAttachmentLimit)
  const maxEmailsPerMonth = effectiveConfig.maxEmailsPerMonth ?? 0
  const maxSignedAgreementsPerMonth = effectiveConfig.maxSignedAgreementsPerMonth ?? effectiveConfig.maxEmailsSignedPerMonth ?? 0
  const maxEmailsSignedPerMonth = effectiveConfig.maxEmailsSignedPerMonth ?? 0
  const maxSignatures = displayTierConfig.maxSignatures ?? effectiveConfig.maxSignatures ?? 0
  const maxRecipientsForSend = (displayTierConfig.maxEmailRecipients ?? 0) > 0 ? displayTierConfig.maxEmailRecipients : (effectiveConfig.maxEmailRecipients ?? 0)
  const maxPdfBytes = displayTierConfig.maxPdfBytes ?? premiumConfig.maxPdfBytes ?? appConfig.maxPdfBytes ?? 1048576
  const logoMaxBytes = displayTierConfig.logoMaxBytes ?? premiumConfig.logoMaxBytes ?? appConfig.logoMaxBytes ?? 20480
  const logoMaxDimension = displayTierConfig.logoMaxDimension ?? premiumConfig.logoMaxDimension ?? appConfig.logoMaxDimension ?? 120
  const currentMonthKey = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` })()
  const hasSubscription = user && !isOneTimeUse
  const effectiveTopUpFromRecords = useMemo(() => {
    const nowMs = Date.now()
    let emails = 0
    let signed = 0
    for (const t of userTopUps) {
      const dateToppedUp = t.dateToppedUp?.toDate ? t.dateToppedUp.toDate() : (t.dateToppedUp ? new Date(t.dateToppedUp) : null)
      if (!dateToppedUp) continue
      const expiresEndOfMonth = t.expiresEndOfMonth || ''
      const thirtyDaysLater = dateToppedUp.getTime() + 30 * 24 * 60 * 60 * 1000
      const isActive = hasSubscription
        ? (expiresEndOfMonth && currentMonthKey <= expiresEndOfMonth)
        : (nowMs <= thirtyDaysLater)
      if (isActive) {
        emails += t.emailsRemaining ?? 0
        signed += t.signedRemaining ?? 0
      }
    }
    return { emails, signed }
  }, [userTopUps, currentMonthKey, hasSubscription])
  const legacyTopUpEmails = (userUsage.monthKey === currentMonthKey ? userUsage.topUpEmails : 0) || 0
  const legacyTopUpSigned = (userUsage.monthKey === currentMonthKey ? userUsage.topUpSignedAgreements : 0) || 0
  const effectiveTopUpEmails = effectiveTopUpFromRecords.emails > 0 ? effectiveTopUpFromRecords.emails : legacyTopUpEmails
  const effectiveTopUpSigned = effectiveTopUpFromRecords.signed > 0 ? effectiveTopUpFromRecords.signed : legacyTopUpSigned
  /** Logged-in, no Stripe sub, no one-off credits, free send not yet used — show banner + allow first send/copy */
  const showFreeSendBanner =
    view === 'app' &&
    !isRecipientView &&
    user &&
    !user.isAnonymous &&
    !hasSubscribedAccount &&
    freeDocumentUsed !== true &&
    (emailOneOffCredits || 0) === 0
  const subscribedTopUpEmails = hasSubscribedAccount ? (userUsage.subscribedEmailTopUpCredit ?? 0) : 0
  const subscribedTopUpSigned = hasSubscribedAccount ? (userUsage.subscribedSignedAgreementTopUpCredit ?? 0) : 0
  const effectiveMaxEmails = maxEmailsPerMonth + effectiveTopUpEmails + subscribedTopUpEmails + (emailOneOffCredits || 0)
  const effectiveMaxSigned = maxSignedAgreementsPerMonth + effectiveTopUpSigned + subscribedTopUpSigned
  const effectiveMaxEmailsSigned = maxEmailsSignedPerMonth + effectiveTopUpSigned + subscribedTopUpSigned

  const handleUpdateSubscriptionPlan = async (tierOverride) => {
    if (!user?.uid) return
    setSubscriptionUpdateStatus('Updating...')
    try {
      const userSnap = await getDocFromServer(doc(db, 'users', user.uid))
      const userData = userSnap.exists ? userSnap.data() : {}
      const customerId = userData.customerId || userData.stripeCustomerId
      const subscriptionId = userData.subscriptionId

      const createCustomerPortalSession = httpsCallable(functions, 'createCustomerPortalSession')

      if (customerId || subscriptionId) {
        try {
          sessionStorage.setItem('portal_pre_tier', (userData.subscriptionTier || subscriptionTier || 'premium').toLowerCase())
          sessionStorage.setItem('portal_pre_billing', ((userData.billingCycle || subscriptionBilling || 'yearly') === 'yearly' || (userData.billingCycle || subscriptionBilling || 'yearly') === 'annual') ? 'annual' : 'monthly')
        } catch (_) { /* ignore */ }
        const savePromise = saveDocumentStateForPaymentReturn()
        const { data } = await createCustomerPortalSession({ returnOrigin: typeof window !== 'undefined' ? window.location.origin : '' })
        if (data?.url) {
          await savePromise
          window.location.href = data.url
          return
        }
      }
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession')
      const billingInterval = subscriptionBilling === 'annual' ? 'annual' : 'monthly'
      const tierToUse = tierOverride ?? subscriptionTier
      const savePromise = saveDocumentStateForPaymentReturn()
      const { data } = await createCheckoutSession({ tierId: tierToUse, billingInterval, returnOrigin: typeof window !== 'undefined' ? window.location.origin : '' })
      if (data?.url) {
        await savePromise
        window.location.href = data.url
        return
      }
      setSubscriptionUpdateStatus('Could not open billing. Try again.')
    } catch (err) {
      setSubscriptionUpdateStatus(err?.message || 'Error opening billing.')
      reportErrorToAdmin('Subscription', err, { userId: user?.uid, tier: subscriptionTier, billing: subscriptionBilling })
    }
    setTimeout(() => setSubscriptionUpdateStatus(''), 4000)
  }

  const handleTopUp = async (topUpTierId) => {
    if (!user?.uid) return
    const topUpTier = tiers[topUpTierId] || TIER_SEED_DATA[topUpTierId]
    if (!topUpTier || topUpTier.status === 'inactive') return
    const emailsAdded = topUpTier.maxEmailsPerMonth ?? 0
    const signedAdded = topUpTier.maxSignedAgreementsPerMonth ?? topUpTier.maxEmailsSignedPerMonth ?? 30
    const price = topUpTier.price ?? 0
    if (emailsAdded <= 0) return
    try {
      const now = new Date()
      const purchaseMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
      let expiresEndOfMonth = purchaseMonth
      if (hasSubscription) {
        const nextMonth = new Date(now.getFullYear(), now.getMonth() + 2, 0)
        expiresEndOfMonth = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}`
      } else {
        const thirtyDaysLater = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
        expiresEndOfMonth = `${thirtyDaysLater.getFullYear()}-${String(thirtyDaysLater.getMonth() + 1).padStart(2, '0')}`
      }
      const newTopUp = {
        tier: topUpTierId,
        dateToppedUp: serverTimestamp(),
        emailsAdded,
        emailsRemaining: emailsAdded,
        signedAdded,
        signedRemaining: signedAdded,
        expiresEndOfMonth,
      }
      const userSnap = await getDoc(doc(db, 'users', user.uid))
      const existingTopUps = (userSnap.exists() ? userSnap.data().topUps : []) || []
      await updateDoc(doc(db, 'users', user.uid), {
        topUps: [...existingTopUps, newTopUp],
        updatedAt: serverTimestamp(),
      })
      setUserTopUps([...existingTopUps, { ...newTopUp, dateToppedUp: now }])
      setIsTopUpOpen(false)
      setIsLimitReachedOpen(false)
      setLimitReachedPendingAction(null)
      const tierLabel = (topUpTierId || '').replace('TopUp', ' ').trim()
      const tierName = tierLabel ? tierLabel.charAt(0).toUpperCase() + tierLabel.slice(1) : 'Standard'
      setPurchaseSuccessData({
        type: 'topup',
        tierName,
        details: `+${emailsAdded} emails, +${signedAdded} signed agreements`,
        price: `$${price}`,
        validity: hasSubscription ? 'Carries over to next month' : 'Valid for 30 days',
      })
      setSendStatus(`Top-up applied: +${emailsAdded} emails, +${signedAdded} signed agreements. Valid for ${hasSubscription ? 'this month and next' : '30 days'}.`)
      setSendStatusType('success')
    } catch (err) {
      setSendStatus(`Top-up failed: ${err?.message || 'Please try again.'}`)
      setSendStatusType('error')
      reportErrorToAdmin('TopUp', err, { userId: user?.uid, topUpTierId })
    }
  }

  const emailsSentThisMonthComputed = useMemo(() => {
    const now = new Date()
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    return emailEvents.filter((e) => {
      if (!e.sentAt && !e.sentAtMs) return false
      const dateRaw = e.sentAt || e.createdAt
      const d = dateRaw ? new Date(dateRaw) : new Date(e.sentAtMs || e.createdAtMs || 0)
      const eventYm = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      return eventYm === ym
    }).length
  }, [emailEvents])

  const emailsSentThisMonth =
    userUsage.monthKey === currentMonthKey && userUsage.emailsSentThisMonth != null
      ? userUsage.emailsSentThisMonth
      : (userSubscription?.monthKey === currentMonthKey && userSubscription?.CurrentEmailsPerMonth != null)
        ? userSubscription.CurrentEmailsPerMonth
        : emailsSentThisMonthComputed

  const agreementsSignedThisMonth = useMemo(() => {
    const now = new Date()
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    return emailEvents.filter((e) => {
      if (e.status !== 'signed') return false
      const dateRaw = e.signedAt || e.linkDetails?.signedAtIso || e.metadata?.signedDate || e.signedDate || e.sentAt || e.createdAt
      const d = dateRaw ? (typeof dateRaw?.toDate === 'function' ? dateRaw.toDate() : new Date(dateRaw)) : new Date(e.createdAtMs || e.sentAtMs || 0)
      const eventYm = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      return eventYm === ym
    }).length
  }, [emailEvents])

  const currentEmailsSignedPerMonth =
    userUsage.monthKey === currentMonthKey && userUsage.currentEmailsSignedPerMonth != null
      ? userUsage.currentEmailsSignedPerMonth
      : (userSubscription?.monthKey === currentMonthKey && userSubscription?.currentEmailsSignedPerMonth != null)
        ? userSubscription.currentEmailsSignedPerMonth
        : agreementsSignedThisMonth

  const currentTotalSignatures = useMemo(() => {
    const now = new Date()
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    return emailEvents
      .filter((e) => {
        if (e.status !== 'signed') return false
        const dateRaw = e.signedAt || e.linkDetails?.signedAtIso || e.metadata?.signedDate || e.signedDate || e.sentAt || e.createdAt
        const d = dateRaw ? (typeof dateRaw?.toDate === 'function' ? dateRaw.toDate() : new Date(dateRaw)) : new Date(e.createdAtMs || e.sentAtMs || 0)
        const eventYm = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        return eventYm === ym
      })
      .reduce((sum, e) => {
        const ld = e.linkDetails || {}
        const signerCount = ld.totalSignatures ?? ((Array.isArray(ld.signers) ? ld.signers.length : 0) || (Array.isArray(e.signers) ? e.signers.length : 0) || 1)
        return sum + signerCount
      }, 0)
  }, [emailEvents])

  const linksCreatedThisMonth = useMemo(() => {
    const now = new Date()
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    return emailEvents.filter((e) => {
      const dateRaw = e.createdAt
      const d = dateRaw ? new Date(dateRaw) : new Date(e.createdAtMs || 0)
      const eventYm = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      return eventYm === ym
    }).length
  }, [emailEvents])

  useEffect(() => {
    if (!isSignatureOpen) return
    const canvas = canvasRef.current
    if (!canvas) return
    const context = canvas.getContext('2d')
    const rect = canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1

    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    context.setTransform(1, 0, 0, 1, 0, 0)
    context.scale(dpr, dpr)
    context.lineCap = 'round'
    context.lineJoin = 'round'
    context.strokeStyle = '#111827'
    context.lineWidth = 2.5

    const url = signatureCustomFieldId
      ? signers[signatureSignerIndex]?.customFields?.[signatureCustomFieldId]
      : signers[signatureSignerIndex]?.signatureDataUrl
    if (url) {
      const image = new Image()
      image.onload = () => {
        context.drawImage(image, 0, 0, rect.width, rect.height)
      }
      image.src = url
    }
  }, [isSignatureOpen, signatureSignerIndex, signatureCustomFieldId, signers])

  const getCanvasPoint = (event) => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    }
  }

  const startDrawing = (event) => {
    event.preventDefault()
    const canvas = canvasRef.current
    if (!canvas) return
    const context = canvas.getContext('2d')
    const { x, y } = getCanvasPoint(event)
    context.beginPath()
    context.moveTo(x, y)
    canvas.setPointerCapture(event.pointerId)
    isDrawingRef.current = true
  }

  const draw = (event) => {
    if (!isDrawingRef.current) return
    const canvas = canvasRef.current
    if (!canvas) return
    const context = canvas.getContext('2d')
    const { x, y } = getCanvasPoint(event)
    context.lineTo(x, y)
    context.stroke()
  }

  const stopDrawing = () => {
    isDrawingRef.current = false
  }

  const handleClearSignature = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const context = canvas.getContext('2d')
    context.setTransform(1, 0, 0, 1, 0, 0)
    context.clearRect(0, 0, canvas.width, canvas.height)
    safeSetSignersFromUser((prev) => {
      const next = [...prev]
      if (next[signatureSignerIndex]) {
        if (signatureCustomFieldId) {
          const cf = { ...(next[signatureSignerIndex].customFields || {}) }
          delete cf[signatureCustomFieldId]
          next[signatureSignerIndex] = { ...next[signatureSignerIndex], customFields: cf }
        } else {
          next[signatureSignerIndex] = { ...next[signatureSignerIndex], signatureDataUrl: '' }
        }
      }
      return next
    })
  }

  const compressLogoToSize = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const maxDim = logoMaxDimension
        let w = img.width
        let h = img.height
        if (w > maxDim || h > maxDim) {
          if (w > h) {
            h = (h * maxDim) / w
            w = maxDim
          } else {
            w = (w * maxDim) / h
            h = maxDim
          }
        }
        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, w, h)
        let quality = 0.7
        let dataUrl = canvas.toDataURL('image/jpeg', quality)
        while (dataUrl.length > logoMaxBytes && quality > 0.1) {
          quality -= 0.1
          dataUrl = canvas.toDataURL('image/jpeg', quality)
        }
        if (dataUrl.length > logoMaxBytes) {
          const smaller = Math.min(w, h) * 0.8
          canvas.width = Math.round(w * 0.8)
          canvas.height = Math.round(h * 0.8)
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
          dataUrl = canvas.toDataURL('image/jpeg', 0.5)
        }
        resolve(dataUrl)
      }
      img.onerror = reject
      img.src = URL.createObjectURL(file)
    })
  }

  const handleLogoSelect = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !file.type.startsWith('image/')) return
    try {
      const compressed = await compressLogoToSize(file)
      setLogoDataUrl(compressed)
    } catch (err) {
      setSendStatus('Failed to process logo. Try a smaller image.')
      setSendStatusType('error')
    }
    e.target.value = ''
  }

  const handleSaveSignature = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dataUrl = compressSignatureCanvasToDataUrl(canvas)
    if (!dataUrl) {
      setSendStatus('Draw a signature before saving.')
      setSendStatusType('error')
      return
    }
    safeSetSignersFromUser((prev) => {
      const next = [...prev]
      if (next[signatureSignerIndex]) {
        if (signatureCustomFieldId) {
          next[signatureSignerIndex] = {
            ...next[signatureSignerIndex],
            customFields: { ...(next[signatureSignerIndex].customFields || {}), [signatureCustomFieldId]: dataUrl },
          }
        } else {
          next[signatureSignerIndex] = { ...next[signatureSignerIndex], signatureDataUrl: dataUrl }
        }
      }
      return next
    })
    setSignatureCustomFieldId(null)
    setIsSignatureOpen(false)
  }

  // --- Layout helpers ---
  const getAllFieldItems = (signerIndex) => {
    const items = []
    if (includeDate) items.push({ id: 'date', label: t('app.date'), fieldType: 'date' })
    if (includeTerms) items.push({ id: 'terms', label: t('app.termsConditions'), fieldType: 'checkbox' })
    if (includeFullName) items.push({ id: 'fullName', label: t('app.fullName'), fieldType: 'text' })
    if (includePhone) items.push({ id: 'phone', label: t('app.phone'), fieldType: 'tel' })
    customFields.forEach((cf) => {
      const addedTo = cf.addedToSigners ?? [0, 1]
      if (signerIndex === undefined || addedTo.includes(signerIndex)) {
        items.push({ id: cf.id, label: cf.label, fieldType: cf.type })
      }
    })
    if (includeSignature) items.push({ id: 'signature', label: t('app.signature'), fieldType: 'signature' })
    return items
  }

  const signingSectionSize = useMemo(() => {
    const bytes = new TextEncoder().encode(JSON.stringify(signers)).length
    const limitBytes = signatureTextLimitKB * 1024
    return {
      kb: (bytes / 1024).toFixed(2),
      bytes,
      isLarge: bytes >= limitBytes * 0.8,
      atLimit: bytes >= limitBytes,
    }
  }, [signers, signatureTextLimitKB])

  const safeSetSignersFromUser = useCallback((updater) => {
    setSigners((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      const bytes = new TextEncoder().encode(JSON.stringify(next)).length
      if (bytes > signatureTextLimitKB * 1024) {
        requestAnimationFrame(() => setSigningLimitReachedOpen(true))
        return prev
      }
      return next
    })
  }, [signatureTextLimitKB])

  const getActiveLayout = (signerIndex) => {
    const allItems = getAllFieldItems(signerIndex)
    const excludedSet = new Set(excludedFromLayout)
    const applyOverride = (item) => ({
      ...item,
      label: labelOverrides[item.id] || item.label,
    })

    if (!fieldLayout || fieldLayout.length === 0) {
      return allItems.filter((item) => !excludedSet.has(item.id)).map((item) => ({ ...applyOverride(item), column: 0 }))
    }
    const result = []
    const includedIds = new Set(allItems.map((f) => f.id))
    for (const layoutItem of fieldLayout) {
      if (excludedSet.has(layoutItem.id)) continue
      if (layoutItem.isSpacer) {
        result.push({ id: layoutItem.id, label: 'Space', fieldType: 'spacer', column: layoutItem.column, isSpacer: true })
      } else if (includedIds.has(layoutItem.id)) {
        const item = allItems.find((f) => f.id === layoutItem.id)
        if (item) result.push({ ...applyOverride(item), column: layoutItem.column })
      }
    }
    for (const item of allItems) {
      if (!fieldLayout.find((f) => f.id === item.id) && !excludedSet.has(item.id)) {
        result.push({ ...applyOverride(item), column: 0 })
      }
    }
    return result
  }

  const handleLayoutDrop = (targetCol, targetFieldId) => {
    if (!dragFieldId) return
    setEditLayout((prev) => {
      const dragged = prev.find((f) => f.id === dragFieldId)
      if (!dragged) return prev
      const withoutDragged = prev.filter((f) => f.id !== dragFieldId)
      const newDragged = { ...dragged, column: targetCol }
      if (!targetFieldId) {
        return [...withoutDragged, newDragged]
      }
      const targetIdx = withoutDragged.findIndex((f) => f.id === targetFieldId)
      const result = [...withoutDragged]
      result.splice(targetIdx === -1 ? result.length : targetIdx, 0, newDragged)
      return result
    })
    setDragFieldId(null)
    setDragOverId(null)
    setDragOverCol(null)
  }

  const openLayoutEditor = () => {
    // Merge active layout + any excluded fields so user can restore them
    const active = getActiveLayout()
    const allItems = getAllFieldItems()
    const excludedSet = new Set(excludedFromLayout)
    const activeIds = new Set(active.filter((f) => !f.isSpacer).map((f) => f.id))

    const editorItems = [
      ...active.map((f) => ({
        ...f,
        labelOverride: f.isSpacer ? undefined : (labelOverrides[f.id] || ''),
      })),
    ]
    // Append excluded (non-spacer) fields at end so user can drag them back in
    for (const item of allItems) {
      if (excludedSet.has(item.id) && !activeIds.has(item.id)) {
        editorItems.push({ ...item, column: 0, labelOverride: labelOverrides[item.id] || '', excluded: true })
      }
    }

    setEditLayout(editorItems)
    setEditColumns(layoutColumns)
    setEditingLabelId(null)
    setRemovingFieldId(null)
    setIsLayoutEditorOpen(true)
  }

  const handleSaveLayout = () => {
    const cols = editColumns
    // Fields marked excluded stay excluded; fields in editLayout but not excluded are included
    const newExcluded = editLayout.filter((f) => !f.isSpacer && f.excluded).map((f) => f.id)
    const included = editLayout.filter((f) => !f.excluded)

    setFieldLayout(included.map((f) => ({
      id: f.id,
      column: cols === 1 ? 0 : f.column,
      ...(f.isSpacer ? { isSpacer: true } : {}),
    })))
    setLayoutColumns(cols)
    setExcludedFromLayout(newExcluded)

    // Persist label overrides
    const newOverrides = { ...labelOverrides }
    for (const f of editLayout) {
      if (!f.isSpacer && f.labelOverride && f.labelOverride.trim()) {
        newOverrides[f.id] = f.labelOverride.trim()
      }
    }
    setLabelOverrides(newOverrides)
    setIsLayoutEditorOpen(false)
  }
  // --- End layout helpers ---

  const renderSignerField = (fieldId, signer, signerIdx, isFieldReadOnly, displayLabel, recipientPreviewLocked = false) => {
    if (fieldId.startsWith('spacer_')) {
      return <div key={fieldId} className="field-spacer" aria-hidden="true" />
    }

    /* Builder + 2 signers: * on your column (sender, index 0). Recipient view: * on your column (index 1). Single signer: * only when recipient is filling */
    const req = !isSignedComplete && (
      (totalSignatures === 2 && ((isRecipientView && signerIdx === 1) || (!isRecipientView && signerIdx === 0))) ||
      (totalSignatures === 1 && isRecipientView && signerIdx === 0)
    )
    const reqMark = req ? <span className="field-required"> *</span> : null
    const todayDate = new Date()
    const minDateObj = new Date(todayDate)
    minDateObj.setDate(minDateObj.getDate() - 30)
    const maxDateObj = new Date(todayDate)
    maxDateObj.setDate(maxDateObj.getDate() + 30)

    if (fieldId === 'date' && includeDate) {
      const datePickerKey = `date-${signerIdx}`
      const dateLabel = labelOverrides.date ?? t('app.date')
      return (
        <div key="date" className="field field--date">
          <span className="field-label">{dateLabel}{reqMark}</span>
          <LazyDatePicker
            selected={signer.date ? parseDateOnly(signer.date) : null}
            open={openDatePickerKey === datePickerKey}
            onFocus={() => !isFieldReadOnly && setOpenDatePickerKey(datePickerKey)}
            onInputClick={() => !isFieldReadOnly && setOpenDatePickerKey(datePickerKey)}
            onChange={(date) => {
              safeSetSignersFromUser((prev) => {
                const n = [...prev]
                n[signerIdx] = { ...n[signerIdx], date: date ? formatDateOnly(date) : '' }
                return n
              })
              setOpenDatePickerKey(null)
            }}
            onCalendarClose={() => setOpenDatePickerKey(null)}
            minDate={!isFieldReadOnly ? minDateObj : undefined}
            maxDate={!isFieldReadOnly ? maxDateObj : undefined}
            dateFormat="yyyy-MM-dd"
            placeholderText={t('app.datePlaceholder')}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            shouldCloseOnSelect={true}
            disabled={isFieldReadOnly}
            className="date-input"
          />
        </div>
      )
    }
    if (fieldId === 'terms' && includeTerms) {
      const termsLabel = labelOverrides.terms ?? t('app.termsAgree')
      return (
        <label key="terms" className="field checkbox-field">
          <input type="checkbox" checked={signer.agreed} disabled={isFieldReadOnly}
            onChange={isFieldReadOnly ? undefined : (e) => safeSetSignersFromUser((prev) => { const n = [...prev]; n[signerIdx] = { ...n[signerIdx], agreed: e.target.checked }; return n })} />
          <span>{termsLabel}{reqMark}</span>
        </label>
      )
    }
    if (fieldId === 'fullName' && includeFullName) {
      const fullNameLabel = labelOverrides.fullName ?? t('app.fullName')
      return (
        <label key="fullName" className="field">
          <span>{fullNameLabel}{reqMark}</span>
          <input type="text" placeholder={t('app.fullNamePlaceholder')} value={signer.fullName} readOnly={isFieldReadOnly}
            onChange={isFieldReadOnly ? undefined : (e) => safeSetSignersFromUser((prev) => { const n = [...prev]; n[signerIdx] = { ...n[signerIdx], fullName: e.target.value }; return n })} />
        </label>
      )
    }
    if (fieldId === 'phone' && includePhone) {
      const phoneLabel = labelOverrides.phone ?? t('app.phone')
      return (
        <label key="phone" className="field">
          <span>{phoneLabel}{reqMark}</span>
          <input type="tel" placeholder={t('app.phonePlaceholder')} value={signer.phone || ''} readOnly={isFieldReadOnly}
            onChange={isFieldReadOnly ? undefined : (e) => safeSetSignersFromUser((prev) => { const n = [...prev]; n[signerIdx] = { ...n[signerIdx], phone: e.target.value }; return n })} />
        </label>
      )
    }
    if (fieldId === 'signature' && includeSignature) {
      const signatureLabel = labelOverrides.signature ?? t('app.signature')
      const readonlySigEmptyLabel = recipientPreviewLocked && !signer.signatureDataUrl ? t('app.recipientSignsWhenTheyOpen') : '—'
      return (
        <div key="signature" className="field signature-field">
          <span>{signatureLabel}{reqMark}</span>
          {isFieldReadOnly ? (
            <div className="signature-box signature-box-readonly">
              {signer.signatureDataUrl ? <img src={signer.signatureDataUrl} alt={t('app.signature')} /> : <span className="signature-placeholder">{readonlySigEmptyLabel}</span>}
            </div>
          ) : (
            <button type="button" className="signature-box" onClick={() => { setSignatureSignerIndex(signerIdx); setSignatureCustomFieldId(null); setIsSignatureOpen(true) }}>
              {signer.signatureDataUrl ? <img src={signer.signatureDataUrl} alt={`${t('app.signature')} preview`} /> : <span className="signature-placeholder">{t('app.clickToSign')}</span>}
            </button>
          )}
        </div>
      )
    }
    // Custom field
    const cf = customFields.find((c) => c.id === fieldId)
    if (cf) {
      const val = (signer.customFields || {})[cf.id]
      const cfLabel = displayLabel || cf.label
      if (cf.type === 'text') {
        return (
          <label key={cf.id} className="field">
            <span>{cfLabel}</span>
            <input type="text" placeholder={t('app.enterFieldPlaceholder', { label: cfLabel })} value={val || ''} readOnly={isFieldReadOnly}
              onChange={isFieldReadOnly ? undefined : (e) => safeSetSignersFromUser((prev) => { const n = [...prev]; n[signerIdx] = { ...n[signerIdx], customFields: { ...(n[signerIdx].customFields || {}), [cf.id]: e.target.value } }; return n })} />
          </label>
        )
      }
      if (cf.type === 'date') {
        const datePickerKey = `cf-${signerIdx}-${cf.id}`
        return (
          <div key={cf.id} className="field field--date">
            <span className="field-label">{cfLabel}</span>
            <LazyDatePicker
              selected={val ? parseDateOnly(val) : null}
              open={openDatePickerKey === datePickerKey}
              onFocus={() => !isFieldReadOnly && setOpenDatePickerKey(datePickerKey)}
              onInputClick={() => !isFieldReadOnly && setOpenDatePickerKey(datePickerKey)}
              onChange={(date) => {
                safeSetSignersFromUser((prev) => {
                  const n = [...prev]
                  n[signerIdx] = {
                    ...n[signerIdx],
                    customFields: {
                      ...(n[signerIdx].customFields || {}),
                      [cf.id]: date ? formatDateOnly(date) : '',
                    },
                  }
                  return n
                })
                setOpenDatePickerKey(null)
              }}
              onCalendarClose={() => setOpenDatePickerKey(null)}
              dateFormat="yyyy-MM-dd"
              placeholderText={t('app.datePlaceholder')}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              shouldCloseOnSelect={true}
              disabled={isFieldReadOnly}
              className="date-input"
            />
          </div>
        )
      }
      if (cf.type === 'checkbox') {
        return (
          <label key={cf.id} className="field checkbox-field">
            <input type="checkbox" checked={!!val} disabled={isFieldReadOnly}
              onChange={isFieldReadOnly ? undefined : (e) => safeSetSignersFromUser((prev) => { const n = [...prev]; n[signerIdx] = { ...n[signerIdx], customFields: { ...(n[signerIdx].customFields || {}), [cf.id]: e.target.checked } }; return n })} />
            <span>{cfLabel}</span>
          </label>
        )
      }
      if (cf.type === 'signature') {
        const readonlyCfSigEmpty = recipientPreviewLocked && !val ? t('app.recipientSignsWhenTheyOpen') : '—'
        return (
          <div key={cf.id} className="field signature-field">
            <span>{cfLabel}</span>
            {isFieldReadOnly ? (
              <div className="signature-box signature-box-readonly">
                {val ? <img src={val} alt={cfLabel} /> : <span className="signature-placeholder">{readonlyCfSigEmpty}</span>}
              </div>
            ) : (
              <button type="button" className="signature-box" onClick={() => { setSignatureSignerIndex(signerIdx); setSignatureCustomFieldId(cf.id); setIsSignatureOpen(true) }}>
                {val ? <img src={val} alt={`${cfLabel} preview`} /> : <span className="signature-placeholder">{t('app.clickToSign')}</span>}
              </button>
            )}
          </div>
        )
      }
      if (cf.type === 'textDisplay') {
        const content = (cf.content || '').slice(0, 100)
        return (
          <div key={cf.id} className="field field--text-display">
            <div className="text-display-content">{content || '—'}</div>
          </div>
        )
      }
      if (cf.type === 'number') {
        return (
          <label key={cf.id} className="field">
            <span>{cfLabel}{reqMark}</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder={t('app.enterNumber')}
              value={val || ''}
              readOnly={isFieldReadOnly}
              onChange={isFieldReadOnly ? undefined : (e) => {
                const v = e.target.value.replace(/\D/g, '')
                safeSetSignersFromUser((prev) => { const n = [...prev]; n[signerIdx] = { ...n[signerIdx], customFields: { ...(n[signerIdx].customFields || {}), [cf.id]: v } }; return n })
              }}
            />
          </label>
        )
      }
      if (cf.type === 'picklist') {
        const options = cf.options || []
        return (
          <label key={cf.id} className="field">
            <span>{cfLabel}{reqMark}</span>
            <select
              value={val || ''}
              disabled={isFieldReadOnly}
              onChange={isFieldReadOnly ? undefined : (e) => safeSetSignersFromUser((prev) => { const n = [...prev]; n[signerIdx] = { ...n[signerIdx], customFields: { ...(n[signerIdx].customFields || {}), [cf.id]: e.target.value } }; return n })}
              className="field-select"
            >
              <option value="">{t('app.selectOption')}</option>
              {options.map((opt, i) => (
                <option key={i} value={opt}>{opt}</option>
              ))}
            </select>
          </label>
        )
      }
      if (cf.type === 'time') {
        const timeVal = val || ''
        const [h24 = 0, minutes = 0] = timeVal.includes(':') ? timeVal.split(':').map(Number) : []
        const h12 = h24 % 12 || 12
        const ampm = h24 < 12 ? 'AM' : 'PM'
        const handleTimeChange = (newH12, newMin, newAmPm) => {
          const h24New = newAmPm === 'AM' ? (newH12 === 12 ? 0 : newH12) : (newH12 === 12 ? 12 : newH12 + 12)
          const newVal = `${String(h24New).padStart(2, '0')}:${String(newMin).padStart(2, '0')}`
          safeSetSignersFromUser((prev) => { const n = [...prev]; n[signerIdx] = { ...n[signerIdx], customFields: { ...(n[signerIdx].customFields || {}), [cf.id]: newVal } }; return n })
        }
        return (
          <label key={cf.id} className="field field--time">
            <span>{cfLabel}{reqMark}</span>
            <div className="time-picker" data-cf-id={cf.id}>
              <select
                value={h12}
                disabled={isFieldReadOnly}
                onChange={(e) => handleTimeChange(parseInt(e.target.value, 10), minutes, ampm)}
                className="time-field-select"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((h) => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
              <span className="time-sep">:</span>
              <select
                value={minutes}
                disabled={isFieldReadOnly}
                onChange={(e) => handleTimeChange(h12, parseInt(e.target.value, 10), ampm)}
                className="time-field-select"
              >
                {Array.from({ length: 60 }, (_, i) => i).map((m) => (
                  <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                ))}
              </select>
              <select
                value={ampm}
                disabled={isFieldReadOnly}
                onChange={(e) => handleTimeChange(h12, minutes, e.target.value)}
                className="time-field-select time-ampm"
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </label>
        )
      }
    }
    return null
  }

  const generateShortId = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let id = ''
    const arr = new Uint8Array(8)
    crypto.getRandomValues(arr)
    for (let i = 0; i < 8; i++) id += chars[arr[i] % chars.length]
    return id
  }

  const createShortSigningLink = async (recipientEmailOverride = '', emailEventDocId = null, onPdfUploadProgress = null) => {
    const trimmedEmail = (recipientEmailOverride || recipientEmail).trim()
    const currentAgreementHtml = typeof editorRef.current?.innerHTML === 'string' ? editorRef.current.innerHTML : agreementHtml
    const shortId = generateShortId()
    const baseUrl = getPublicAppBaseUrl()
    const shortUrl = `${baseUrl}${(typeof window !== 'undefined' ? window.location.pathname : '') || '/'}?id=${shortId}`

    const linkDetails = {
      shortId,
      signingLinkUrl: shortUrl,
      scopeTitle: scopeTitle || 'Project Scope',
      agreementHtml: currentAgreementHtml || '',
      logoDataUrl: (canUseLogo && logoDataUrl) ? logoDataUrl : null,
      logoLocation: (canUseLogo && logoDataUrl) ? logoLocation : 'bottom-center',
      includeDate,
      includeTerms,
      includeFullName,
      includeSignature,
      includePhone,
      totalSignatures,
      customFields: customFields.length > 0 ? customFields : null,
      layoutColumns: layoutColumns || 1,
      fieldLayout: fieldLayout && fieldLayout.length > 0 ? fieldLayout : null,
      labelOverrides: labelOverrides && Object.keys(labelOverrides).length > 0 ? labelOverrides : null,
      excludedFromLayout: excludedFromLayout && excludedFromLayout.length > 0 ? excludedFromLayout : null,
      recipientEmail: trimmedEmail || null,
      senderName: senderName?.trim() || null,
      senderEmail: senderEmail?.trim() || null,
      ownerId: user?.uid || null,
      docId: emailEventDocId || null,
      signers: signers,
      verification: { verifyPhoto, verifyEmail, verifyOTP, verifyPhone },
      createdAt: serverTimestamp(),
    }

    if (fullPdfPages.length > 0 && user?.uid && emailEventDocId) {
      const pngToJpegDataUrl = (pngDataUrl, quality = 0.55) =>
        new Promise((resolve, reject) => {
          const img = new Image()
          img.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width = img.width
            canvas.height = img.height
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0)
            resolve(canvas.toDataURL('image/jpeg', quality))
          }
          img.onerror = reject
          img.src = pngDataUrl
        })
      const fullPdfPagePaths = []
      const linkId = `${user.uid}_${emailEventDocId}`
      for (let i = 0; i < fullPdfPages.length; i++) {
        const jpegDataUrl = await pngToJpegDataUrl(fullPdfPages[i].imageDataUrl)
        const path = `temporaryLinks/${linkId}/page-${i}.jpg`
        const storageRef = ref(storage, path)
        await uploadString(storageRef, jpegDataUrl, 'data_url')
        fullPdfPagePaths.push(path)
        if (typeof onPdfUploadProgress === 'function') onPdfUploadProgress(i + 1, fullPdfPages.length)
      }
      linkDetails.fullPdfPagePaths = fullPdfPagePaths
      linkDetails.fullPdfPageCount = fullPdfPages.length
    }

    if (emailEventDocId && user?.uid) {
      await updateDoc(doc(db, 'users', user.uid, 'emailEvents', emailEventDocId), {
        agreementHtml: currentAgreementHtml || '',
        linkDetails,
        viewLinkUrl: shortUrl,
      })
    } else {
      throw new Error('Email event and user are required to create a signing link.')
    }
    return shortUrl
  }

  const createSigningLink = (emailOverride = '', docId = null) => {
    const trimmedEmail = (emailOverride || recipientEmail).trim()
    if (!trimmedEmail) return ''
    const baseUrl = getPublicAppBaseUrl()
    const agreementPayload = encodeURIComponent(
      btoa(unescape(encodeURIComponent(agreementHtml)))
    )
    const scopePayload = encodeURIComponent(scopeTitle)
    const incParams = `includeDate=${includeDate ? 1 : 0}&includeTerms=${includeTerms ? 1 : 0}&includeFullName=${includeFullName ? 1 : 0}&includeSignature=${includeSignature ? 1 : 0}&includePhone=${includePhone ? 1 : 0}&totalSignatures=${totalSignatures}`
    const logoParam = (canUseLogo && logoDataUrl) ? `&logo=${encodeURIComponent(btoa(logoDataUrl))}` : ''
    const logoLocParam = (canUseLogo && logoDataUrl) ? `&logoLocation=${encodeURIComponent(logoLocation)}` : ''
    const docIdParam = docId ? `&docId=${docId}` : ''
    const ownerIdParam = user?.uid ? `&ownerId=${user.uid}` : ''
    return `${baseUrl}${window.location.pathname}?recipient=${encodeURIComponent(
      trimmedEmail
    )}&scope=${scopePayload}&agreement=${agreementPayload}&${incParams}${logoParam}${logoLocParam}${docIdParam}${ownerIdParam}`
  }

  const validateBeforeSendOrCopy = () => {
    const trimmedName = senderName.trim()
    if (!trimmedName) {
      return 'From name/organisation is required.'
    }
    const trimmedSender = senderEmail.trim()
    if (!trimmedSender) {
      return 'From email address is required.'
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedSender)) {
      return 'Enter a valid from email address.'
    }
    const recipients = recipientEmail
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean)
    if (recipients.length === 0) {
      return 'Enter at least one recipient email address.'
    }
    const invalidRecipients = recipients.filter(
      (email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    )
    if (invalidRecipients.length > 0) {
      return `These recipient emails are invalid: ${invalidRecipients.join(', ')}.`
    }
    const agreementText = agreementHtml.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    if (!agreementText && fullPdfPages.length === 0) {
      return t('app.agreementRequired')
    }
    if (agreementText.length > charLimit) {
      setLimitReachedReason('characters')
      setCharLimitReachedOpen(true)
      return 'OVER_LIMIT'
    }
    const agreementSizeKb = new TextEncoder().encode(agreementHtml).length / 1024
    if (agreementSizeKb > maxAgreementSizeKb) {
      setLimitReachedReason('size')
      setCharLimitReachedOpen(true)
      return 'OVER_LIMIT'
    }
    const signingSectionBytes = new TextEncoder().encode(JSON.stringify(signers)).length
    if (signingSectionBytes > signatureTextLimitKB * 1024) {
      setSigningLimitReachedOpen(true)
      return 'OVER_LIMIT'
    }
    if (totalSignatures === 2) {
      const activeLayoutForValidation = getActiveLayout(0)
      const activeIds = new Set(activeLayoutForValidation.filter((f) => !f.isSpacer).map((f) => f.id))
      const s0 = signers[0]
      const senderErrors = []
      if (activeIds.has('date') && !s0?.date) senderErrors.push('date')
      if (activeIds.has('terms') && !s0?.agreed) senderErrors.push('terms')
      if (activeIds.has('fullName') && !s0?.fullName?.trim()) senderErrors.push('full name')
      if (activeIds.has('phone') && !s0?.phone?.trim()) senderErrors.push('phone')
      if (activeIds.has('signature') && !s0?.signatureDataUrl) senderErrors.push('signature')
      customFields.forEach((cf) => {
        if (!activeIds.has(cf.id)) return
        const val = (s0?.customFields || {})[cf.id]
        if (cf.type === 'signature' && !val) senderErrors.push(cf.label)
        else if (cf.type === 'text' && !String(val || '').trim()) senderErrors.push(cf.label)
        else if (cf.type === 'date' && !val) senderErrors.push(cf.label)
        else if (cf.type === 'number' && (val === undefined || val === '')) senderErrors.push(cf.label)
        else if (cf.type === 'picklist' && !val) senderErrors.push(cf.label)
        else if (cf.type === 'time' && !val) senderErrors.push(cf.label)
      })
      if (senderErrors.length > 0) {
        return `Complete your signing fields first: ${senderErrors.join(', ')}.`
      }
    }
    return null
  }

  const RECIPIENT_EMAIL_ERROR = 'Enter at least one recipient email address.'
  useEffect(() => {
    if (!isAuthModalOpen) {
      setAuthTermsAccepted(false)
      setAuthTermsAcceptError('')
    }
  }, [isAuthModalOpen])

  useEffect(() => {
    if (authMode === 'login') {
      setAuthTermsAccepted(false)
      setAuthTermsAcceptError('')
    }
  }, [authMode])

  useEffect(() => {
    if (sendStatus === RECIPIENT_EMAIL_ERROR && sendStatusType === 'error') {
      setHighlightRecipientEmailField(true)
      requestAnimationFrame(() => {
        recipientEmailInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    }
  }, [sendStatus, sendStatusType])

  const handleSendEmail = async () => {
    const validationError = validateBeforeSendOrCopy()
    if (validationError) {
      if (validationError !== 'OVER_LIMIT') {
      setSendStatus(validationError)
      setSendStatusType('error')
      }
      return
    }
    // Require a real account; after signup they stay on the document (no auto subscription modal).
    if (!user || user.isAnonymous) {
      setOpenPaywallAfterAuth('send')
      setAuthMode('signup')
      setIsAuthModalOpen(true)
      saveDocumentStateForPaymentReturn()
      return
    }
    const hasSubscription = !!(userSubscriptionId || userCustomerId)
    // For non-subscribed users, refetch credits + free-send flag from server
    let creditsCount = emailOneOffCredits || 0
    let freeUsed = freeDocumentUsed === true
    if (!hasSubscription && user?.uid) {
      try {
        const snap = await getDocFromServer(doc(db, 'users', user.uid))
        if (snap.exists()) {
          const d = snap.data()
          const c = Math.max(0, d?.emailOneOffCredits ?? 0)
          if (c !== creditsCount) setEmailOneOffCredits(c)
          creditsCount = c
          freeUsed = d?.freeDocumentUsed === true
          setFreeDocumentUsed(freeUsed)
        }
      } catch (_) { /* use existing state */ }
    }
    const hasCredits = creditsCount > 0
    if (!hasSubscription && !hasCredits && freeUsed) {
      setPaywallPendingAction('send')
      setSubscribeOrPayShowOneTime(false)
      setBillingCycle('yearly')
      setSubscriptionTier('standard')
      setIsOneTimeUse(false)
      setIsSubscribeOrPayOpen(true)
      return
    }

    const trimmedSenderEmail = senderEmail.trim()
    const recipients = recipientEmail
      .split(',')
      .map((email) => email.trim())
      .filter(Boolean)
    if (recipients.length > maxRecipientsForSend) {
      setSendStatus(`Maximum ${maxRecipientsForSend} recipient(s) allowed for your plan.`)
      setSendStatusType('error')
      return
    }
    const emailsToCount = hasSubscription ? recipients.length : 1
    const wouldExceedEmailLimit = effectiveMaxEmails > 0 && (emailsSentThisMonth + emailsToCount > effectiveMaxEmails)
    const hasReachedSignedLimit = effectiveMaxEmailsSigned > 0 && (currentEmailsSignedPerMonth >= effectiveMaxEmailsSigned)
    // Subscribed users: send works unless (1) Total Monthly Emails Sent + top-up emails exhausted, OR (2) Emails Signed (this month) + top-up signed exhausted.
    // effectiveMaxEmails/Signed = monthly limit + subscribed top-up (and legacy); block only when that full pool is used.
    const eligibleFreeSend = !hasSubscription && !hasCredits && !freeUsed
    const hasSendCapacity =
      hasCredits ||
      eligibleFreeSend ||
      (hasSubscription &&
        (emailsSentThisMonth + emailsToCount <= effectiveMaxEmails) &&
        !hasReachedSignedLimit)
    if (!hasSendCapacity) {
      if (userSubscriptionId || userCustomerId) {
        setLimitReachedPendingAction('send')
        setUpgradeModalLimitReason(wouldExceedEmailLimit ? 'emails' : 'signed')
        setIsLimitReachedOpen(true)
      } else {
        setPaywallPendingAction('send')
        setSubscribeOrPayShowOneTime(false)
        setBillingCycle('yearly')
        setSubscriptionTier('standard')
        setIsOneTimeUse(false)
        setIsSubscribeOrPayOpen(true)
      }
        return
      }
    if (!hasCredits && (wouldExceedEmailLimit || hasReachedSignedLimit)) {
      setLimitReachedPendingAction('send')
      setUpgradeModalLimitReason(wouldExceedEmailLimit ? 'emails' : 'signed')
      setIsLimitReachedOpen(true)
      return
    }

    const trimmedScope = (scopeTitle || 'Agreement').trim()

    const agreementText = agreementHtml.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    if (!agreementText && fullPdfPages.length === 0) {
      setSendStatus(t('app.agreementOrPdfRequired'))
      setSendStatusType('error')
      return
    }

    if (totalSignatures === 2) {
      const activeLayoutForSend = getActiveLayout(0)
      const activeIdsForSend = new Set(activeLayoutForSend.filter((f) => !f.isSpacer).map((f) => f.id))
      const s0 = signers[0]
      const senderErrors = []
      if (activeIdsForSend.has('date') && !s0?.date) senderErrors.push('date')
      if (activeIdsForSend.has('terms') && !s0?.agreed) senderErrors.push('terms')
      if (activeIdsForSend.has('fullName') && !s0?.fullName?.trim()) senderErrors.push('full name')
      if (activeIdsForSend.has('phone') && !s0?.phone?.trim()) senderErrors.push('phone')
      if (activeIdsForSend.has('signature') && !s0?.signatureDataUrl) senderErrors.push('signature')
      customFields.forEach((cf) => {
        if (!activeIdsForSend.has(cf.id)) return
        const val = (s0?.customFields || {})[cf.id]
        if (cf.type === 'signature' && !val) senderErrors.push(cf.label)
        else if (cf.type === 'text' && !String(val || '').trim()) senderErrors.push(cf.label)
        else if (cf.type === 'date' && !val) senderErrors.push(cf.label)
        else if (cf.type === 'number' && (val === undefined || val === '')) senderErrors.push(cf.label)
        else if (cf.type === 'picklist' && !val) senderErrors.push(cf.label)
        else if (cf.type === 'time' && !val) senderErrors.push(cf.label)
      })
      if (senderErrors.length > 0) {
        setSendStatus(`Complete your signing fields first: ${senderErrors.join(', ')}.`)
        setSendStatusType('error')
        return
      }
    }

    const totalAttachmentSize = attachments.reduce((sum, f) => sum + f.size, 0)
    if (canUseAttachments && attachments.length > 0 && totalAttachmentSize > maxAttachmentBytes) {
      const limitMb = maxAttachmentBytes / 1024 / 1024
      setSendStatus(`Total attachment size (${(totalAttachmentSize / 1024 / 1024).toFixed(2)} MB) exceeds ${limitMb} MB limit.`)
      setSendStatusType('error')
      return
    }
    if (canUseAttachments && attachments.length > 0 && totalAttachmentSize > effectiveAttachmentLimit) {
      setSendStatus(`Total attachment size (${(totalAttachmentSize / 1024 / 1024).toFixed(2)} MB) exceeds ${(effectiveAttachmentLimit / 1024 / 1024).toFixed(0)} MB limit for email delivery. Please reduce attachment sizes.`)
      setSendStatusType('error')
      return
    }

    setIsSendConfirmOpen(true)
    return
  }

  const performSendEmail = async () => {
    const recipients = recipientEmail
      .split(',')
      .map((email) => email.trim())
      .filter(Boolean)
    const trimmedSenderEmail = senderEmail.trim()

    const hasAttachments = canUseAttachments && attachments.length > 0
    const attachmentPayloads = []
    if (hasAttachments) {
      for (let i = 0; i < attachments.length; i++) {
        const file = attachments[i]
        let dataUrl = file.dataUrl
        if (!dataUrl && file instanceof File) {
          dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(file)
        })
        }
        const base64 = (typeof dataUrl === 'string' && dataUrl.includes(',')) ? dataUrl.split(',')[1] : ''
        if (base64) {
          attachmentPayloads.push({ filename: (file.name || `attachment_${i + 1}`).trim(), base64 })
        }
      }
    }

    setSendStatus(`Sending ${recipients.length} emails...`)
    setSendStatusType('')
    setSendProgress(0)
    setSendStepMessage('Creating records…')
    let sentCount = 0
    const docIds = []
    const emailReminderDaysRequested = (() => {
      if (!canUseEmailReminders) return []
      const days = []
      if (emailReminder1d) days.push(1)
      if (emailReminder3d) days.push(3)
      if (emailReminder7d) days.push(7)
      return [...new Set(days)].sort((a, b) => a - b)
    })()
    let reminderEmailCopy = null
    if (emailReminderDaysRequested.length > 0) {
      try {
        reminderEmailCopy = {
          subject: t('email.reminderSubject'),
          title: t('email.reminderTitle'),
          subtitle: t('email.reminderSubtitle'),
          fromLabel: t('email.fromLabel'),
          body: t('email.reminderBody'),
          button: t('email.signButton'),
          copyLink: t('email.copyLink'),
          ignoreNote: t('email.reminderIgnoreNote'),
        }
      } catch (_) {
        reminderEmailCopy = null
      }
    }
    try {
      if (user) {
          const templateName = templates.find((t) => t.id === activeTemplateId)?.name || null
        for (const email of recipients) {
          try {
          const docRef = await addDoc(collection(db, 'users', user.uid, 'emailEvents'), {
            docId: '',
            ownerId: user.uid,
            templateId: activeTemplateId || null,
            templateName: templateName || null,
            scopeTitle: scopeTitle || 'Agreement',
              recipientEmails: [email],
            status: 'sent',
            createdAt: serverTimestamp(),
            sentAt: null,
            viewLinkUrl: '',
            signedAt: null,
            signers: [],
            metadata: {},
            signerName: '',
            signerEmail: '',
            signedDate: null,
            emailReminderDaysRequested,
            emailRemindersActive: emailReminderDaysRequested.length > 0,
            emailRemindersDelivered: [],
            reminderEmailCopy,
          })
          await updateDoc(docRef, { docId: docRef.id })
            docIds.push(docRef.id)
        } catch (err) {
          console.error('Failed to create document record:', err)
            throw err
        }
      }
    }
      setSendProgress(10)
      setSendStepMessage('Creating signing links & uploading PDF…')
    const sendSignRequest = httpsCallable(functions, 'sendSignRequestEmail')
      const originSend = typeof window !== 'undefined' ? window.location.origin : ''
      const siteUrlForEmail = (import.meta.env.VITE_PUBLIC_BASE_URL || (originSend && !originSend.includes('web.app') ? originSend.replace(/\/$/, '') : 'https://primedocumentsign.com')).replace(/\/$/, '')
      const emailTemplateEn = {
        subject: 'Please sign this agreement',
        title: 'Document to Sign',
        subtitle: "You've been asked to sign a document",
        fromLabel: 'From',
        body: 'Click the button below to open the document and add your signature. Any recipient can use this link to access and sign, no account or subscription required.',
        button: 'Sign Document',
        copyLink: 'Or copy this link:',
        ignoreNote: 'If you did not expect this email, you can safely ignore it.',
        promoHeadline: 'Join Prime Document Sign today — from just $2/month.',
        promoText: 'Send agreements, collect e-signatures, and download audit-ready PDFs. Start in minutes — no printer, no postage.',
        promoButton: 'Visit primedocumentsign.com',
        promoUrl: siteUrlForEmail,
      }
      let emailTemplate = emailTemplateEn
      try {
        const subj = t('email.signSubject')
        const title = t('email.signTitle')
        const subtitle = t('email.signSubtitle')
        const fromLabel = t('email.fromLabel')
        const body = t('email.signBody')
        const button = t('email.signButton')
        const copyLink = t('email.copyLink')
        const ignoreNote = t('email.ignoreNote')
        const promoHeadline = t('email.signPromoHeadline')
        const promoText = t('email.signPromoText')
        const promoButton = t('email.signPromoButton')
        if (subj && subj !== 'email.signSubject' && title && title !== 'email.signTitle') {
          emailTemplate = {
            subject: subj,
            title,
            subtitle,
            fromLabel,
            body,
            button,
            copyLink,
            ignoreNote,
            promoHeadline,
            promoText,
            promoButton,
            promoUrl: siteUrlForEmail,
          }
        }
      } catch (_) {
        emailTemplate = emailTemplateEn
      }
      const n = recipients.length
      for (let i = 0; i < n; i++) {
        const email = recipients[i]
        const docId = docIds[i]
        const onPdfProgress = (current, total) => {
          if (total > 0) setSendProgress(10 + Math.round((50 * (i + current / total)) / n))
        }
        const signingLink = await createShortSigningLink(email, docId, onPdfProgress)
        setSendProgress(10 + Math.round((50 * (i + 1)) / n))
      try {
        await sendSignRequest({
          recipientEmail: email,
          senderEmail: trimmedSenderEmail,
          senderName: senderName.trim(),
          signingLink,
            bccTestEmail: isQuickSend,
            attachments: attachmentPayloads,
            emailTemplate,
            scopeTitle: scopeTitle || 'Agreement',
        })
        sentCount += 1
          setSendProgress(60 + Math.round((sentCount / n) * 40))
          setSendStepMessage(`Sending emails… ${sentCount}/${n}`)
          if (docId && user?.uid) {
            try {
              await updateDoc(doc(db, 'users', user.uid, 'emailEvents', docId), { sentAt: serverTimestamp() })
            } catch (_) {}
          }
      } catch (error) {
          setSendProgress(null)
          setSendStepMessage('')
        let fullMessage = error?.message || 'Failed to send one or more emails. Please try again.'
        if (error?.code) fullMessage = `${fullMessage} (${error.code})`
        if (error?.details != null) {
          const detailStr = typeof error.details === 'object' ? JSON.stringify(error.details) : String(error.details)
          fullMessage = `${fullMessage}\n${detailStr}`
        }
        setSendStatus(fullMessage)
        setSendStatusType('error')
        reportErrorToAdmin('SendEmail-sendSignRequest', error, { recipientEmail: email, signingLink, docId })
        return
      }
    }
    } catch (err) {
      setSendProgress(null)
      setSendStepMessage('')
      setSendStatus('Failed to create signing links. Please try again.')
      setSendStatusType('error')
      reportErrorToAdmin('SendEmail-createShortSigningLink', err, { recipientCount: recipients.length })
      return
    }
    const hasSub = !!(userSubscriptionId || userCustomerId)
    let freshUd = null
    let serverCredits = Math.max(0, emailOneOffCredits || 0)
    let serverFreeUsed = freeDocumentUsed === true
    if (user?.uid) {
      try {
        const sn = await getDocFromServer(doc(db, 'users', user.uid))
        freshUd = sn.exists() ? sn.data() : {}
        serverCredits = Math.max(0, freshUd.emailOneOffCredits ?? 0)
        serverFreeUsed = freshUd.freeDocumentUsed === true
      } catch (_) {
        freshUd = null
      }
    }
    const creditsOnlyUser = !hasSub && serverCredits > 0
    const willConsumeFree = Boolean(user?.uid && !hasSub && serverCredits === 0 && !serverFreeUsed)
    const emailsToAdd = creditsOnlyUser ? 1 : sentCount
    const newMonthTotal = emailsSentThisMonth + emailsToAdd
    const subscriptionLimit = maxEmailsPerMonth + effectiveTopUpEmails
    const effectiveSubscriptionLimit = creditsOnlyUser ? 0 : subscriptionLimit
    const usedCredit = effectiveSubscriptionLimit <= 0 || newMonthTotal > effectiveSubscriptionLimit
    if (user?.uid) {
      const now = new Date()
      const usageMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
      const isNewMonth = userUsage.monthKey !== usageMonthKey
      const emailsToWrite = isNewMonth ? emailsToAdd : newMonthTotal
      try {
        if (willConsumeFree) {
          await updateDoc(doc(db, 'users', user.uid), { freeDocumentUsed: true, updatedAt: serverTimestamp() })
          setFreeDocumentUsed(true)
          const ud = freshUd || {}
          appendFreeAccountSendLog(db, {
            userId: user.uid,
            email: user.email || ud.email || '',
            firstName: ud.firstName || '',
            lastName: ud.lastName || '',
            recipientCount: sentCount,
            scopeTitle: scopeTitle || 'Agreement',
          }).catch((e) => console.error('[freeAccountSend]', e))
        } else {
        const updatePayload = {
            'subscription.CurrentEmailsPerMonth': emailsToWrite,
          'subscription.monthKey': usageMonthKey,
          updatedAt: serverTimestamp(),
        }
          if (usedCredit && serverCredits > 0) {
          updatePayload.emailOneOffCredits = increment(-1)
          setEmailOneOffCredits((prev) => Math.max(0, prev - 1))
            const remainingOneOff = serverCredits - 1
            if (!hasSub && remainingOneOff === 0) {
              Object.assign(updatePayload, {
                'subscription.maxEmailsPerMonth': 0,
                'subscription.maxEmailsSignedPerMonth': 0,
                'subscription.maxSignedAgreementsPerMonth': 0,
                'subscription.maxEmailSignedSignaturesPerMonth': 0,
                'subscription.attachments': false,
                'subscription.pdfExtract': false,
                'subscription.EmailEvents': false,
                'subscription.logo': false,
                'subscription.verification': false,
                'subscription.PrioritySupportEmail': false,
                'subscription.maxAttachments': 0,
                'subscription.maxAttachmentBytes': 0,
                'subscription.maxCharacters': 0,
                'subscription.maxAgreementSizeKb': 100,
                'subscription.maxEmailRecipients': 0,
                'subscription.maxTemplates': 25,
                'subscription.templates': true,
              })
            }
          }
          const overageAfter = Math.max(0, newMonthTotal - maxEmailsPerMonth)
          const overageBefore = Math.max(0, emailsSentThisMonth - maxEmailsPerMonth)
          const decrementFromTopUp = overageAfter - overageBefore
          const subscribedTopUpAvailable = hasSub ? (userUsage.subscribedEmailTopUpCredit ?? 0) : 0
          const usedFromSubscribedTopUp = decrementFromTopUp > 0 && subscribedTopUpAvailable > 0
            ? Math.min(decrementFromTopUp, subscribedTopUpAvailable)
            : 0
          const decrementFromLegacyTopUps = decrementFromTopUp - usedFromSubscribedTopUp
          if (usedFromSubscribedTopUp > 0) {
            updatePayload['subscription.SubscribedEmailTopUpCredit'] = increment(-usedFromSubscribedTopUp)
          }
          if (decrementFromLegacyTopUps > 0 && userTopUps.length > 0) {
            let topUps = []
            if (freshUd && Array.isArray(freshUd.topUps)) {
              topUps = freshUd.topUps
            } else {
          const userSnap = await getDoc(doc(db, 'users', user.uid))
              topUps = (userSnap.exists() ? userSnap.data().topUps : []) || []
            }
            let remaining = decrementFromLegacyTopUps
          const updatedTopUps = topUps.map((t) => {
            if (remaining <= 0) return t
            const dateToppedUp = t.dateToppedUp?.toDate ? t.dateToppedUp.toDate() : (t.dateToppedUp ? new Date(t.dateToppedUp) : null)
            if (!dateToppedUp) return t
            const expiresEndOfMonth = t.expiresEndOfMonth || ''
            const thirtyDaysLater = (dateToppedUp.getTime ? dateToppedUp.getTime() : new Date(dateToppedUp).getTime()) + 30 * 24 * 60 * 60 * 1000
              const isActive = hasSub ? (expiresEndOfMonth && usageMonthKey <= expiresEndOfMonth) : (Date.now() <= thirtyDaysLater)
            if (!isActive || (t.emailsRemaining ?? 0) <= 0) return t
            const take = Math.min(remaining, t.emailsRemaining ?? 0)
            remaining -= take
            return { ...t, emailsRemaining: (t.emailsRemaining ?? 0) - take }
          })
          updatePayload.topUps = updatedTopUps
          setUserTopUps(updatedTopUps)
        }
        await updateDoc(doc(db, 'users', user.uid), updatePayload)
          setUserUsage((prev) => (prev.monthKey === usageMonthKey ? { ...prev, emailsSentThisMonth: emailsToWrite, ...(usedFromSubscribedTopUp > 0 ? { subscribedEmailTopUpCredit: Math.max(0, (prev.subscribedEmailTopUpCredit ?? 0) - usedFromSubscribedTopUp) } : {}) } : prev))
        }
      } catch (err) {
        console.error('Failed to update usage:', err)
      }
    }
    setSendProgress(100)
    setSendStepMessage('')
    setSendStatus(`Email sent successfully to ${sentCount} recipient(s).`)
    setSendStatusType('success')
    setTimeout(() => {
      setSendProgress(null)
    }, 800)
    fetchEmailEvents()
  }

  const handleCopyLink = async () => {
    const validationError = validateBeforeSendOrCopy()
    if (validationError) {
      if (validationError !== 'OVER_LIMIT') {
      setSendStatus(validationError)
      setSendStatusType('error')
      }
      return
    }
    // Require a real account; after signup they stay on the document (no auto subscription modal).
    if (!user || user.isAnonymous) {
      setOpenPaywallAfterAuth('copyLink')
      setAuthMode('signup')
      setIsAuthModalOpen(true)
      return
    }
    const hasSubscriptionCopy = !!(userSubscriptionId || userCustomerId)
    let creditsCountCopy = emailOneOffCredits || 0
    let freeUsedCopy = freeDocumentUsed === true
    if (!hasSubscriptionCopy && user?.uid) {
      try {
        const snap = await getDocFromServer(doc(db, 'users', user.uid))
        if (snap.exists()) {
          const d = snap.data()
          creditsCountCopy = Math.max(0, d?.emailOneOffCredits ?? 0)
          if (creditsCountCopy !== (emailOneOffCredits || 0)) setEmailOneOffCredits(creditsCountCopy)
          freeUsedCopy = d?.freeDocumentUsed === true
          setFreeDocumentUsed(freeUsedCopy)
        }
      } catch (_) {
        /* use state */
      }
    }
    if (!hasSubscriptionCopy && creditsCountCopy === 0 && freeUsedCopy) {
      setPaywallPendingAction('copyLink')
      setSubscribeOrPayShowOneTime(false)
      setBillingCycle('yearly')
      setSubscriptionTier('standard')
      setIsOneTimeUse(false)
      setIsSubscribeOrPayOpen(true)
      return
    }
    const hasReachedSignedLimit = effectiveMaxEmailsSigned > 0 && (currentEmailsSignedPerMonth >= effectiveMaxEmailsSigned)
    if (hasReachedSignedLimit) {
      setLimitReachedPendingAction('copyLink')
      setUpgradeModalLimitReason('signed')
      setIsLimitReachedOpen(true)
      return
    }

    setIsCopyLinkLoading(true)
    setSendStatus('')
    setSendStatusType('')

    let docId = null
    if (user && recipientEmail) {
      try {
        const templateName = templates.find((t) => t.id === activeTemplateId)?.name || null
        const docRef = await addDoc(collection(db, 'users', user.uid, 'emailEvents'), {
          docId: '',
          ownerId: user.uid,
          templateId: activeTemplateId || null,
          templateName: templateName || null,
          scopeTitle: scopeTitle || 'Agreement',
          recipientEmails: [recipientEmail],
          status: 'sent',
          createdAt: serverTimestamp(),
          sentAt: null,
          viewLinkUrl: '',
          signedAt: null,
          signers: [],
          metadata: {},
          signerName: '',
          signerEmail: '',
          signedDate: null
        })
        docId = docRef.id
        await updateDoc(docRef, { docId: docRef.id })
      } catch (err) {
        console.error('Failed to create document record:', err)
      }
    }

    try {
      const signingLink = await createShortSigningLink(recipientEmail, docId)
      await navigator.clipboard.writeText(signingLink)
      setSendStatus('Signing link copied to clipboard.')
      setSendStatusType('success')
      if (user?.uid && !hasSubscriptionCopy && creditsCountCopy === 0 && !freeUsedCopy) {
        try {
          await updateDoc(doc(db, 'users', user.uid), { freeDocumentUsed: true, updatedAt: serverTimestamp() })
          setFreeDocumentUsed(true)
          const uSnap = await getDoc(doc(db, 'users', user.uid))
          const ud = uSnap.exists() ? uSnap.data() : {}
          appendFreeAccountSendLog(db, {
            userId: user.uid,
            email: user.email || ud.email || '',
            firstName: ud.firstName || '',
            lastName: ud.lastName || '',
            recipientCount: 1,
            scopeTitle: scopeTitle || 'Agreement',
          }).catch((e) => console.error('[freeAccountSend]', e))
        } catch (consumeErr) {
          console.error('[copyLink] consume free send', consumeErr)
        }
      }
      fetchEmailEvents()
    } catch (err) {
      setSendStatus(err?.message || 'Failed to create or copy link. Please try again.')
      setSendStatusType('error')
      reportErrorToAdmin('CopyLink', err, { docId, recipientEmail })
    } finally {
      setIsCopyLinkLoading(false)
    }
  }

  const signingLink = createSigningLink()
  const missingFields = []
  const signersToValidate =
    totalSignatures === 1
      ? [signers[0]]
      : totalSignatures === 2
        ? isRecipientView
          ? [signers[1]]
          : [signers[0]]
        : signers
  for (let i = 0; i < signersToValidate.length; i++) {
    const s = signersToValidate[i]
    if (!s) continue
    const signerIdx = signers.indexOf(s)
    const activeLayoutForSigner = getActiveLayout(signerIdx)
    const activeFieldIds = new Set(activeLayoutForSigner.filter((f) => !f.isSpacer).map((f) => f.id))
    const label =
      totalSignatures === 2
        ? (isRecipientView ? 'Signer 2' : 'Your signing fields')
        : `signer ${i + 1}`
    if (activeFieldIds.has('date') && !s.date) missingFields.push(`date (${label})`)
    if (activeFieldIds.has('terms') && !s.agreed) missingFields.push(`terms (${label})`)
    if (activeFieldIds.has('fullName') && !s.fullName?.trim()) missingFields.push(`full name (${label})`)
    if (activeFieldIds.has('phone') && !s.phone?.trim()) missingFields.push(`phone (${label})`)
    if (activeFieldIds.has('signature') && !s.signatureDataUrl) missingFields.push(`signature (${label})`)
    customFields.forEach((cf) => {
      if (!activeFieldIds.has(cf.id)) return
      const val = (s.customFields || {})[cf.id]
      if (cf.type === 'signature' && !val) missingFields.push(`${cf.label} (${label})`)
      else if (cf.type === 'text' && !String(val || '').trim()) missingFields.push(`${cf.label} (${label})`)
      else if (cf.type === 'date' && !val) missingFields.push(`${cf.label} (${label})`)
      else if (cf.type === 'number' && (val === undefined || val === '')) missingFields.push(`${cf.label} (${label})`)
      else if (cf.type === 'picklist' && !val) missingFields.push(`${cf.label} (${label})`)
      else if (cf.type === 'time' && !val) missingFields.push(`${cf.label} (${label})`)
    })
  }
  const isComplete = missingFields.length === 0
  const sanitizedAgreementHtml = DOMPurify.sanitize(agreementHtml, AGREEMENT_EDITOR_PURIFY_OPTIONS)
  const previewAgreementHtml = sanitizedAgreementHtml
  const agreementPlainText = sanitizedAgreementHtml
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  // Treat empty/whitespace-only content as 0 for display (avoids showing e.g. 14 from default markup)
  const isAgreementContentEmpty = !agreementHtml || agreementHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().replace(/\s/g, '') === ''
  const displayCharCount = isAgreementContentEmpty ? 0 : agreementPlainText.length
  const displaySizeKb = isAgreementContentEmpty ? 0 : new TextEncoder().encode(agreementHtml).length / 1024

  useEffect(() => {
    if (view === 'app' && editorRef.current && draftRestored) {
      editorRef.current.innerHTML = agreementHtml
      lastValidHtmlRef.current = agreementHtml
      setDraftRestored(false)
    }
  }, [view, draftRestored, agreementHtml])

  // Sync state → editor when state has content but editor is empty (e.g. after payment return or draft restore before ref was set)
  useEffect(() => {
    if (view !== 'app' || !editorRef.current || !agreementHtml) return
    const hasStateContent = agreementHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().replace(/\s/g, '') !== ''
    if (!hasStateContent) return
    const editorHtml = editorRef.current.innerHTML || ''
    const hasEditorContent = editorHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().replace(/\s/g, '') !== ''
    if (!hasEditorContent) {
      editorRef.current.innerHTML = agreementHtml
      lastValidHtmlRef.current = agreementHtml
    }
  }, [view, agreementHtml])

  // After payment return or draft restore, sync agreement text to editor (retry so we catch late-mounted editor)
  useLayoutEffect(() => {
    if (view !== 'app' || !agreementHtml) return
    const hasContent = agreementHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() !== ''
    if (!hasContent) return
    const apply = () => {
      if (!editorRef.current) return
      const editorHtml = editorRef.current.innerHTML || ''
      const hasEditorContent = editorHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() !== ''
      if (!hasEditorContent) {
        editorRef.current.innerHTML = agreementHtml
        lastValidHtmlRef.current = agreementHtml
      }
    }
    apply()
    const t1 = setTimeout(apply, 0)
    const t2 = setTimeout(apply, 50)
    const t3 = setTimeout(apply, 200)
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3) }
  }, [view, agreementHtml])

  // Signature block flows directly below agreement text (no spacer pushing it to next page)

  const FONT_SIZE_OPTIONS = [8, 9, 10, 11, 12, 13, 14, 16, 18, 20, 22, 24, 26, 28, 32, 36, 40, 48, 56, 64, 72]

  const updateActiveFormats = () => {
    if (!editorRef.current) return
    if (document.activeElement?.closest?.('.font-size-select')) return
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return
    if (!editorRef.current.contains(selection.anchorNode)) return

    let header = 'P'
    let fontSize = '13'
    const useLockedFontSize = Date.now() < fontSizeLockUntilRef.current || lastAppliedFontSizeRef.current != null
    if (useLockedFontSize) {
      fontSize = String(lastAppliedFontSizeRef.current ?? lastSelectedFontSizeRef.current)
      lastAppliedFontSizeRef.current = null
    }
    const anchorNode = selection.anchorNode

    const inlineFormatActiveFromAnchor = (format) => {
      if (!anchorNode) return false
      let n = anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.parentElement : anchorNode
      while (n && editorRef.current.contains(n)) {
        if (n.nodeType === Node.ELEMENT_NODE) {
          if (format === 'bold' && (n.nodeName === 'B' || n.nodeName === 'STRONG')) return true
          if (format === 'underline' && n.nodeName === 'U') return true
          if (format === 'strike' && (n.nodeName === 'S' || n.nodeName === 'STRIKE' || n.nodeName === 'DEL')) return true
          try {
            const computed = window.getComputedStyle(n)
            if (format === 'bold') {
              const weight = computed.fontWeight
              const numericWeight = parseInt(weight, 10)
              if ((Number.isFinite(numericWeight) && numericWeight >= 600) || weight === 'bold') return true
            }
            if (format === 'underline' && computed.textDecorationLine.includes('underline')) return true
            if (format === 'strike' && computed.textDecorationLine.includes('line-through')) return true
          } catch (_) {
            /* ignore */
          }
        }
        n = n.parentElement
      }
      return false
    }

    const listActiveFromAnchor = (tagName) => {
      if (!anchorNode) return false
      const el = anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.parentElement : anchorNode
      return !!el?.closest?.(tagName)
    }

    /** queryCommandState('italic') is wrong after mixed CSS/span styling; walk DOM + computed style */
    const italicActiveFromAnchor = () => {
      if (!anchorNode) return document.queryCommandState('italic')
      let n = anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.parentElement : anchorNode
      while (n && editorRef.current.contains(n)) {
        if (n.nodeName === 'I' || n.nodeName === 'EM') return true
        if (n.nodeType === Node.ELEMENT_NODE) {
          try {
            if (window.getComputedStyle(n).fontStyle === 'italic') return true
          } catch (_) {
            /* ignore */
          }
        }
        n = n.parentElement
      }
      return false
    }

    if (anchorNode && anchorNode.parentElement) {
      const headerEl = anchorNode.parentElement.closest('.rt-h1,.rt-h2,.rt-h3')
      if (headerEl) {
        if (headerEl.classList.contains('rt-h1')) header = 'H1'
        if (headerEl.classList.contains('rt-h2')) header = 'H2'
        if (headerEl.classList.contains('rt-h3')) header = 'H3'
      } else {
        const blockHeader = document
          .queryCommandValue('formatBlock')
          ?.toString()
          .replace(/[<>]/g, '')
          .toUpperCase()
        header = blockHeader && blockHeader !== 'DIV' ? blockHeader : 'P'
      }
      if (!useLockedFontSize) {
        const sizeEl = anchorNode.parentElement.closest('[data-font-size]')
        if (sizeEl) {
          fontSize = sizeEl.getAttribute('data-font-size') || '13'
          lastSelectedFontSizeRef.current = parseInt(fontSize, 10) || 13
        } else {
          // No data-font-size ancestor: use computed font size (e.g. default paragraph 13px)
          const el = anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.parentElement : anchorNode
          let computedPx = 13
          if (el && typeof window.getComputedStyle === 'function') {
            const computed = window.getComputedStyle(el).fontSize
            const parsed = parseInt(computed, 10)
            if (Number.isFinite(parsed)) computedPx = parsed
          }
          const nearest = FONT_SIZE_OPTIONS.reduce((a, b) =>
            Math.abs(computedPx - a) <= Math.abs(computedPx - b) ? a : b
          )
          fontSize = String(nearest)
          lastSelectedFontSizeRef.current = nearest
        }
      }
      const fontEl = anchorNode.parentElement.closest('[data-font-family]')
      let fontFamily = 'Arial'
      if (fontEl) {
        const ff = fontEl.getAttribute('data-font-family')
        const match = FONT_OPTIONS.find((f) => f.value === ff)
        fontFamily = match ? match.name : 'Arial'
      }
      const nextFormats = {
        bold: inlineFormatActiveFromAnchor('bold'),
        italic: italicActiveFromAnchor(),
        underline: inlineFormatActiveFromAnchor('underline'),
        strike: inlineFormatActiveFromAnchor('strike'),
        unordered: listActiveFromAnchor('ul'),
        ordered: listActiveFromAnchor('ol'),
        header: header && header !== 'DIV' ? header : 'P',
        fontSize,
        fontFamily,
      }
      const override = activeFormatOverrideRef.current
      if (override && override.until > Date.now()) {
        setActiveFormats((prev) => ({ ...prev, ...nextFormats, ...override.values }))
    } else {
        activeFormatOverrideRef.current = null
        setActiveFormats(nextFormats)
      }
    } else {
      fontSize = String(lastSelectedFontSizeRef.current ?? 13)
      const nextFormats = {
        bold: inlineFormatActiveFromAnchor('bold'),
        italic: italicActiveFromAnchor(),
        underline: inlineFormatActiveFromAnchor('underline'),
        strike: inlineFormatActiveFromAnchor('strike'),
        unordered: listActiveFromAnchor('ul'),
        ordered: listActiveFromAnchor('ol'),
        header: header && header !== 'DIV' ? header : 'P',
        fontSize,
      }
      const override = activeFormatOverrideRef.current
      if (override && override.until > Date.now()) {
        setActiveFormats((prev) => ({
          ...prev,
          ...nextFormats,
          ...override.values,
          fontFamily: prev.fontFamily || 'Arial',
        }))
      } else {
        activeFormatOverrideRef.current = null
        setActiveFormats((prev) => ({
        ...prev,
          ...nextFormats,
        fontFamily: prev.fontFamily || 'Arial',
      }))
      }
    }
  }

  useEffect(() => {
    document.addEventListener('selectionchange', updateActiveFormats)
    return () => {
      document.removeEventListener('selectionchange', updateActiveFormats)
    }
  }, [])

  const applyFormat = (command, value = null) => {
    const ed = editorRef.current
    if (!ed) return

    // Preserve selection across focus() — some browsers collapse selection when refocusing contenteditable
    const sel = window.getSelection()
    let savedRange = null
    if (sel && sel.rangeCount > 0 && ed.contains(sel.anchorNode)) {
      savedRange = sel.getRangeAt(0).cloneRange()
    }

    const isCollapsedSelection = !savedRange || savedRange.collapsed
    if (isCollapsedSelection) {
      const toggleMap = {
        bold: 'bold',
        italic: 'italic',
        underline: 'underline',
        strikeThrough: 'strike',
        insertUnorderedList: 'unordered',
        insertOrderedList: 'ordered',
      }
      const key = toggleMap[command]
      if (key) {
        const existingOverride = activeFormatOverrideRef.current?.until > Date.now()
          ? activeFormatOverrideRef.current.values
          : {}
        const baseValues = {
          bold: activeFormats.bold,
          italic: activeFormats.italic,
          underline: activeFormats.underline,
          strike: activeFormats.strike,
          unordered: activeFormats.unordered,
          ordered: activeFormats.ordered,
          ...existingOverride,
        }
        const nextValues = { ...baseValues }
        if (key === 'unordered') {
          nextValues.unordered = !baseValues.unordered
          nextValues.ordered = false
        } else if (key === 'ordered') {
          nextValues.ordered = !baseValues.ordered
          nextValues.unordered = false
        } else {
          nextValues[key] = !baseValues[key]
        }
        activeFormatOverrideRef.current = {
          until: Date.now() + 1500,
          values: nextValues,
        }
        flushSync(() => {
          setActiveFormats((prev) => ({
            ...prev,
            ...nextValues,
          }))
        })
      }
    }

    ed.focus()
    if (savedRange && sel) {
      try {
        sel.removeAllRanges()
        sel.addRange(savedRange)
      } catch (_) {
        /* ignore */
      }
    }

    // foreColor uses styleWithCSS=true elsewhere; leaving that on breaks italic/bold on colored text in WebKit/Chromium.
    // Use semantic <i>/<b>/… so formatting composes reliably with spans (color, font size, etc.).
    if (['bold', 'italic', 'underline', 'strikeThrough'].includes(command)) {
      try {
        document.execCommand('styleWithCSS', false, false)
      } catch (_) {
        /* ignore */
      }
    }

    document.execCommand(command, false, value)
    const html = ed.innerHTML
      lastValidHtmlRef.current = html
      setAgreementHtml(html)
    updateActiveFormats()
  }

  const applyHeaderInline = (level) => {
    if (!editorRef.current) return
    editorRef.current.focus()
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return
    const range = selection.getRangeAt(0)
    if (range.collapsed) return

    const span = document.createElement('span')
    span.className = `rt-${level.toLowerCase()}`
    const contents = range.cloneContents()
    span.appendChild(contents)
    range.deleteContents()
    range.insertNode(span)

    const newRange = document.createRange()
    newRange.selectNodeContents(span)
    selection.removeAllRanges()
    selection.addRange(newRange)
    if (editorRef.current) {
      const html = editorRef.current.innerHTML
      lastValidHtmlRef.current = html
      setAgreementHtml(html)
    }
    setActiveFormats((prev) => ({ ...prev, header: level }))
    updateActiveFormats()
  }

  const FONT_OPTIONS = [
    { name: 'Arial', value: 'Arial, Helvetica, sans-serif' },
    { name: 'Georgia', value: 'Georgia, serif' },
    { name: 'Times New Roman', value: '"Times New Roman", Times, serif' },
    { name: 'Courier New', value: '"Courier New", Courier, monospace' },
    { name: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
    { name: 'Tahoma', value: 'Tahoma, Geneva, sans-serif' },
    { name: 'Trebuchet MS', value: '"Trebuchet MS", Helvetica, sans-serif' },
    { name: 'Palatino', value: 'Palatino Linotype, "Book Antiqua", Palatino, serif' },
    { name: 'Lucida Sans', value: '"Lucida Sans Unicode", "Lucida Grande", sans-serif' },
    { name: 'Impact', value: 'Impact, Charcoal, sans-serif' },
  ]

  const applyFontFamilyInline = (fontFamily) => {
    if (!editorRef.current) return
    editorRef.current.focus()
    const selection = window.getSelection()
    if (savedFormatSelectionRef.current && selection) {
      try {
        selection.removeAllRanges()
        selection.addRange(savedFormatSelectionRef.current)
      } catch (_) {}
      savedFormatSelectionRef.current = null
    }
    if (!selection || selection.rangeCount === 0) return
    const range = selection.getRangeAt(0)

    const span = document.createElement('span')
    span.style.fontFamily = fontFamily
    span.setAttribute('data-font-family', fontFamily)

    if (range.collapsed) {
      span.appendChild(document.createTextNode('\u200B'))
      range.insertNode(span)
      const newRange = document.createRange()
      newRange.setStart(span.firstChild, 1)
      newRange.collapse(true)
      selection.removeAllRanges()
      selection.addRange(newRange)
    } else {
      const contents = range.cloneContents()
      span.appendChild(contents)
      range.deleteContents()
      range.insertNode(span)
      const newRange = document.createRange()
      newRange.selectNodeContents(span)
      selection.removeAllRanges()
      selection.addRange(newRange)
    }

    if (editorRef.current) {
      const html = editorRef.current.innerHTML
      lastValidHtmlRef.current = html
      setAgreementHtml(html)
    }
    const displayName = FONT_OPTIONS.find((f) => f.value === fontFamily)?.name || fontFamily
    setActiveFormats((prev) => ({ ...prev, fontFamily: displayName }))
    updateActiveFormats()
  }

  /** Unwrap [data-font-size] elements inside a DocumentFragment (in memory). Used so we don't mutate the live DOM before using the range. */
  const unwrapFontSizeSpansInFragment = (fragment) => {
    const nodes = Array.from(fragment.childNodes)
    for (const node of nodes) {
      if (node.nodeType === Node.ELEMENT_NODE && node.hasAttribute?.('data-font-size')) {
        while (node.firstChild) fragment.insertBefore(node.firstChild, node)
        fragment.removeChild(node)
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        unwrapFontSizeSpansInFragment(node)
      }
    }
  }

  const unwrapFontSizeSpansInRange = (editor, range) => {
    const toUnwrap = []
    const root =
      range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
        ? range.commonAncestorContainer
        : range.commonAncestorContainer.parentElement
    if (!root || !editor.contains(root)) return
    // Use only standard Range API (compareBoundaryPoints). Do not use containsNode:
    // it is non-standard, missing in Firefox, and can fail after DOM mutations.
    const rangeFullyContainsNode = (r, node) => {
      try {
        const nodeRange = node.ownerDocument.createRange()
        try { nodeRange.selectNode(node) } catch (_) { nodeRange.selectNodeContents(node) }
        return r.compareBoundaryPoints(Range.START_TO_START, nodeRange) <= 0 &&
          r.compareBoundaryPoints(Range.END_TO_END, nodeRange) >= 0
      } catch (_) { return false }
    }
    const collect = (node) => {
      if (
        node.nodeType === Node.ELEMENT_NODE &&
        node.hasAttribute?.('data-font-size') &&
        rangeFullyContainsNode(range, node) &&
        node !== range.startContainer &&
        node !== range.endContainer
      ) {
        toUnwrap.push(node)
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        for (const child of node.childNodes) collect(child)
      }
    }
    collect(root)
    const depth = (el) => {
      let d = 0
      let p = el
      while (p && p !== editor) {
        d++
        p = p.parentNode
      }
      return d
    }
    toUnwrap.sort((a, b) => depth(b) - depth(a))
    for (const el of toUnwrap) {
      const parent = el.parentNode
      while (el.firstChild) parent.insertBefore(el.firstChild, el)
      parent.removeChild(el)
    }
    if (range.collapsed) {
      let n = range.startContainer
      if (n.nodeType === Node.TEXT_NODE) n = n.parentElement
      while (n && n !== editor) {
        if (n.hasAttribute?.('data-font-size') && n !== range.startContainer) {
          const parent = n.parentNode
          while (n.firstChild) parent.insertBefore(n.firstChild, n)
          parent.removeChild(n)
          break
        }
        n = n.parentElement
      }
    }
  }

  const applyFontSizeInline = (size) => {
    if (!editorRef.current) return
    const numSize = Number(size)
    lastSelectedFontSizeRef.current = numSize
    lastAppliedFontSizeRef.current = numSize
    fontSizeLockUntilRef.current = Date.now() + 400
    flushSync(() => setActiveFormats((prev) => ({ ...prev, fontSize: String(size) })))
    editorRef.current.focus()

    const sel = window.getSelection()
    if (savedFormatSelectionRef.current && sel) {
      try {
        sel.removeAllRanges()
        sel.addRange(savedFormatSelectionRef.current)
      } catch (_) {}
      savedFormatSelectionRef.current = null
    }

    if (!sel) return
    let range
    if (sel.rangeCount > 0) {
      const r = sel.getRangeAt(0)
      if (editorRef.current.contains(r.commonAncestorContainer)) {
        range = r
      }
    }
    // Only apply when selection is actually in the editor. Otherwise we would insert at start
    // and change the first word to the new size when the user had picked size from dropdown without focus in editor.
    if (!range) return

    const span = document.createElement('span')
    span.style.fontSize = `${size}px`
    span.setAttribute('data-font-size', size)

    if (range.collapsed) {
      span.appendChild(document.createTextNode('\u200B'))
      range.insertNode(span)
      const newRange = document.createRange()
      newRange.setStart(span.firstChild, 1)
      newRange.collapse(true)
      sel.removeAllRanges()
      sel.addRange(newRange)
    } else {
      // Extract selection first, then unwrap in the fragment. Never mutate the live DOM
      // before using the range, or the range can become invalid and only part of the
      // selection gets the new size (or nothing) on the first apply.
      const contents = range.cloneContents()
      range.deleteContents()
      unwrapFontSizeSpansInFragment(contents)
      span.appendChild(contents)
      range.insertNode(span)
      const newRange = document.createRange()
      newRange.selectNodeContents(span)
      sel.removeAllRanges()
      sel.addRange(newRange)
    }

    if (editorRef.current) {
      const html = editorRef.current.innerHTML
      lastValidHtmlRef.current = html
      setAgreementHtml(html)
    }
    setActiveFormats((prev) => ({ ...prev, fontSize: String(size) }))
  }

  const saveFormatSelection = () => {
    const sel = window.getSelection()
    if (editorRef.current && sel && sel.rangeCount > 0 && editorRef.current.contains(sel.anchorNode)) {
      savedFormatSelectionRef.current = sel.getRangeAt(0).cloneRange()
    } else {
      savedFormatSelectionRef.current = null
    }
  }

  const applyHighlightInline = (color) => {
    if (!editorRef.current) return
    editorRef.current.focus()
    const sel = window.getSelection()
    if (savedSelectionRef.current && sel) {
      try {
        sel.removeAllRanges()
        sel.addRange(savedSelectionRef.current)
      } catch (_) {}
    }
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return
    const range = selection.getRangeAt(0)
    if (range.collapsed) return

    const root = range.commonAncestorContainer.nodeType === Node.TEXT_NODE
      ? range.commonAncestorContainer.parentNode
      : range.commonAncestorContainer
    const iterator = document.createNodeIterator(root, NodeFilter.SHOW_TEXT, null)
    const textNodesToWrap = []
    let node
    while ((node = iterator.nextNode())) {
      if (range.intersectsNode(node)) textNodesToWrap.push(node)
    }

    for (const textNode of textNodesToWrap) {
      const r = document.createRange()
      r.setStart(textNode, range.startContainer === textNode ? range.startOffset : 0)
      r.setEnd(textNode, range.endContainer === textNode ? range.endOffset : textNode.length)
      if (r.collapsed) continue

      const span = document.createElement('span')
      span.style.backgroundColor = color
      span.className = 'text-highlight'
      span.appendChild(r.extractContents())
      r.insertNode(span)
    }

    savedSelectionRef.current = null
    if (editorRef.current) {
      const html = editorRef.current.innerHTML
      lastValidHtmlRef.current = html
      setAgreementHtml(html)
    }
    updateActiveFormats()
  }

  const removeHighlightInline = () => {
    if (!editorRef.current) return
    editorRef.current.focus()
    const sel = window.getSelection()
    if (savedSelectionRef.current && sel) {
      try {
        sel.removeAllRanges()
        sel.addRange(savedSelectionRef.current)
      } catch (_) {}
    }
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return
    const range = selection.getRangeAt(0)
    if (range.collapsed) return

    const spans = editorRef.current.querySelectorAll('.text-highlight')
    spans.forEach((span) => {
      if (range.intersectsNode(span)) {
        const parent = span.parentNode
        while (span.firstChild) {
          parent.insertBefore(span.firstChild, span)
        }
        parent.removeChild(span)
      }
    })

    savedSelectionRef.current = null
    if (editorRef.current) {
      const html = editorRef.current.innerHTML
      lastValidHtmlRef.current = html
      setAgreementHtml(html)
    }
    updateActiveFormats()
  }

  const HIGHLIGHT_COLORS = [
    { name: 'None', value: 'none' },
    { name: 'Yellow', value: '#fef08a' },
    { name: 'Green', value: '#bbf7d0' },
    { name: 'Orange', value: '#fed7aa' },
    { name: 'Light blue', value: '#bae6fd' },
    { name: 'Pink', value: '#fbcfe8' },
    { name: 'Purple', value: '#e9d5ff' },
    { name: 'Lavender', value: '#ddd6fe' },
    { name: 'Mint', value: '#99f6e4' },
    { name: 'Cyan', value: '#a5f3fc' },
    { name: 'Salmon', value: '#fecaca' },
    { name: 'Peach', value: '#ffedd5' },
    { name: 'Coral', value: '#fda4af' },
    { name: 'Sky blue', value: '#7dd3fc' },
    { name: 'Lime', value: '#d9f99d' },
  ]

  const applyTextColorInline = (color) => {
    if (!editorRef.current) return
    editorRef.current.focus()
    const sel = window.getSelection()
    if (savedSelectionRef.current && sel) {
      try {
        sel.removeAllRanges()
        sel.addRange(savedSelectionRef.current)
      } catch (_) {}
    }
    document.execCommand('styleWithCSS', false, true)
    document.execCommand('foreColor', false, color)
    // Reset so bold/italic/underline use HTML tags; otherwise italic toggles look "on" but don't paint on colored text.
    try {
      document.execCommand('styleWithCSS', false, false)
    } catch (_) {
      /* ignore */
    }
    savedSelectionRef.current = null
    if (editorRef.current) {
      const html = editorRef.current.innerHTML
      lastValidHtmlRef.current = html
      setAgreementHtml(html)
    }
    updateActiveFormats()
  }

  const TEXT_COLORS = [
    { name: 'Black', value: '#000000' },
    { name: 'White', value: '#ffffff' },
    { name: 'Red', value: '#dc2626' },
    { name: 'Blue', value: '#2563eb' },
    { name: 'Green', value: '#16a34a' },
    { name: 'Orange', value: '#ea580c' },
    { name: 'Purple', value: '#9333ea' },
    { name: 'Gray', value: '#64748b' },
    { name: 'Dark blue', value: '#1e40af' },
    { name: 'Brown', value: '#92400e' },
  ]

  const handleUndo = () => applyFormat('undo')
  const handleRedo = () => applyFormat('redo')
  const handleIndent = () => applyFormat('indent')
  const handleOutdent = () => applyFormat('outdent')

  const clearEditorSelections = () => {
    const ed = editorRef.current
    if (!ed) return

    setIsHighlightOpen(false)
    setIsTextColorOpen(false)
    ed.focus()

    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || !ed.contains(selection.anchorNode)) return

    const range = selection.getRangeAt(0).cloneRange()
    range.collapse(false)

    const cleanSpan = document.createElement('span')
    cleanSpan.style.fontFamily = FONT_OPTIONS[0].value
    cleanSpan.setAttribute('data-font-family', FONT_OPTIONS[0].value)
    cleanSpan.style.fontSize = '13px'
    cleanSpan.setAttribute('data-font-size', '13')
    cleanSpan.style.fontWeight = '400'
    cleanSpan.style.fontStyle = 'normal'
    cleanSpan.style.textDecoration = 'none'
    cleanSpan.style.color = '#000000'
    cleanSpan.appendChild(document.createTextNode('\u200B'))
    range.insertNode(cleanSpan)

    const isInlineFormattingWrapper = (node) => {
      if (!node || node.nodeType !== Node.ELEMENT_NODE) return false
      if (node.classList?.contains('text-highlight')) return true
      if (node.hasAttribute?.('data-font-size') || node.hasAttribute?.('data-font-family')) return true
      return ['B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'DEL', 'A', 'SPAN'].includes(node.nodeName)
    }

    let parent = cleanSpan.parentElement
    while (parent && parent !== ed && isInlineFormattingWrapper(parent)) {
      const nextSibling = cleanSpan.nextSibling
      if (nextSibling) {
        const trailingClone = parent.cloneNode(false)
        while (cleanSpan.nextSibling) trailingClone.appendChild(cleanSpan.nextSibling)
        parent.parentNode.insertBefore(trailingClone, parent.nextSibling)
      }
      const outerParent = parent.parentNode
      outerParent.insertBefore(cleanSpan, parent.nextSibling)
      parent = cleanSpan.parentElement
    }

    const cleanRange = document.createRange()
    cleanRange.setStart(cleanSpan.firstChild, 1)
    cleanRange.collapse(true)
    selection.removeAllRanges()
    selection.addRange(cleanRange)

    savedFormatSelectionRef.current = cleanRange.cloneRange()

    lastSelectedFontSizeRef.current = 13
    lastAppliedFontSizeRef.current = 13
    fontSizeLockUntilRef.current = Date.now() + 400
    setSelectedHighlightColor('#fef08a')
    setSelectedTextColor('#000000')
    setActiveFormats((prev) => ({
      ...prev,
      bold: false,
      italic: false,
      underline: false,
      strike: false,
      unordered: false,
      ordered: false,
      header: 'P',
      fontSize: '13',
      fontFamily: 'Arial',
    }))

    const html = ed.innerHTML
    lastValidHtmlRef.current = html
    setAgreementHtml(html)
    updateActiveFormats()
  }

  const openLinkModal = () => {
    const sel = window.getSelection()
    if (editorRef.current && sel && sel.rangeCount > 0 && editorRef.current.contains(sel.anchorNode)) {
      savedLinkSelectionRef.current = sel.getRangeAt(0).cloneRange()
      const selectedText = sel.toString()
      setLinkTitle(selectedText)
    } else {
      savedLinkSelectionRef.current = null
      setLinkTitle('')
    }
    setLinkUrl('')
    setIsLinkModalOpen(true)
  }

  const applyLink = () => {
    const url = linkUrl.trim()
    if (!url) return
    const href = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`
    const title = linkTitle.trim() || href
    if (!editorRef.current) return
    editorRef.current.focus()
    const sel = window.getSelection()
    if (savedLinkSelectionRef.current && sel) {
      try {
        sel.removeAllRanges()
        sel.addRange(savedLinkSelectionRef.current)
      } catch (_) {}
    }
    const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null
    const a = document.createElement('a')
    a.href = href
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    a.textContent = title
    if (range) {
      range.deleteContents()
      range.insertNode(a)
    } else {
      document.execCommand('insertHTML', false, `<a href="${href}" target="_blank" rel="noopener noreferrer">${title.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</a>`)
    }
    savedLinkSelectionRef.current = null
    const html = editorRef.current.innerHTML
    lastValidHtmlRef.current = html
    setAgreementHtml(html)
    setIsLinkModalOpen(false)
    setLinkTitle('')
    setLinkUrl('')
    updateActiveFormats()
  }


  const handlePdfUpload = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (file.type !== 'application/pdf') {
      setPdfError('Please upload a PDF file.')
      return
    }
    if (file.size > maxPdfBytes) {
      setPdfError(`File too large. Maximum size is ${maxPdfBytes / 1024 / 1024} MB.`)
      return
    }
    setPdfError('')
    setIsPdfLoading(true)
    setPdfProgress(0)
    try {
      const pdfjsLib = await import('pdfjs-dist')
      await configurePdfWorker(pdfjsLib)

      const arrayBuffer = await file.arrayBuffer()
      setPdfProgress(5)
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer })
      const pdf = await loadingTask.promise
      const numPages = pdf.numPages
      const textParts = []
      for (let i = 1; i <= numPages; i++) {
        const page = await pdf.getPage(i)
        const textContent = await page.getTextContent()
        const pageText = textContent.items.map((item) => item.str).join(' ')
        textParts.push(pageText)
        setPdfProgress(Math.round(5 + (90 * i) / numPages))
      }
      setPdfProgress(98)
      const fullText = textParts.join('\n\n')
      const escapeHtml = (s) =>
        String(s)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;')
      const paragraphs = fullText.split(/\n\n+/).filter(Boolean)
      const html =
        paragraphs.length > 0
          ? paragraphs.map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`).join('')
          : '<p><br></p>'

      setAgreementHtml(html)
      if (editorRef.current) {
        editorRef.current.innerHTML = html
        lastValidHtmlRef.current = html
      }
      setPdfProgress(100)
    } catch (err) {
      setPdfError(err?.message || 'Failed to read PDF. Try a different file.')
    } finally {
      setIsPdfLoading(false)
      setPdfProgress(0)
    }
  }

  const handleFullPdfUpload = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (file.type !== 'application/pdf') {
      setPdfError('Please upload a PDF file.')
      return
    }
    if (file.size > maxPdfBytes) {
      setPdfError(`File too large. Maximum size is ${maxPdfBytes / 1024 / 1024} MB.`)
      return
    }
    setPdfError('')
    setIsFullPdfLoading(true)
    setPdfProgress(0)
    try {
      const pdfjsLib = await import('pdfjs-dist')
      await configurePdfWorker(pdfjsLib)

      const arrayBuffer = await file.arrayBuffer()
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer })
      const pdf = await loadingTask.promise
      const numPages = pdf.numPages
      const pages = []

      const escapeHtml = (s) =>
        String(s)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;')

      for (let i = 1; i <= numPages; i++) {
        const page = await pdf.getPage(i)
        setPdfProgress(Math.round((95 * (i - 1)) / numPages))
        const viewport = page.getViewport({ scale: 2.5 })
        const canvas = document.createElement('canvas')
        canvas.width = viewport.width
        canvas.height = viewport.height
        const ctx = canvas.getContext('2d')
        const renderTask = page.render({ canvasContext: ctx, viewport })
        await renderTask.promise
        const imageDataUrl = canvas.toDataURL('image/png')

        const textContent = await page.getTextContent()
        const pageText = textContent.items.map((item) => item.str).join(' ')
        const paragraphs = pageText.split(/\n\n+/).filter(Boolean)
        const overlayHtml =
          paragraphs.length > 0
            ? paragraphs.map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`).join('')
            : '<p><br></p>'

        pages.push({ imageDataUrl, overlayHtml, pageIndex: i })
      }

      setPdfProgress(100)
      setFullPdfPages(pages)
      const emptyHtml = '<p><br></p>'
      setAgreementHtml(emptyHtml)
      if (editorRef.current) {
        editorRef.current.innerHTML = emptyHtml
        lastValidHtmlRef.current = emptyHtml
      }
    } catch (err) {
      setPdfError(err?.message || 'Failed to load PDF. Try a different file.')
    } finally {
      setIsFullPdfLoading(false)
      setPdfProgress(0)
    }
  }

  const handleFullPdfOverlayInput = (pageIndex, html) => {
    setFullPdfPages((prev) => {
      const next = prev.map((p) =>
        p.pageIndex === pageIndex ? { ...p, overlayHtml: html } : p
      )
      const combined = next.map((p) => p.overlayHtml).join('')
      setAgreementHtml(combined)
      lastValidHtmlRef.current = combined
      return next
    })
  }

  const clearFullPdf = () => {
    setFullPdfPages([])
    setPdfError('')
  }

  const handlePdfEditorUpload = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (file.type !== 'application/pdf') {
      setPdfEditorError('Please upload a PDF file.')
      return
    }
    if (file.size > maxPdfBytes) {
      setPdfEditorError(`File too large. Maximum size is ${maxPdfBytes / 1024 / 1024} MB.`)
      return
    }
    setPdfEditorError('')
    setPdfEditorLoading(true)
    try {
      const pdfjsLib = await import('pdfjs-dist')
      await configurePdfWorker(pdfjsLib)
      const arrayBuffer = await file.arrayBuffer()
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer })
      const pdf = await loadingTask.promise
      const numPages = pdf.numPages
      const pages = []
      for (let i = 1; i <= numPages; i++) {
        const page = await pdf.getPage(i)
        const viewport = page.getViewport({ scale: 2.5 })
        const canvas = document.createElement('canvas')
        canvas.width = viewport.width
        canvas.height = viewport.height
        const ctx = canvas.getContext('2d')
        const renderTask = page.render({ canvasContext: ctx, viewport })
        await renderTask.promise
        const imageDataUrl = canvas.toDataURL('image/png')
        pages.push({
          imageDataUrl,
          pageIndex: i,
          width: viewport.width,
          height: viewport.height,
          textOverlays: [],
        })
      }
      setPdfEditorPages(pages)
      setPdfEditorAddingText(null)
      setPdfEditorInputValue('')
    } catch (err) {
      setPdfEditorError(err?.message || 'Failed to load PDF. Try a different file.')
    } finally {
      setPdfEditorLoading(false)
    }
  }

  const handlePdfEditorPageClick = (pageIndex, event) => {
    const page = pdfEditorPages.find((p) => p.pageIndex === pageIndex)
    if (!page) return
    const rect = event.currentTarget.getBoundingClientRect()
    const relX = event.clientX - rect.left
    const relY = event.clientY - rect.top
    const pdfX = (relX / rect.width) * page.width
    const pdfY = (relY / rect.height) * page.height
    setPdfEditorAddingText({ pageIndex, x: pdfX, y: pdfY })
    setPdfEditorInputValue('')
    requestAnimationFrame(() => document.getElementById('pdf-editor-text-input')?.focus())
  }

  const handlePdfEditorConfirmText = () => {
    if (!pdfEditorAddingText || !pdfEditorInputValue.trim()) return
    const { pageIndex, x, y } = pdfEditorAddingText
    const overlay = {
      id: `t_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      text: pdfEditorInputValue.trim(),
      x,
      y,
      fontSize: pdfEditorFontSize,
    }
    setPdfEditorPages((prev) =>
      prev.map((p) =>
        p.pageIndex === pageIndex
          ? { ...p, textOverlays: [...p.textOverlays, overlay] }
          : p
      )
    )
    setPdfEditorAddingText(null)
    setPdfEditorInputValue('')
  }

  const handlePdfEditorRemoveOverlay = (pageIndex, overlayId) => {
    setPdfEditorPages((prev) =>
      prev.map((p) =>
        p.pageIndex === pageIndex
          ? { ...p, textOverlays: p.textOverlays.filter((o) => o.id !== overlayId) }
          : p
      )
    )
  }

  const handlePdfEditorDownload = async () => {
    if (pdfEditorPages.length === 0) return
    const JsPDF = await getJsPDF()
    const toJpeg = (dataUrl) => {
      if (!dataUrl?.startsWith('data:image/png')) return Promise.resolve(dataUrl)
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          const c = document.createElement('canvas')
          c.width = img.width
          c.height = img.height
          c.getContext('2d').drawImage(img, 0, 0)
          resolve(c.toDataURL('image/jpeg', PDF_JPEG_QUALITY))
        }
        img.onerror = reject
        img.src = dataUrl
      })
    }
    const first = pdfEditorPages[0]
    const pdf = new JsPDF('p', 'pt', [first.width, first.height])
    for (let i = 0; i < pdfEditorPages.length; i++) {
      if (i > 0) pdf.addPage([pdfEditorPages[i].width, pdfEditorPages[i].height])
      const page = pdfEditorPages[i]
      const imgData = await toJpeg(page.imageDataUrl)
      pdf.addImage(imgData, 'JPEG', 0, 0, page.width, page.height)
      for (const overlay of page.textOverlays) {
        pdf.setFontSize(overlay.fontSize)
        pdf.setTextColor(0, 0, 0)
        pdf.text(overlay.text, overlay.x, overlay.y + overlay.fontSize * 0.75)
      }
    }
    pdf.save('edited-document.pdf')
  }

  const clearPdfEditor = () => {
    setPdfEditorPages([])
    setPdfEditorAddingText(null)
    setPdfEditorInputValue('')
    setPdfEditorError('')
  }

  const handleEditorInput = (event) => {
    activeFormatOverrideRef.current = null
    const html = event.currentTarget.innerHTML
    const sizeKb = new TextEncoder().encode(html).length / 1024
    // Compare counts using the same metric as the UI counter, and the previous *accepted* HTML only.
    // innerText vs sanitized plain length can diverge (line breaks, normalization), which falsely looked
    // like a "net add" on focus/click and blocked deleting when already over the limit.
    const prevPlainLen = agreementPlainCharCountForLimit(lastValidHtmlRef.current)
    const newPlainLen = agreementPlainCharCountForLimit(html)
    const prevSizeKb = new TextEncoder().encode(lastValidHtmlRef.current || '').length / 1024
    // Over limit: only block when this edit *increases* plain length or byte size past the cap.
    if (newPlainLen > charLimit && newPlainLen > prevPlainLen) {
      requestAnimationFrame(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = lastValidHtmlRef.current
          setAgreementHtml(lastValidHtmlRef.current)
        }
        setLimitReachedReason('characters')
        setCharLimitReachedOpen(true)
      })
      return
    }
    if (sizeKb > maxAgreementSizeKb && sizeKb > prevSizeKb) {
      requestAnimationFrame(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = lastValidHtmlRef.current
          setAgreementHtml(lastValidHtmlRef.current)
        }
        setLimitReachedReason('size')
        setCharLimitReachedOpen(true)
      })
      return
    }
    lastValidHtmlRef.current = html
    setAgreementHtml(html)
    updateActiveFormats()
  }

  const handleEditorPaste = (event) => {
    const liveHtml = editorRef.current?.innerHTML ?? agreementHtml
    const plainLength = agreementPlainCharCountForLimit(liveHtml)
    const currentSizeKb = new TextEncoder().encode(liveHtml).length / 1024
    const pastedText = event.clipboardData?.getData?.('text/plain') || ''
    const pastedHtml = event.clipboardData?.getData?.('text/html') || ''
    if (!pastedText && !pastedHtml) return
    let selectionPlainLen = 0
    let selectionSizeKb = 0
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      try {
        const range = sel.getRangeAt(0)
        if (!range.collapsed) {
          const frag = range.cloneContents()
          const div = document.createElement('div')
          div.appendChild(frag)
          selectionPlainLen = agreementPlainCharCountForLimit(div.innerHTML)
          selectionSizeKb = new TextEncoder().encode(div.innerHTML).length / 1024
        }
      } catch (_) {}
    }
    const pastedCharCount = pastedText.replace(/\r\n/g, '\n').length
    const pastedSizeKb = pastedHtml ? new TextEncoder().encode(pastedHtml).length / 1024 : pastedCharCount / 1024
    const netPlain = plainLength - selectionPlainLen + pastedCharCount
    const netSizeKb = currentSizeKb - selectionSizeKb + pastedSizeKb
    const deltaPlain = netPlain - plainLength
    const deltaSizeKb = netSizeKb - currentSizeKb
    const blockChars =
      (plainLength < charLimit && netPlain > charLimit) || (plainLength >= charLimit && deltaPlain > 0)
    const blockSize =
      (currentSizeKb < maxAgreementSizeKb && netSizeKb > maxAgreementSizeKb) ||
      (currentSizeKb >= maxAgreementSizeKb && deltaSizeKb > 0)
    if (blockChars || blockSize) {
      event.preventDefault()
      setLimitReachedReason(blockChars ? 'characters' : 'size')
      setCharLimitReachedOpen(true)
    }
  }

  const handleEditorKeyDown = (event) => {
    const isMeta = event.metaKey || event.ctrlKey
    const liveHtml = editorRef.current?.innerHTML ?? agreementHtml
    const agreementSizeKb = new TextEncoder().encode(liveHtml).length / 1024
    const atCharLimit = agreementPlainCharCountForLimit(liveHtml) >= charLimit
    const atSizeLimit = agreementSizeKb >= maxAgreementSizeKb
    const atLimit = atCharLimit || atSizeLimit
    const navOrEditKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'PageUp',
      'PageDown',
      'Tab',
      'Escape',
    ]
    const selNav = window.getSelection()
    const hasNonCollapsedRange =
      selNav &&
      selNav.rangeCount > 0 &&
      editorRef.current?.contains(selNav.anchorNode) &&
      !selNav.getRangeAt(0).collapsed
    const singleVisibleChar =
      typeof event.key === 'string' &&
      event.key.length === 1 &&
      !event.ctrlKey &&
      !event.altKey
    if (atLimit && !isMeta && !navOrEditKeys.includes(event.key)) {
      const mayReplaceSelection = hasNonCollapsedRange && (singleVisibleChar || event.key === 'Enter')
      if (!mayReplaceSelection) {
      event.preventDefault()
      setLimitReachedReason(atCharLimit ? 'characters' : 'size')
      setCharLimitReachedOpen(true)
      return
      }
    }

    if (event.key === 'Enter' && !isMeta && editorRef.current) {
      const sel = window.getSelection()
      if (sel && sel.rangeCount > 0 && editorRef.current.contains(sel.anchorNode)) {
        const anchorNode = sel.anchorNode
        let node = anchorNode?.nodeType === Node.TEXT_NODE ? anchorNode.parentElement : anchorNode
        const li = node?.closest?.('li')
        const listEl = node?.closest?.('ul, ol')
        const isInList = !!(li && listEl)
        const isLiEmpty = isInList && (() => {
          const text = (li.textContent || '').replace(/\u200B/g, '').replace(/\u00A0/g, ' ').trim()
          return text === ''
        })()

        if (isLiEmpty) {
          event.preventDefault()
          document.execCommand('outdent', false, null)
          if (editorRef.current) {
            const html = editorRef.current.innerHTML
            lastValidHtmlRef.current = html
            setAgreementHtml(html)
          }
          return
        }

        let intendedSize = lastSelectedFontSizeRef.current ?? 13
        const sizeEl = anchorNode?.parentElement?.closest?.('[data-font-size]')
        if (sizeEl) intendedSize = parseInt(sizeEl.getAttribute('data-font-size'), 10) || 13
        lastSelectedFontSizeRef.current = intendedSize

        event.preventDefault()
        document.execCommand('insertParagraph', false, null)

        const selAfter = window.getSelection()
        if (selAfter && selAfter.rangeCount > 0 && editorRef.current.contains(selAfter.anchorNode)) {
          const range = selAfter.getRangeAt(0)
          if (range.collapsed) {
            const span = document.createElement('span')
            span.style.fontSize = `${intendedSize}px`
            span.setAttribute('data-font-size', intendedSize)
            span.appendChild(document.createTextNode('\u200B'))
            range.insertNode(span)
            const newRange = document.createRange()
            newRange.setStart(span.firstChild, 1)
            newRange.collapse(true)
            selAfter.removeAllRanges()
            selAfter.addRange(newRange)
          }
        }
        if (editorRef.current) {
          const html = editorRef.current.innerHTML
          lastValidHtmlRef.current = html
          setAgreementHtml(html)
        }
        setActiveFormats((prev) => ({ ...prev, fontSize: String(intendedSize) }))
        return
      }
    }

    if (!isMeta) return

    if (event.key.toLowerCase() === 'z') {
      event.preventDefault()
      if (event.shiftKey) {
        handleRedo()
      } else {
        handleUndo()
      }
    }

    if (event.key.toLowerCase() === 'y') {
      event.preventDefault()
      handleRedo()
    }
  }
  const generateSignedPdf = async (sourceRef = documentRef) => {
    if (sourceRef !== documentRef && !sourceRef.current) return null
    if (sourceRef === documentRef && !documentRef.current) return null

    const JsPDF = await getJsPDF()
    const html2canvas = await getHtml2Canvas()

    if (fullPdfPages.length > 0 && sourceRef === previewRef) {
      const pdf = new JsPDF('p', 'pt', 'a4')
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const marginTop = PDF_MARGIN_TOP
      const marginBottom = PDF_MARGIN_BOTTOM
      const marginSide = PDF_MARGIN_SIDE
      const imgWidth = pageWidth - marginSide * 2
      const usableHeight = pageHeight - marginTop - marginBottom

      const toDataUrlForPdf = async (page) => {
        const url = page.imageDataUrl
        if (!url || typeof url !== 'string') return null
        if (url.startsWith('data:')) return url
        if (page.storagePath) {
          try {
            const storageRef = ref(storage, page.storagePath)
            const blob = await getBlob(storageRef)
            return await new Promise((resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => resolve(reader.result)
              reader.onerror = reject
              reader.readAsDataURL(blob)
            })
          } catch (e) {
            console.error('Failed to load page from Storage:', e)
          }
        }
        try {
          const res = await fetch(url, { mode: 'cors' })
          if (!res.ok) throw new Error(`Fetch failed: ${res.status}`)
          const blob = await res.blob()
          return await new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result)
            reader.onerror = reject
            reader.readAsDataURL(blob)
          })
        } catch (e) {
          console.error('Failed to fetch page image:', e)
          throw e
        }
      }

      const toJpegIfPng = (dataUrl) => {
        if (!dataUrl?.startsWith('data:image/png')) return Promise.resolve(dataUrl)
        return new Promise((resolve, reject) => {
          const img = new Image()
          img.onload = () => {
            const c = document.createElement('canvas')
            c.width = img.width
            c.height = img.height
            c.getContext('2d').drawImage(img, 0, 0)
            resolve(c.toDataURL('image/jpeg', PDF_JPEG_QUALITY))
          }
          img.onerror = reject
          img.src = dataUrl
        })
      }
      let lastPdfImageBottomPt = null
      for (let i = 0; i < fullPdfPages.length; i++) {
        if (i > 0) pdf.addPage()
        const page = fullPdfPages[i]
        let imgDataUrl = page.imageDataUrl
        if (imgDataUrl && (imgDataUrl.startsWith('http://') || imgDataUrl.startsWith('https://'))) {
          try {
            imgDataUrl = await toDataUrlForPdf(page)
            if (!imgDataUrl) throw new Error('Could not load page image')
          } catch (e) {
            console.error('Failed to convert page image to data URL:', e)
            throw e
          }
        }
        imgDataUrl = await toJpegIfPng(imgDataUrl)
        const img = document.createElement('img')
        await new Promise((resolve, reject) => {
          img.onload = () => resolve()
          img.onerror = () => reject(new Error('Failed to load page image'))
          img.crossOrigin = imgDataUrl?.startsWith('data:') ? null : 'anonymous'
          img.src = imgDataUrl
        })
        let w = imgWidth
        let h = (img.height * imgWidth) / img.width
        if (h > usableHeight) {
          h = usableHeight
          w = (img.width * usableHeight) / img.height
        }
        const x = marginSide
        const y = marginTop + (usableHeight - h) / 2
        pdf.addImage(imgDataUrl, 'JPEG', x, y, w, h)
        lastPdfImageBottomPt = y + h
      }

      if (agreementPreviewRef.current && agreementPlainText.trim().length > 0) {
        lastPdfImageBottomPt = null
        const previewSourceEl = agreementPreviewRef.current.closest?.('.pdf-preview-source')
        const originalStyles = previewSourceEl ? previewSourceEl.getAttribute('style') : null
        if (previewSourceEl) {
          previewSourceEl.style.cssText = 'position: fixed !important; left: 0 !important; top: 0 !important; z-index: -9999 !important; opacity: 0.01 !important; pointer-events: none !important; width: 820px !important;'
          await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))
          await new Promise((r) => setTimeout(r, 150))
        }
        try {
          const agreementEl = agreementPreviewRef.current
          const agreementCanvas = await html2canvas(agreementEl, {
            scale: PDF_SCALE,
            backgroundColor: '#ffffff',
            useCORS: true,
            allowTaint: true,
            logging: false,
            scrollY: -window.scrollY,
            scrollX: -window.scrollX,
            windowHeight: Math.max(window.innerHeight, (agreementEl?.scrollHeight ?? 0) + 200),
          })
          if (previewSourceEl) previewSourceEl.style.cssText = originalStyles || ''
          pdf.addPage()
          paginateCanvasOntoPdf(pdf, agreementCanvas, {
            imgWidth,
            marginSide,
            marginTop,
            marginTopFirstPage: marginTop,
            marginBottom,
            sliceInsetPt: PDF_PAGE_SLICE_INSET_PT,
            treatFirstPageOfCanvasAsDocumentFirst: false,
          })
        } catch {
          if (previewSourceEl) previewSourceEl.style.cssText = originalStyles || ''
        }
      }

      if (signersPreviewRef.current) {
        const previewSourceEl = signersPreviewRef.current.closest?.('.pdf-preview-source')
        const originalStyles = previewSourceEl ? previewSourceEl.getAttribute('style') : null
        if (previewSourceEl) {
          previewSourceEl.style.cssText = 'position: fixed !important; left: 0 !important; top: 0 !important; z-index: -9999 !important; opacity: 0.01 !important; pointer-events: none !important; width: 820px !important;'
          await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))
          await new Promise((r) => setTimeout(r, 150))
        }
        try {
          const signersEl = signersPreviewRef.current
          const signersCanvas = await html2canvas(signersEl, {
            scale: PDF_SCALE,
            backgroundColor: '#ffffff',
            useCORS: true,
            allowTaint: true,
            logging: false,
            scrollY: -window.scrollY,
            scrollX: -window.scrollX,
            windowHeight: Math.max(window.innerHeight, (signersEl?.scrollHeight ?? 0) + 200),
          })
          if (previewSourceEl) previewSourceEl.style.cssText = originalStyles || ''
          const signersTrimmed = trimCanvasBelowLastContent(signersCanvas, { padBottomPx: 48, minHeightPx: 32 })
          const signersImg = signersTrimmed.toDataURL('image/jpeg', PDF_JPEG_QUALITY)
          const sh = (signersTrimmed.height * imgWidth) / signersTrimmed.width
          const maxContentBottom = marginTop + usableHeight
          const gapBelowPdfPt = 14
          let placedSigners = false
          if (
            lastPdfImageBottomPt != null &&
            sh <= maxContentBottom - lastPdfImageBottomPt - gapBelowPdfPt - PDF_SIGNATURE_PAGE_FIT_SLACK_PT
          ) {
            pdf.addImage(signersImg, 'JPEG', marginSide, lastPdfImageBottomPt + gapBelowPdfPt, imgWidth, sh)
            placedSigners = true
          }
          if (!placedSigners) {
          pdf.addPage()
          const signersH = Math.min(sh, usableHeight)
          const signersY = marginTop + (usableHeight - signersH) / 2
          pdf.addImage(signersImg, 'JPEG', marginSide, signersY, imgWidth, signersH)
          }
        } catch {
          if (previewSourceEl) previewSourceEl.style.cssText = originalStyles || ''
        }
      }
      return pdf.output('datauristring')
    }

    const targetRef = sourceRef === documentRef ? documentRef : previewRef
    if (!targetRef.current) return null

    const previewSourceEl = targetRef.current.closest?.('.pdf-preview-source')
    const originalStyles = previewSourceEl ? previewSourceEl.getAttribute('style') : null

    if (previewSourceEl) {
      previewSourceEl.style.cssText = 'position: fixed !important; left: 0 !important; top: 0 !important; z-index: -9999 !important; opacity: 0.01 !important; pointer-events: none !important; width: 820px !important;'
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))
      await new Promise((r) => setTimeout(r, 150))
    }

    const pdf = new JsPDF('p', 'pt', 'a4')
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const marginTop = PDF_MARGIN_TOP
    const marginTopFirstPage = PDF_MARGIN_TOP_FIRST_PAGE
    const marginBottom = PDF_MARGIN_BOTTOM
    const marginSide = PDF_MARGIN_SIDE
    const imgWidth = pageWidth - marginSide * 2

    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))

    const targetEl = targetRef.current
    const elHeight = targetEl?.scrollHeight ?? 0
    const fullCanvas = await html2canvas(targetEl, {
      scale: PDF_SCALE,
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: true,
      logging: false,
      scrollY: -window.scrollY,
      scrollX: -window.scrollX,
      windowHeight: Math.max(window.innerHeight, elHeight + 200),
      onclone: (clonedDoc, el) => {
        el.style.overflow = 'visible'
        el.style.maxHeight = 'none'
        let parent = el.parentElement
        while (parent && parent !== clonedDoc.body) {
          parent.style.overflow = 'visible'
          parent.style.maxHeight = 'none'
          parent = parent.parentElement
        }
      },
    })

    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))

    const sigProtected =
      sourceRef === previewRef && signersPreviewRef?.current && targetEl
        ? getSignatureProtectedCanvasRange(targetEl, signersPreviewRef.current, PDF_SCALE)
        : null

    paginateCanvasOntoPdf(pdf, fullCanvas, {
      imgWidth,
      marginSide,
      marginTop,
      marginTopFirstPage,
      marginBottom,
      sliceInsetPt: PDF_PAGE_SLICE_INSET_PT,
      treatFirstPageOfCanvasAsDocumentFirst: true,
      protectedYRange: sigProtected,
    })

    if (previewSourceEl) {
      previewSourceEl.style.cssText = originalStyles || ''
    }

    return pdf.output('datauristring')
  }

  /** PDF for email attachment – max 20MB. Uses JPEG. Captures agreement + signers as one flow so signature block follows directly below text. */
  const generateEmailAttachmentPdf = async () => {
    const sourceRef = previewRef
    if (!sourceRef?.current) return null
    const targetRef = sourceRef.current
    const previewSourceEl = targetRef.closest?.('.pdf-preview-source')
    const originalStyles = previewSourceEl?.getAttribute('style') || null

    // When document has full PDF pages, avoid html2canvas on the whole DOM (hits canvas size limits → white pages).
    // Build PDF from page images directly so preview works for large (e.g. 2MB+) uploads.
    if (fullPdfPages.length > 0) {
      try {
        const dataUrl = await generateSignedPdf(sourceRef)
        return dataUrl
      } catch (e) {
        console.error('Preview PDF (full pages path) failed:', e)
        return null
      }
    }

    const scale = (typeof window !== 'undefined' && window.innerWidth <= 768) ? PDF_SCALE_MOBILE : PDF_SCALE
    const maxPdfBytes = 20 * 1024 * 1024
    const pageWidth = 595.28
    const pageHeight = 841.89
    const marginTop = PDF_MARGIN_TOP
    const marginTopFirstPage = PDF_MARGIN_TOP_FIRST_PAGE
    const marginBottom = PDF_MARGIN_BOTTOM
    const marginSide = PDF_MARGIN_SIDE
    const imgWidth = pageWidth - marginSide * 2

    const sigProtected =
      signersPreviewRef?.current && previewRef?.current
        ? getSignatureProtectedCanvasRange(previewRef.current, signersPreviewRef.current, scale)
        : null

    const addCanvasToPdf = (pdf, canvas, q, usePng, isFirstContentInDoc = true) => {
      paginateCanvasOntoPdf(pdf, canvas, {
        imgWidth,
        marginSide,
        marginTop,
        marginTopFirstPage,
        marginBottom,
        sliceInsetPt: PDF_PAGE_SLICE_INSET_PT,
        jpegQuality: q,
        usePng,
        treatFirstPageOfCanvasAsDocumentFirst: isFirstContentInDoc,
        protectedYRange: sigProtected,
      })
    }

    if (previewSourceEl) {
      previewSourceEl.style.cssText = 'position: fixed !important; left: 0 !important; top: 0 !important; z-index: -9999 !important; opacity: 0.01 !important; pointer-events: none !important; width: 820px !important;'
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))
      await new Promise((r) => setTimeout(r, 150))
    }
    try {
      const html2canvas = await getHtml2Canvas()
      const JsPDF = await getJsPDF()
      const elHeight = targetRef?.scrollHeight ?? 0
      const fullCanvas = await html2canvas(targetRef, {
        scale,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true,
        logging: false,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        windowHeight: Math.max(window.innerHeight, elHeight + 200),
        onclone: (clonedDoc, el) => {
          el.style.overflow = 'visible'
          el.style.maxHeight = 'none'
          let p = el.parentElement
          while (p && p !== clonedDoc.body) {
            p.style.overflow = 'visible'
            p.style.maxHeight = 'none'
            p = p.parentElement
          }
        },
      })

      if (previewSourceEl) previewSourceEl.style.cssText = originalStyles || ''

      const buildPdf = (usePng, qual) => {
        const pdf = new JsPDF('p', 'pt', 'a4')
        addCanvasToPdf(pdf, fullCanvas, qual, usePng, true)
        return pdf.output('datauristring')
      }
      let qual = PDF_JPEG_QUALITY
      let out = buildPdf(false, qual)
      while ((out.length * 3) / 4 > maxPdfBytes && qual > 0.5) {
        qual -= 0.05
        out = buildPdf(false, qual)
      }
      if ((out.length * 3) / 4 <= maxPdfBytes) return out
      out = buildPdf(true, 1)
      return (out.length * 3) / 4 <= maxPdfBytes ? out : null
    } catch (e) {
      if (previewSourceEl) previewSourceEl.style.cssText = originalStyles || ''
      return null
    }
  }

  /** Single fingerprint for preview PDF cache + regeneration — must cover everything rendered under .pdf-preview-source (layout builder, labels, exclusions, signers, logo). */
  const previewPdfCacheKey = useMemo(() => {
    let signersKey = ''
    let fieldLayoutKey = ''
    let customFieldsKey = ''
    let excludedKey = ''
    let labelOverridesKey = ''
    try {
      signersKey = JSON.stringify(signers)
    } catch {
      signersKey = String(signers?.length ?? 0)
    }
    try {
      fieldLayoutKey = JSON.stringify(fieldLayout || [])
    } catch {
      fieldLayoutKey = ''
    }
    try {
      customFieldsKey = JSON.stringify(customFields || [])
    } catch {
      customFieldsKey = ''
    }
    try {
      excludedKey = JSON.stringify(excludedFromLayout || [])
    } catch {
      excludedKey = ''
    }
    try {
      labelOverridesKey = JSON.stringify(labelOverrides || {})
    } catch {
      labelOverridesKey = ''
    }
    return [
      agreementHtml || '',
      fullPdfPages.length,
      fullPdfPages.map((p) => (p.imageDataUrl || '').length).join(','),
      totalSignatures,
      layoutColumns,
      signersKey,
      fieldLayoutKey,
      customFieldsKey,
      excludedKey,
      labelOverridesKey,
      includeDate ? 1 : 0,
      includeTerms ? 1 : 0,
      includeFullName ? 1 : 0,
      includePhone ? 1 : 0,
      includeSignature ? 1 : 0,
      canUseLogo ? 1 : 0,
      (logoDataUrl || '').length,
      logoLocation || '',
    ].join('\x1e')
  }, [
    agreementHtml,
    fullPdfPages,
    totalSignatures,
    layoutColumns,
    signers,
    fieldLayout,
    customFields,
    excludedFromLayout,
    labelOverrides,
    includeDate,
    includeTerms,
    includeFullName,
    includePhone,
    includeSignature,
    canUseLogo,
    logoDataUrl,
    logoLocation,
  ])

  const goBackToAppFromEmailActivity = useCallback(() => {
    setView('app')
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.get('view') === 'email-activity') {
        url.searchParams.delete('view')
        const qs = url.searchParams.toString()
        window.history.replaceState({}, '', url.pathname + (qs ? `?${qs}` : '') + (url.hash || ''))
      }
    } catch (_) {}
  }, [])

  const handleBrandClick = () => {
    if (view === 'email-activity') {
      goBackToAppFromEmailActivity()
      return
    }
    const openInNewTab = (user && user.email && !user.isAnonymous) || view === 'app'
    const homePath = locPath('/')
    if (openInNewTab) {
      window.open(typeof window !== 'undefined' ? `${window.location.origin}${homePath}` : homePath, '_blank')
    } else {
      window.location.href = homePath
    }
  }

  const goToMarketingSeoPath = useCallback((e, path) => {
    e?.preventDefault?.()
    if (typeof window === 'undefined') return
    const logical = normalizeMarketingPathname(path)
    const url = buildLocalizedPath(logical, language)
    const qsHash = `${window.location.search}${window.location.hash}`
    window.history.pushState({}, '', `${url}${qsHash}`)
    setMarketingPathname(logical)
    setBlogSlug(null)
    setView('auth')
    setResourcesMenuOpen(false)
    setMobileNavOpen(false)
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [language])

  const goToBlogList = useCallback((e) => {
    e?.preventDefault?.()
    if (typeof window === 'undefined') return
    const url = buildLocalizedPath('/blog', language)
    const qsHash = `${window.location.search}${window.location.hash}`
    window.history.pushState({}, '', `${url}${qsHash}`)
    setMarketingPathname('/blog')
    setBlogSlug(null)
    setView('blog')
    setResourcesMenuOpen(false)
    setMobileNavOpen(false)
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [language])

  const goToBlogPost = useCallback((e, slug) => {
    e?.preventDefault?.()
    if (typeof window === 'undefined' || !slug) return
    const url = buildLocalizedPath(`/blog/${slug}`, language)
    const qsHash = `${window.location.search}${window.location.hash}`
    window.history.pushState({}, '', `${url}${qsHash}`)
    setMarketingPathname(`/blog/${slug}`)
    setBlogSlug(slug)
    setView('blog-post')
    setMobileNavOpen(false)
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [language])

  useEffect(() => {
    if (isRecipientView) {
      previousAppTabRef.current = appTab
      return
    }
    if (previousAppTabRef.current === appTab) return
    previousAppTabRef.current = appTab
    if (appTab === 'document') return

    const navEl = appTabNavRef.current
    if (!navEl || typeof window === 'undefined' || typeof document === 'undefined') return

    const fixedHeaderHeight = document.querySelector('.site-nav--simple')?.getBoundingClientRect().height ?? 0
    const navTop = navEl.getBoundingClientRect().top + window.scrollY

    window.scrollTo({
      top: Math.max(0, navTop - fixedHeaderHeight - 28),
      behavior: 'smooth',
    })
  }, [appTab, isRecipientView])

  const setPreviewUrlFromDataUrl = (dataUrl) => {
    if (!dataUrl || typeof dataUrl !== 'string') return null
    if (previewBlobUrlRef.current) {
      URL.revokeObjectURL(previewBlobUrlRef.current)
      previewBlobUrlRef.current = null
    }
    try {
      const blob = dataUrlToBlob(dataUrl)
      const url = URL.createObjectURL(blob)
      previewBlobUrlRef.current = url
      return url
    } catch (e) {
      return null
    }
  }

  const scrollToDocumentSection = useCallback((step) => {
    if (typeof document === 'undefined') return
    const el = document.getElementById(`doc-section-${step}`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const handlePreview = () => {
    if (editorRef.current) {
      const editorHtml = editorRef.current.innerHTML
      const hasEditorContent = editorHtml && editorHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().replace(/\s/g, '') !== ''
      if (hasEditorContent) {
        lastValidHtmlRef.current = editorHtml
        setAgreementHtml(editorHtml)
      }
    }
    setIsPreviewOpen(true)
    setPreviewStatus('')
    setPreviewError(null)
    const cache = previewPdfCacheRef.current
    if (cache.url && cache.signature === previewPdfCacheKey) {
      const urlToSet = setPreviewUrlFromDataUrl(cache.url)
      if (urlToSet) {
        setPreviewPdfUrl(urlToSet)
        setPreviewEmailSizeBytes(cache.sizeBytes)
        setPreviewEmailSizeLoading(false)
        useCachedPreviewRef.current = true
      } else {
    setPreviewEmailSizeLoading(true)
    setPreviewEmailSizeBytes(null)
        setPreviewPdfUrl('')
      }
    } else {
      setPreviewEmailSizeLoading(true)
      setPreviewEmailSizeBytes(null)
      setPreviewPdfUrl('')
    }
  }

  useEffect(() => {
    setPreviewIframeLoaded(false)
  }, [previewPdfUrl])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(max-width: 768px)')
    const handler = () => setIsMobileView(mq.matches)
    handler()
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  /* Desktop floating action sidebar: keep vertically centered but clamp so it never overlaps the footer */
  useEffect(() => {
    if (typeof window === 'undefined') return
    const root = document.documentElement
    const mqDesktopSidebar = window.matchMedia('(min-width: 900px)')
    const clear = () => {
      root.style.removeProperty('--sticky-send-footer-nudge')
    }
    const update = () => {
      if (!mqDesktopSidebar.matches || view !== 'app' || isRecipientView || appTab !== 'document') {
        clear()
        return
      }
      const bar = stickySendBarRef.current
      const footer = document.querySelector('.site-footer')
      if (!bar || !footer) {
        clear()
        return
      }
      const vh = window.innerHeight
      const margin = 16
      const footerTop = footer.getBoundingClientRect().top
      const barHalf = bar.offsetHeight / 2
      if (!Number.isFinite(barHalf) || barHalf <= 0) {
        clear()
        return
      }
      const minCenter = margin + barHalf
      const maxCenter = footerTop - margin - barHalf
      const idealCenter = vh / 2
      let clampedCenter
      if (maxCenter >= minCenter) {
        clampedCenter = Math.min(Math.max(idealCenter, minCenter), maxCenter)
      } else {
        clampedCenter = (minCenter + maxCenter) / 2
      }
      const nudgePx = clampedCenter - idealCenter
      root.style.setProperty('--sticky-send-footer-nudge', `${Math.round(nudgePx * 10) / 10}px`)
    }
    let raf = 0
    const schedule = () => {
      if (raf) return
      raf = window.requestAnimationFrame(() => {
        raf = 0
        update()
      })
    }
    schedule()
    window.addEventListener('scroll', schedule, true)
    window.addEventListener('resize', schedule)
    mqDesktopSidebar.addEventListener('change', schedule)
    let ro = null
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(schedule)
      const footerEl = document.querySelector('.site-footer')
      if (footerEl) ro.observe(footerEl)
      requestAnimationFrame(() => {
        if (stickySendBarRef.current) ro?.observe(stickySendBarRef.current)
      })
    }
    return () => {
      window.removeEventListener('scroll', schedule, true)
      window.removeEventListener('resize', schedule)
      mqDesktopSidebar.removeEventListener('change', schedule)
      if (raf) window.cancelAnimationFrame(raf)
      if (ro) ro.disconnect()
      clear()
    }
  }, [view, isRecipientView, appTab, sendProgress])

  useEffect(() => {
    if (!isPreviewOpen || !previewRef?.current) {
      if (!isPreviewOpen) {
        setPreviewEmailSizeLoading(false)
        setPreviewPdfUrl('')
        setPreviewIframeLoaded(false)
        setPreviewError(null)
        setPreviewPdfPagesMobile([])
        setPreviewPdfPagesMobileLoading(false)
        if (previewBlobUrlRef.current) {
          URL.revokeObjectURL(previewBlobUrlRef.current)
          previewBlobUrlRef.current = null
        }
      }
      return
    }
    if (useCachedPreviewRef.current) {
      useCachedPreviewRef.current = false
      return
    }
    setPreviewError(null)
    let cancelled = false
    const run = async () => {
      await new Promise((r) => setTimeout(r, 100))
      if (cancelled) return
      try {
      const dataUrl = await generateEmailAttachmentPdf()
      if (cancelled) return
      setPreviewEmailSizeLoading(false)
      if (dataUrl && typeof dataUrl === 'string') {
          const urlToSet = setPreviewUrlFromDataUrl(dataUrl)
          if (urlToSet) {
            setPreviewPdfUrl(urlToSet)
        const commaIdx = dataUrl.indexOf(',')
        const bytes = commaIdx >= 0 ? Math.floor(((dataUrl.length - commaIdx - 1) * 3) / 4) : 0
        setPreviewEmailSizeBytes(bytes)
            previewPdfCacheRef.current = { url: dataUrl, signature: previewPdfCacheKey, sizeBytes: bytes }
      } else {
        setPreviewPdfUrl('')
        setPreviewEmailSizeBytes(null)
            setPreviewError('Preview failed. Try again.')
          }
        } else {
          setPreviewPdfUrl('')
          setPreviewEmailSizeBytes(null)
          setPreviewError('Preview failed. Try again.')
        }
      } catch (e) {
        if (!cancelled) {
          setPreviewEmailSizeLoading(false)
          setPreviewPdfUrl('')
          setPreviewEmailSizeBytes(null)
          setPreviewError('Preview failed. Try again.')
        }
      }
    }
    run()
    return () => { cancelled = true }
  }, [isPreviewOpen, previewRetryKey, previewPdfCacheKey])

  /* Mobile: render PDF to canvas pages so preview works on iOS (iframe PDF is broken in Safari) */
  useEffect(() => {
    if (!previewPdfUrl || !isPreviewOpen || !isMobileView) {
      if (!isMobileView || !isPreviewOpen) setPreviewPdfPagesMobile([])
      setPreviewPdfPagesMobileLoading(false)
      return
    }
    let cancelled = false
    setPreviewPdfPagesMobileLoading(true)
    ;(async () => {
      try {
        const pdfjsLib = await import('pdfjs-dist')
        await configurePdfWorker(pdfjsLib)
        const res = await fetch(previewPdfUrl)
        if (cancelled) return
        const arrayBuffer = await res.arrayBuffer()
        if (cancelled) return
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer })
        const pdf = await loadingTask.promise
        if (cancelled) return
        const numPages = pdf.numPages
        const scale = Math.min(2, Math.max(1, (typeof window !== 'undefined' ? window.innerWidth : 400) / 400))
        const pages = []
        for (let i = 1; i <= numPages; i++) {
          if (cancelled) return
          const page = await pdf.getPage(i)
          const viewport = page.getViewport({ scale })
          const canvas = document.createElement('canvas')
          canvas.width = viewport.width
          canvas.height = viewport.height
          const ctx = canvas.getContext('2d')
          const renderTask = page.render({ canvasContext: ctx, viewport })
          await renderTask.promise
          pages.push({ pageIndex: i, imageDataUrl: canvas.toDataURL('image/jpeg', 0.92) })
        }
        if (!cancelled) setPreviewPdfPagesMobile(pages)
      } catch (e) {
        if (!cancelled) setPreviewPdfPagesMobile([])
      } finally {
        if (!cancelled) setPreviewPdfPagesMobileLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [previewPdfUrl, isPreviewOpen, isMobileView])

  useEffect(() => {
    if (isPreviewOpen) return
    const hasContent =
      (agreementHtml && agreementHtml.replace(/<[^>]*>/g, '').replace(/\s/g, '').trim()) || fullPdfPages.length > 0
    if (!hasContent) return
    const timer = setTimeout(async () => {
      if (!previewRef?.current) return
      try {
        const dataUrl = await generateEmailAttachmentPdf()
        if (dataUrl && typeof dataUrl === 'string') {
          const commaIdx = dataUrl.indexOf(',')
          const sizeBytes = commaIdx >= 0 ? Math.floor(((dataUrl.length - commaIdx - 1) * 3) / 4) : 0
          previewPdfCacheRef.current = {
            url: dataUrl,
            signature: previewPdfCacheKey,
            sizeBytes,
          }
        }
      } catch (_) {}
    }, 800)
    return () => clearTimeout(timer)
  }, [agreementHtml, fullPdfPages, isPreviewOpen, previewPdfCacheKey])

  const openSaveTemplate = () => {
    const currentTemplate = templates.find((item) => item.id === activeTemplateId)
    setTemplateNameInput(currentTemplate?.name || '')
    setTemplateError('')
    setIsSaveTemplateOpen(true)
  }

  const persistTemplate = async (mode) => {
    if (!user) {
      setTemplateError('You must be logged in to save templates.')
      return
    }
    const trimmedName = templateNameInput.trim()
    if (!trimmedName) {
      setTemplateError('Template name is required.')
      return
    }

    const html =
      editorRef.current?.innerHTML || agreementHtml || lastValidHtmlRef.current || ''
    const plainText = editorRef.current?.innerText || ''

    if (mode === 'overwrite' && activeTemplateId) {
      try {
        const docRef = doc(db, 'users', user.uid, 'userTemplates', activeTemplateId)
        await updateDoc(docRef, {
          name: trimmedName,
          agreementHeader: scopeTitle,
          scopeTitle,
          agreementHtml: html,
          agreementPlainText: plainText,
          updatedAt: serverTimestamp(),
          includeDate,
          includeTerms,
          includeFullName,
          includeSignature,
          includePhone,
          totalSignatures,
          customFields,
          signers,
          logo: logoDataUrl,
          logoDataUrl,
          logoLocation,
          verification: {
            verifyPhoto,
            verifyEmail,
            verifyOTP,
            verifyPhone,
          },
          verifyPhoto,
          verifyEmail,
          verifyOTP,
          verifyPhone,
        })
        setIsSaveTemplateOpen(false)
        setTemplateSaveSuccess({ templateName: trimmedName, isUpdate: true })
      } catch (error) {
        setTemplateError('Failed to update template.')
        console.error(error)
      }
      return
    }

    if (templates.length >= maxTemplates) {
      setTemplateError(`You can save up to ${maxTemplates} templates. Delete one to add more.`)
      return
    }

    try {
      await addDoc(collection(db, 'users', user.uid, 'userTemplates'), {
        ownerId: user?.uid,
        name: trimmedName,
        agreementHeader: scopeTitle,
        scopeTitle,
        agreementHtml: html,
        agreementPlainText: plainText,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        version: 1,
        isArchived: false,
        includeDate,
        includeTerms,
        includeFullName,
        includeSignature,
        includePhone,
        totalSignatures,
        customFields,
        signers,
        logo: logoDataUrl,
        logoDataUrl,
        logoLocation,
        verification: {
          verifyPhoto,
          verifyEmail,
          verifyOTP,
          verifyPhone,
        },
        verifyPhoto,
        verifyEmail,
        verifyOTP,
        verifyPhone,
      })
      setIsSaveTemplateOpen(false)
      setTemplateSaveSuccess({ templateName: trimmedName, isUpdate: false })
    } catch (error) {
      setTemplateError('Failed to save template.')
      console.error(error)
    }
  }

  const loadTemplate = (template) => {
    setActiveTemplateId(template.id)
    setScopeTitle(template.scopeTitle || template.agreementHeader || '')
    setAgreementHtml(template.agreementHtml)
    if (template.totalSignatures !== undefined) setTotalSignatures(template.totalSignatures)
    if (Array.isArray(template.signers) && template.signers.length > 0) setSigners(template.signers)
    if (Array.isArray(template.customFields) && template.customFields.length > 0) setCustomFields(template.customFields)
    
    // Logo
    const logo = template.logo || template.logoDataUrl
    if (logo) setLogoDataUrl(logo)
    if (template.logoLocation) setLogoLocation(template.logoLocation)
    
    // Verification
    if (template.verifyPhoto !== undefined) setVerifyPhoto(template.verifyPhoto)
    else if (template.verification?.verifyPhoto !== undefined) setVerifyPhoto(template.verification.verifyPhoto)
    else setVerifyPhoto(false)

    if (template.verifyEmail !== undefined) setVerifyEmail(template.verifyEmail)
    else if (template.verification?.verifyEmail !== undefined) setVerifyEmail(template.verification.verifyEmail)
    else setVerifyEmail(false)

    if (template.verifyOTP !== undefined) setVerifyOTP(template.verifyOTP)
    else if (template.verification?.verifyOTP !== undefined) setVerifyOTP(template.verification.verifyOTP)
    else setVerifyOTP(false)

    if (template.verifyPhone !== undefined) setVerifyPhone(template.verifyPhone)
    else if (template.verification?.verifyPhone !== undefined) setVerifyPhone(template.verification.verifyPhone)
    else setVerifyPhone(false)

    if (editorRef.current) {
      editorRef.current.innerHTML = template.agreementHtml
      lastValidHtmlRef.current = template.agreementHtml
    }
    setIsHistoryOpen(false)
  }

  const hasExistingEditorContent = () => {
    const text = (agreementHtml || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    return text.length > 0
  }

  const applyStarterTemplate = (templateId) => {
    const name = t(`templates.starter.${templateId}.name`)
    const scopeTitle = t(`templates.starter.${templateId}.scopeTitle`)
    const html = t(`templates.starter.${templateId}.html`)
    setActiveTemplateId(null)
    setScopeTitle(scopeTitle || name || '')
    setAgreementHtml(html)
    if (editorRef.current) {
      editorRef.current.innerHTML = html
      lastValidHtmlRef.current = html
    }
    setIsStartFromTemplateOpen(false)
    setTemplateReplaceConfirmOpen(false)
    setPendingStarterTemplate(null)
  }

  const handleStarterTemplateSelect = (templateId) => {
    if (hasExistingEditorContent()) {
      setPendingStarterTemplate(templateId)
      setIsStartFromTemplateOpen(false)
      setTemplateReplaceConfirmOpen(true)
    } else {
      applyStarterTemplate(templateId)
    }
  }

  const deleteTemplate = async (templateId) => {
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'userTemplates', templateId))
      if (activeTemplateId === templateId) {
        setActiveTemplateId(null)
      }
    } catch (error) {
      console.error('Failed to delete template:', error)
    }
  }

  const handleDone = async () => {
    if (!isComplete) {
      setCompletionStatus(
        `Please complete the required fields: ${missingFields.join(', ')}.`
      )
      setCompletionStatusType('error')
      return
    }

    setCompletionStatusType('')
    setIsSendingConfirmation(true)
    setCompletionStatus('Preparing signed document...')
    if (editorRef.current) {
      const html = editorRef.current.innerHTML
      lastValidHtmlRef.current = html
      setAgreementHtml(html)
    }

    // Audit trail (for verification)
    const auditTimestamp = new Date().toISOString()
    const auditEmail = recipientEmail || 'Not provided'
    const auditPhones = signers.map((s) => s.phone || '').filter(Boolean).join(', ') || 'Not provided'
    const shortId = new URLSearchParams(window.location.search).get('id')

    const ipPromise = fetch('https://api.ipify.org?format=json', { signal: AbortSignal.timeout(2000) })
      .then((r) => r.json())
      .then((j) => j.ip || 'Not available')
      .catch(() => 'Not available')

    const canonicalForHash = [
      scopeTitle || '',
      agreementPlainText,
      ...signers.map((s) => {
        const cfStr = customFields.map((cf) => (s.customFields || {})[cf.id] ?? '').join('|')
        return `${s.fullName || ''}|${s.date || ''}|${s.agreed}|${s.phone || ''}|${cfStr}`
      }),
    ].join('\n')
    let auditDocumentHash = 'Not available'
    try {
      const buf = new TextEncoder().encode(canonicalForHash)
      const hashBuf = await crypto.subtle.digest('SHA-256', buf)
      auditDocumentHash = Array.from(new Uint8Array(hashBuf))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
    } catch (_) {}
    const auditUserAgent = typeof navigator !== 'undefined' ? navigator.userAgent : ''
    const auditTimezone = typeof Intl !== 'undefined' && Intl.DateTimeFormat
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : ''

    const baseUrl = getPublicAppBaseUrl()
    const pathname = typeof window.location.pathname === 'string' && window.location.pathname ? window.location.pathname : '/'
    const signerPageUrl = shortId
      ? `${baseUrl.replace(/\/$/, '')}${pathname.startsWith('/') ? pathname : `/${pathname}`}?id=${shortId}`
      : `${baseUrl.replace(/\/$/, '')}${pathname}${window.location.search || ''}`

    const auditIp = await ipPromise

    let resolvedDocId = currentDocId
    let resolvedOwnerId = docOwnerId
    let resolvedSenderEmail = null

    if (shortId) {
      try {
        const submitSignature = httpsCallable(functions, 'submitDocumentSignature')
        const submitRes = await submitSignature({
          shortId,
          signers,
          agreementHtml: lastValidHtmlRef.current || agreementHtml || '',
          scopeTitle: scopeTitle || 'Agreement',
          recipientEmail: recipientEmail || '',
          viewSignedUrl: signerPageUrl,
          auditTimestamp,
          auditIp,
          auditUserAgent,
          auditDocumentHash,
          auditTimezone,
        })
        const { ownerId: oid, docId: did, senderEmail: sem } = submitRes.data || {}
        if (oid) resolvedOwnerId = oid
        if (did) resolvedDocId = did
        if (sem) resolvedSenderEmail = sem
        setIsSignedComplete(true)
        setSignedAtDisplay(auditTimestamp)
      } catch (e) {
        console.error('Failed to submit signature:', e)
        setCompletionStatus(e?.message || 'Failed to save signature. Please try again.')
        setCompletionStatusType('error')
        setIsSendingConfirmation(false)
        return
      }
    }

    // Backend (submitDocumentSignature) already sends one "Agreement Signed" email to the sender per sign — do not call sendSigned here to avoid duplicate emails
    setCompletionStatus('Signature saved. Confirmation email sent to sender.')
      setCompletionStatusType('success')
      setIsSignedComplete(true)
      setIsSendingConfirmation(false)
  }

  const handleDownloadDocument = async () => {
    if (isDownloadingDocument) return
    setIsDownloadingDocument(true)
    try {
      const dataUrl = await generateSignedPdf(previewRef)
      if (!dataUrl) {
        setCompletionStatus('Could not generate PDF.')
        setCompletionStatusType('error')
        return
      }
      const base64 = dataUrl.split(',')[1]
      if (!base64) return
      const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
      const blob = new Blob([bytes], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${(scopeTitle || 'signed-document').replace(/[^a-zA-Z0-9-_]/g, '-')}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error('Download PDF failed:', e)
      setCompletionStatus('Failed to generate PDF for download.')
      setCompletionStatusType('error')
    } finally {
      setIsDownloadingDocument(false)
    }
  }

  const contactMessageLimit = appConfig.contactMessageLimit ?? 1000
  const contactAttachmentLimit = appConfig.contactAttachmentLimit ?? 5
  const contactAttachmentMaxBytes = appConfig.contactAttachmentMaxBytes ?? 2 * 1024 * 1024

  const contactFieldErrors = useMemo(() => {
    const errs = {}
    if (!contactFirstName.trim()) errs.firstName = t('contact.errorFirstNameRequired')
    if (!contactLastName.trim()) errs.lastName = t('contact.errorLastNameRequired')
    const email = contactEmail.trim()
    if (!email) errs.email = t('contact.errorEmailRequired')
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = t('contact.errorEmailInvalid')
    if (!contactMessage.trim()) errs.message = t('contact.errorMessageRequired')
    else if (contactMessage.length > contactMessageLimit) errs.message = t('contact.errorMessageTooLong', { max: contactMessageLimit, current: contactMessage.length })
    const totalBytes = contactAttachments.reduce((sum, a) => sum + (a.file?.size || 0), 0)
    if (contactAttachments.length > contactAttachmentLimit) errs.attachments = t('contact.errorAttachmentsCount', { max: contactAttachmentLimit })
    else if (totalBytes > contactAttachmentMaxBytes) errs.attachments = t('contact.errorAttachmentsSize', { maxMb: (contactAttachmentMaxBytes / 1024 / 1024).toFixed(0), current: (totalBytes / 1024 / 1024).toFixed(2) })
    return errs
  }, [t, contactFirstName, contactLastName, contactEmail, contactMessage, contactAttachments, contactMessageLimit, contactAttachmentLimit, contactAttachmentMaxBytes])
  const isContactFormValid = Object.keys(contactFieldErrors).length === 0

  // Shared ContactForm props – identical validation for logged-in (Contact Support) and logged-out (Contact us) users
  const contactFormProps = {
    firstName: contactFirstName,
    setFirstName: setContactFirstName,
    lastName: contactLastName,
    setLastName: setContactLastName,
    email: contactEmail,
    setEmail: setContactEmail,
    message: contactMessage,
    setMessage: setContactMessage,
    messageCharLimit: contactMessageLimit,
    location: contactLocation,
    setLocation: setContactLocation,
    phone: contactPhone,
    setPhone: setContactPhone,
    company: contactCompany,
    setCompany: setContactCompany,
    links: contactLinks,
    setLinks: setContactLinks,
    attachments: contactAttachments,
    onAddAttachments: (files) => {
      const maxCount = contactAttachmentLimit
      const maxBytes = contactAttachmentMaxBytes
      setContactAttachments((prev) => {
        let list = [...prev]
        let totalBytes = list.reduce((s, a) => s + (a.file?.size || 0), 0)
        for (const file of files || []) {
          if (list.length >= maxCount) break
          if (totalBytes + file.size > maxBytes) break
          list.push({ file })
          totalBytes += file.size
        }
        return list
      })
    },
    onRemoveAttachment: (i) => {
      const file = contactAttachments[i]?.file
      const name = file?.name || 'this attachment'
      setConfirmDeleteState({
        title: t('modal.removeAttachment'),
        message: t('modal.removeAttachmentFromMessage', { name }),
        confirmLabel: t('modal.yesRemoveLabel'),
        onConfirm: () => setContactAttachments((prev) => prev.filter((_, idx) => idx !== i)),
      })
    },
    fieldErrors: contactFieldErrors,
    showFieldErrors: contactAttemptedSubmit,
    status: contactStatus,
    statusType: contactStatusType,
    onFieldChange: () => { setContactStatus(''); setContactAttemptedSubmit(false) },
    submitLabel: t('contact.sendMessage'),
  }

  const handleContactSubmit = async (subject = 'New contact request', successMessage = 'Message sent successfully.') => {
    setContactAttemptedSubmit(true)
    const trimmedFirst = contactFirstName.trim()
    const trimmedLast = contactLastName.trim()
    const trimmedEmail = contactEmail.trim()
    const trimmedMessage = contactMessage.trim()
    const fromName = `${trimmedFirst} ${trimmedLast}`.trim()
    if (!isContactFormValid) {
      setContactStatus('Please fix the validation errors above.')
      setContactStatusType('error')
      return
    }

    setContactStatus('Sending message...')
    setContactStatusType('')
    try {
      const arrayBufferToBase64 = (buffer) => {
        const bytes = new Uint8Array(buffer)
        let binary = ''
        const chunkSize = 8192
        for (let i = 0; i < bytes.length; i += chunkSize) {
          const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length))
          binary += String.fromCharCode.apply(null, chunk)
        }
        return btoa(binary)
      }
      const attachmentPayloads = await Promise.all(
        contactAttachments.map(async ({ file }) => {
          const buf = await file.arrayBuffer()
          return { filename: file.name, base64: arrayBufferToBase64(buf) }
        })
      )
      const sendContact = httpsCallable(functions, 'sendContactForm')
      await sendContact({
        fromName,
        fromEmail: trimmedEmail,
        subject,
        message: trimmedMessage,
        location: contactLocation.trim() || undefined,
        phone: contactPhone.trim() || undefined,
        company: contactCompany.trim() || undefined,
        links: contactLinks.trim() || undefined,
        attachments: attachmentPayloads,
      })
      setContactStatus(successMessage)
      setContactStatusType('success')
      setContactFirstName('')
      setContactLastName('')
      setContactEmail('')
      setContactMessage('')
      setContactLocation('')
      setContactPhone('')
      setContactCompany('')
      setContactLinks('')
      setContactAttachments([])
      setContactAttemptedSubmit(false)
      // Keep Contact Support modal open so user sees success message; they can click Close
    } catch (error) {
      setContactStatus(error?.message || 'Failed to send message. Please try again.')
      setContactStatusType('error')
      reportErrorToAdmin('ContactForm', error, { subject, fromName: `${trimmedFirst} ${trimmedLast}`.trim(), fromEmail: trimmedEmail })
    }
  }

  if (hasSigningLinkId && (signingLinkLoading || authLoading)) {
    if (showStaleSessionMessage) {
      return (
        <div className="stale-session-screen">
          <div className="stale-session-card">
            <p className="stale-session-message">{t('app.staleSessionMessage')}</p>
            <p className="stale-session-hint">{t('app.refreshBrowser')}</p>
            <button type="button" className="primary-button stale-session-refresh-btn" onClick={() => window.location.reload()}>
              {t('app.refreshButton')}
            </button>
          </div>
        </div>
      )
    }
    return (
      <div className="signing-link-loading">
        <div className="signing-link-loading-content">
          <div className="signing-link-loading-spinner" aria-hidden />
          <p className="signing-link-loading-text">Loading document…</p>
          <div className="signing-link-loading-bar">
            <div className="signing-link-loading-bar-fill" />
          </div>
        </div>
      </div>
    )
  }

  if (hasSigningLinkId && isLinkExpired) {
    return (
      <div className="link-expired-page">
        <div className="link-expired-card">
          <div className="link-expired-badge">Link expired</div>
          <div className="link-expired-icon" aria-hidden>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 8v4M12 16h.01"/>
            </svg>
          </div>
          <h1 className="link-expired-title">This link is no longer active</h1>
          <p className="link-expired-message">
            The signing link has expired. Please contact the sender to request a new email with a fresh link.
          </p>
          <p className="link-expired-hint">If you believe this is an error, please ask them to send the document again.</p>
        </div>
      </div>
    )
  }

  if (authLoading && !hasSigningLinkId) {
    if (showStaleSessionMessage) {
    return (
        <div className="stale-session-screen">
          <div className="stale-session-card">
            <p className="stale-session-message">{t('app.staleSessionMessage')}</p>
            <p className="stale-session-hint">{t('app.refreshBrowser')}</p>
            <button type="button" className="primary-button stale-session-refresh-btn" onClick={() => window.location.reload()}>
              {t('app.refreshButton')}
            </button>
          </div>
        </div>
      )
    }
    return (
      <div className="app-loading-screen">
        <div className="app-loading-spinner" aria-hidden />
        <p className="app-loading-text">Loading…</p>
      </div>
    )
  }

  if (user && !userProfileLoaded) {
    if (showStaleSessionMessage) {
      return (
        <div className="stale-session-screen">
          <div className="stale-session-card">
            <p className="stale-session-message">{t('app.staleSessionMessage')}</p>
            <p className="stale-session-hint">{t('app.refreshBrowser')}</p>
            <button type="button" className="primary-button stale-session-refresh-btn" onClick={() => window.location.reload()}>
              {t('app.refreshButton')}
            </button>
          </div>
        </div>
      )
    }
    return (
      <div className="app-loading-screen">
        <div className="app-loading-spinner" aria-hidden />
        <p className="app-loading-text">Loading…</p>
      </div>
    )
  }

  if (user && appConfig.maintenance) {
    return <MaintenancePage />
  }

  return (
    <div className={`app ${view === 'auth' ? 'app--landing' : ''}`}>
      {connectionBannerVisible && (
        <div className="connection-error-banner" role="alert">
          <span className="connection-error-banner__icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </span>
          <p className="connection-error-banner__text">{t('connectionError.message')}</p>
          <div className="connection-error-banner__actions">
            <button type="button" className="connection-error-banner__btn" onClick={() => window.location.reload()}>{t('connectionError.refresh')}</button>
            <button type="button" className="connection-error-banner__dismiss" onClick={() => setConnectionBannerVisible(false)} aria-label="Dismiss">×</button>
          </div>
        </div>
      )}
      {isAuthModalOpen && (() => {
        const authCard = (
          <div className="auth-card" data-signup-pricing={authMode === 'signup' && signupStep === 2 ? '' : undefined}>
            <div className="auth-tabs">
              <button type="button" className={`auth-tab ${authMode === 'login' ? 'is-active' : ''}`} onClick={() => { setAuthMode('login'); setAuthError(''); setAuthTermsAcceptError(''); setAuthEmailError(''); setAuthPasswordError(''); setAuthReferralError(''); setAuthReferralOtherError(''); setShowForgotPassword(false); setForgotPasswordSent(false); setSignupStep(1) }}>{t('auth.logIn')}</button>
              <button type="button" className={`auth-tab ${authMode === 'signup' ? 'is-active' : ''}`} onClick={() => { setAuthMode('signup'); setAuthError(''); setAuthTermsAcceptError(''); setAuthEmailError(''); setAuthPasswordError(''); setAuthReferralError(''); setAuthReferralOtherError(''); setSignupStep(1) }}>{t('auth.signUp')}</button>
            </div>
            {authMode === 'login' ? (
              <div className="auth-form">
                {showForgotPassword ? (
                  <>
                    <label className="field"><span>Email</span><input type="email" placeholder="john@doe.com" value={authEmail} onChange={(e) => setAuthEmail(e.target.value)} disabled={forgotPasswordSent} /></label>
                    {forgotPasswordSent ? <p className="status-note">{t('auth.checkEmailReset')}</p> : <button type="button" className="primary-button auth-action-btn" disabled={authSubmitting} onClick={async () => { setAuthError(''); if (!authEmail.trim()) { setAuthError(t('auth.enterEmail')); return; } setAuthSubmitting(true); try { await resetPassword(authEmail.trim()); setForgotPasswordSent(true); } catch (err) { const { message, showConnectionBanner } = getAuthErrorMessage(err, t); setAuthError(message); if (showConnectionBanner) setConnectionBannerVisible(true); } finally { setAuthSubmitting(false); } }}>{t('auth.sendResetLink')}</button>}
                    <button type="button" className="link-button" onClick={() => { setShowForgotPassword(false); setForgotPasswordSent(false); setAuthError(''); }}>{t('auth.backToLogIn')}</button>
                  </>
                ) : (
                  <>
                    {authError && <p className="status-note status-error">{authError}</p>}
                    <label className="field"><span>{t('auth.emailLabel')}</span><input type="email" placeholder={t('auth.emailPlaceholder')} value={authEmail} onChange={(e) => { setAuthEmail(e.target.value); setAuthError(''); }} /></label>
                    <label className="field"><span>{t('auth.passwordLabel')}</span><input type="password" placeholder={t('auth.passwordPlaceholder')} value={authPassword} onChange={(e) => { setAuthPassword(e.target.value); setAuthError(''); }} /></label>
                    <div className="auth-row"><button type="button" className="link-button" onClick={() => setShowForgotPassword(true)}>{t('auth.forgotPassword')}</button></div>
                    <button type="button" className="primary-button full-width" disabled={authSubmitting} onClick={async () => { setAuthError(''); if (!authEmail.trim() || !authPassword) { setAuthError(t('auth.enterEmailPassword')); return; } setAuthSubmitting(true); try { await authLogin(authEmail.trim(), authPassword); setIsAuthModalOpen(false); } catch (err) { const { message, showConnectionBanner } = getAuthErrorMessage(err, t); setAuthError(message); if (showConnectionBanner) setConnectionBannerVisible(true); } finally { setAuthSubmitting(false); } }}>{authSubmitting ? t('auth.loggingIn') : t('auth.logIn')}</button>
                  </>
                )}
              </div>
            ) : (
              <div className="auth-form">
                {signupStep === 1 && (
                  <>
                    {authError && <p className="status-note status-error">{authError}</p>}
                    <div className="auth-field-row"><span className="auth-field-label">{t('auth.firstNameLabel')}</span><div className="auth-field-input-wrap"><input type="text" placeholder={t('auth.firstNamePlaceholder')} value={authFirstName} onChange={(e) => setAuthFirstName(e.target.value)} /></div></div>
                    <div className="auth-field-row"><span className="auth-field-label">{t('auth.lastNameLabel')}</span><div className="auth-field-input-wrap"><input type="text" placeholder={t('auth.lastNamePlaceholder')} value={authLastName} onChange={(e) => setAuthLastName(e.target.value)} /></div></div>
                    <div className={`auth-field-row ${authEmailError ? 'auth-field-row--error' : ''}`}><span className="auth-field-label">{t('auth.emailLabel')}</span><div className="auth-field-input-wrap"><input type="email" placeholder={t('auth.emailPlaceholder')} value={authEmail} onChange={(e) => { setAuthEmail(e.target.value); setAuthError(''); setAuthEmailError(''); }} onBlur={() => { if (authEmail.trim() && !isValidEmail(authEmail)) setAuthEmailError('Please enter a valid email address.'); else setAuthEmailError(''); }} />{authEmailError && <span className="auth-field-error">{authEmailError}</span>}</div></div>
                    <div className={`auth-field-row ${authPasswordError ? 'auth-field-row--error' : ''}`}><span className="auth-field-label">{t('auth.passwordLabel')}</span><div className="auth-field-input-wrap"><input type="password" placeholder={t('auth.passwordPlaceholder')} value={authPassword} onChange={(e) => { setAuthPassword(e.target.value); setAuthError(''); setAuthPasswordError(''); }} onBlur={() => { if (authPassword && !isValidPassword(authPassword)) setAuthPasswordError(t('auth.passwordSpecial')); else setAuthPasswordError(''); }} />{authPasswordError && <span className="auth-field-error">{authPasswordError}</span>}</div></div>
                    <div className={`auth-field-row ${authPassword !== authConfirmPassword && authConfirmPassword ? 'auth-field-row--error' : ''}`}><span className="auth-field-label">{t('auth.confirmPasswordLabel')}</span><div className="auth-field-input-wrap"><input type="password" placeholder={t('auth.confirmPasswordPlaceholder')} value={authConfirmPassword} onChange={(e) => { setAuthConfirmPassword(e.target.value); setAuthError(''); }} />{authPassword !== authConfirmPassword && authConfirmPassword && <span className="auth-field-error">{t('auth.passwordsNoMatch')}</span>}</div></div>
                    <div className={`auth-field-row ${authReferralError ? 'auth-field-row--error' : ''}`}>
                      <span className="auth-field-label" id="auth-referral-label">
                        {t('auth.referralSourceLabel')}
                        <span className="field-required" aria-hidden="true"> *</span>
                      </span>
                      <div className="auth-field-input-wrap">
                        <select
                          id="auth-referral-select"
                          className="auth-field-select"
                          aria-labelledby="auth-referral-label"
                          aria-required="true"
                          value={authReferralSource}
                          onChange={(e) => {
                            setAuthReferralSource(e.target.value)
                            setAuthReferralError('')
                            setAuthReferralOtherError('')
                            if (e.target.value !== REFERRAL_SOURCE_OTHER) setAuthReferralOtherDetail('')
                          }}
                        >
                          <option value="">{t('auth.referralSourcePlaceholder')}</option>
                          {REFERRAL_SOURCES.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                        {authReferralError ? <span className="auth-field-error">{authReferralError}</span> : null}
                      </div>
                    </div>
                    {authReferralSource === REFERRAL_SOURCE_OTHER ? (
                      <div className={`auth-field-row ${authReferralOtherError ? 'auth-field-row--error' : ''}`}>
                        <span className="auth-field-label" id="auth-referral-other-label">{t('auth.referralOtherLabel')}</span>
                        <div className="auth-field-input-wrap">
                          <input
                            id="auth-referral-other-input"
                            type="text"
                            autoComplete="off"
                            placeholder={t('auth.referralOtherPlaceholder')}
                            value={authReferralOtherDetail}
                            onChange={(e) => { setAuthReferralOtherDetail(e.target.value); setAuthReferralOtherError('') }}
                            aria-labelledby="auth-referral-other-label"
                          />
                          {authReferralOtherError ? <span className="auth-field-error">{authReferralOtherError}</span> : null}
                        </div>
                      </div>
                    ) : null}
                    <div className="auth-terms-block" id="auth-signup-terms-block">
                      <div className="auth-terms-row">
                        <input
                          id="auth-signup-terms"
                          type="checkbox"
                          className="auth-terms-checkbox"
                          checked={authTermsAccepted}
                          onChange={(e) => {
                            setAuthTermsAccepted(e.target.checked)
                            setAuthError('')
                            setAuthTermsAcceptError('')
                          }}
                        />
                        <div className="auth-terms-copy">
                          <label className="auth-terms-label-hit" htmlFor="auth-signup-terms">
                            <span className="auth-terms-intro">{t('auth.termsConfirmStart')}</span>
                          </label>
                          <a
                            href={locPath('/terms')}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="auth-terms-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {t('footer.terms')}
                          </a>
                          <label className="auth-terms-label-hit" htmlFor="auth-signup-terms">
                            <span className="auth-terms-outro">{t('auth.termsConfirmEnd')}</span>
                          </label>
                        </div>
                      </div>
                      {authTermsAcceptError ? (
                        <p className="status-note status-error auth-terms-inline-error" role="alert" id="auth-signup-terms-error">
                          {authTermsAcceptError}
                        </p>
                      ) : null}
                    </div>
                    <button type="button" className="primary-button full-width" style={{ marginTop: '8px' }} disabled={authSubmitting} onClick={async () => {
                      setAuthError('')
                      setAuthTermsAcceptError('')
                      setAuthEmailError('')
                      setAuthPasswordError('')
                      setAuthReferralError('')
                      setAuthReferralOtherError('')
                      if (!authTermsAccepted) {
                        setAuthTermsAcceptError(t('auth.termsMustAccept'))
                        try {
                          document.getElementById('auth-signup-terms')?.focus?.()
                          document.getElementById('auth-signup-terms-block')?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
                        } catch (_) {}
                        return
                      }
                      if (!authEmail.trim()) { setAuthError(t('auth.enterEmail')); return; }
                      if (!isValidEmail(authEmail)) { setAuthEmailError(t('auth.errorEmailInvalid')); return; }
                      if (authPassword.length < 8) { setAuthPasswordError(t('auth.passwordMinLength')); return; }
                      if (!isValidPassword(authPassword)) { setAuthPasswordError(t('auth.passwordSpecial')); return; }
                      if (authPassword !== authConfirmPassword) { setAuthError(t('auth.passwordsNoMatch')); return; }
                      if (!authReferralSource) { setAuthReferralError(t('auth.referralSourceRequired')); return; }
                      if (authReferralSource === REFERRAL_SOURCE_OTHER && !authReferralOtherDetail.trim()) { setAuthReferralOtherError(t('auth.referralOtherRequired')); return; }
                      setAuthSubmitting(true)
                      try {
                        const emailExists = await checkEmailExists(authEmail.trim())
                        if (emailExists) { setIsEmailExistsModalOpen(true); return; }
                        const userData = { firstName: authFirstName.trim(), lastName: authLastName.trim(), subscriptionTier: 'premium', billingCycle: 'monthly', isOneTimeUse: true, preferredLanguage: language, referralSource: authReferralSource, referralSourceDetail: authReferralSource === REFERRAL_SOURCE_OTHER ? authReferralOtherDetail.trim() : '' }
                        await authSignUp(authEmail.trim(), authPassword, userData)
                        try { if (!openPaywallAfterAuth) sessionStorage.setItem('justCreatedAccount', '1') } catch (_) {}
                        setIsAuthModalOpen(false)
                        setView('app')
                      } catch (err) {
                        const { message, showConnectionBanner } = getAuthErrorMessage(err, t)
                        if (err?.code === 'auth/email-already-in-use') { setIsEmailExistsModalOpen(true) } else { setAuthError(message); if (showConnectionBanner) setConnectionBannerVisible(true) }
                      } finally { setAuthSubmitting(false) }
                    }}>{authSubmitting ? t('auth.creatingAccount') : t('auth.createAccountButton')}</button>
                  </>
                )}
                {signupStep === 2 && (
                  <>
                    <button type="button" className="link-button" onClick={() => { setSignupStep(1); setAuthTermsAcceptError(''); setAuthError('') }} style={{ alignSelf: 'flex-start', marginBottom: '12px' }}>← {t('auth.backToAccountDetails')}</button>
                    {authMode === 'signup' && (
                      <div className="one-time-use-link-wrap">
                        <button type="button" className={`one-time-use-link ${showOneTimeOptions ? 'active' : ''}`} onClick={() => { setShowOneTimeOptions((prev) => !prev); if (showOneTimeOptions) { setIsOneTimeUse(false) } else { setSubscriptionTier('premium'); setIsOneTimeUse(true) } }}>
                          {showOneTimeOptions ? <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg> {t('auth.backToSubscriptions')}</> : <>{t('auth.lookingForOneTimeUse')} <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg></>}
                        </button>
                      </div>
                    )}
                    {!showOneTimeOptions && (<div className="signup-billing-toggle subscription-billing-toggle"><button type="button" className={`toggle-option ${billingCycle === 'yearly' ? 'is-active' : ''}`} onClick={() => setBillingCycle('yearly')}>{t('pricing.billAnnually')}</button><button type="button" className={`toggle-option ${billingCycle === 'monthly' ? 'is-active' : ''}`} onClick={() => setBillingCycle('monthly')}>{t('pricing.billMonthly')}</button></div>)}
                    {!showOneTimeOptions && (
                      <div className="subscription-tiers signup-tiers">
                        <div className={`tier-card ${subscriptionTier === 'basic' ? 'selected' : ''}`} role="button" tabIndex={0} onClick={() => { setSubscriptionTier('basic'); setIsOneTimeUse(false); }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSubscriptionTier('basic'); setIsOneTimeUse(false); } }}>
                          {billingCycle === 'yearly' && <span className="tier-discount-badge">-{tierPricing.basic.discountPercent}%</span>}
                          <div className="tier-header"><div className="tier-icon-wrap"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg></div><h4>{t('pricing.tierBasic')}</h4><p className="tier-desc">{t('pricing.descBasic')}</p></div>
                          {billingCycle === 'yearly' && <p className="tier-annual-billing">Billed ${tierPricing.basic.annual}/yr</p>}
                          <div className="tier-price-row"><span className="tier-price">${billingCycle === 'monthly' ? tierPricing.basic.monthly : (tierPricing.basic.annual / 12) % 1 === 0 ? tierPricing.basic.annual / 12 : (tierPricing.basic.annual / 12).toFixed(2)}</span><span className="tier-period">{t('pricing.perMonth')}</span></div>
                          <ul className="tier-features"><li><CheckIcon /> {t('pricing.featureRecipients5')}</li><li><CheckIcon /> {t('pricing.featureEmails5')}</li><li><CheckIcon /> {t('pricing.featureTemplates3')}</li><li><CheckIcon /> {t('pricing.featureChars3k')}</li><li><CheckIcon /> {t('pricing.featureAttachmentsNone')}</li><li><CheckIcon /> {t('pricing.featurePdfUpload')}</li><li><CheckIcon /> {t('pricing.featureCustomFields')}</li></ul>
                          <button type="button" className="tier-button">{subscriptionTier === 'basic' ? 'Selected' : 'Select'}</button>
                        </div>
                        <div className={`tier-card ${subscriptionTier === 'standard' ? 'selected' : ''}`} role="button" tabIndex={0} onClick={() => { setSubscriptionTier('standard'); setIsOneTimeUse(false); }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSubscriptionTier('standard'); setIsOneTimeUse(false); } }}>
                          {billingCycle === 'yearly' && <span className="tier-discount-badge">-{tierPricing.standard.discountPercent}%</span>}
                          <div className="tier-header"><div className="tier-icon-wrap"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg></div><h4>{t('pricing.tierStandard')}</h4><p className="tier-desc">{t('pricing.descStandard')}</p></div>
                          {billingCycle === 'yearly' && <p className="tier-annual-billing">Billed ${tierPricing.standard.annual}/yr</p>}
                          <div className="tier-price-row"><span className="tier-price">${billingCycle === 'monthly' ? tierPricing.standard.monthly : (tierPricing.standard.annual / 12) % 1 === 0 ? tierPricing.standard.annual / 12 : (tierPricing.standard.annual / 12).toFixed(2)}</span><span className="tier-period">{t('pricing.perMonth')}</span></div>
                          <ul className="tier-features"><li><CheckIcon /> {t('pricing.featureRecipients10')}</li><li><CheckIcon /> {t('pricing.featureEmails50')}</li><li><CheckIcon /> {t('pricing.featureTemplates10')}</li><li><CheckIcon /> {t('pricing.featureChars20k')}</li><li><CheckIcon /> {t('pricing.featureAttachments3')}</li><li><CheckIcon /> {t('pricing.featureAuditTrail')}</li><li><CheckIcon /> {t('pricing.featurePdfUpload')}</li><li><CheckIcon /> {t('pricing.featurePdfExtract')}</li><li><CheckIcon /> {t('pricing.featureCustomFields')}</li><li><CheckIcon /> {t('pricing.featureCopyLink')}</li>{!appConfig.hideLogoFeature && <li><CheckIcon /> {t('pricing.featureLogo')}</li>}</ul>
                          <button type="button" className="tier-button">{subscriptionTier === 'standard' ? 'Selected' : 'Select'}</button>
                        </div>
                        <div className={`tier-card highlighted ${subscriptionTier === 'premium' ? 'selected' : ''}`} role="button" tabIndex={0} onClick={() => { setSubscriptionTier('premium'); setIsOneTimeUse(false); }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSubscriptionTier('premium'); setIsOneTimeUse(false); } }}>
                          {billingCycle === 'yearly' && <span className="tier-discount-badge">-{tierPricing.premium.discountPercent}%</span>}
                          <div className="tier-header"><div className="tier-icon-wrap"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg></div><h4>{t('pricing.tierPremium')}</h4><p className="tier-desc">{t('pricing.descPremium')}</p></div>
                          {billingCycle === 'yearly' && <p className="tier-annual-billing">Billed ${tierPricing.premium.annual}/yr</p>}
                          <div className="tier-price-row"><span className="tier-price">${billingCycle === 'monthly' ? tierPricing.premium.monthly : (tierPricing.premium.annual / 12) % 1 === 0 ? tierPricing.premium.annual / 12 : (tierPricing.premium.annual / 12).toFixed(2)}</span><span className="tier-period">{t('pricing.perMonth')}</span></div>
                          <ul className="tier-features"><li><CheckIcon /> {t('pricing.featureRecipients10')}</li><li><CheckIcon /> {t('pricing.featureEmails150')}</li><li><CheckIcon /> {t('pricing.featureTemplates25')}</li><li><CheckIcon /> {t('pricing.featureChars30k')}</li><li><CheckIcon /> {t('pricing.featureAttachments10')}</li><li><CheckIcon /> {t('pricing.featureAuditTrail')}</li><li><CheckIcon /> {t('pricing.featurePdfUpload')}</li><li><CheckIcon /> {t('pricing.featurePdfExtract')}</li><li><CheckIcon /> {t('pricing.featureCustomFields')}</li><li><CheckIcon /> {t('pricing.featureCopyLink')}</li>{!appConfig.hideVerificationFeature && <li><CheckIcon /> {t('pricing.featureVerification')}</li>}{!appConfig.hideLogoFeature && <li><CheckIcon /> {t('pricing.featureLogo')}</li>}</ul>
                          <button type="button" className="tier-button">{subscriptionTier === 'premium' ? 'Selected' : 'Select'}</button>
                        </div>
                      </div>
                    )}
                    {showOneTimeOptions && (
                      <div className="subscription-tiers signup-tiers one-time-tiers one-time-single">
                        <div
                          className="tier-card popular highlighted"
                          role="button"
                          tabIndex={0}
                          onClick={() => { setSubscriptionTier('premium'); setIsOneTimeUse(true) }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSubscriptionTier('premium'); setIsOneTimeUse(true) } }}
                        >
                          <span className="tier-badge">ALL FEATURES</span>
                          <h4>One-time use</h4>
                          <p className="tier-desc">Full access for 1 send. Pay again for another send.</p>
                          <ul className="tier-features">
                            <li><CheckIcon /> {t('pricing.featureTemplates25')}</li>
                            <li><CheckIcon /> {t('pricing.featureRecipients10')}</li>
                            <li><CheckIcon /> {t('pricing.oneTimeSendIncluded', { price: tierPricing.oneTimePrice })}</li>
                            <li><CheckIcon /> {t('pricing.featureChars30k')}</li>
                            <li><CheckIcon /> {t('pricing.featureAttachments10')}</li>
                            <li><CheckIcon /> {t('pricing.featureAuditTrail')}</li>
                            <li><CheckIcon /> {t('pricing.featurePdfUpload')}</li>
                            <li><CheckIcon /> {t('pricing.featurePdfExtract')}</li>
                            <li><CheckIcon /> {t('pricing.featureCustomFields')}</li>
                            <li><CheckIcon /> {t('pricing.featureCopyLink')}</li>
                            {!appConfig.hideLogoFeature && <li><CheckIcon /> {t('pricing.featureLogo')}</li>}
                            {!appConfig.hideVerificationFeature && <li><CheckIcon /> {t('pricing.featureVerification')}</li>}
                          </ul>
                          <p className="tier-price">${tierPricing.oneTimePrice} <span>one time</span></p>
                          <button type="button" className="tier-button tier-button--action primary" onClick={(e) => { e.stopPropagation(); setSubscriptionTier('premium'); setIsOneTimeUse(true) }}>Select</button>
                        </div>
                      </div>
                    )}
                    {isOneTimeUse && <p className="one-time-selected-hint">{`One-time plan: all features, 1 send. Pay $${tierPricing.oneTimePrice} again for each additional send.`}</p>}
                    <button type="button" className="primary-button full-width" disabled={authSubmitting} onClick={async () => { setAuthError(''); setAuthReferralError(''); setAuthReferralOtherError(''); if (!authReferralSource) { setAuthReferralError(t('auth.referralSourceRequired')); return; } if (authReferralSource === REFERRAL_SOURCE_OTHER && !authReferralOtherDetail.trim()) { setAuthReferralOtherError(t('auth.referralOtherRequired')); return; } if (!authEmail.trim()) { setAuthError('Enter your email.'); return; } if (authPassword.length < 8) { setAuthError('Password must be at least 8 characters.'); return; } if (!isValidPassword(authPassword)) { setAuthError('Password must include a special character.'); return; } if (authPassword !== authConfirmPassword) { setAuthError('Passwords do not match.'); return; } setAuthSubmitting(true); try { const emailExists = await checkEmailExists(authEmail.trim()); if (emailExists) { setIsEmailExistsModalOpen(true); return; } if (!isOneTimeUse) { const userData = { firstName: authFirstName.trim(), lastName: authLastName.trim(), subscriptionTier, billingCycle, isOneTimeUse, preferredLanguage: language, referralSource: authReferralSource, referralSourceDetail: authReferralSource === REFERRAL_SOURCE_OTHER ? authReferralOtherDetail.trim() : '' }; if (!user) { await authSignUp(authEmail.trim(), authPassword, userData); try { if (!openPaywallAfterAuth) sessionStorage.setItem('justCreatedAccount', '1') } catch (_) {} } const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession'); const billingInterval = billingCycle === 'yearly' ? 'annual' : 'monthly'; const { data } = await createCheckoutSession({ tierId: subscriptionTier, billingInterval, returnOrigin: typeof window !== 'undefined' ? window.location.origin : '' }); if (data?.url) { await saveDocumentStateForPaymentReturn(); window.location.href = data.url; return; } } else { const userData = { firstName: authFirstName.trim(), lastName: authLastName.trim(), subscriptionTier, billingCycle, isOneTimeUse, preferredLanguage: language, referralSource: authReferralSource, referralSourceDetail: authReferralSource === REFERRAL_SOURCE_OTHER ? authReferralOtherDetail.trim() : '' }; await authSignUp(authEmail.trim(), authPassword, userData); try { sessionStorage.setItem('justCreatedAccount', '1') } catch (_) {} if (premiumOneOff?.link) window.open(premiumOneOff.link, '_blank'); setIsAuthModalOpen(false); setSendStatus('Complete payment in the new tab, then return here.'); } } catch (err) { const { message, showConnectionBanner } = getAuthErrorMessage(err, t); if (err.code === 'auth/email-already-in-use') { setIsEmailExistsModalOpen(true); } else { setAuthError(message); if (showConnectionBanner) setConnectionBannerVisible(true); } } finally { setAuthSubmitting(false); } }}>{authSubmitting ? (isOneTimeUse ? 'Creating account…' : 'Subscribe') : (isOneTimeUse ? 'Create account' : 'Subscribe')}</button>
                  </>
                )}
              </div>
            )}
            {((authMode === 'login' && !showForgotPassword) || (authMode === 'signup' && signupStep === 1)) && (
              <div className="auth-modal__google-section">
                <div className="auth-divider">{t('auth.or')}</div>
                <button type="button" className="google-login-btn google-login-btn--standalone" disabled={authSubmitting} onClick={async () => {
                  setAuthError('')
                  setAuthTermsAcceptError('')
                  setAuthReferralError('')
                  setAuthReferralOtherError('')
                  if (authMode === 'signup' && signupStep === 1 && !authTermsAccepted) {
                    setAuthTermsAcceptError(t('auth.termsMustAccept'))
                    try {
                      document.getElementById('auth-signup-terms')?.focus?.()
                      document.getElementById('auth-signup-terms-block')?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
                    } catch (_) {}
                    return
                  }
                  if (authMode === 'signup' && signupStep === 1) {
                    if (!authReferralSource) {
                      setAuthReferralError(t('auth.referralSourceRequired'))
                      try {
                        document.getElementById('auth-referral-select')?.focus?.()
                        document.getElementById('auth-referral-select')?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
                      } catch (_) {}
                      return
                    }
                    if (authReferralSource === REFERRAL_SOURCE_OTHER && !authReferralOtherDetail.trim()) {
                      setAuthReferralOtherError(t('auth.referralOtherRequired'))
                      try {
                        document.getElementById('auth-referral-other-input')?.focus?.()
                        document.getElementById('auth-referral-other-input')?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
                      } catch (_) {}
                      return
                    }
                  }
                  setAuthSubmitting(true)
                  try {
                    await signInWithGoogle(authMode === 'login' ? { subscriptionTier, billingCycle, isOneTimeUse, preferredLanguage: language } : { subscriptionTier: 'premium', billingCycle: 'monthly', isOneTimeUse: true, preferredLanguage: language, referralSource: authReferralSource, referralSourceDetail: authReferralSource === REFERRAL_SOURCE_OTHER ? authReferralOtherDetail.trim() : '' })
                    setIsAuthModalOpen(false)
                    if (authMode === 'signup') setView('app')
                  } catch (err) {
                    const { message, showConnectionBanner } = getAuthErrorMessage(err, t)
                    setAuthError(message)
                    if (showConnectionBanner) setConnectionBannerVisible(true)
                  } finally {
                    setAuthSubmitting(false)
                  }
                }}><svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg"><path d="M17.64 9.2c0-.637-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.715H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.159 6.656 3.58 9 3.58z" fill="#EA4335"/></svg> {authMode === 'login' ? t('auth.signInWithGoogle') : t('auth.continueWithGoogle')}</button>
              </div>
            )}
          </div>
        );
        return (
          <div className="modal-backdrop account-modal-backdrop auth-modal-backdrop" role="dialog" aria-modal="true">
            <div className="modal auth-modal auth-modal--modern" onClick={(e) => e.stopPropagation()}>
              <div className="account-modal__header auth-modal__header">
                <div className="account-modal__header-text">
                  <div className="auth-modal__header-logo">
                    <PublicPictureImg name="primedocumentsignlogo" alt="Prime Document Sign" width={220} height={66} decoding="async" />
                  </div>
                  <h3>{authMode === 'login' ? t('auth.welcomeBack') : t('auth.createAccount')}</h3>
                  <p className="account-modal__subtitle">
                    {authMode === 'login' ? t('auth.logInToManage') : t('auth.startSending')}
                  </p>
                </div>
                <button type="button" className="account-modal__close ghost-button" aria-label={t('modal.close')} onClick={() => { setOpenPaywallAfterAuth(null); setIsAuthModalOpen(false); }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              </div>
              <div className="account-modal__body auth-modal__body">
              {authCard}
              </div>
            </div>
          </div>
        );
      })()}
      {view === 'auth' || view === 'blog' || view === 'blog-post' ? (
        <header
          className={`site-nav site-nav--marketing ${view === 'auth' ? 'site-nav--landing' : ''} ${mobileNavOpen ? 'site-nav--mobile-open' : ''}`}
        >
          <div className="site-nav__inner">
          <div className="brand">
            <button type="button" className="brand-link brand-link--redesign" onClick={handleBrandClick}>
            {view === 'auth' ? (
                <PublicPictureImg
                  name="primedocumentsignlogo"
                  alt="Prime Document Sign"
                  className="nav-logo-blend"
                  width={320}
                  height={136}
                  fetchPriority="high"
                  decoding="async"
                />
              ) : (
                <>
                  <span className="nav-brand-icon-wrap" aria-hidden="true">
                    <img src="/icon_only.svg" alt={t('seo.brandLogoMarkAlt')} className="nav-brand-mark-img" width={26} height={26} />
                </span>
                  <span className="nav-brand-wordmark">
                    <span className="nav-brand-wordmark-top">Prime Document</span>
                    <span className="nav-brand-wordmark-bottom">Sign</span>
                  </span>
                </>
              )}
            </button>
          </div>
          <nav className="site-links">
            <div className="site-links__lang-wrap">
              <div className="site-links__lang">
                <LanguageSelector compact theme={view === 'auth' ? 'dark' : undefined} />
              </div>
            </div>
            <div className="site-links__desktop">
            {view !== 'auth' && (
                <button type="button" className="nav-link-btn" onClick={() => { setView('auth'); setResourcesMenuOpen(false); window.history.pushState({}, '', locPath('/')); setMarketingPathname('/'); window.scrollTo({ top: 0, behavior: 'auto' }); }}>{t('nav.home')}</button>
              )}
              <a href="#how" onClick={view !== 'auth' ? (e) => { e.preventDefault(); setView('auth'); setResourcesMenuOpen(false); setTimeout(() => document.getElementById('how')?.scrollIntoView({ behavior: 'smooth' }), 100); } : undefined}>{t('nav.howItWorks')}</a>
              <a href="#pricing" onClick={view !== 'auth' ? (e) => { e.preventDefault(); setView('auth'); setResourcesMenuOpen(false); setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 100); } : undefined}>{t('nav.pricing')}</a>
              {language === 'en' ? (
                <a
                  href={locPath('/about')}
                  onClick={(e) => {
                    goToMarketingSeoPath(e, '/about')
                    setResourcesMenuOpen(false)
                  }}
                >
                  {t('nav.aboutUs')}
                </a>
              ) : null}
              <a href="#faq" onClick={view !== 'auth' ? (e) => { e.preventDefault(); setView('auth'); setResourcesMenuOpen(false); setTimeout(() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' }), 100); } : undefined}>{t('nav.faqs')}</a>
              {language === 'en' ? (
                <div className="site-nav__dropdown-wrap" ref={resourcesNavRef}>
                  <button
                    type="button"
                    className="site-nav__dropdown-trigger nav-link-btn"
                    aria-expanded={resourcesMenuOpen}
                    aria-haspopup="true"
                    onClick={() => setResourcesMenuOpen((o) => !o)}
                  >
                    {t('nav.resources')}
                    <svg className="site-nav__dropdown-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M6 9l6 6 6-6"/></svg>
                  </button>
                  {resourcesMenuOpen ? (
                    <div className="site-nav__dropdown-panel" role="menu">
                      <a href={locPath('/esignature')} role="menuitem" className="site-nav__dropdown-item" onClick={(e) => goToMarketingSeoPath(e, '/esignature')}>{t('footer.linkEsignature')}</a>
                      <a href={locPath('/templates')} role="menuitem" className="site-nav__dropdown-item" onClick={(e) => goToMarketingSeoPath(e, '/templates')}>{t('footer.linkTemplates')}</a>
                      <a href={locPath('/templates/nda')} role="menuitem" className="site-nav__dropdown-item site-nav__dropdown-item--nested" onClick={(e) => goToMarketingSeoPath(e, '/templates/nda')}>{t('footer.linkTplNda')}</a>
                      <a href={locPath('/templates/contract')} role="menuitem" className="site-nav__dropdown-item site-nav__dropdown-item--nested" onClick={(e) => goToMarketingSeoPath(e, '/templates/contract')}>{t('footer.linkTplContract')}</a>
                      <a href={locPath('/templates/freelance-agreement')} role="menuitem" className="site-nav__dropdown-item site-nav__dropdown-item--nested" onClick={(e) => goToMarketingSeoPath(e, '/templates/freelance-agreement')}>{t('footer.linkTplFreelance')}</a>
                      <a href={locPath('/templates/lease-agreement')} role="menuitem" className="site-nav__dropdown-item site-nav__dropdown-item--nested" onClick={(e) => goToMarketingSeoPath(e, '/templates/lease-agreement')}>{t('footer.linkTplLease')}</a>
                      <a href={locPath('/templates/service-agreement')} role="menuitem" className="site-nav__dropdown-item site-nav__dropdown-item--nested" onClick={(e) => goToMarketingSeoPath(e, '/templates/service-agreement')}>{t('footer.linkTplService')}</a>
                      <a href={locPath('/templates/independent-contractor')} role="menuitem" className="site-nav__dropdown-item site-nav__dropdown-item--nested" onClick={(e) => goToMarketingSeoPath(e, '/templates/independent-contractor')}>{t('footer.linkTplContractor')}</a>
                      <a href={locPath('/templates/purchase-order')} role="menuitem" className="site-nav__dropdown-item site-nav__dropdown-item--nested" onClick={(e) => goToMarketingSeoPath(e, '/templates/purchase-order')}>{t('footer.linkTplPurchaseOrder')}</a>
                      <a href={locPath('/templates/photo-release')} role="menuitem" className="site-nav__dropdown-item site-nav__dropdown-item--nested" onClick={(e) => goToMarketingSeoPath(e, '/templates/photo-release')}>{t('footer.linkTplPhotoRelease')}</a>
                      <a href={locPath('/security')} role="menuitem" className="site-nav__dropdown-item" onClick={(e) => goToMarketingSeoPath(e, '/security')}>{t('footer.linkSecurity')}</a>
                      <a href={locPath('/why-primedocumentsign')} role="menuitem" className="site-nav__dropdown-item" onClick={(e) => goToMarketingSeoPath(e, '/why-primedocumentsign')}>{t('footer.linkWhyPrimeDocuSign')}</a>
                      <a href={locPath('/esignature-legal')} role="menuitem" className="site-nav__dropdown-item" onClick={(e) => goToMarketingSeoPath(e, '/esignature-legal')}>{t('footer.linkEsignLegal')}</a>
                      <a href={locPath('/docusign-alternative')} role="menuitem" className="site-nav__dropdown-item" onClick={(e) => goToMarketingSeoPath(e, '/docusign-alternative')}>{t('footer.docusignAlt')}</a>
                      <a href={locPath('/docusign-vs-prime-document-sign')} role="menuitem" className="site-nav__dropdown-item" onClick={(e) => goToMarketingSeoPath(e, '/docusign-vs-prime-document-sign')}>{t('footer.linkDocuSignVs')}</a>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {language === 'en' ? (
                <button type="button" className="nav-link-btn site-nav__blog-btn" onClick={goToBlogList}>{t('nav.blog')}</button>
              ) : null}
              <a href="#contact" onClick={view !== 'auth' ? (e) => { e.preventDefault(); setView('auth'); setResourcesMenuOpen(false); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 100); } : undefined}>{t('nav.contact')}</a>
            </div>
            <div className="site-nav__auth site-nav__auth--desktop">
              <button type="button" className="nav-auth-btn nav-auth-btn--ghost" onClick={() => { setAuthMode('signup'); setIsAuthModalOpen(true); }}>{t('nav.logIn')}</button>
              <button type="button" className="nav-auth-btn nav-auth-btn--primary nav-auth-btn--hero" onClick={() => { setAuthMode('signup'); setIsAuthModalOpen(true); }}>{t('nav.getStarted')}</button>
            </div>
            <button type="button" className="nav-hamburger" onClick={() => { setResourcesMenuOpen(false); setMobileNavOpen((o) => !o); }} aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileNavOpen}>
              <span className="nav-hamburger-bar" />
              <span className="nav-hamburger-bar" />
              <span className="nav-hamburger-bar" />
              </button>
            <div className="site-nav__auth site-nav__auth--mobile">
              <button type="button" className="nav-auth-btn nav-auth-btn--primary nav-auth-btn--hero" onClick={() => { setAuthMode('signup'); setIsAuthModalOpen(true); }}>{t('nav.logIn')}</button>
            </div>
          </nav>
          </div>
          <div className="site-nav__mobile-backdrop" onClick={() => setMobileNavOpen(false)} aria-hidden="true" />
          <div className="site-nav__mobile-menu" role="dialog" aria-label="Navigation menu">
            <div className="site-nav__mobile-menu-header">
              <span className="site-nav__mobile-menu-title">Menu</span>
              <button type="button" className="site-nav__mobile-menu-close" onClick={() => setMobileNavOpen(false)} aria-label="Close menu">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="site-nav__mobile-lang">
              <LanguageSelector compact theme="dark" />
            </div>
            <div className="site-nav__mobile-menu-links">
              <p className="site-nav__mobile-menu-links-label">Navigation</p>
              {view !== 'auth' && (
                <button type="button" className="site-nav__mobile-link" onClick={() => { window.history.pushState({}, '', locPath('/')); setMarketingPathname('/'); setView('auth'); setMobileNavOpen(false); window.scrollTo({ top: 0, behavior: 'auto' }); }}>{t('nav.home')}</button>
              )}
              <button type="button" className="site-nav__mobile-link" onClick={() => { window.history.pushState({}, '', locPath('/')); setMarketingPathname('/'); setView('auth'); setMobileNavOpen(false); setTimeout(() => document.getElementById('how')?.scrollIntoView({ behavior: 'smooth' }), 150); }}>{t('nav.howItWorks')}</button>
              <button type="button" className="site-nav__mobile-link" onClick={() => { setView('auth'); setMobileNavOpen(false); setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 150); }}>{t('nav.pricing')}</button>
              {language === 'en' ? (
                <a
                  className="site-nav__mobile-link"
                  href={locPath('/about')}
                  onClick={(e) => {
                    goToMarketingSeoPath(e, '/about')
                    setMobileNavOpen(false)
                  }}
                >
                  {t('nav.aboutUs')}
                </a>
              ) : null}
              <button type="button" className="site-nav__mobile-link" onClick={() => { setView('auth'); setMobileNavOpen(false); setTimeout(() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' }), 150); }}>{t('nav.faqs')}</button>
              {language === 'en' ? (
                <>
                  <a className="site-nav__mobile-link" href={locPath('/esignature')} onClick={(e) => goToMarketingSeoPath(e, '/esignature')}>{t('footer.linkEsignature')}</a>
                  <a className="site-nav__mobile-link" href={locPath('/templates')} onClick={(e) => goToMarketingSeoPath(e, '/templates')}>{t('footer.linkTemplates')}</a>
                  <a className="site-nav__mobile-link site-nav__mobile-link--nested" href={locPath('/templates/nda')} onClick={(e) => goToMarketingSeoPath(e, '/templates/nda')}>{t('footer.linkTplNda')}</a>
                  <a className="site-nav__mobile-link site-nav__mobile-link--nested" href={locPath('/templates/contract')} onClick={(e) => goToMarketingSeoPath(e, '/templates/contract')}>{t('footer.linkTplContract')}</a>
                  <a className="site-nav__mobile-link site-nav__mobile-link--nested" href={locPath('/templates/freelance-agreement')} onClick={(e) => goToMarketingSeoPath(e, '/templates/freelance-agreement')}>{t('footer.linkTplFreelance')}</a>
                  <a className="site-nav__mobile-link site-nav__mobile-link--nested" href={locPath('/templates/lease-agreement')} onClick={(e) => goToMarketingSeoPath(e, '/templates/lease-agreement')}>{t('footer.linkTplLease')}</a>
                  <a className="site-nav__mobile-link site-nav__mobile-link--nested" href={locPath('/templates/service-agreement')} onClick={(e) => goToMarketingSeoPath(e, '/templates/service-agreement')}>{t('footer.linkTplService')}</a>
                  <a className="site-nav__mobile-link site-nav__mobile-link--nested" href={locPath('/templates/independent-contractor')} onClick={(e) => goToMarketingSeoPath(e, '/templates/independent-contractor')}>{t('footer.linkTplContractor')}</a>
                  <a className="site-nav__mobile-link site-nav__mobile-link--nested" href={locPath('/templates/purchase-order')} onClick={(e) => goToMarketingSeoPath(e, '/templates/purchase-order')}>{t('footer.linkTplPurchaseOrder')}</a>
                  <a className="site-nav__mobile-link site-nav__mobile-link--nested" href={locPath('/templates/photo-release')} onClick={(e) => goToMarketingSeoPath(e, '/templates/photo-release')}>{t('footer.linkTplPhotoRelease')}</a>
                  <a className="site-nav__mobile-link" href={locPath('/security')} onClick={(e) => goToMarketingSeoPath(e, '/security')}>{t('footer.linkSecurity')}</a>
                  <a className="site-nav__mobile-link" href={locPath('/why-primedocumentsign')} onClick={(e) => { goToMarketingSeoPath(e, '/why-primedocumentsign'); setMobileNavOpen(false) }}>{t('footer.linkWhyPrimeDocuSign')}</a>
                  <a className="site-nav__mobile-link" href={locPath('/esignature-legal')} onClick={(e) => goToMarketingSeoPath(e, '/esignature-legal')}>{t('footer.linkEsignLegal')}</a>
                  <a className="site-nav__mobile-link" href={locPath('/docusign-alternative')} onClick={(e) => goToMarketingSeoPath(e, '/docusign-alternative')}>{t('footer.docusignAlt')}</a>
                  <a className="site-nav__mobile-link" href={locPath('/docusign-vs-prime-document-sign')} onClick={(e) => goToMarketingSeoPath(e, '/docusign-vs-prime-document-sign')}>{t('footer.linkDocuSignVs')}</a>
                  <button type="button" className="site-nav__mobile-link" onClick={(e) => goToBlogList(e)}>{t('nav.blog')}</button>
                </>
              ) : null}
              <button type="button" className="site-nav__mobile-link" onClick={() => { setView('auth'); setMobileNavOpen(false); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 150); }}>{t('nav.contact')}</button>
            </div>
            <div className="site-nav__mobile-menu-auth">
              <button type="button" className="nav-auth-btn nav-auth-btn--secondary site-nav__mobile-auth-btn" onClick={() => { setAuthMode('signup'); setIsAuthModalOpen(true); setMobileNavOpen(false); }}>{t('nav.logIn')}</button>
              <button type="button" className="nav-auth-btn nav-auth-btn--primary site-nav__mobile-auth-btn" onClick={() => { setAuthMode('signup'); setIsAuthModalOpen(true); setMobileNavOpen(false); }}>{t('nav.getStarted')}</button>
            </div>
          </div>
        </header>
      ) : view === 'email-activity' ? (
        <header className="app-header main-header app-header--email-activity">
          <div className="main-header__inner email-activity-top-header__inner">
            <button type="button" className="app-header__brand brand brand--signed-in app-header__brand-btn" onClick={handleBrandClick}>
              <PublicPictureImg name="primedocumentsignlogo" alt="Prime Document Sign" className="app-header__logo-full" width={240} height={76} decoding="async" />
              <span className="brand-subtitle email-activity-header-crumb">{t('app.emailActivityNavLabel')}</span>
            </button>
            <div className="app-header__right">
              <LanguageSelector compact />
              {user && user.email && !user.isAnonymous && (
                <>
                  {!hasSubscribedAccount ? (
                <button
                  type="button"
                      className="app-header__btn app-header__btn--primary app-header__btn--subscribe-now primary-button"
                      onClick={() => setIsSubscriptionOpen(true)}
                >
                      {t('app.subscribeNowFromPrice')}
                </button>
                  ) : null}
                <button
                  type="button"
                    className="app-header__btn app-header__btn--secondary app-header__btn--profile ghost-button"
                    onClick={() => setIsAccountOpen(true)}
                    aria-label={t('app.myAccount')}
                    title={t('app.myAccount')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </button>
                </>
              )}
              <button type="button" className="ghost-button nav-back-btn" onClick={goBackToAppFromEmailActivity}>
                <svg className="nav-back-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                {t('app.backToApp')}
              </button>
            </div>
          </div>
        </header>
      ) : !isRecipientView && !(user && user.email && !user.isAnonymous) ? (
        <header className={`site-nav site-nav--landing site-nav--simple ${mobileNavOpen ? 'site-nav--mobile-open' : ''}`}>
          <div className="site-nav__inner">
            <div className="brand">
              <button type="button" className="brand-link" onClick={handleBrandClick}>
                <PublicPictureImg name="primedocumentsignlogo" alt="Prime Document Sign" className="nav-logo-blend nav-logo-blend--compact" width={320} height={136} decoding="async" />
              </button>
          </div>
            <nav className="site-links">
              <div className="site-links__lang-wrap">
                <div className="site-links__lang">
                  <LanguageSelector compact theme="dark" />
                </div>
              </div>
              <div className="site-nav__auth site-nav__auth--desktop">
                <button type="button" className="nav-auth-btn nav-auth-btn--secondary site-nav__back-btn" onClick={() => setView('auth')}>
            <svg className="nav-back-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                  {t('app.backShort')}
                </button>
          </div>
              <button type="button" className="nav-hamburger" onClick={() => setMobileNavOpen((o) => !o)} aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileNavOpen}>
                <span className="nav-hamburger-bar" />
                <span className="nav-hamburger-bar" />
                <span className="nav-hamburger-bar" />
              </button>
              <div className="site-nav__auth site-nav__auth--mobile">
                <button type="button" className="nav-auth-btn nav-auth-btn--secondary site-nav__back-btn" onClick={() => setView('auth')}>
                  <svg className="nav-back-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                  {t('app.backShort')}
                </button>
                  </div>
            </nav>
                  </div>
          <div className="site-nav__mobile-backdrop" onClick={() => setMobileNavOpen(false)} aria-hidden="true" />
          <div className="site-nav__mobile-menu site-nav__mobile-menu--simple" role="dialog" aria-label="Menu">
            <div className="site-nav__mobile-menu-header">
              <span className="site-nav__mobile-menu-title">Menu</span>
              <button type="button" className="site-nav__mobile-menu-close" onClick={() => setMobileNavOpen(false)} aria-label="Close menu">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="site-nav__mobile-lang">
              <LanguageSelector compact theme="dark" />
            </div>
            <div className="site-nav__mobile-menu-auth">
              <button type="button" className="nav-auth-btn nav-auth-btn--secondary site-nav__mobile-auth-btn site-nav__back-btn" onClick={() => { setView('auth'); setMobileNavOpen(false); }}>
                <svg className="nav-back-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                {t('app.backShort')}
              </button>
            </div>
          </div>
        </header>
      ) : null}

      <main className={`page ${view === 'auth' && !isRecipientView ? 'page--landing' : ''} ${view === 'blog' || view === 'blog-post' ? 'page--blog' : ''} ${isRecipientView ? 'page--recipient' : ''} ${view === 'app' && !isRecipientView ? 'page--has-sticky-bar' : ''} ${view === 'email-activity' ? 'page--email-activity' : ''}`}>
        {!isRecipientView && (view === 'blog' || view === 'blog-post' || (view === 'auth' && !isRecipientView)) ? (
          <>
            <FaqJsonLd jsonLdString={landingFaqJsonLd} />
            <FaqJsonLd id="pds-pricing-offers-jsonld" jsonLdString={pricingOffersJsonLd} />
          </>
        ) : null}
        {isRecipientView && (
          <div className="recipient-header" role="banner">
                  <button
                    type="button"
              className="recipient-header__brand"
              onClick={() => window.open('https://primedocumentsign.com', '_blank', 'noopener,noreferrer')}
              aria-label="Prime Document Sign (opens website)"
                  >
              <PublicPictureImg name="recipient-brand-logo" alt={t('seo.recipientHeroLogoAlt')} className="recipient-header__logo" width={394} height={120} />
                  </button>
          </div>
              )}
        {view === 'auth' && isRecipientView && !hasSigningLinkId && (
          <div className="back-to-home-wrap" role="region" aria-label="Empty page">
            <p className="back-to-home-message">This link doesn&apos;t have a document.</p>
            <a href={locPath('/')} className="primary-button back-to-home-btn">Back to Home</a>
            </div>
          )}
        {view === 'auth' && !isRecipientView && (
          <div className="landing landing--white">
            {isSeoLandingPathname(marketingPathname) && SEO_ARTICLE_PAGE_KEYS[marketingPathname] && (() => {
              const pageCfg = SEO_ARTICLE_PAGE_KEYS[marketingPathname]
              if (pageCfg.legalPage === 'terms') {
                return (
                  <article className="section docusign-alt-landing legal-document-page" id={pageCfg.htmlId}>
                    <div className="docusign-alt-landing__inner">
                      <p className="docusign-alt-landing__crumb">
                        <a href={locPath('/')}>{t('nav.home')}</a>
                        {' · '}
                        {t(pageCfg.breadcrumbKey)}
                      </p>
                      <h1 className="docusign-alt-landing__title">{t(pageCfg.breadcrumbKey)}</h1>
                      <p className="legal-document-updated">{t(pageCfg.updatedLabelKey)}</p>
                      <div className="terms-body">
                        <TermsOfServiceBody />
                      </div>
                      <p className="docusign-alt-landing__prose docusign-alt-landing__seo-crosslinks">
                        <span className="docusign-alt-landing__seo-crosslinks-label">{t('seoLanding.trustRelatedHeading')}</span>{' '}
                        <a href={locPath('/privacy')} onClick={(e) => goToMarketingSeoPath(e, '/privacy')}>
                          {t('footer.privacy')}
                        </a>
                      </p>
                      <div className="docusign-alt-landing__actions">
                        <button type="button" className="primary-button" onClick={openSignupAuthModal}>
                          {t(pageCfg.ctaKey)}
                        </button>
                        <a href={pageCfg.pricingHref} className="ghost-button docusign-alt-landing__secondary">
                          {t(pageCfg.pricingLabelKey)}
                        </a>
                      </div>
                    </div>
                  </article>
                )
              }
              if (pageCfg.legalPage === 'privacy') {
                return (
                  <article className="section docusign-alt-landing legal-document-page" id={pageCfg.htmlId}>
                    <div className="docusign-alt-landing__inner">
                      <p className="docusign-alt-landing__crumb">
                        <a href={locPath('/')}>{t('nav.home')}</a>
                        {' · '}
                        {t(pageCfg.breadcrumbKey)}
                      </p>
                      <h1 className="docusign-alt-landing__title">{t(pageCfg.breadcrumbKey)}</h1>
                      <p className="legal-document-updated">{t(pageCfg.updatedLabelKey)}</p>
                      <div className="terms-body">
                        <PrivacyPolicyBody />
                      </div>
                      <p className="docusign-alt-landing__prose docusign-alt-landing__seo-crosslinks">
                        <span className="docusign-alt-landing__seo-crosslinks-label">{t('seoLanding.trustRelatedHeading')}</span>{' '}
                        <a href={locPath('/terms')} onClick={(e) => goToMarketingSeoPath(e, '/terms')}>
                          {t('footer.terms')}
                        </a>
                      </p>
                      <div className="docusign-alt-landing__actions">
                        <button type="button" className="primary-button" onClick={openSignupAuthModal}>
                          {t(pageCfg.ctaKey)}
                        </button>
                        <a href={pageCfg.pricingHref} className="ghost-button docusign-alt-landing__secondary">
                          {t(pageCfg.pricingLabelKey)}
                        </a>
                      </div>
                    </div>
                  </article>
                )
              }
              const articleFaqs = getSeoArticleFaqItems(marketingPathname, tierPricing, DOCUSIGN_ALT_FAQ_ITEMS)
              const faqSectionId = `${pageCfg.htmlId}-faq`
              return (
                <article className="section docusign-alt-landing" id={pageCfg.htmlId}>
                  <div className="docusign-alt-landing__inner">
                    <p className="docusign-alt-landing__crumb">
                      <a href={locPath('/')}>{t('nav.home')}</a>
                      {' · '}
                      {pageCfg.breadcrumbPredecessor ? (
                        <>
                          <a
                            href={locPath(pageCfg.breadcrumbPredecessor.hrefLogical)}
                            onClick={(e) => goToMarketingSeoPath(e, pageCfg.breadcrumbPredecessor.hrefLogical)}
                          >
                            {t(pageCfg.breadcrumbPredecessor.labelKey)}
                          </a>
                          {' · '}
                        </>
                      ) : null}
                      {t(pageCfg.breadcrumbKey)}
                    </p>
                    <h1 className="docusign-alt-landing__title">{t(pageCfg.titleKey)}</h1>
                    {pageCfg.htmlId === 'docusign-vs-prime-top' ? (
                      <DocuSignVsPrimeIntroBlocks t={t} />
                    ) : (
                      <>
                        {pageCfg.leadKey ? <p className="docusign-alt-landing__lead">{t(pageCfg.leadKey)}</p> : null}
                        {(pageCfg.sectionBlocks ?? []).map((block) => (
                          <section key={block.titleKey} className="docusign-alt-landing__section">
                            <h2 className="docusign-alt-landing__section-title">{t(block.titleKey)}</h2>
                            {(block.bodyKeys ?? []).map((bk) => (
                              <p
                                key={bk}
                                className={`docusign-alt-landing__prose${pageCfg.bodyKeysPreline?.includes(bk) ? ' docusign-alt-landing__prose--preline' : ''}`}
                              >
                                {t(bk)}
                              </p>
                            ))}
                          </section>
                        ))}
                        {(pageCfg.bodyKeys ?? []).map((bk) => (
                          <p
                            key={bk}
                            className={`docusign-alt-landing__prose${pageCfg.bodyKeysPreline?.includes(bk) ? ' docusign-alt-landing__prose--preline' : ''}`}
                          >
                            {t(bk)}
                          </p>
                        ))}
                      </>
                    )}
                    {pageCfg.crossLinks?.length ? (
                      <p className="docusign-alt-landing__prose docusign-alt-landing__seo-crosslinks">
                        <span className="docusign-alt-landing__seo-crosslinks-label">{t('seoLanding.trustRelatedHeading')}</span>{' '}
                        {pageCfg.crossLinks.map((cl, i) => (
                          <span key={cl.hrefLogical}>
                            {i > 0 ? ' · ' : ''}
                            <a
                              href={locPath(cl.hrefLogical)}
                              onClick={(e) => goToMarketingSeoPath(e, cl.hrefLogical)}
                            >
                              {t(cl.labelKey)}
                            </a>
                          </span>
                        ))}
                      </p>
                    ) : null}
                    {pageCfg.showCompareTableAfterBody ? (
                      <>
                        <Suspense fallback={<div className="compare-table-skeleton" aria-hidden />}>
                          <DocuSignVsPrimeTableLazy tierPricing={tierPricing} />
                        </Suspense>
                        {pageCfg.htmlId === 'docusign-vs-prime-top' ? (
                          <DocuSignVsPrimeExtendedSections t={t} variant="landing" onStartFree={openSignupAuthModal} />
                        ) : null}
                        <p className="docusign-alt-landing__prose docusign-alt-landing__compare-blog-tip">
                          <a href={locPath('/blog/docusign-vs-prime-document-sign')} onClick={(e) => goToBlogPost(e, 'docusign-vs-prime-document-sign')}>Read the full DocuSign vs Prime Document Sign blog article</a> for more context, see the{' '}
                          <a href={locPath('/alternatives')} onClick={(e) => goToMarketingSeoPath(e, '/alternatives')}>alternatives hub</a>, or browse{' '}
                          <a href={locPath('/blog')} onClick={goToBlogList}>all guides</a>.
                        </p>
                      </>
                    ) : null}
                    <div className="docusign-alt-landing__actions">
                      <button type="button" className="primary-button" onClick={openSignupAuthModal}>
                        {t(pageCfg.ctaKey)}
                      </button>
                      <a href={pageCfg.pricingHref} className="ghost-button docusign-alt-landing__secondary">
                        {t(pageCfg.pricingLabelKey)}
                      </a>
                    </div>
                    {articleFaqs.length > 0 && pageCfg.faqHeadingKey ? (
                      <>
                        <h2 className="docusign-alt-landing__faq-heading" id={faqSectionId}>
                          {t(pageCfg.faqHeadingKey)}
                        </h2>
                        <div className="faq-accordion docusign-alt-landing__faq">
                          {articleFaqs.map((item) => (
                            <details key={item.qKey}>
                              <summary>{t(item.qKey)}</summary>
                              {renderFaqAnswerBody(t, { ...item, params: item.params ?? faqItemParams(item, tierPricing) })}
                            </details>
                          ))}
                        </div>
                      </>
                    ) : null}
                  </div>
                </article>
              )
            })()}
            <section className="hero hero--modern">
              {/* Decorative animated background elements */}
              <div className="hero-orb hero-orb--1" aria-hidden="true" />
              <div className="hero-orb hero-orb--2" aria-hidden="true" />
              <div className="hero-orb hero-orb--3" aria-hidden="true" />
              <div className="hero-grid-overlay" aria-hidden="true" />
              {!isSeoLandingPathname(marketingPathname) ? (
                <h1 className="seo-hero-primary-heading">{t('seo.heroPrimaryHeading')}</h1>
              ) : null}
              <div className="hero-top-banner">
                {/* Left column: headline + CTAs */}
                <div className="hero-banner-left">
                  <span className="hero-banner-eyebrow">{t('hero.bannerEyebrow')}</span>
                  {isSeoLandingPathname(marketingPathname) ? (
                  <h2 className="hero-banner-headline">
                    {t('hero.bannerHeadlinePrefix')}{' '}
                    <span
                      key={`${language}-${heroCycleIdx}`}
                      className={`hero-cycling-word${heroCycleLeaving ? ' hero-cycling-word--leaving' : ' hero-cycling-word--entering'}`}
                    >
                      {t(HERO_BANNER_CYCLE_KEYS[heroCycleIdx])}
                    </span>
                    {' '}
                    {t('hero.bannerHeadlineMid')}
                    <span className="hero-banner-headline-accent"> {t('hero.bannerHeadlineAccent')}</span>
                  </h2>
                  ) : (
                  <h2 className="hero-banner-headline">
                    {t('hero.bannerHeadlinePrefix')}{' '}
                    <span
                      key={`${language}-${heroCycleIdx}`}
                      className={`hero-cycling-word${heroCycleLeaving ? ' hero-cycling-word--leaving' : ' hero-cycling-word--entering'}`}
                    >
                      {t(HERO_BANNER_CYCLE_KEYS[heroCycleIdx])}
                    </span>
                    {' '}
                    {t('hero.bannerHeadlineMid')}
                    <span className="hero-banner-headline-accent"> {t('hero.bannerHeadlineAccent')}</span>
                  </h2>
                  )}
                  <p className="hero-banner-desc">{t('hero.bannerDesc')}</p>
                  <div className="hero-banner-ctas">
                  <div className="hero-banner-ctas__primary-stack">
                  <button
                    type="button"
                      className="hero-cta hero-cta--hero-primary"
                      onClick={openSignupAuthModal}
                    >
                      {t('hero.ctaTrialSignup')}
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                  </button>
                  <button
                    type="button"
                      className="hero-cta hero-cta--hero-primary"
                      onClick={() => {
                        setIsQuickSend(true)
                        setView('app')
                        const url = new URL(window.location.href)
                        url.hash = 'document-page'
                        window.history.replaceState({}, '', url.pathname + url.search + '#document-page')
                      }}
                    >
                      {t('hero.ctaFreeDocument')}
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                  </button>
                  </div>
                  <button
                    type="button"
                      className="hero-cta hero-cta--hero-secondary"
                      onClick={() => document.getElementById('how')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                      {t('hero.watchDemo')}
                  </button>
            </div>
                  <p className="hero-banner-note">{t('hero.ctaPrimaryNote')}</p>
                  <div className="hero-banner-trust-row">
                    <span className="hero-banner-trust-item">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      {t('hero.trustLocked')}
                    </span>
                    <span className="hero-banner-trust-sep" aria-hidden="true">·</span>
                    <span className="hero-banner-trust-item">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                      {t('hero.trustNoCard')}
                    </span>
                    <span className="hero-banner-trust-sep" aria-hidden="true">·</span>
                    <span className="hero-banner-trust-item">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      {t('hero.trustReady')}
                    </span>
                  </div>
                </div>
                {/* Right column: features card + audience */}
                <div className="hero-banner-right">
                  <div className="hero-banner-features-card">
                    <div className="hero-trust-accordions hero-firebase-top--merged" aria-label={t('hero.topTrustListAria')}>
                      {HERO_LANDING_ACCORDION_SECTIONS.map((section) => {
                        const open = heroLandingAccOpen[section.stateKey]
                        const triggerId = `hero-acc-${section.id}-trigger`
                        const panelId = `hero-acc-${section.id}-panel`
                        return (
                          <div key={section.id} className="hero-trust-accordion">
                            <h3 className="hero-trust-accordion__heading">
                  <button
                    type="button"
                                id={triggerId}
                                className="hero-trust-accordion__trigger"
                                aria-expanded={open}
                                aria-controls={panelId}
                                onClick={() => setHeroLandingAccOpen((s) => ({ ...s, [section.stateKey]: !s[section.stateKey] }))}
                              >
                                <span className="hero-firebase-top__tick" aria-hidden="true">
                                  <HeroAccentTick />
                                </span>
                                <span className="hero-trust-accordion__title">{t(section.titleKey)}</span>
                                <svg
                                  className={`hero-trust-accordion__chevron${open ? ' hero-trust-accordion__chevron--open' : ''}`}
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <path d="M6 9l6 6 6-6" />
                                </svg>
                  </button>
                            </h3>
                            <div
                              id={panelId}
                              className="hero-trust-accordion__panel"
                              role="region"
                              aria-labelledby={triggerId}
                              hidden={!open}
                            >
                              <ul className="hero-trust-accordion__sublist">
                                {section.lines.map((line, lineIdx) => {
                                  if (line.type === 'privacyTail') {
                                    return (
                                      <li key={`${section.id}-p`} className="hero-trust-accordion__sub-line">
                                        <span className="hero-trust-accordion__sub-tick" aria-hidden="true">
                                          <HeroAccentTick className="hero-accent-check--sm" />
                                        </span>
                                        <span>
                                          {t(line.beforeKey)}
                  <a
                    href={locPath('/privacy')}
                                            className="hero-trust-accordion__privacy-btn"
                    onClick={(e) => goToMarketingSeoPath(e, '/privacy')}
                  >
                                            {t('hero.firebaseTrustPrivacyLink')}
                  </a>
                                          {t(line.afterKey)}
                                        </span>
                                      </li>
                                    )
                                  }
                                  return (
                                    <li key={line.key || `${section.id}-${lineIdx}`} className="hero-trust-accordion__sub-line">
                                      <span className="hero-trust-accordion__sub-tick" aria-hidden="true">
                                        <HeroAccentTick className="hero-accent-check--sm" />
                                      </span>
                                      <span>{t(line.key)}</span>
                                    </li>
                                  )
                                })}
                              </ul>
                </div>
                          </div>
                        )
                      })}
                    </div>
                    <p className="hero-firebase-trust__fineprint hero-firebase-trust__fineprint--hero-top">
                      <a href={locPath('/privacy')} className="hero-firebase-trust__privacy-link" onClick={(e) => goToMarketingSeoPath(e, '/privacy')}>
                        {t('hero.firebaseTrustPrivacyLink')}
                  </a>
                      {t('hero.firebaseTrustFineprintAfter')}
                    </p>
                  <div
                    className="hero-top-banner__coupon hero-price-banner hero-coupon-banner hero-coupon-banner--account-offer"
                    role="button"
                    tabIndex={0}
                    onClick={openSignupAuthModal}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter' && e.key !== ' ') return
                      e.preventDefault()
                      openSignupAuthModal()
                    }}
                  >
                    <span className="hero-coupon-banner__text-wrap">
                      <span className="hero-coupon-banner__text hero-coupon-banner__text--trial-headline">
                        {t('hero.createAccountTrialHeadline')}
                      </span>
                      <div
                        className="hero-coupon-banner__copy-card"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <code className="hero-coupon-banner__copy-card-code" title={HERO_TRIAL_PROMO_CODE} aria-label={`${t('hero.createAccountPromoCodeAria')}: ${HERO_TRIAL_PROMO_CODE}`}>
                          {HERO_TRIAL_PROMO_CODE}
                        </code>
                        <button
                          type="button"
                          className="hero-coupon-banner__copy-btn"
                          aria-label={t('hero.createAccountPromoCopy')}
                          onClick={async (e) => {
                            e.stopPropagation()
                            try {
                              await navigator.clipboard.writeText(HERO_TRIAL_PROMO_CODE)
                              setHeroPromoCodeCopied(true)
                              window.setTimeout(() => setHeroPromoCodeCopied(false), 2200)
                            } catch (_) {}
                          }}
                        >
                          {heroPromoCodeCopied ? t('hero.createAccountPromoCopied') : t('hero.createAccountPromoCopy')}
                        </button>
                      </div>
                      <div className="hero-coupon-banner__notes">
                        <span className="hero-coupon-banner__note hero-coupon-banner__note--small">{t('hero.createAccountNote')}</span>
                        <span className="hero-coupon-banner__note hero-coupon-banner__note--small">{t('hero.createAccountOfferLimit')}</span>
                      </div>
                    </span>
                  </div>
                  </div>
                  <div className="hero-audience-cards" aria-label={t('hero.audienceCardsAria')}>
                    <article className="hero-audience-card">
                      <div className="hero-audience-card__icon-wrap" aria-hidden="true">
                        <span className="hero-audience-card__icon">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                        </span>
                      </div>
                      <div className="hero-audience-card__text">
                        <p className="hero-audience-card__label">{t('hero.personaSmbTitle')}</p>
                        <p className="hero-audience-card__body">{t('hero.personaSmbBody')}</p>
                        <p className="hero-audience-card__trigger">{t('hero.personaSmbTrigger')}</p>
                      </div>
                    </article>
                    <article className="hero-audience-card">
                      <div className="hero-audience-card__icon-wrap" aria-hidden="true">
                        <span className="hero-audience-card__icon">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                        </span>
                      </div>
                      <div className="hero-audience-card__text">
                        <p className="hero-audience-card__label">{t('hero.personaFreelanceTitle')}</p>
                        <p className="hero-audience-card__body">{t('hero.personaFreelanceBody')}</p>
                        <p className="hero-audience-card__trigger">{t('hero.personaFreelanceTrigger')}</p>
                      </div>
                    </article>
                    <article className="hero-audience-card">
                      <div className="hero-audience-card__icon-wrap" aria-hidden="true">
                        <span className="hero-audience-card__icon">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                        </span>
                      </div>
                      <div className="hero-audience-card__text">
                        <p className="hero-audience-card__label">{t('hero.personaFounderTitle')}</p>
                        <p className="hero-audience-card__body">{t('hero.personaFounderBody')}</p>
                        <p className="hero-audience-card__trigger">{t('hero.personaFounderTrigger')}</p>
                      </div>
                    </article>
                  </div>
                </div>
              </div>
              <div
                className={`hero-cta-below-banner${landingHeroCtaPulsePhase != null ? ' hero-cta-below-banner--cta-pulse-active' : ''}`}
              >
                  <button
                    type="button"
                    className={`hero-cta hero-cta--watch-demo${landingHeroCtaPulsePhase === 0 ? ' hero-cta--pulse-emphasis' : ''}`}
                    onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    {t('hero.watchDemo')}
                  </button>
                  <button
                    type="button"
                  className={`hero-cta hero-cta--quicksend${landingHeroCtaPulsePhase === 1 ? ' hero-cta--pulse-emphasis' : ''}`}
                  onClick={() => {
                    setIsQuickSend(true)
                    setView('app')
                    const url = new URL(window.location.href)
                    url.hash = 'document-page'
                    window.history.replaceState({}, '', url.pathname + url.search + '#document-page')
                  }}
                >
                  {t('hero.ctaFreeDocument')}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                  </button>
                  <p className="hero-cta-note">{t('hero.ctaPrimaryNote')}</p>
                </div>
              <div className="hero-perfect-for hero-card--modern" aria-labelledby="hero-perfect-for-title">
                <h3 id="hero-perfect-for-title" className="hero-card-title">{t('useCases.title')}</h3>
                <ul className="hero-perfect-for__list">
                  <li>
                    <span className="hero-perfect-for__icon" aria-hidden="true">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M12 18v-6"/><path d="M9 15h6"/></svg>
                    </span>
                    <span>{t('useCases.freelancers')}</span>
                  </li>
                  <li>
                    <span className="hero-perfect-for__icon" aria-hidden="true">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    </span>
                    <span>{t('useCases.agencies')}</span>
                  </li>
                  <li>
                    <span className="hero-perfect-for__icon" aria-hidden="true">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                    </span>
                    <span>{t('useCases.landlords')}</span>
                  </li>
                  <li>
                    <span className="hero-perfect-for__icon" aria-hidden="true">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>
                    </span>
                    <span>{t('useCases.startups')}</span>
                  </li>
                </ul>
                <div className="hero-perfect-for__actions">
                <div
                  id="hero-perfect-for-more"
                  className={`hero-landing-expand${landingExpandHeroSideLists ? ' hero-landing-expand--open' : ''}`}
                  aria-hidden={!landingExpandHeroSideLists}
                >
                  <div className="hero-landing-expand__inner">
                    <ul className="hero-perfect-for__list hero-perfect-for__list--more">
                      <li>
                        <span className="hero-perfect-for__icon" aria-hidden="true">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 17h14v-5H5v5Z"/><path d="M19 17h1a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1h-1"/><path d="M5 11H4a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h1"/><path d="M7 11V7a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v4"/></svg>
                        </span>
                        <span>{t('useCases.carBoat')}</span>
                      </li>
                      <li>
                        <span className="hero-perfect-for__icon" aria-hidden="true">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                        </span>
                        <span>{t('useCases.consultants')}</span>
                      </li>
                      <li>
                        <span className="hero-perfect-for__icon" aria-hidden="true">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                        </span>
                        <span>{t('useCases.equipmentRental')}</span>
                      </li>
                      <li>
                        <span className="hero-perfect-for__icon" aria-hidden="true">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>
                        </span>
                        <span>{t('useCases.contractors')}</span>
                      </li>
                      <li>
                        <span className="hero-perfect-for__icon" aria-hidden="true">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                        </span>
                        <span>{t('useCases.website')}</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <button
                  type="button"
                  className="hero-landing-more-btn"
                  aria-expanded={landingExpandHeroSideLists}
                  aria-controls="hero-perfect-for-more hero-benefits-more"
                  onClick={() => setLandingExpandHeroSideLists((v) => !v)}
                >
                  {landingExpandHeroSideLists ? t('hero.landingShowLess') : t('hero.landingShowMore')}
                  </button>
                </div>
              </div>
              <div className="hero-text">
                <span className="hero-pill hero-pill--animate">{t('hero.pill')}</span>
                <h2 className="hero-headline hero-headline--accent-purple">{t('hero.headline')}</h2>
                <p className="hero-tagline">{t('hero.tagline')}</p>
              </div>
              <div className="hero-card hero-card--modern">
                <h3 className="hero-card-title">{t('hero.whyChooseUs')}</h3>
                <ul className="hero-benefits">
                  <li><span className="hero-benefit-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span><span className="hero-benefit-price-wrap"><a href="#pricing" className="hero-benefit-link">{t('hero.priceBanner')}</a></span></li>
                  <li className="hero-benefit-one-agreement"><span className="hero-benefit-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span><span className="hero-benefit-price-wrap">{(() => { const s = t('hero.oneAgreement'); const i = s.indexOf('$6'); const premiumIdx = s.indexOf('Premium '); const highlight = (str) => { const idx = str.indexOf('$6'); if (idx === -1) return str; return <>{str.slice(0, idx)}<span className="hero-benefit-price-highlight">$6</span>{str.slice(idx + 2)}</>; }; if (premiumIdx >= 0) { const before = s.slice(0, premiumIdx + 7); const after = s.slice(premiumIdx + 7); return <>{highlight(before)}<br className="hero-benefit-one-agreement-br" />{after}</>; } return i === -1 ? s : highlight(s); })()}</span></li>
                  <li><span className="hero-benefit-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>{t('hero.benefit1')}</li>
                  <li><span className="hero-benefit-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>{t('hero.benefit2')}</li>
                </ul>
                <div className="hero-benefits__actions">
                <div
                  id="hero-benefits-more"
                  className={`hero-landing-expand${landingExpandHeroSideLists ? ' hero-landing-expand--open' : ''}`}
                  aria-hidden={!landingExpandHeroSideLists}
                >
                  <div className="hero-landing-expand__inner">
                    <ul className="hero-benefits hero-benefits--more">
                      <li><span className="hero-benefit-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>{t('hero.benefit4')}</li>
                      <li><span className="hero-benefit-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>{t('hero.benefit5')}</li>
                      <li><span className="hero-benefit-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>{t('hero.benefit7')}</li>
                      <li><span className="hero-benefit-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>{t('hero.benefit8')}</li>
                      <li><span className="hero-benefit-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>{t('hero.benefit9')}</li>
                      <li><span className="hero-benefit-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>{t('hero.benefit10')}</li>
                      <li><span className="hero-benefit-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>{t('hero.benefit11')}</li>
                      <li><span className="hero-benefit-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>{t('hero.benefit12')}</li>
                      <li><span className="hero-benefit-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>{t('hero.benefit13')}</li>
                </ul>
                  </div>
                </div>
                <button
                  type="button"
                  className="hero-landing-more-btn"
                  aria-expanded={landingExpandHeroSideLists}
                  aria-controls="hero-perfect-for-more hero-benefits-more"
                  onClick={() => setLandingExpandHeroSideLists((v) => !v)}
                >
                  {landingExpandHeroSideLists ? t('hero.landingShowLess') : t('hero.landingShowMore')}
                </button>
                </div>
              </div>
              <div className="trust-signals trust-signals--in-hero" aria-label="Trust and security">
                <div className="trust-signals__inner">
                  <ul className="trust-signals__list">
                    <li><span className="trust-signals__check" aria-hidden="true"><HeroAccentTick /></span>{t('trust.secureLinks')}</li>
                    <li><span className="trust-signals__check" aria-hidden="true"><HeroAccentTick /></span>{t('trust.auditTrail')}</li>
                    <li><span className="trust-signals__check" aria-hidden="true"><HeroAccentTick /></span>{t('trust.verifiablePdfs')}</li>
                  </ul>
                  <p className="trust-signals__footer">{t('trust.footer')}</p>
                </div>
              </div>
            </section>

            {/* ── Social proof stats bar ── */}
            <div className="hero-stats-bar" aria-label="Platform statistics">
              <div className="hero-stats-bar__inner">
                <div className="hero-stat">
                  <span className="hero-stat__num">10,000<span className="hero-stat__plus">+</span></span>
                  <span className="hero-stat__label">Documents Sent</span>
              </div>
                <div className="hero-stats-bar__div" aria-hidden="true" />
                <div className="hero-stat">
                  <span className="hero-stat__num">&lt;&nbsp;3<span className="hero-stat__unit"> min</span></span>
                  <span className="hero-stat__label">Avg. Sign Time</span>
                  </div>
                <div className="hero-stats-bar__div" aria-hidden="true" />
                <div className="hero-stat">
                  <span className="hero-stat__num">100<span className="hero-stat__unit">%</span></span>
                  <span className="hero-stat__label">Legally Binding</span>
                  </div>
                <div className="hero-stats-bar__div" aria-hidden="true" />
                <div className="hero-stat">
                  <span className="hero-stat__num">AES-256</span>
                  <span className="hero-stat__label">Encrypted</span>
                  </div>
                </div>
                  </div>

            <section id="how" className="section section-steps">
              <div className="section-steps__inner">
                <span className="section-steps__eyebrow">How It Works</span>
                <p className="section-steps__subtitle">3 Simple Steps to <span className="section-steps__subtitle-accent">Get Signed</span></p>
                <div className="section-steps__grid">
                  {[
                    { num: 1, label: t('hero.step1Create'), name: 'docCreation' },
                    { num: 2, label: t('hero.step2Sign'), name: 'signing' },
                    { num: 3, label: t('hero.step3Receive'), name: 'aftersigned' },
                  ].map((step, idx) => {
                    const dim = STEP_CARD_IMAGE_LAYOUT[step.name]
                    return (
                    <div key={idx} className="step-card-wrap">
                      <div className="step-card-wrap__header">
                        <span className="step-card__badge">{step.num}</span>
                        <h4 className="step-card__label">{step.label}</h4>
                  </div>
                      <button
                        type="button"
                        className="step-card"
                        onClick={() => setStepZoomIndex(idx)}
                      >
                        <div className="step-card__image-wrap">
                          <PublicPictureImg
                            name={step.name}
                            alt={t('seo.howItWorksStepAlt', { step: step.num, label: step.label })}
                            width={dim.width}
                            height={dim.height}
                            loading="lazy"
                          />
                  </div>
                      </button>
                </div>
                    )
                  })}
                  </div>
                  </div>
            </section>

            {stepZoomIndex !== null && createPortal(
              <div
                ref={stepImageZoomRef}
                className="step-image-zoom-backdrop"
                role="dialog"
                aria-modal="true"
                aria-label="Step image full screen"
                tabIndex={0}
                onClick={(e) => {
                  const t = e.target
                  if (t === e.currentTarget || (t?.classList?.contains('step-image-zoom__click-catcher'))) {
                    setStepZoomIndex(null)
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setStepZoomIndex(null)
                  if (e.key === 'ArrowLeft') { e.preventDefault(); setStepZoomIndex((i) => (i <= 0 ? 2 : i - 1)) }
                  if (e.key === 'ArrowRight') { e.preventDefault(); setStepZoomIndex((i) => (i >= 2 ? 0 : i + 1)) }
                }}
                onTouchStart={(e) => {
                  if (e.touches.length === 1) {
                    stepZoomTouchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
                  }
                }}
                onTouchMove={(e) => {
                  if (e.touches.length !== 1 || !stepZoomTouchStart.current) return
                  const dx = e.touches[0].clientX - stepZoomTouchStart.current.x
                  const dy = e.touches[0].clientY - stepZoomTouchStart.current.y
                  if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 15 && e.cancelable) {
                    e.preventDefault()
                  }
                }}
                onTouchEnd={(e) => {
                  if (e.changedTouches.length !== 1 || !stepZoomTouchStart.current) return
                  const dx = e.changedTouches[0].clientX - stepZoomTouchStart.current.x
                  stepZoomTouchStart.current = null
                  if (dx > 50) {
                    if (e.cancelable) e.preventDefault()
                    handleStepZoomSwipe('prev')
                  } else if (dx < -50) {
                    if (e.cancelable) e.preventDefault()
                    handleStepZoomSwipe('next')
                  }
                }}
              >
                <div className="step-image-zoom__click-catcher" onClick={() => setStepZoomIndex(null)} aria-hidden="true" />
                <div className="step-image-zoom__content">
                  <div className="step-image-zoom__top">
                    <button type="button" className="step-image-zoom__close" onClick={(e) => { e.stopPropagation(); setStepZoomIndex(null) }} aria-label="Close">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                    </button>
                  </div>
                  <div className="step-image-zoom__middle">
                    <button type="button" className="step-image-zoom__prev" onClick={(e) => { e.stopPropagation(); setStepZoomIndex((i) => (i <= 0 ? 2 : i - 1)) }} aria-label="Previous">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
                    </button>
                    <div className="step-image-zoom__img-wrap" onClick={(e) => e.stopPropagation()}>
                      <img
                        src={STEP_IMAGES[stepZoomIndex]}
                        alt={t('seo.howItWorksStepAlt', {
                          step: stepZoomIndex + 1,
                          label: [t('hero.step1Create'), t('hero.step2Sign'), t('hero.step3Receive')][stepZoomIndex],
                        })}
                        className="step-image-zoom__img"
                        draggable={false}
                      />
                </div>
                    <button type="button" className="step-image-zoom__next" onClick={(e) => { e.stopPropagation(); setStepZoomIndex((i) => (i >= 2 ? 0 : i + 1)) }} aria-label="Next">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}

            <section id="demo" className="section section-demo">
              <div className="section-demo__inner">
                <span className="demo-section-eyebrow">See It In Action</span>
                <h3 className="section-demo__title">{t('nav.seeDemo')}</h3>
                {(appConfig.youtubeDemoVideo || (appConfig.showCarouselVideos && appConfig.carouselVideos)) ? (() => {
                  const mainVideoId = getYouTubeVideoId(appConfig.youtubeDemoVideo)
                  const carouselVideosList = (appConfig.carouselVideos || '').split(',').map((s) => s.trim()).filter(Boolean)
                  const carouselHeadersList = (appConfig.carouselVideoHeaders || '').split(',').map((s) => s.trim())
                  const carouselItems = carouselVideosList.map((url, i) => ({
                    id: getYouTubeVideoId(url),
                    url,
                    header: carouselHeadersList[i] || `Video ${i + 1}`,
                  })).filter((item) => item.id)
                  const displayVideoId = demoSelectedVideoId ?? mainVideoId ?? (carouselItems[0]?.id)
                  const hasCarousel = appConfig.showCarouselVideos && carouselItems.length > 0
                  const visibleCount = 3
                  const maxOffset = Math.max(0, carouselItems.length - visibleCount)
                  return (
                    <>
                      {displayVideoId && (
                        <div className="section-demo__video-wrap">
                          <div className="section-demo__video-inner">
                            <iframe
                              src={`https://www.youtube.com/embed/${displayVideoId}?rel=0`}
                              title={t('seo.demoIframeTitle')}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                              className="section-demo__iframe"
                            />
                  </div>
                  </div>
                      )}
                      {hasCarousel && (
                        <div className="section-demo__carousel">
                          <button
                            type="button"
                            className="section-demo__carousel-btn section-demo__carousel-btn--prev"
                            onClick={() => setDemoCarouselOffset((o) => Math.max(0, o - 1))}
                            disabled={demoCarouselOffset <= 0}
                            aria-label="Previous videos"
                          >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
                          </button>
                          <div className="section-demo__carousel-track">
                            {carouselItems.slice(demoCarouselOffset, demoCarouselOffset + visibleCount).map((item, idx) => (
                              <button
                                key={`${item.id}-${demoCarouselOffset + idx}`}
                                type="button"
                                className={`section-demo__carousel-item ${displayVideoId === item.id ? 'is-active' : ''}`}
                                onClick={() => setDemoSelectedVideoId(item.id)}
                              >
                                <div className="section-demo__carousel-thumb">
                                  <img src={`https://img.youtube.com/vi/${item.id}/mqdefault.jpg`} alt={`${t('seo.youtubeVideoThumbnail')}: ${item.header}`} loading="lazy" />
                  </div>
                                <span className="section-demo__carousel-label">{item.header}</span>
                              </button>
                            ))}
                </div>
                          <button
                            type="button"
                            className="section-demo__carousel-btn section-demo__carousel-btn--next"
                            onClick={() => setDemoCarouselOffset((o) => Math.min(maxOffset, o + 1))}
                            disabled={demoCarouselOffset >= maxOffset}
                            aria-label="Next videos"
                          >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                          </button>
                        </div>
                      )}
                    </>
                  )
                })() : (
                  <p className="section-demo__placeholder">Add a YouTube URL in Firebase: appConfiguration → app → youtubeDemoVideo</p>
                )}
              </div>
            </section>

            <section id="pricing" className="section dark">
              <span className="pricing-section-eyebrow">Simple, Transparent Pricing</span>
              <p className="pricing-section-title">Plans for <span>Every Budget</span></p>
              <p className="pricing-section-subtitle">Start free. Scale when you need to. Cancel anytime.</p>
              <div className="pricing-page-toggle signup-billing-toggle subscription-billing-toggle">
                <button
                  type="button"
                  className={`toggle-option ${pricingBillingCycle === 'yearly' ? 'is-active' : ''}`}
                  onClick={() => setPricingBillingCycle('yearly')}
                >
                  {t('pricing.billAnnually')}
                </button>
                <button
                  type="button"
                  className={`toggle-option ${pricingBillingCycle === 'monthly' ? 'is-active' : ''}`}
                  onClick={() => setPricingBillingCycle('monthly')}
                >
                  {t('pricing.billMonthly')}
                </button>
                <span className="pricing-page-toggle__note">Cancel anytime (No lock in contract)</span>
              </div>
              <div className="subscription-tiers pricing-page-tiers">
                {(['basic', 'standard', 'premium']).map((tierId) => {
                  const tierData = tiers[tierId] ?? TIER_SEED_DATA[tierId]
                  const stripeArr = tierData?.Stripe ?? tierData?.stripe ?? []
                  const monthlyOption = stripeArr.find((s) => (s.type || '').toLowerCase() === 'monthly')
                  const annualOption = stripeArr.find((s) => (s.type || '').toLowerCase() === 'annual')
                  const monthlyPrice = monthlyOption?.price ?? 0
                  const annualPrice = annualOption?.price ?? 0
                  const isYearly = pricingBillingCycle === 'yearly'
                  const displayPrice = isYearly ? (annualPrice / 12) : monthlyPrice
                  const periodLabel = t('pricing.perMonth')
                  const displayPriceFormatted = (typeof displayPrice === 'number' && Number.isInteger(displayPrice)) ? displayPrice : (typeof displayPrice === 'number' ? displayPrice.toFixed(2) : displayPrice)
                  const annualSavingsPercent = monthlyPrice > 0 && annualPrice >= 0
                    ? Math.round(((monthlyPrice * 12 - annualPrice) / (monthlyPrice * 12)) * 100)
                    : 0
                  const showSavingsBadge = pricingBillingCycle === 'yearly' && annualSavingsPercent > 0
                  const isPopular = tierId === 'standard'
                  const titles = { basic: t('pricing.tierBasic'), standard: t('pricing.tierStandard'), premium: t('pricing.tierPremium') }
                  const descs = { basic: t('pricing.descBasic'), standard: t('pricing.descStandard'), premium: t('pricing.descPremium') }
                  const BasicIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>)
                  const PremiumIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>)
                  const Icon = tierId === 'premium' ? PremiumIcon : BasicIcon
                  return (
                    <div key={tierId} className={`tier-card ${isPopular ? 'highlighted popular' : ''} ${tierId === 'standard' ? 'popular' : ''}`}>
                      {isPopular && <span className="tier-badge">{t('pricing.mostPopular')}</span>}
                      {showSavingsBadge && <span className="tier-discount-badge">-{annualSavingsPercent}%</span>}
                  <div className="tier-header">
                        <div className="tier-icon-wrap"><Icon /></div>
                        <h4>{titles[tierId]}</h4>
                        <p className="tier-desc">{descs[tierId]}</p>
                    </div>
                      {isYearly && annualPrice > 0 && (
                        <p className="tier-annual-billing">Billed ${annualPrice}/yr</p>
                      )}
                  <div className="tier-price-row">
                        <span className="tier-price">${displayPriceFormatted}</span>
                        <span className="tier-period">{periodLabel}</span>
                  </div>
                  <ul className="tier-features">
                        {tierId === 'basic' && (<><li><CheckIcon /> {t('pricing.featureRecipients5')}</li><li><CheckIcon /> {t('pricing.featureEmails5')}</li><li><CheckIcon /> {t('pricing.featureTemplates3')}</li><li><CheckIcon /> {t('pricing.featureChars3k')}</li><li><CheckIcon /> {t('pricing.featureAttachmentsNone')}</li><li><CheckIcon /> {t('pricing.featurePdfUpload')}</li><li><CheckIcon /> {t('pricing.featureCustomFields')}</li></>)}
                        {tierId === 'standard' && (<><li><CheckIcon /> {t('pricing.featureRecipients10')}</li><li><CheckIcon /> {t('pricing.featureEmails50')}</li><li><CheckIcon /> {t('pricing.featureTemplates10')}</li><li><CheckIcon /> {t('pricing.featureChars20k')}</li><li><CheckIcon /> {t('pricing.featureAttachments3')}</li><li><CheckIcon /> {t('pricing.featureAuditTrail')}</li><li><CheckIcon /> {t('pricing.featurePdfUpload')}</li><li><CheckIcon /> {t('pricing.featurePdfExtract')}</li><li><CheckIcon /> {t('pricing.featureCustomFields')}</li><li><CheckIcon /> {t('pricing.featureCopyLink')}</li>{!appConfig.hideLogoFeature && <li><CheckIcon /> {t('pricing.featureLogo')}</li>}</>)}
                        {tierId === 'premium' && (<><li><CheckIcon /> {t('pricing.featureRecipients10')}</li><li><CheckIcon /> {t('pricing.featureEmails150')}</li><li><CheckIcon /> {t('pricing.featureTemplates25')}</li><li><CheckIcon /> {t('pricing.featureChars30k')}</li><li><CheckIcon /> {t('pricing.featureAttachments10')}</li><li><CheckIcon /> {t('pricing.featureAuditTrail')}</li><li><CheckIcon /> {t('pricing.featurePdfUpload')}</li><li><CheckIcon /> {t('pricing.featurePdfExtract')}</li><li><CheckIcon /> {t('pricing.featureCustomFields')}</li><li><CheckIcon /> {t('pricing.featureCopyLink')}</li>{!appConfig.hideVerificationFeature && <li><CheckIcon /> {t('pricing.featureVerification')}</li>}{!appConfig.hideLogoFeature && <li><CheckIcon /> {t('pricing.featureLogo')}</li>}</>)}
                  </ul>
                      <button type="button" className={`tier-button tier-button--action ${isPopular ? 'primary' : ''}`} onClick={async () => { setSubscriptionTier(tierId); setBillingCycle(pricingBillingCycle); if (user) { try { const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession'); const billingInterval = pricingBillingCycle === 'yearly' ? 'annual' : 'monthly'; const savePromise = saveDocumentStateForPaymentReturn(); const { data } = await createCheckoutSession({ tierId, billingInterval, returnOrigin: typeof window !== 'undefined' ? window.location.origin : '' }); if (data?.url) { await savePromise; window.location.href = data.url; return; } } catch (e) { console.error('Checkout failed:', e); } } setAuthMode('signup'); setIsAuthModalOpen(true); }}>{t('pricing.getStarted')}</button>
                </div>
                  )
                })}
              </div>
            </section>

            <section id="about" className="section light section-about">
              <div className="section-about__head">
                <span className="section-about__label">{t('about.label')}</span>
                <h3 className="section-about__title">{t('about.sectionHeadlineBefore')}<span>{t('about.sectionHeadlineAccent')}</span></h3>
                <p className="section-about__subtitle section-subtitle">
                  {t('about.subtitle')}
                </p>
                <div className="section-about__direct">
                  <p className="section-about__direct-p">{t('about.directAnswerP1')}</p>
                  <p className="section-about__direct-p">{t('about.directAnswerP2')}</p>
                </div>
              </div>
              <div className="about-rows">
                <div className="about-row about-row--reverse">
                  <div className="about-row__media card-image">
                    <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80" alt={t('about.builtForSpeed')} loading="lazy" />
                  </div>
                  <div className="about-row__content">
                    <span className="about-row__tag">{t('about.speed')}</span>
                    <h4>{t('about.builtForSpeed')}</h4>
                    <p className="about-row__prose about-row__prose--preline">{t('about.builtForSpeedDesc')}</p>
                  </div>
                </div>
                <div className="about-row" id="about-firebase-security">
                  <div className="about-row__media about-row__media--firebase" aria-hidden="true">
                    <div className="about-firebase-visual">
                      <div className="about-firebase-visual__glow" />
                      <div className="about-firebase-visual__icons">
                        <span className="about-firebase-visual__icon about-firebase-visual__icon--cloud">
                          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>
                        </span>
                        <span className="about-firebase-visual__icon about-firebase-visual__icon--shield">
                          <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                        </span>
                        <span className="about-firebase-visual__icon about-firebase-visual__icon--lock">
                          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        </span>
                      </div>
                      <p className="about-firebase-visual__badge">{t('about.firebaseVisualBadge')}</p>
                    </div>
                  </div>
                  <div className="about-row__content">
                    <span className="about-row__tag about-row__tag--firebase">{t('about.dataSecurity')}</span>
                    <h4>{t('about.dataSecurityTitle')}</h4>
                    <div className="about-data-security-prose">
                      <p className="about-row__prose about-row__prose--preline">{t('about.dataSecurityDesc')}</p>
                      <a href={locPath('/privacy')} className="about-data-security-prose__link" onClick={(e) => goToMarketingSeoPath(e, '/privacy')}>
                        {t('about.dataSecurityPrivacyCta')}
                      </a>
                    </div>
                  </div>
                </div>
                <div className="about-row about-row--reverse">
                  <div className="about-row__media card-image">
                    <img src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=1200&q=80" alt={t('about.legalTitle')} loading="lazy" />
                  </div>
                  <div className="about-row__content">
                    <span className="about-row__tag about-row__tag--legal">{t('about.legalPill')}</span>
                    <h4>{t('about.legalTitle')}</h4>
                    <p className="about-row__prose about-row__prose--preline">{t('about.legalDesc')}</p>
                  </div>
                </div>
                <div className="about-row">
                  <div className="about-row__media card-image">
                    <img src="https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?auto=format&fit=crop&w=1200&q=80" alt={t('about.privacyFocused')} loading="lazy" />
                  </div>
                  <div className="about-row__content">
                    <span className="about-row__tag about-row__tag--privacy">{t('about.privacyPill')}</span>
                    <h4>{t('about.privacyFocused')}</h4>
                    <p className="about-row__prose about-row__prose--preline">{t('about.privacyFocusedDesc')}</p>
                  </div>
                </div>
                <div className="about-row about-row--reverse">
                  <div className="about-row__media card-image">
                    <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80" alt={t('about.madeForTeams')} loading="lazy" />
                  </div>
                  <div className="about-row__content">
                    <span className="about-row__tag">{t('about.teams')}</span>
                    <h4>{t('about.madeForTeams')}</h4>
                    <p className="about-row__prose about-row__prose--preline">{t('about.madeForTeamsDesc')}</p>
                  </div>
                </div>
              </div>
              <div className="section-about__pricing-note">
                <p className="section-about__pricing-note__title">{t('about.pricingNoteTitle')}</p>
                <p className="section-about__pricing-note__body">{t('about.pricingNoteBody')}</p>
                <a href="#pricing" className="section-about__pricing-note__cta">
                  {t('about.pricingNoteCta')}
                </a>
              </div>
            </section>

            <div className="section-faq-wrap">
            <section id="faq" className="section section-faq" aria-labelledby="faq-main-heading">
              <div className="section-faq__inner">
              <div className="section-faq__head">
                <span className="section-faq__eyebrow">{t('faq.eyebrow')}</span>
                <h3 id="faq-main-heading" className="section-faq__title">{t('faq.titleBefore')}<span className="section-faq__title-accent">{t('faq.titleAccent')}</span></h3>
                <p className="section-faq__subtitle">{t('faq.subtitleLead')}<button type="button" className="section-faq__contact-link" onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}>{t('faq.subtitleLink')}</button></p>
              </div>
              <div className="faq-search-wrap">
                <p className="faq-search__hint">{t('faq.searchHint')}</p>
                <div className="faq-search__input-wrap">
                  <svg className="faq-search__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                  <input
                    type="search"
                    className="faq-search__input"
                    placeholder={t('faq.searchPlaceholder')}
                    value={faqSearch}
                    onChange={(e) => setFaqSearch(e.target.value)}
                    aria-label={t('faq.searchHint')}
                  />
                  {faqSearch && (
                    <button type="button" className="faq-search__clear" onClick={() => setFaqSearch('')} aria-label={t('faq.clearSearchAria')}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                    </button>
                  )}
                </div>
              </div>
              <div className="faq-accordion">
                {(() => {
                  const restFaqItems = [
                    { qKey: 'faq.multipleRecipientsQ', aKey: 'faq.multipleRecipientsA' },
                    { qKey: 'faq.reuseTemplatesQ', aKey: 'faq.reuseTemplatesA' },
                    { qKey: 'faq.recipientAccountQ', aKey: 'faq.recipientAccountA' },
                    { qKey: 'faq.confirmationEmailQ', aKey: 'faq.confirmationEmailA' },
                    { qKey: 'faq.previewPdfQ', aKey: 'faq.previewPdfA' },
                    { qKey: 'faq.editAgreementQ', aKey: 'faq.editAgreementA' },
                    { qKey: 'faq.uploadPdfQ', aKey: 'faq.uploadPdfA' },
                    { qKey: 'faq.extractPdfQ', aKey: 'faq.extractPdfA' },
                    { qKey: 'faq.richTextEditorQ', aKey: 'faq.richTextEditorA' },
                    { qKey: 'faq.previewWhenQ', aKey: 'faq.previewWhenA' },
                    { qKey: 'faq.documentLifetimeQ', aKey: 'faq.documentLifetimeA' },
                    { qKey: 'faq.editLayoutQ', aKey: 'faq.editLayoutA' },
                    { qKey: 'faq.customFieldsQ', aKey: 'faq.customFieldsA' },
                    { qKey: 'faq.multipleSignaturesQ', aKey: 'faq.multipleSignaturesA' },
                    { qKey: 'faq.documentBuilderTipsQ', aKey: 'faq.documentBuilderTipsA' },
                    ...getAiCompareFaqItems(tierPricing),
                    { qKey: 'faq.clearEverythingQ', aKey: 'faq.clearEverythingA' },
                    { qKey: 'faq.forgotPasswordQ', aKey: 'faq.forgotPasswordA' },
                    { qKey: 'faq.saveTemplateQ', aKey: 'faq.saveTemplateA' },
                    { qKey: 'faq.loadTemplateQ', aKey: 'faq.loadTemplateA' },
                    { qKey: 'faq.templateLimitsQ', aKey: 'faq.templateLimitsA' },
                    { qKey: 'faq.recipientNoEmailQ', aKey: 'faq.recipientNoEmailA' },
                    { qKey: 'faq.editAfterSendQ', aKey: 'faq.editAfterSendA' },
                    { qKey: 'faq.changeAccountQ', aKey: 'faq.changeAccountA' },
                    { qKey: 'faq.browserSupportQ', aKey: 'faq.browserSupportA' },
                    { qKey: 'faq.copyLinkQ', aKey: 'faq.copyLinkA' },
                    { qKey: 'faq.resendDocumentQ', aKey: 'faq.resendDocumentA' },
                    { qKey: 'faq.signatureRequiredQ', aKey: 'faq.signatureRequiredA', signatureRequired: true },
                    { qKey: 'faq.attachmentsQ', aKey: 'faq.attachmentsA' },
                    { qKey: 'faq.emailActivityQ', aKey: 'faq.emailActivityA' },
                    { qKey: 'faq.emailRemindersQ', aKey: 'faq.emailRemindersA' },
                    { qKey: 'faq.defaultSenderQ', aKey: 'faq.defaultSenderA' },
                    { qKey: 'faq.mobileSignQ', aKey: 'faq.mobileSignA' },
                    { qKey: 'faq.getHelpQ', aKey: 'faq.getHelpA' },
                    { qKey: 'faq.dataSecureQ', aKey: 'faq.dataSecureA' },
                    { qKey: 'faq.fileFormatsQ', aKey: 'faq.fileFormatsA' },
                    { qKey: 'faq.deleteTemplateQ', aKey: 'faq.deleteTemplateA' },
                    ...(appConfig.hideLogoFeature ? [] : [{ qKey: 'faq.ownLogoQ', aKey: 'faq.ownLogoA' }]),
                  ]
                  const faqGroups = [
                    {
                      heading: { id: 'pricing-faq', labelKey: 'faq.pricingGroupHeading' },
                      leadKey: 'faq.pricingSectionLead',
                      items: LANDING_PRICING_FAQ_GROUP_ITEMS,
                    },
                    ...(isSeoLandingPathname(marketingPathname)
                      ? []
                      : [
                          {
                            heading: { id: 'faq-spotlight', labelKey: 'faq.faqTopicsSubheading' },
                            leadKey: 'faq.seoSpotlightLead',
                            items: SEO_FAQ_SPOTLIGHT_ITEMS,
                          },
                        ]),
                    { heading: null, leadKey: null, items: restFaqItems },
                  ]
                  const query = faqSearch.trim().toLowerCase()
                  const words = query ? query.split(/\s+/).filter(Boolean) : []
                  const itemMatches = (raw) => {
                    const params = raw.params ?? faqItemParams(raw, tierPricing)
                    const q = t(raw.qKey).toLowerCase()
                    const a = t(raw.aKey, params).toLowerCase()
                        return words.every((w) => q.includes(w) || a.includes(w))
                  }
                  const filteredGroups = words.length
                    ? faqGroups
                        .map((g) => ({ ...g, items: g.items.filter(itemMatches) }))
                        .filter((g) => g.items.length > 0)
                    : faqGroups
                  const flatEntries = []
                  for (const g of filteredGroups) {
                    if (g.heading) {
                      flatEntries.push({ type: 'heading', id: g.heading.id, labelKey: g.heading.labelKey })
                      if (g.leadKey) flatEntries.push({ type: 'lead', labelKey: g.leadKey })
                    }
                    for (const raw of g.items) {
                      flatEntries.push({
                        type: 'item',
                        qKey: raw.qKey,
                        aKey: raw.aKey,
                        params: raw.params ?? faqItemParams(raw, tierPricing),
                        multiParagraph: raw.multiParagraph,
                        signatureRequired: raw.signatureRequired,
                      })
                    }
                  }
                  const totalQuestions = flatEntries.filter((e) => e.type === 'item').length
                  const displayEntries =
                    words.length || faqExpanded ? flatEntries : takeCollapsedFaqEntries(flatEntries, 5)
                  if (words.length && totalQuestions === 0) {
                    return <p className="faq-no-results">{t('faq.noResults')}</p>
                  }
                  return (
                    <>
                      {displayEntries.map((entry, i) => {
                        if (entry.type === 'heading') {
                          return (
                            <h4 key={`faq-h-${entry.id}`} id={entry.id} className="faq-group-heading">
                              {t(entry.labelKey)}
                            </h4>
                          )
                        }
                        if (entry.type === 'lead') {
                          return (
                            <p key={`faq-l-${entry.labelKey}-${i}`} className="faq-group-lead">
                              {t(entry.labelKey)}
                            </p>
                          )
                        }
                        const faq = entry
                        return faq.signatureRequired ? (
                          <details key={faq.qKey} onToggle={(e) => setSignatureRequiredFaqOpen(e.target.open)}>
                            <summary>{t(faq.qKey)}</summary>
                            {renderFaqAnswerBody(t, faq)}
                            {signatureRequiredFaqOpen && (isLocalAdminFaqTest || appConfig.showAdminButton !== false) && (
                              <div className="faq-admin-wrap">
                                <button type="button" className="ghost-button" onClick={(ev) => { ev.preventDefault(); setIsAdminOpen(true); }}>Admin</button>
                              </div>
                            )}
                          </details>
                        ) : (
                          <details key={faq.qKey}>
                            <summary>{t(faq.qKey)}</summary>
                            {renderFaqAnswerBody(t, faq)}
                  </details>
                        )
                      })}
                      {!words.length && totalQuestions > 5 && (
                        <button type="button" className="faq-toggle-btn" onClick={() => setFaqExpanded(!faqExpanded)}>
                          {faqExpanded ? t('faq.less') : t('faq.more')}
                </button>
                      )}
                    </>
                  )
                })()}
              </div>
              </div>
            </section>
            </div>

            <section
              id="testimonials"
              className="section light section-testimonials"
              aria-labelledby="testimonials-heading"
            >
              <TestimonialsJsonLd reviews={testimonialSchemaReviews} />
              <div className="section-testimonials__head">
                <span className="section-testimonials__eyebrow">{t('testimonials.eyebrow')}</span>
                <h3 id="testimonials-heading" className="section-testimonials__title">
                  {t('testimonials.headlineBefore')}
                  <span>{t('testimonials.headlineAccent')}</span>
                </h3>
                <p className="section-testimonials__lead">{t('testimonials.introLead')}</p>
              </div>
              <div className="testimonials-carousel">
                {[
                  { name: 'Dwayne J', quoteKey: 'testimonials.quote1' },
                  { name: 'James K.', quoteKey: 'testimonials.quote2' },
                  { name: 'Elena R.', quoteKey: 'testimonials.quote3' },
                  { name: 'David L.', quoteKey: 'testimonials.quote4' },
                  { name: 'Priya S.', quoteKey: 'testimonials.quote5' },
                  { name: 'Marcus T.', quoteKey: 'testimonials.quote6' },
                ].slice(testimonialPage * 3, testimonialPage * 3 + 3).map((item, i) => (
                  <article key={i} className="testimonial-card">
                    <div className="testimonial-card__quote-icon" aria-hidden="true">"</div>
                    <div className="testimonial-card__stars" aria-label="5 out of 5 stars">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg key={star} className="testimonial-card__star" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                      ))}
                    </div>
                    <blockquote className="testimonial-card__quote" cite="https://primedocumentsign.com/">
                    <p className="testimonial-card__text">{t(item.quoteKey)}</p>
                    </blockquote>
                    <div className="testimonial-card__separator" />
                    <div className="testimonial-card__author">
                      <div className="testimonial-card__avatar" aria-hidden="true">{item.name.charAt(0)}</div>
                      <span className="testimonial-card__name">{item.name}</span>
                    </div>
                  </article>
                ))}
              </div>
              <div className="testimonials-pagination">
                <button
                  type="button"
                  className="testimonials-pagination__btn ghost-button"
                  onClick={() => setTestimonialPage((p) => Math.max(0, p - 1))}
                  disabled={testimonialPage === 0}
                  aria-label={t('testimonials.prev')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                  {t('testimonials.prev')}
                </button>
                <span className="testimonials-pagination__dots">
                  <span className={testimonialPage === 0 ? 'is-active' : ''} aria-current={testimonialPage === 0 ? 'true' : undefined} />
                  <span className={testimonialPage === 1 ? 'is-active' : ''} aria-current={testimonialPage === 1 ? 'true' : undefined} />
                </span>
                <button
                  type="button"
                  className="testimonials-pagination__btn ghost-button"
                  onClick={() => setTestimonialPage((p) => Math.min(1, p + 1))}
                  disabled={testimonialPage === 1}
                  aria-label={t('testimonials.next')}
                >
                  {t('testimonials.next')}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </button>
              </div>
            </section>

            {false && (
            <section id="pdf-editor" className="section dark section-pdf-editor">
              <div className="section-pdf-editor__head">
                <span className="section-pdf-editor__label">Free tool</span>
                <h3 className="section-pdf-editor__title">PDF Editor</h3>
                <p className="section-pdf-editor__subtitle section-subtitle">
                  Upload a PDF, add text anywhere on the document, and download your edited file.
                </p>
              </div>
              <div className="pdf-editor-tool">
                <input
                  ref={pdfEditorInputRef}
                  type="file"
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  onChange={handlePdfEditorUpload}
                />
                {pdfEditorPages.length === 0 ? (
                  <div className="pdf-editor-upload-zone">
                    <button
                      type="button"
                      className="primary-button pdf-editor-upload-btn"
                      onClick={() => pdfEditorInputRef.current?.click()}
                      disabled={pdfEditorLoading}
                    >
                      {pdfEditorLoading ? 'Loading PDF…' : 'Upload PDF'}
                    </button>
                    <p className="pdf-editor-upload-hint">Max {(maxPdfBytes / 1024 / 1024).toFixed(0)} MB. Click anywhere on the document to add text.</p>
                    {pdfEditorError && <p className="pdf-editor-error">{pdfEditorError}</p>}
                  </div>
                ) : (
                  <div className="pdf-editor-workspace">
                    <div className="pdf-editor-toolbar">
                      <label className="pdf-editor-font-size-label">
                        <span>Text size</span>
                        <select
                          value={pdfEditorFontSize}
                          onChange={(e) => setPdfEditorFontSize(Number(e.target.value))}
                          className="pdf-editor-font-size-select"
                        >
                          {[8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36].map((s) => (
                            <option key={s} value={s}>{s}pt</option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        className="ghost-button"
                        onClick={() => pdfEditorInputRef.current?.click()}
                        disabled={pdfEditorLoading}
                      >
                        Replace PDF
                      </button>
                      <button type="button" className="ghost-button" onClick={clearPdfEditor}>
                        Clear
                      </button>
                      <button
                        type="button"
                        className="primary-button"
                        onClick={handlePdfEditorDownload}
                      >
                        Download PDF
                      </button>
                    </div>
                    <div className="pdf-editor-pages">
                      {pdfEditorPages.map((page) => (
                        <div key={page.pageIndex} className="pdf-editor-page-wrap">
                          <div
                            className="pdf-editor-page"
                            onClick={(e) => handlePdfEditorPageClick(page.pageIndex, e)}
                            style={{ width: '100%', maxWidth: Math.min(page.width, 612), aspectRatio: `${page.width} / ${page.height}` }}
                          >
                            <img
                              src={page.imageDataUrl}
                              alt={t('app.pdfAgreementPageAlt', { page: page.pageIndex })}
                              className="pdf-editor-page-img"
                            />
                            {page.textOverlays.map((o) => (
                              <span
                                key={o.id}
                                className="pdf-editor-overlay"
                                style={{
                                  left: `${(o.x / page.width) * 100}%`,
                                  top: `${(o.y / page.height) * 100}%`,
                                  fontSize: o.fontSize,
                                }}
                              >
                                {o.text}
                                <button
                                  type="button"
                                  className="pdf-editor-overlay-remove"
                                  onClick={(ev) => { ev.stopPropagation(); handlePdfEditorRemoveOverlay(page.pageIndex, o.id) }}
                                  aria-label="Remove text"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                            {pdfEditorAddingText?.pageIndex === page.pageIndex && (
                              <div
                                className="pdf-editor-input-wrap"
                                style={{
                                  left: `${(pdfEditorAddingText.x / page.width) * 100}%`,
                                  top: `${(pdfEditorAddingText.y / page.height) * 100}%`,
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  id="pdf-editor-text-input"
                                  type="text"
                                  value={pdfEditorInputValue}
                                  onChange={(e) => setPdfEditorInputValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handlePdfEditorConfirmText()
                                    if (e.key === 'Escape') setPdfEditorAddingText(null)
                                  }}
                                  placeholder="Type text…"
                                  autoFocus
                                />
                                <button type="button" className="primary-button pdf-editor-confirm-btn" onClick={handlePdfEditorConfirmText}>
                                  Add
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
            )}

            {!isSeoLandingPathname(marketingPathname) ? <MarketingAiSummary variant="landing" /> : null}

            <section id="contact" className="section contact section-contact--redesign">
              <div className="contact-redesign__inner">
                {/* Left: info panel */}
                <div className="contact-redesign__info">
                  <span className="pricing-section-eyebrow">{t('contact.eyebrow')}</span>
                  <h2 className="contact-redesign__title">{t('contact.headlineBefore')}<span>{t('contact.headlineAccent')}</span></h2>
                  <p className="contact-redesign__subtitle">{t('contact.subtitle')}</p>
                  <ul className="contact-redesign__meta">
                    <li>
                      <span className="contact-redesign__meta-icon" aria-hidden="true">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      </span>
                      <div>
                        <span className="contact-redesign__meta-label">{t('contact.responseTimeLabel')}</span>
                        <span className="contact-redesign__meta-value">{t('contact.responseTimeValue')}</span>
                      </div>
                    </li>
                    <li>
                      <span className="contact-redesign__meta-icon" aria-hidden="true">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      </span>
                      <div>
                        <span className="contact-redesign__meta-label">{t('contact.secureLabel')}</span>
                        <span className="contact-redesign__meta-value">{t('contact.secureValue')}</span>
                      </div>
                    </li>
                  </ul>
                  <div className="contact-redesign__trust">
                    <span className="contact-redesign__trust-badge">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="20 6 9 17 4 12"/></svg>
                      {t('contact.trustNoSpam')}
                    </span>
                    <span className="contact-redesign__trust-badge">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="20 6 9 17 4 12"/></svg>
                      {t('contact.trustHumanSupport')}
                    </span>
                  </div>
                </div>
                {/* Right: form card */}
                <div className="contact-redesign__form">
                  <div className="contact-card contact-card--modern">
                <ContactForm
                  {...contactFormProps}
                  onSubmit={() => handleContactSubmit()}
                />
                  </div>
                </div>
              </div>
            </section>

            {(() => {
              const _authCardUnused = (
              <div className="auth-card" data-signup-pricing={authMode === 'signup' && signupStep === 2 ? '' : undefined}>
                <div className="auth-header">
                  <h2>{authMode === 'login' ? t('auth.welcomeBack') : t('auth.createAccount')}</h2>
                  <p>
                    {authMode === 'login'
                      ? t('auth.logInToManage')
                      : t('auth.startSending')}
                  </p>
                </div>

                <div className="auth-tabs">
                  <button
                    type="button"
                    className={`auth-tab ${authMode === 'login' ? 'is-active' : ''}`}
                    onClick={() => { setAuthMode('login'); setAuthError(''); setAuthEmailError(''); setAuthPasswordError(''); setShowForgotPassword(false); setForgotPasswordSent(false); setSignupStep(1) }}
                  >
                    {t('auth.logIn')}
                  </button>
                  <button
                    type="button"
                    className={`auth-tab ${authMode === 'signup' ? 'is-active' : ''}`}
                    onClick={() => { setAuthMode('signup'); setAuthError(''); setAuthEmailError(''); setAuthPasswordError(''); setSignupStep(1) }}
                  >
                    {t('auth.signUp')}
                  </button>
                </div>

                {authMode === 'login' ? (
                  <div className="auth-form">
                    {showForgotPassword ? (
                      <>
                        <label className="field">
                          <span>{t('auth.emailLabel')}</span>
                          <input
                            type="email"
                            placeholder={t('auth.emailPlaceholder')}
                            value={authEmail}
                            onChange={(e) => setAuthEmail(e.target.value)}
                            disabled={forgotPasswordSent}
                          />
                        </label>
                        {forgotPasswordSent ? (
                          <p className="status-note">{t('auth.checkEmailReset')}</p>
                        ) : (
                          <button
                            type="button"
                            className="primary-button auth-action-btn"
                            disabled={authSubmitting}
                            onClick={async () => {
                              setAuthError('')
                              if (!authEmail.trim()) {
                                setAuthError(t('auth.enterEmail'))
                                return
                              }
                              setAuthSubmitting(true)
                              try {
                                await resetPassword(authEmail.trim())
                                setForgotPasswordSent(true)
                              } catch (err) {
                                setAuthError(err.message || t('auth.resetEmailFailed'))
                              } finally {
                                setAuthSubmitting(false)
                              }
                            }}
                          >
                            {t('auth.sendResetLink')}
                          </button>
                        )}
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => {
                            setShowForgotPassword(false)
                            setForgotPasswordSent(false)
                            setAuthError('')
                          }}
                        >
                          {t('auth.backToLogIn')}
                        </button>
                      </>
                    ) : (
                      <>
                        {authError && <p className="status-note status-error">{authError}</p>}
                        <label className="field">
                          <span>{t('auth.emailLabel')}</span>
                          <input
                            type="email"
                            placeholder={t('auth.emailPlaceholder')}
                            value={authEmail}
                            onChange={(e) => { setAuthEmail(e.target.value); setAuthError('') }}
                          />
                        </label>
                        <label className="field">
                          <span>{t('auth.passwordLabel')}</span>
                          <input
                            type="password"
                            placeholder={t('auth.passwordPlaceholder')}
                            value={authPassword}
                            onChange={(e) => { setAuthPassword(e.target.value); setAuthError('') }}
                          />
                        </label>
                        <div className="auth-row">
                          <button
                            type="button"
                            className="link-button"
                            onClick={() => setShowForgotPassword(true)}
                          >
                            {t('auth.forgotPassword')}
                          </button>
                        </div>
                        <button
                          type="button"
                          className="primary-button full-width"
                          disabled={authSubmitting}
                          onClick={async () => {
                            setAuthError('')
                            if (!authEmail.trim() || !authPassword) {
                              setAuthError(t('auth.enterEmailPassword'))
                              return
                            }
                            setAuthSubmitting(true)
                            try {
                              await authLogin(authEmail.trim(), authPassword)
                              setIsAuthModalOpen(false)
                            } catch (err) {
                              setAuthError(err.message || t('auth.logInFailed'))
                            } finally {
                              setAuthSubmitting(false)
                            }
                          }}
                        >
                          {authSubmitting ? t('auth.loggingIn') : t('auth.logIn')}
                        </button>
                        <div className="auth-divider">{t('auth.or')}</div>
                        <button
                          type="button"
                          className="google-login-btn auth-action-btn"
                          disabled={authSubmitting}
                          onClick={async () => {
                            setAuthError('')
                            setAuthSubmitting(true)
                            try {
                              await signInWithGoogle({
                                subscriptionTier,
                                billingCycle,
                                isOneTimeUse,
                                preferredLanguage: language,
                              })
                              setIsAuthModalOpen(false)
                            } catch (err) {
                              const { message, showConnectionBanner } = getAuthErrorMessage(err, t)
                              setAuthError(message)
                              if (showConnectionBanner) setConnectionBannerVisible(true)
                            } finally {
                              setAuthSubmitting(false)
                            }
                          }}
                        >
                          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg"><path d="M17.64 9.2c0-.637-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.715H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.159 6.656 3.58 9 3.58z" fill="#EA4335"/></svg> Continue with Google
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="auth-form">
                    {authError && <p className="status-note status-error">{authError}</p>}
                    
                    {/* Step 1: Account Details */}
                    {signupStep === 1 && (
                    <>
                      <div className="auth-field-row">
                        <span className="auth-field-label">{t('auth.firstNameLabel')}</span>
                        <div className="auth-field-input-wrap">
                          <input
                            type="text"
                            placeholder={t('auth.firstNamePlaceholder')}
                            value={authFirstName}
                            onChange={(e) => { setAuthFirstName(e.target.value); setAuthError('') }}
                          />
                        </div>
                      </div>
                      <div className="auth-field-row">
                        <span className="auth-field-label">{t('auth.lastNameLabel')}</span>
                        <div className="auth-field-input-wrap">
                          <input
                            type="text"
                            placeholder={t('auth.lastNamePlaceholder')}
                            value={authLastName}
                            onChange={(e) => { setAuthLastName(e.target.value); setAuthError('') }}
                          />
                        </div>
                      </div>
                      <div className={`auth-field-row ${authEmailError ? 'auth-field-row--error' : ''}`}>
                        <span className="auth-field-label">{t('auth.emailLabel')}</span>
                        <div className="auth-field-input-wrap">
                          <input
                            type="email"
                          placeholder={t('auth.emailPlaceholder')}
                            value={authEmail}
                            onChange={(e) => { setAuthEmail(e.target.value); setAuthError(''); setAuthEmailError('') }}
                            onBlur={() => { if (authEmail.trim() && !isValidEmail(authEmail)) setAuthEmailError(t('auth.errorEmailInvalid')); else setAuthEmailError('') }}
                          />
                          {authEmailError && <span className="auth-field-error">{authEmailError}</span>}
                        </div>
                      </div>
                      <div className={`auth-field-row ${authPasswordError ? 'auth-field-row--error' : ''}`}>
                        <span className="auth-field-label">{t('auth.passwordLabel')}</span>
                        <div className="auth-field-input-wrap">
                          <input
                            type="password"
                            placeholder={t('auth.passwordPlaceholderHint')}
                            value={authPassword}
                            onChange={(e) => { setAuthPassword(e.target.value); setAuthError(''); setAuthPasswordError('') }}
                            onBlur={() => { if (authPassword && !isValidPassword(authPassword)) setAuthPasswordError(t('auth.passwordBlurError')); else setAuthPasswordError('') }}
                          />
                          {authPasswordError && <span className="auth-field-error">{authPasswordError}</span>}
                        </div>
                      </div>
                      <div className={`auth-field-row ${authPassword !== authConfirmPassword && authConfirmPassword ? 'auth-field-row--error' : ''}`}>
                        <span className="auth-field-label">{t('auth.confirmPasswordLabel')}</span>
                        <div className="auth-field-input-wrap">
                          <input
                            type="password"
                          placeholder={t('auth.confirmPasswordPlaceholder')}
                            value={authConfirmPassword}
                            onChange={(e) => { setAuthConfirmPassword(e.target.value); setAuthError('') }}
                          />
                          {authPassword !== authConfirmPassword && authConfirmPassword && <span className="auth-field-error">{t('auth.passwordsNoMatch')}</span>}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="primary-button full-width"
                        style={{ marginTop: '8px' }}
                        disabled={authSubmitting}
                        onClick={async () => {
                          setAuthError('')
                          setAuthEmailError('')
                          setAuthPasswordError('')
                          if (!authEmail.trim()) {
                            setAuthError(t('auth.enterEmail'))
                            return
                          }
                          if (!isValidEmail(authEmail)) {
                            setAuthEmailError(t('auth.errorEmailInvalid'))
                            return
                          }
                          if (authPassword.length < 8) {
                            setAuthPasswordError(t('auth.passwordMinLength'))
                            return
                          }
                          if (!isValidPassword(authPassword)) {
                            setAuthPasswordError(t('auth.passwordSpecial'))
                            return
                          }
                          if (authPassword !== authConfirmPassword) {
                            setAuthError(t('auth.passwordsNoMatch'))
                            return
                          }
                          setAuthSubmitting(true)
                          try {
                            const emailExists = await checkEmailExists(authEmail.trim())
                            if (emailExists) {
                              setIsEmailExistsModalOpen(true)
                            return
                          }
                          setSignupStep(2)
                          } finally {
                            setAuthSubmitting(false)
                          }
                        }}
                      >
                        {t('auth.continue')}
                      </button>
                      <div className="auth-divider">{t('auth.or')}</div>
                      <button
                        type="button"
                        className="google-login-btn auth-action-btn"
                        disabled={authSubmitting}
                        onClick={async () => {
                          setAuthError('')
                          setAuthSubmitting(true)
                          try {
                            await signInWithGoogle({
                              subscriptionTier,
                              billingCycle,
                              isOneTimeUse,
                              preferredLanguage: language,
                            })
                            setSignupStep(2)
                          } catch (err) {
                            const { message, showConnectionBanner } = getAuthErrorMessage(err, t)
                            setAuthError(message)
                            if (showConnectionBanner) setConnectionBannerVisible(true)
                          } finally {
                            setAuthSubmitting(false)
                          }
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg"><path d="M17.64 9.2c0-.637-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.715H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.159 6.656 3.58 9 3.58z" fill="#EA4335"/></svg> {t('auth.continueWithGoogle')}
                      </button>
                    </>
                    )}

                    {/* Step 2: Pricing */}
                    {signupStep === 2 && (
                    <>
                      <button 
                        type="button" 
                        className="link-button" 
                        onClick={() => setSignupStep(1)}
                        style={{ alignSelf: 'flex-start', marginBottom: '12px' }}
                      >
                        ← {t('auth.backToAccountDetails')}
                      </button>

                      {authMode === 'signup' && (
                        <div className="one-time-use-link-wrap">
                          <button
                            type="button"
                            className={`one-time-use-link ${showOneTimeOptions ? 'active' : ''}`}
                            onClick={() => {
                              setShowOneTimeOptions((prev) => !prev)
                              if (showOneTimeOptions) {
                                setIsOneTimeUse(false)
                              } else {
                                setSubscriptionTier('premium')
                                setIsOneTimeUse(true)
                              }
                            }}
                          >
                            {showOneTimeOptions ? (
                              <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                                {t('auth.backToSubscriptions')}
                              </>
                            ) : (
                              <>
                                {t('auth.lookingForOneTimeUse')}
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                              </>
                            )}
                          </button>
                        </div>
                      )}
                      {!showOneTimeOptions && (
                      <div className="signup-billing-toggle subscription-billing-toggle">
                        <button
                          type="button"
                          className={`toggle-option ${billingCycle === 'yearly' ? 'is-active' : ''}`}
                          onClick={() => setBillingCycle('yearly')}
                        >
                          {t('auth.billAnnually')}
                        </button>
                        <button
                          type="button"
                          className={`toggle-option ${billingCycle === 'monthly' ? 'is-active' : ''}`}
                          onClick={() => setBillingCycle('monthly')}
                        >
                          {t('auth.billMonthly')}
                        </button>
                      </div>
                      )}
                      {!showOneTimeOptions && (
                      <div className="subscription-tiers signup-tiers">
                      <div
                        className="tier-card highlighted selected"
                        role="button"
                        tabIndex={0}
                        onClick={() => { setSubscriptionTier('premium'); setIsOneTimeUse(false) }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSubscriptionTier('premium'); setIsOneTimeUse(false) } }}
                      >
                        {billingCycle === 'yearly' && <span className="tier-discount-badge">-{tierPricing.premium.discountPercent}%</span>}
                        <div className="tier-header">
                            <div className="tier-icon-wrap">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path></svg>
                            </div>
                            <h4>Premium</h4>
                            <p className="tier-desc">For power users with advanced needs.</p>
                          </div>
                          {billingCycle === 'yearly' && <p className="tier-annual-billing">Billed ${tierPricing.premium.annual}/yr</p>}
                          <div className="tier-price-row">
                            <span className="tier-price">${billingCycle === 'monthly' ? tierPricing.premium.monthly : (tierPricing.premium.annual / 12) % 1 === 0 ? tierPricing.premium.annual / 12 : (tierPricing.premium.annual / 12).toFixed(2)}</span>
                            <span className="tier-period">/mo</span>
                          </div>
                          <ul className="tier-features">
                            <li><CheckIcon /> {t('pricing.featureTemplates25')}</li>
                            <li><CheckIcon /> {t('pricing.featureEmails150')}</li>
                            <li><CheckIcon /> {t('pricing.featureChars30k')}</li>
                            <li><CheckIcon /> {t('pricing.featureAttachments10')}</li>
                            {!appConfig.hideVerificationFeature && <li><CheckIcon /> {t('pricing.featureVerification')}</li>}
                            <li><CheckIcon /> {t('pricing.featureAuditTrail')}</li>
                            <li><CheckIcon /> {t('pricing.featurePdfUpload')}</li>
                            <li><CheckIcon /> {t('pricing.featurePdfExtract')}</li>
                            <li><CheckIcon /> {t('pricing.featureCustomFields')}</li>
                            <li><CheckIcon /> {t('pricing.featureCopyLink')}</li>
                          </ul>
                          <button type="button" className="tier-button tier-button--action">
                            {subscriptionTier === 'premium' ? 'Selected' : 'Select'}
                          </button>
                        </div>
                      </div>
                      )}
                      {showOneTimeOptions && (
                      <div className="subscription-tiers signup-tiers one-time-tiers one-time-single">
                        <div
                          className="tier-card popular highlighted"
                          role="button"
                          tabIndex={0}
                          onClick={() => { setSubscriptionTier('premium'); setIsOneTimeUse(true) }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSubscriptionTier('premium'); setIsOneTimeUse(true) } }}
                        >
                          <span className="tier-badge">ALL FEATURES</span>
                          <h4>One-time use</h4>
                          <p className="tier-desc">Full access for 1 send. Pay again for another send.</p>
                          <ul className="tier-features">
                            <li><CheckIcon /> {t('pricing.featureTemplates25')}</li>
                            <li><CheckIcon /> {t('pricing.featureRecipients10')}</li>
                            <li><CheckIcon /> {t('pricing.oneTimeSendIncluded', { price: tierPricing.oneTimePrice })}</li>
                            <li><CheckIcon /> {t('pricing.featureChars30k')}</li>
                            <li><CheckIcon /> {t('pricing.featureAttachments10')}</li>
                            <li><CheckIcon /> {t('pricing.featureAuditTrail')}</li>
                            <li><CheckIcon /> {t('pricing.featurePdfUpload')}</li>
                            <li><CheckIcon /> {t('pricing.featurePdfExtract')}</li>
                            <li><CheckIcon /> {t('pricing.featureCustomFields')}</li>
                            <li><CheckIcon /> {t('pricing.featureCopyLink')}</li>
                            {!appConfig.hideLogoFeature && <li><CheckIcon /> {t('pricing.featureLogo')}</li>}
                            {!appConfig.hideVerificationFeature && <li><CheckIcon /> {t('pricing.featureVerification')}</li>}
                          </ul>
                          <p className="tier-price">${tierPricing.oneTimePrice} <span>one time</span></p>
                          <button type="button" className="tier-button tier-button--action primary" onClick={(e) => { e.stopPropagation(); setSubscriptionTier('premium'); setIsOneTimeUse(true) }}>Select</button>
                        </div>
                      </div>
                      )}
                      {isOneTimeUse && (
                        <p className="one-time-selected-hint">{`One-time plan: all features, 1 send. Pay $${tierPricing.oneTimePrice} again for each additional send.`}</p>
                      )}
                      <button
                        type="button"
                        className="primary-button full-width"
                        disabled={authSubmitting}
                        onClick={async () => {
                          setAuthError('')
                          if (!authEmail.trim()) {
                            setAuthError(t('auth.enterEmail'))
                            return
                          }
                          if (authPassword.length < 8) {
                            setAuthError(t('auth.passwordMinLength'))
                            return
                          }
                          if (!isValidPassword(authPassword)) {
                            setAuthError(t('auth.passwordSpecial'))
                            return
                          }
                          if (authPassword !== authConfirmPassword) {
                            setAuthError(t('auth.passwordsNoMatch'))
                            return
                          }
                          setAuthSubmitting(true)
                          try {
                            const emailExists = await checkEmailExists(authEmail.trim())
                            if (emailExists) {
                              setIsEmailExistsModalOpen(true)
                              return
                            }
                            if (!isOneTimeUse) {
                              const userData = {
                                firstName: authFirstName.trim(),
                                lastName: authLastName.trim(),
                                subscriptionTier,
                                billingCycle,
                                isOneTimeUse,
                              preferredLanguage: language,
                              }
                              if (!user) { await authSignUp(authEmail.trim(), authPassword, userData); try { if (!openPaywallAfterAuth) sessionStorage.setItem('justCreatedAccount', '1') } catch (_) {} }
                              const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession')
                              const billingInterval = billingCycle === 'yearly' ? 'annual' : 'monthly'
                              const { data } = await createCheckoutSession({ tierId: subscriptionTier, billingInterval, returnOrigin: typeof window !== 'undefined' ? window.location.origin : '' })
                              if (data?.url) {
                                await saveDocumentStateForPaymentReturn()
                                window.location.href = data.url
                                return
                              }
                            } else {
                              const userData = {
                                firstName: authFirstName.trim(),
                                lastName: authLastName.trim(),
                                subscriptionTier,
                                billingCycle,
                                isOneTimeUse,
                                preferredLanguage: language,
                              }
                              const cred = await authSignUp(authEmail.trim(), authPassword, userData)
                              try { if (!openPaywallAfterAuth) sessionStorage.setItem('justCreatedAccount', '1') } catch (_) {}
                              if (premiumOneOff?.link && cred?.user?.uid) {
                                await saveDocumentStateForPaymentReturn(cred.user.uid)
                                window.location.href = getOneOffPaymentUrl(premiumOneOff.link, cred.user.uid)
                              }
                              setIsAuthModalOpen(false)
                            }
                          } catch (err) {
                            const { message, showConnectionBanner } = getAuthErrorMessage(err, t)
                            if (err.code === 'auth/email-already-in-use') {
                              setIsEmailExistsModalOpen(true)
                            } else {
                              setAuthError(message)
                              if (showConnectionBanner) setConnectionBannerVisible(true)
                            }
                          } finally {
                            setAuthSubmitting(false)
                          }
                        }}
                      >
                        {authSubmitting ? (isOneTimeUse ? 'Creating account…' : 'Subscribe') : (isOneTimeUse ? 'Create account' : 'Subscribe')}
                      </button>
                      <p className="auth-footnote">
                        By signing up you agree to our{' '}
                        <a
                          href={locPath('/terms')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link-button"
                        >
                          Terms of Service
                        </a>
                        .
                      </p>
                    </>
                    )}
                  </div>
                )}
              </div>
              );
              return null;
            })()}
          </div>
        )}

        {view === 'blog' && (
          <div className="blog-page">
            <div className="blog-page-inner">
              <h1 className="blog-page-title">{t('blog.pageTitle')}</h1>
              <p className="blog-page-subtitle">{t('blog.pageSubtitle')}</p>
              <div className="blog-list">
                {[...BLOG_POSTS]
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((post) => (
                  <article key={post.slug} className="blog-card">
                    <h2 className="blog-card-title">
                      <a href={locPath(`/blog/${post.slug}`)} className="blog-card-link" onClick={(e) => goToBlogPost(e, post.slug)}>
                        {post.title}
                      </a>
                    </h2>
                    <p className="blog-card-meta">{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    <p className="blog-card-desc">{post.description}</p>
                    <a href={locPath(`/blog/${post.slug}`)} className="blog-card-cta" onClick={(e) => goToBlogPost(e, post.slug)}>Read more</a>
                  </article>
                ))}
              </div>
              <MarketingAiSummary variant="blog" />
            </div>
          </div>
        )}

        {view === 'blog-post' && blogSlug && (() => {
          const post = BLOG_POSTS.find((p) => p.slug === blogSlug)
          if (!post) return null
          return (
            <div className="blog-page blog-post-page">
              <BlogPostingJsonLd
                post={post}
                pageUrl={
                  typeof window !== 'undefined'
                    ? buildSelfCanonicalUrl(window.location.origin, window.location.pathname)
                    : buildAbsoluteUrl(`/blog/${blogSlug}`, language)
                }
              />
              <div className="blog-page-inner blog-post-inner">
                <a href={locPath('/blog')} className="blog-back" onClick={goToBlogList}>← Back to Blog</a>
                <article className="blog-article">
                  <header className="blog-article-header">
                    <h1 className="blog-article-title">{post.title}</h1>
                    <p className="blog-article-meta">{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </header>
                  <div className="blog-article-body">
                    <Suspense fallback={<div className="blog-body-skeleton" aria-hidden />}>
                      <BlogPostBodyLazy slug={blogSlug} tierPricing={tierPricing} />
                    </Suspense>
                  </div>
                </article>
                <MarketingAiSummary variant="blog" />
              </div>
            </div>
          )
        })()}

        {view === 'complete-subscription' && (
          <section className="complete-subscription-screen" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: '40px 24px', textAlign: 'center' }}>
            <h2 style={{ margin: '0 0 12px', fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' }}>Complete your subscription</h2>
            <p style={{ margin: '0 0 24px', fontSize: '1rem', color: '#64748b', maxWidth: 400 }}>You need to select a plan and complete payment before accessing the app.</p>
            <button type="button" className="primary-button" onClick={() => setIsSubscriptionOpen(true)} style={{ padding: '14px 28px', fontSize: '1rem', fontWeight: 600 }}>Choose your plan</button>
          </section>
        )}

        {view === 'app' && (
          <>
            {isRecipientView && recipientRequiresAuth && !recipientAuthCompleted ? (
              <section className="recipient-auth-gate">
                <div className="recipient-auth-card">
                  <h2>Sign in to continue</h2>
                  <p>Please sign in with Google to view and sign this document. This helps us verify your identity.</p>
                  {recipientAuthError && (
                    <p className="recipient-auth-error">{recipientAuthError}</p>
                  )}
                  <div className="recipient-auth-actions">
                    <button
                      type="button"
                      className="primary-button recipient-google-btn"
                      onClick={async () => {
                        setRecipientAuthError('')
                        setRecipientAuthLoading(true)
                        try {
                          await signInWithGoogle({})
                          setRecipientAuthCompleted(true)
                        } catch (err) {
                          const { message, showConnectionBanner } = getAuthErrorMessage(err, t)
                          setRecipientAuthError(message)
                          if (showConnectionBanner) setConnectionBannerVisible(true)
                        } finally {
                          setRecipientAuthLoading(false)
                        }
                      }}
                      disabled={recipientAuthLoading}
                    >
                      {recipientAuthLoading ? (
                        'Signing in…'
                      ) : (
                        <>
                          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                            <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
                            <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-2.7.82c-2.08 0-3.86-1.4-4.5-3.42H1.83v2.07A8 8 0 0 0 8.98 17z"/>
                            <path fill="#FBBC05" d="M4.5 10.46a4.84 4.84 0 0 1 0-3.08V5.31H1.83a8 8 0 0 0 0 7.18l2.67-2.03z"/>
                            <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.3L4.5 7.35a4.77 4.77 0 0 1 4.48-3.17z"/>
                          </svg>
                          Sign in with Google
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="ghost-button recipient-skip-btn"
                      onClick={() => setRecipientAuthCompleted(true)}
                    >
                      Skip auth (for testing)
                    </button>
                  </div>
                </div>
              </section>
            ) : (
            <>
        {/* App tab navigation: brand + Document | Templates | … + language / save / account (signed-in) */}
            {!isRecipientView && isSignedInAppUser && (
          <nav ref={appTabNavRef} className="app-tab-nav app-tab-nav--sticky" aria-label="App navigation">
            <div className="app-tab-nav__brand">
              <button type="button" className="app-tab-nav__brand-btn" onClick={handleBrandClick}>
                <PublicPictureImg name="primedocumentsignlogowhite" alt="Prime Document Sign" className="app-tab-nav__logo" width={320} height={64} decoding="async" />
              </button>
            </div>
            <div className="app-tab-nav__tabs" role="tablist">
              <button type="button" className={`app-tab-nav__tab ${appTab === 'document' ? 'app-tab-nav__tab--active' : ''}`} aria-current={appTab === 'document' ? 'page' : undefined} onClick={() => setAppTab('document')}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                Document
              </button>
              <button type="button" className={`app-tab-nav__tab ${appTab === 'templates' ? 'app-tab-nav__tab--active' : ''}`} aria-current={appTab === 'templates' ? 'page' : undefined} onClick={() => setAppTab('templates')}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                Templates
              </button>
              <button type="button" className={`app-tab-nav__tab ${appTab === 'email-activity' ? 'app-tab-nav__tab--active' : ''}`} aria-current={appTab === 'email-activity' ? 'page' : undefined} onClick={() => setAppTab('email-activity')}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                Email Activity
              </button>
              <button type="button" className={`app-tab-nav__tab ${appTab === 'limits' ? 'app-tab-nav__tab--active' : ''}`} aria-current={appTab === 'limits' ? 'page' : undefined} onClick={() => setAppTab('limits')}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7H14.5a3.5 3.5 0 0 1 0 7H7"/></svg>
                Limits
              </button>
            </div>
            <div className="app-tab-nav__actions">
              <LanguageSelector compact />
              <div className="app-header__actions">
                {!hasSubscribedAccount ? (
                  <button
                    type="button"
                    className="app-header__btn app-header__btn--primary app-header__btn--subscribe-now app-header__btn--subscribe-now--tabnav primary-button"
                    onClick={() => setIsSubscriptionOpen(true)}
                    aria-label={t('app.subscribeNowFromPrice')}
                  >
                    <span className="app-tab-nav__subscribe-label">{t('app.subscribeNowFromPrice')}</span>
                  </button>
                ) : null}
                {showSaveTemplateButton ? (
                  <button
                    type="button"
                    className="app-header__btn app-header__btn--primary app-header__btn--save-template primary-button"
                    onClick={openSaveTemplate}
                    aria-label={t('app.saveTemplate')}
                  >
                    <svg className="app-header__btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8 15 3"/></svg>
                    <span className="app-tab-nav__save-label">{t('app.saveTemplate')}</span>
                  </button>
                ) : null}
                <button
                  type="button"
                  className="app-header__btn app-header__btn--secondary app-header__btn--profile ghost-button"
                  onClick={() => setIsAccountOpen(true)}
                  aria-label={t('app.myAccount')}
                  title={t('app.myAccount')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </button>
              </div>
            </div>
          </nav>
        )}
        {/* ── Templates inline tab ─────────────────────────────── */}
        {isSignedInAppUser && appTab === 'templates' && !isRecipientView && (
          <div className="inline-tab-panel inline-tab-panel--templates">
            <div className="inline-tab-panel__header">
              <h2 className="inline-tab-panel__title">Templates</h2>
              <p className="inline-tab-panel__subtitle">Load a saved template to reuse your document, fields, and settings.</p>
      </div>
            {maxTemplates > 0 && (
              <div className="inline-tab-panel__usage">
                <span className="inline-tab-panel__usage-label">{t('app.templatesUsed')}</span>
                <div className="inline-tab-panel__usage-row">
                  <span className="inline-tab-panel__usage-count"><strong>{templates.length}</strong> / {maxTemplates}</span>
                  <div className="inline-tab-panel__usage-track" role="progressbar" aria-valuenow={templates.length} aria-valuemin={0} aria-valuemax={maxTemplates}>
                    <div className="inline-tab-panel__usage-fill" style={{ width: `${Math.min(100, (templates.length / maxTemplates) * 100)}%` }} />
              </div>
              </div>
                </div>
            )}
            {templates.length === 0 ? (
              <div className="inline-tab-panel__empty">
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/></svg>
                <h4>No templates saved yet</h4>
                <p>Create a document, add your content and signature fields, then save it as a template from the editor.</p>
              </div>
            ) : (
              <div className="inline-tab-panel__list">
                {templates.map((template) => {
                  const signerCount = Array.isArray(template.signers) ? template.signers.length : (template.totalSignatures ?? 1)
                  const customFieldCount = Array.isArray(template.customFields) ? template.customFields.length : 0
                  const updatedAt = template.updatedAt ? new Date(template.updatedAt) : null
                  return (
                    <div key={template.id} className="inline-tab-panel__item">
                      <button type="button" className="inline-tab-panel__item-main" onClick={() => { loadTemplate(template); setAppTab('document'); }}>
                        <div className="inline-tab-panel__item-info">
                          <span className="inline-tab-panel__item-name">{template.name}</span>
                          <span className="inline-tab-panel__item-sub">{template.scopeTitle || 'Untitled agreement'}</span>
                          <div className="inline-tab-panel__item-chips">
                            <span className="inline-tab-panel__chip">{signerCount} signer{signerCount !== 1 ? 's' : ''}</span>
                            {customFieldCount > 0 && <span className="inline-tab-panel__chip">{customFieldCount} custom field{customFieldCount !== 1 ? 's' : ''}</span>}
            </div>
                        </div>
                        {updatedAt && <span className="inline-tab-panel__item-date">Updated {updatedAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
              </button>
                      <button type="button" className="inline-tab-panel__item-delete" onClick={() => setConfirmDeleteState({ title: t('modal.deleteTemplate'), message: t('modal.deleteTemplateConfirm', { name: template.name }), confirmLabel: t('modal.yesDelete'), onConfirm: () => deleteTemplate(template.id) })} title={t('modal.deleteTemplate')}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                        Delete
              </button>
            </div>
                  )
                })}
                </div>
              )}
          </div>
        )}

        {/* ── Email Activity inline tab (email events table) ────────── */}
        {isSignedInAppUser && appTab === 'email-activity' && !isRecipientView && (
          <div className="inline-tab-panel inline-tab-panel--signatures">
            <div className="inline-tab-panel__header">
              <h2 className="inline-tab-panel__title">Email Activity</h2>
              <p className="inline-tab-panel__subtitle">{t('app.emailActivityPageSubtitle')}</p>
            </div>
            <div className="inline-tab-panel__toolbar">
              <label className="field field--inline">
                <span>{t('app.emailActivityMonthFilter')}</span>
                <select value={emailActivityMonthFilter} onChange={(e) => setEmailActivityMonthFilter(e.target.value)} className="email-activity-month-select" aria-label={t('app.emailActivityMonthFilterAria')}>
                  <option value="">{t('app.emailActivityMonthAll')}</option>
                  {[...new Set(emailEvents.map((e) => {
                    const dateRaw = e.sentAt || e.createdAt
                    const d = dateRaw ? new Date(dateRaw) : new Date(e.createdAtMs || 0)
                    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
                  }))].sort().reverse().map((ym) => (
                    <option key={ym} value={ym}>{new Date(ym + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</option>
                  ))}
                </select>
              </label>
            </div>
            {emailActivityLoading ? (
              <p className="email-activity-loading">{t('app.emailActivityLoading')}</p>
            ) : (
              <div className="inline-tab-panel__table-wrap email-activity-table-wrap">
                <table className="email-activity-table" role="grid">
                  <thead>
                    <tr>
                      <th scope="col">{t('app.emailActivityColSent')}</th>
                      <th scope="col">{t('app.emailActivityColReminders')}</th>
                      <th scope="col">{t('app.emailActivityColRecipients')}</th>
                      <th scope="col">{t('app.emailActivityColStatus')}</th>
                      <th scope="col">{t('app.emailActivityColSigners')}</th>
                      <th scope="col">{t('app.emailActivityColSigned')}</th>
                      <th scope="col">{t('app.emailActivityColLink')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailEvents.filter((e) => {
                      if (!emailActivityMonthFilter) return true
                      const dateRaw = e.sentAt || e.createdAt
                      const d = dateRaw ? new Date(dateRaw) : new Date(e.createdAtMs || 0)
                      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` === emailActivityMonthFilter
                    }).map((ev) => {
                      const sentDateRaw = ev.sentAt || ev.createdAt
                      const sentDateStr = sentDateRaw ? new Date(sentDateRaw).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : (ev.createdAt ? new Date(ev.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—')
                      const recipients = (ev.recipientEmails || []).join(', ') || '—'
                      const status = ev.status === 'signed' ? t('app.emailActivityStatusSigned') : t('app.emailActivityStatusSent')
                      const signersList = ev.signers && Array.isArray(ev.signers) && ev.signers.length > 0 ? ev.signers : (ev.signerName || ev.signerEmail) ? [{ signerName: ev.signerName, signerEmail: ev.signerEmail }] : []
                      const signersDisplay = signersList.length > 0 ? signersList.map((s) => s.signerName || s.signerEmail || 'Signer').filter(Boolean).join(', ') : '—'
                      const lastSigned = (() => {
                        const fromSigners = ev.signers?.length > 0 ? ev.signers.reduce((latest, s) => { const d = s.signedDate ? new Date(s.signedDate) : null; return d && (!latest || d > latest) ? d : latest }, null) : null
                        if (fromSigners) return fromSigners
                        const fromEvent = ev.linkDetails?.signedAtIso || ev.metadata?.signedDate || ev.signedDate
                        return fromEvent ? new Date(fromEvent) : null
                      })()
                      const signedDateStr = lastSigned ? lastSigned.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—'
                      const reminderRows = Array.isArray(ev.emailRemindersDelivered) && ev.emailRemindersDelivered.length > 0 ? [...ev.emailRemindersDelivered].filter((r) => r && (r.dayOffset === 1 || r.dayOffset === 3 || r.dayOffset === 7)).sort((a, b) => (a.dayOffset || 0) - (b.dayOffset || 0)) : []
                      return (
                        <tr key={ev.id}>
                          <td>{sentDateStr}</td>
                          <td className="email-activity-reminders">{reminderRows.length === 0 ? t('app.emailActivityReminderNone') : (<ul className="email-activity-reminder-list">{reminderRows.map((r, ri) => { const when = r.sentAt ? new Date(r.sentAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—'; const labelKey = r.dayOffset === 1 ? 'app.emailActivityReminderDay1' : r.dayOffset === 3 ? 'app.emailActivityReminderDay3' : 'app.emailActivityReminderDay7'; return (<li key={`${r.dayOffset}-${ri}`}>{t(labelKey)}{when && when !== '—' ? ` · ${when}` : ''}</li>) })}</ul>)}</td>
                          <td className="email-activity-recipients">{recipients}</td>
                          <td><span className={`email-activity-status email-activity-status--${ev.status}`}>{status}</span></td>
                          <td className="email-activity-signers">{signersDisplay}</td>
                          <td>{signedDateStr}</td>
                          <td>{(ev.agreementHtml === 'expired' || ev.linkDetails?.agreementHtml === 'expired' || ev.signingLinkUrl === 'expired' || ev.linkDetails?.signingLinkUrl === 'expired') ? (<span className="email-activity-expired">{t('app.emailActivityLinkExpired')}</span>) : (() => { const linkUrl = getEmailActivityViewLink(ev); return linkUrl && linkUrl !== 'expired' ? (<a href={linkUrl} target="_blank" rel="noopener noreferrer" className="email-activity-pdf-link">{t('app.emailActivityViewLink')}</a>) : '—' })()}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                {emailEvents.filter((e) => { if (!emailActivityMonthFilter) return true; const dateRaw = e.sentAt || e.createdAt; const d = dateRaw ? new Date(dateRaw) : new Date(e.createdAtMs || 0); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` === emailActivityMonthFilter }).length === 0 && (
                  <p className="email-activity-empty">{t('app.emailActivityEmpty')}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Limits inline tab (usage cards) ────────── */}
        {isSignedInAppUser && appTab === 'limits' && !isRecipientView && (
          <div className="inline-tab-panel inline-tab-panel--limits">
            <div className="inline-tab-panel__header">
              <h2 className="inline-tab-panel__title">Limits</h2>
              <p className="inline-tab-panel__subtitle">Track sends, limits, and signed documents.</p>
            </div>
            <section className="email-activity-dashboard" aria-label="Usage dashboard">
              <div className="email-activity-dashboard-header">
                <h3 className="email-activity-dashboard-title">{t('app.emailActivityUsageSection')}</h3>
              </div>
              <div className="email-activity-dashboard-grid">
                {(userCustomerId || userSubscriptionId) && (
                  <div className="usage-card usage-card--plan">
                    <span className="usage-card-label">{t('app.emailActivityCurrentPlan')}</span>
                    <span className="usage-card-value usage-card-value--plan">{subscriptionTier.charAt(0).toUpperCase() + subscriptionTier.slice(1)}</span>
                  </div>
                )}
                <div className="usage-card usage-card--emails">
                  <span className="usage-card-label">{t('app.emailActivityMonthlySends')}</span>
                  <span className="usage-card-value">{emailsSentThisMonth}<span className="usage-card-of"> / {maxEmailsPerMonth}</span></span>
                  {maxEmailsPerMonth > 0 && (
                    <div className="usage-card-progress" role="progressbar" aria-valuenow={emailsSentThisMonth} aria-valuemin={0} aria-valuemax={maxEmailsPerMonth}>
                      <div className="usage-card-progress-bar" style={{ width: `${Math.min(100, (emailsSentThisMonth / maxEmailsPerMonth) * 100)}%` }} />
                    </div>
                  )}
                </div>
                <div className="usage-card usage-card--reminders">
                  <span className="usage-card-label">{t('app.emailActivityReminderEmailsThisMonth')}</span>
                  <span className="usage-card-value usage-card-value--reminder">{(userUsage.reminderEmailsThisMonth ?? 0)}</span>
                  <span className="usage-card-hint usage-card-hint--reminder">{t('app.emailActivityReminderEmailsHint')}</span>
                </div>
                <div className="usage-card usage-card--templates">
                  <span className="usage-card-label">{t('app.emailActivityTemplatesSaved')}</span>
                  <span className="usage-card-value">{templates.length}<span className="usage-card-of"> / {maxTemplates > 0 ? maxTemplates : '∞'}</span></span>
                  {maxTemplates > 0 && (
                    <div className="usage-card-progress" role="progressbar" aria-valuenow={templates.length} aria-valuemin={0} aria-valuemax={maxTemplates}>
                      <div className="usage-card-progress-bar" style={{ width: `${Math.min(100, (templates.length / maxTemplates) * 100)}%` }} />
                    </div>
                  )}
                </div>
                <div className="usage-card usage-card--signed">
                  <span className="usage-card-label">{t('app.emailActivitySignedThisMonth')}</span>
                  <span className="usage-card-value">{currentEmailsSignedPerMonth}<span className="usage-card-of"> / {maxEmailsSignedPerMonth}</span></span>
                  {maxEmailsSignedPerMonth > 0 && (
                    <div className="usage-card-progress" role="progressbar" aria-valuenow={currentEmailsSignedPerMonth} aria-valuemin={0} aria-valuemax={maxEmailsSignedPerMonth}>
                      <div className="usage-card-progress-bar" style={{ width: `${Math.min(100, (currentEmailsSignedPerMonth / maxEmailsSignedPerMonth) * 100)}%` }} />
                    </div>
                  )}
                </div>
                {(userCustomerId || userSubscriptionId) && (
                  <>
                    <div className="usage-card usage-card--topup-emails">
                      <span className="usage-card-label">{t('app.emailActivityTopUpSends')}</span>
                      <span className="usage-card-value">{(userUsage.subscribedEmailTopUpCredit ?? 0)} <span className="usage-card-of">{t('app.emailActivityRemaining')}</span></span>
                    </div>
                    <div className="usage-card usage-card--topup-signed">
                      <span className="usage-card-label">{t('app.emailActivityTopUpSigned')}</span>
                      <span className="usage-card-value">{(userUsage.subscribedSignedAgreementTopUpCredit ?? 0)} <span className="usage-card-of">{t('app.emailActivityRemaining')}</span></span>
                    </div>
                  </>
                )}
              </div>
            </section>
          </div>
        )}

        {/* ── Document tab ─────────────────────────────────────── */}
        {((!isSignedInAppUser && !isRecipientView) || appTab === 'document' || isRecipientView) && <section className={`document ${!isRecipientView ? 'document--create' : ''} ${!isRecipientView && (showFreeSendBanner || (hasSubscribedAccount && ((userUsage.subscribedEmailTopUpCredit ?? 0) > 0 || (userUsage.subscribedSignedAgreementTopUpCredit ?? 0) > 0)) || (!hasSubscribedAccount && (emailOneOffCredits || 0) > 0)) ? 'document--has-credits-bar' : ''}`} ref={documentRef}>
        {!isRecipientView && (
            <>
            {/* Free send + credit banners below the blue line, above the first card */}
            {(showFreeSendBanner || (hasSubscribedAccount && ((userUsage.subscribedEmailTopUpCredit ?? 0) > 0 || (userUsage.subscribedSignedAgreementTopUpCredit ?? 0) > 0)) || (!hasSubscribedAccount && (emailOneOffCredits || 0) > 0)) && (
              <div className="document-credits-wrap">
                {showFreeSendBanner && (
                  <div className="document-free-send-banner document-credits-banner" role="status">
                    <span className="document-free-send-banner__text">{t('app.freeSendBanner')}</span>
      </div>
                )}
                {(hasSubscribedAccount && ((userUsage.subscribedEmailTopUpCredit ?? 0) > 0 || (userUsage.subscribedSignedAgreementTopUpCredit ?? 0) > 0)) && (
                  <div className="send-card-topup-banner document-credits-banner">
                    <span className="send-card-topup-label">{t('app.topUpCredits')}</span>
                    <span className="send-card-topup-value">{(userUsage.subscribedEmailTopUpCredit ?? 0)} {t('app.emailSends')}</span>
                    <span className="send-card-topup-sep">·</span>
                    <span className="send-card-topup-value">{(userUsage.subscribedSignedAgreementTopUpCredit ?? 0)} {t('app.signedAgreements')}</span>
                    <span className="send-card-topup-hint">{t('app.usedAfterAllowance')}</span>
              </div>
                )}
                {!hasSubscribedAccount && (emailOneOffCredits || 0) > 0 && (
                  <div className="send-card-credits-banner document-credits-banner send-card-credits-banner--has-credits">
                    <span className="send-card-credits-label">{t('app.sendCredits')}</span>
                    <span className="send-card-credits-value">{emailOneOffCredits ?? 0}</span>
                    <span className="send-card-credits-hint">{t('app.creditEqualsSend')}</span>
          </div>
        )}
              </div>
            )}
            </>
          )}
          {!isRecipientView && (
            <>
            <div className={`start-from-template-row ${!isSignedInAppUser ? 'start-from-template-row--guest' : ''}`}>
                  <button
                    type="button"
                className="ghost-button start-from-template-btn"
                onClick={() => setIsStartFromTemplateOpen(true)}
                  >
                {t('app.startFromTemplate')}
                  </button>
                </div>
            <div id="doc-section-1" className="admin-editor document-section-jump">
              <div className="document-section-header document-section-header--branded document-section-header--with-pdf">
                <div className="section-header-bar">
                  <div className="document-section-header__title">
                    <span className="section-badge">1</span>
                    <span className="section-icon" aria-hidden>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                    </span>
                    <span>{t('app.agreementText')}</span>
              </div>
                </div>
                <div className="document-section-header__pdf">
                  <input ref={pdfInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={handlePdfUpload} />
                  <input ref={fullPdfInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={handleFullPdfUpload} />
                  <div className="pdf-upload-row pdf-upload-row--centered">
                    <div className="pdf-upload-group">
                      <button type="button" className="ghost-button pdf-upload-button" onClick={() => pdfInputRef.current?.click()} disabled={isPdfLoading || isFullPdfLoading || !canUsePdfExtract} title={!canUsePdfExtract ? 'PDF import is not included on your current plan.' : undefined}>
                        {t('app.uploadPdfExtract')}
              </button>
                      <button type="button" className="help-icon pdf-help-icon" aria-label="Extract text help" data-tooltip="Extracts text from each page in order and inserts it into the agreement editor below. You can then edit the text. Does not extract images, tables, or complex layouts, text only. Scanned or image-based PDFs may not work well.">?</button>
            </div>
                    <div className="pdf-upload-group pdf-upload-group-full">
                      <button type="button" className="ghost-button pdf-upload-button" onClick={() => fullPdfInputRef.current?.click()} disabled={isPdfLoading || isFullPdfLoading || !canUsePdfExtract} title={!canUsePdfExtract ? 'PDF import is not included on your current plan.' : undefined}>
                        {t('app.fullPdfUpload')}
              </button>
                      <button type="button" className="help-icon pdf-help-icon" aria-label="Full PDF upload help" data-tooltip="Displays the full PDF with images, layout, and formatting exactly as in the original file. Use this when you need to show the document as-is. You can optionally add agreement text below the PDF.">?</button>
            </div>
                    {fullPdfPages.length > 0 && (
                      <button type="button" className="ghost-button pdf-clear-button" onClick={clearFullPdf}>Clear PDF</button>
              )}
            </div>
                  {(isPdfLoading || isFullPdfLoading) && (
                    <div className="pdf-progress-wrap">
                      <p className="pdf-progress-message">{isFullPdfLoading ? t('app.loadingPdf') : 'Extracting text…'} This could take a moment.</p>
                      <div className="pdf-progress-bar"><div className="pdf-progress-fill" style={{ width: `${pdfProgress}%` }} /></div>
                    </div>
                  )}
                  {pdfError && <p className="pdf-upload-error">{pdfError}</p>}
          </div>
              </div>
              {fullPdfPages.length === 0 && (
              <div className="agreement-editor-frame">
              <div className="rich-toolbar">
                <div className="rich-tools">
                  <div className="rich-tools-font-section">
                    <div className="rich-tools-font-row">
                  <label className="rich-select font-family-select" data-tooltip="Font" onMouseDown={saveFormatSelection}>
                    <select
                      className="rich-select-control"
                      onChange={(event) => {
                            const value = event.target.value
                            if (!value) return
                            const opt = FONT_OPTIONS.find((f) => f.name === value)
                            if (opt) applyFontFamilyInline(opt.value)
                          }}
                          value={activeFormats.fontFamily}
                        >
                          {FONT_OPTIONS.map(({ name, value }) => (
                            <option key={name} value={name} style={{ fontFamily: value }}>
                              {name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="rich-select font-size-select" data-tooltip="Font size" onMouseDown={saveFormatSelection}>
                        <select
                          className="rich-select-control"
                          onChange={(event) => {
                            const value = event.target.value
                            if (!value) return
                            applyFontSizeInline(value)
                          }}
                          value={activeFormats.fontSize}
                        >
                          {FONT_SIZE_OPTIONS.map((size) => (
                            <option key={size} value={String(size)}>
                              {size}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>
                  <div className="rich-tools-format-grid">
                      <div className="highlight-dropdown-wrap" ref={highlightRef}>
                        <button
                          type="button"
                          className={`rich-button highlight-trigger ${isHighlightOpen ? 'is-active' : ''}`}
                          onMouseDown={(event) => {
                            event.preventDefault()
                            const sel = window.getSelection()
                            if (editorRef.current && sel && sel.rangeCount > 0 && editorRef.current.contains(sel.anchorNode)) {
                              savedSelectionRef.current = sel.getRangeAt(0).cloneRange()
                            } else {
                              savedSelectionRef.current = null
                            }
                          }}
                          onClick={() => setIsHighlightOpen((prev) => !prev)}
                          data-tooltip="Highlight"
                          title="Highlight"
                        >
                          <svg className="highlight-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                            <path fill="#94a3b8" d="M8 2h8l3 6v12l-2 2H7l-2-2V8L8 2z" />
                            <path fill={selectedHighlightColor === 'none' ? 'transparent' : selectedHighlightColor} d="M9 8h6v10l-1 2h-4l-1-2V8z" />
                          </svg>
                        </button>
                        {isHighlightOpen && (
                          <div className="highlight-dropdown">
                            {HIGHLIGHT_COLORS.map(({ name, value }) => (
                              <button
                                key={value}
                                type="button"
                                className={`highlight-dropdown-option ${selectedHighlightColor === value ? 'is-selected' : ''}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setSelectedHighlightColor(value)
                                  if (value === 'none') {
                                    removeHighlightInline()
                                  } else {
                                    applyHighlightInline(value)
                                  }
                                  setIsHighlightOpen(false)
                                }}
                                title={value === 'none' ? 'Remove highlight' : `Highlight ${name}`}
                              >
                                <span className={`highlight-option-swatch ${value === 'none' ? 'highlight-option-none' : ''}`} style={value === 'none' ? {} : { backgroundColor: value }} />
                                <span>{name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="highlight-dropdown-wrap text-color-dropdown-wrap" ref={textColorRef}>
                        <button
                          type="button"
                          className={`rich-button text-color-trigger ${isTextColorOpen ? 'is-active' : ''}`}
                          onMouseDown={(event) => {
                            event.preventDefault()
                            const sel = window.getSelection()
                            if (editorRef.current && sel && sel.rangeCount > 0 && editorRef.current.contains(sel.anchorNode)) {
                              savedSelectionRef.current = sel.getRangeAt(0).cloneRange()
                            } else {
                              savedSelectionRef.current = null
                            }
                            setIsTextColorOpen((prev) => !prev)
                          }}
                          data-tooltip="Text color"
                          title="Text color"
                        >
                          <span className="text-color-a" style={{ color: selectedTextColor }}>A</span>
                        </button>
                        {isTextColorOpen && (
                          <div className="highlight-dropdown text-color-dropdown">
                            {TEXT_COLORS.map(({ name, value }) => (
                              <button
                                key={value}
                                type="button"
                                className={`highlight-dropdown-option ${selectedTextColor === value ? 'is-selected' : ''}`}
                                onMouseDown={(e) => {
                                  e.preventDefault()
                                  setSelectedTextColor(value)
                                  applyTextColorInline(value)
                                  setIsTextColorOpen(false)
                                }}
                                title={`Text color ${name}`}
                              >
                                <span className={`highlight-option-swatch text-color-swatch ${value.toLowerCase() === '#ffffff' ? 'text-color-swatch-white' : ''}`} style={{ backgroundColor: value }} />
                                <span>{name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="rich-button rich-icon"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={openLinkModal}
                        data-tooltip="Link"
                      >
                        <svg className="icon-link" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                          <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                          <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                        </svg>
                      </button>
                  <button
                    type="button"
                    className={`rich-button rich-icon ${
                      activeFormats.bold ? 'is-active' : ''
                    }`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => applyFormat('bold')}
                    data-tooltip="Bold"
                  >
                    B
                  </button>
                  <button
                    type="button"
                    className={`rich-button rich-icon ${
                      activeFormats.italic ? 'is-active' : ''
                    }`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => applyFormat('italic')}
                    data-tooltip="Italic"
                  >
                    I
                  </button>
                  <button
                    type="button"
                    className={`rich-button rich-icon ${
                      activeFormats.underline ? 'is-active' : ''
                    }`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => applyFormat('underline')}
                    data-tooltip="Underline"
                  >
                    U
                  </button>
                  <button
                    type="button"
                    className={`rich-button rich-icon ${
                      activeFormats.strike ? 'is-active' : ''
                    }`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => applyFormat('strikeThrough')}
                    data-tooltip="Strike"
                  >
                    S
                  </button>
                  <button
                    type="button"
                    className={`rich-button rich-icon ${
                      activeFormats.unordered ? 'is-active' : ''
                    }`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => applyFormat('insertUnorderedList')}
                    data-tooltip="List"
                  >
                    <span className="icon-lines" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className={`rich-button rich-icon ${
                      activeFormats.ordered ? 'is-active' : ''
                    }`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => applyFormat('insertOrderedList')}
                    data-tooltip="Numbered list"
                  >
                    <span className="icon-numbers" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="rich-button rich-icon"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={handleUndo}
                    data-tooltip="Undo"
                  >
                    ↺
                  </button>
                  </div>
                  <span className="rich-tool-divider rich-tool-divider--after-format" aria-hidden="true" />
                  <button
                    type="button"
                    className="rich-button rich-icon rich-toolbar-redo"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={handleRedo}
                    data-tooltip="Redo"
                  >
                    ↻
                  </button>
                </div>
                    <button
                      type="button"
                      className="rich-button rich-button--clear-format"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={clearEditorSelections}
                    >
                      Clear selections
                    </button>
                  </div>

              <label className="field agreement-field-with-pdf field--required">
                <div
                  ref={(el) => {
                    editorRef.current = el
                    if (!el) return
                    if (pendingEditorHtmlRef.current) {
                      const html = pendingEditorHtmlRef.current
                      el.innerHTML = html
                      lastValidHtmlRef.current = html
                      pendingEditorHtmlRef.current = null
                      try {
                        sessionStorage.removeItem(PAYMENT_RETURN_STORAGE_KEY)
                        localStorage.removeItem(PAYMENT_RETURN_LOCAL_KEY)
                      } catch (_) {}
                      return
                    }
                    // After account creation we show loading then remount the editor empty; state still has content. Restore from lastValidHtmlRef.
                    const editorText = (el.innerHTML || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().replace(/\s/g, '')
                    const savedText = (lastValidHtmlRef.current || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().replace(/\s/g, '')
                    if (!editorText && savedText) {
                      el.innerHTML = lastValidHtmlRef.current
                    }
                  }}
                  className={`rich-editor ${isAgreementContentEmpty ? 'is-empty' : ''}`}
                  contentEditable
                  data-placeholder="Click here to start and create agreement text in here...."
                  onInput={handleEditorInput}
                  onKeyDown={handleEditorKeyDown}
                  onPaste={handleEditorPaste}
                />
                <span className={`char-count ${(displayCharCount >= charLimit * 0.8 || displaySizeKb >= maxAgreementSizeKb * 0.8) ? 'char-count--warning' : ''}`}>
                  {displayCharCount}/{charLimit} characters
                  {' · '}
                  {displaySizeKb.toFixed(2)}/{maxAgreementSizeKb} KB
                </span>
              </label>
              </div>
              )}

              {fullPdfPages.length > 0 && (
              <div className="field full-pdf-viewer-wrap">
                <div className="full-pdf-header">
                  <span>PDF document</span>
                </div>
                <div className="full-pdf-pages">
                  {fullPdfPages.map((page) => (
                    <div key={page.pageIndex} className="full-pdf-page">
                      <img
                        src={page.imageDataUrl}
                        alt={t('app.pdfAgreementPageAlt', { page: page.pageIndex })}
                        className="full-pdf-page-img"
                      />
                    </div>
                  ))}
                </div>
              </div>
              )}

              {canUseAttachments && (
              <div className={`field attachment-field ${attachments.length > 0 && attachments.reduce((s, f) => s + f.size, 0) > effectiveAttachmentLimit ? 'attachment-field--over-limit' : ''}`}>
                <div className="attachment-label-row">
                  <span>{t('app.attachments')}</span>
                  <button
                    type="button"
                    className="help-icon attachment-help-icon"
                    aria-label="Attachments help"
                    data-tooltip={t('app.attachmentsHint')}
                  >
                    ?
                  </button>
                </div>
                <div className="attachment-actions-row">
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files || [])
                      const limit = maxAttachments
                      setAttachmentError('')
                    let currentTotal = attachments.reduce((s, f) => s + f.size, 0)
                      const valid = []
                    let skipped = false
                      for (const file of files) {
                        if (valid.length + attachments.length >= limit) {
                          setAttachmentError(`Maximum ${limit} attachments allowed.`)
                        skipped = true
                        break
                      }
                      if (currentTotal + file.size > effectiveAttachmentLimit) {
                        setAttachmentError(`Total size would exceed ${(effectiveAttachmentLimit / 1024 / 1024).toFixed(0)} MB limit. Some files were not added.`)
                        skipped = true
                        break
                        }
                        valid.push(file)
                      currentTotal += file.size
                      }
                      setAttachments((prev) =>
                        [...prev, ...valid].slice(0, limit)
                      )
                      e.target.value = ''
                    }}
                  />
                  <button
                    type="button"
                    className="ghost-button attachment-add"
                    onClick={() => attachmentInputRef.current?.click()}
                    disabled={attachments.length >= maxAttachments}
                  >
                  {t('app.addAttachment')}
                  </button>
                  {showSaveTemplateButton && (
                    <button
                      type="button"
                      className="ghost-button"
                      onClick={openSaveTemplate}
                    >
                      {t('app.saveTemplate')}
                    </button>
                  )}
                </div>
                {attachmentError && <p className="attachment-error">{attachmentError}</p>}
                {attachments.length > 0 && (
                  <>
                    <p className={`attachment-total ${attachments.reduce((s, f) => s + f.size, 0) > effectiveAttachmentLimit ? 'attachment-total--over' : ''}`}>
                      Total: {(attachments.reduce((s, f) => s + f.size, 0) / 1024 / 1024).toFixed(2)} MB / {(effectiveAttachmentLimit / 1024 / 1024).toFixed(0)} MB
                      {attachments.reduce((s, f) => s + f.size, 0) > effectiveAttachmentLimit && (
                        <span className="attachment-over-warning"> (over limit, reduce size to send)</span>
                      )}
                    </p>
                    <ul className="attachment-list">
                      {attachments.map((file, i) => (
                        <li key={`${file.name}-${i}`}>
                          <span className="attachment-name">{file.name}</span>
                          <span className="attachment-size">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                          <button
                            type="button"
                            className="attachment-remove"
                            aria-label={`Remove ${file.name}`}
                            onClick={() => setConfirmDeleteState({
                              title: t('modal.removeAttachment'),
                              message: t('modal.removeAttachmentConfirm', { name: file.name }),
                              confirmLabel: t('modal.yesRemoveLabel'),
                              onConfirm: () => setAttachments((prev) => prev.filter((_, j) => j !== i)),
                            })}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              )}
            </div>
            </>
          )}

          {!appConfig.hideLogoFeature && (isRecipientView ? logoDataUrl : (canUseLogo && logoDataUrl)) && logoLocation.startsWith('top-') && (
            <div className={`document-logo document-logo--${logoLocation}`}>
              <img src={logoDataUrl} alt={t('app.documentSenderLogoAlt')} />
            </div>
          )}
          {isRecipientView && fullPdfPages.length > 0 && (
            <div className="full-pdf-pages full-pdf-pages--readonly">
              {fullPdfPages.map((page) => (
                <div key={page.pageIndex} className="full-pdf-page">
                  <img
                    src={page.imageDataUrl}
                    alt={t('app.pdfAgreementPageAlt', { page: page.pageIndex })}
                    className="full-pdf-page-img"
                  />
                </div>
              ))}
            </div>
          )}
          {isRecipientView && (
            <div
              className="agreement-text agreement-text--readonly"
              dangerouslySetInnerHTML={{ __html: sanitizedAgreementHtml }}
              role="article"
              aria-readonly="true"
            />
          )}

          <div className="form-grid-with-include">
            <div id="doc-section-2" className={`signature-section-wrap document-section-jump ${!isRecipientView ? 'signature-section-wrap--branded' : ''}`}>
              {!isRecipientView ? (
                <div className="section-header-bar">
                  <div className="signature-section-header signature-section-header--branded">
                    <span className="section-badge">2</span>
                    <span className="section-icon" aria-hidden>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                    </span>
                    <span>{t('app.signatureSection')}</span>
                    <span className="signature-section-review-note">Review below - Add / Remove fields and change layout ( Optional )</span>
                  </div>
                </div>
              ) : (
                <div className="signature-section-header">{t('app.signatureSection')}</div>
              )}
              {!isRecipientView && (
                <>
                <div className="form-total-signatures-section form-total-signatures-section--merged">
                  <div className="form-sig-left form-sig-left--also-sign">
                    <label className="also-need-to-sign-label">
                      <input
                        type="checkbox"
                        className="also-need-to-sign-input"
                        checked={totalSignatures === 2}
                        onChange={(e) => setTotalSignatures(e.target.checked ? 2 : 1)}
                      />
                      <span className="also-need-to-sign-text">
                        {t('app.alsoNeedToSign')}
                        <span
                          className="include-help"
                          data-tooltip={t('app.alsoNeedToSignTooltip')}
                          aria-label={t('app.alsoNeedToSignTooltip')}
                        >
                          ?
                        </span>
                      </span>
                    </label>
                    <p className="also-need-to-sign-hint">
                      {totalSignatures === 2 ? t('app.alsoNeedToSignHintTwo') : t('app.alsoNeedToSignHintOne')}
                    </p>
                  </div>
                  <div className="form-sig-center">
                    <span className="form-include-label">
                      {t('app.createNewField')}
                      <span
                        className="include-help add-field-help"
                        data-tooltip={t('app.addFieldTooltip', { max: appConfig.maxCustomFields ?? 20 })}
                        aria-label="Create new field help"
                      >
                        ?
                      </span>
                    </span>
                    <button
                      type="button"
                      className="add-custom-field-btn"
                      onClick={() => {
                        setAddFieldLabel('')
                        setAddFieldType('text')
                        setAddFieldError('')
                        setIsAddFieldOpen(true)
                      }}
                      disabled={customFields.length >= (appConfig.maxCustomFields ?? 20) || signingSectionSize.atLimit}
                      title={signingSectionSize.atLimit ? `Signing section limit (${signatureTextLimitKB} KB) reached. Remove some content first.` : undefined}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                      {t('app.addField')}
                    </button>
                  </div>
                  <div className="form-sig-right">
                    <span className="form-include-label">
                      {t('app.layout')}
                      <span
                        className="include-help"
                        data-tooltip="Layout controls how signature fields are arranged for each signer. Use Edit layout to switch between 1 or 2 columns, reorder fields, and move anything that gets too crowded so the signing section stays clean and easy to complete."
                        aria-label="Layout help"
                      >
                        ?
                      </span>
                    </span>
                    <div className="form-sig-right-controls">
                      {!isSignedComplete && (
                        <button type="button" className="layout-edit-btn" onClick={openLayoutEditor}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                          {t('app.editLayout')}
                        </button>
                      )}
                      {layoutColumns === 2 && (
                        <span className="layout-cols-badge">2 columns</span>
                      )}
                    </div>
                  </div>
                </div>
                <p className={`signing-section-size${signingSectionSize.isLarge ? ' signing-section-size--warning' : ''}${signingSectionSize.atLimit ? ' signing-section-size--limit' : ''}`} title={`Total size of signature field values (text, dates, signatures). Maximum ${signatureTextLimitKB} KB per plan.`}>
                  {signingSectionSize.kb} / {signatureTextLimitKB} KB
                  <span className="signing-section-size-label">signing section</span>
                </p>
                </>
              )}
              <div className="form-signers-grid">
              {(!isRecipientView && totalSignatures === 2 ? [1, 0] : signers.map((_, idx) => idx)).map((i) => {
                const signer = signers[i]
                if (!signer) return null
                const isSigner1ReadOnly = isRecipientView && totalSignatures === 2 && i === 0
                const creatorRecipientSlotLocked = !isRecipientView && totalSignatures === 2 && i === 1
                const creatorSingleRecipientLocked = !isRecipientView && totalSignatures === 1 && i === 0
                const recipientPreviewLocked = creatorRecipientSlotLocked || creatorSingleRecipientLocked
                const isFieldReadOnly = isSignedComplete || isSigner1ReadOnly || recipientPreviewLocked
                const activeLayout = getActiveLayout(i)
                const col0 = activeLayout.filter((f) => f.column === 0)
                const col1 = activeLayout.filter((f) => f.column === 1)
                const showSignerTitle = signers.length > 1 || (!isRecipientView && totalSignatures === 1)
                let blockTitle = `Signer ${i + 1}`
                if (!isRecipientView && totalSignatures === 2) {
                  blockTitle = i === 1 ? t('app.signerBlockRecipient') : t('app.signerBlockYou')
                } else if (!isRecipientView && totalSignatures === 1) {
                  blockTitle = t('app.signerBlockRecipient')
                }
                return (
                <div key={i} className={`signer-block${layoutColumns === 2 ? ' signer-block--wide' : ''}${recipientPreviewLocked ? ' signer-block--preview-locked' : ''}`}>
                  {showSignerTitle && (
                    <h4 className="signer-block-title">{blockTitle}</h4>
                  )}
                  {creatorRecipientSlotLocked && (
                    <p className="signer-block-locked-hint">{t('app.recipientSignerLockedHint')}</p>
                  )}
                  {creatorSingleRecipientLocked && (
                    <p className="signer-block-locked-hint">{t('app.singleRecipientSignerLockedHint')}</p>
                  )}
                  {layoutColumns === 2 ? (
                    <div className="form-fields-2col">
                      <div className="form-fields-col">
                        {col0.map((f) => renderSignerField(f.id, signer, i, isFieldReadOnly, f.label, recipientPreviewLocked))}
                      </div>
                      <div className="form-fields-col">
                        {col1.length > 0
                          ? col1.map((f) => renderSignerField(f.id, signer, i, isFieldReadOnly, f.label, recipientPreviewLocked))
                          : <div className="form-fields-col-empty">{t('app.noFields')}</div>
                        }
                      </div>
                    </div>
                  ) : (
                    <div className="form-fields-column">
                      {activeLayout.map((f) => renderSignerField(f.id, signer, i, isFieldReadOnly, f.label, recipientPreviewLocked))}
                    </div>
                  )}
                </div>
              )})}
              </div>
            </div>
            {!isRecipientView && canUseLogo && !appConfig.hideLogoFeature && (
              <div className="logo-section">
                <span className="form-include-label">Logo</span>
                <p className="logo-hint">
                  Small image (max {logoMaxBytes >= 1024 ? `${(logoMaxBytes / 1024).toFixed(0)}KB` : `${logoMaxBytes}B`}) shown in the document.
                  {logoDataUrl && (
                    <span className="logo-size"> Size: {(new TextEncoder().encode(logoDataUrl).length / 1024).toFixed(2)} KB</span>
                  )}
                </p>
                <div className="logo-location-row">
                  <label className="logo-location-label">Logo location</label>
                  <select
                    className="logo-location-select"
                    value={logoLocation}
                    onChange={(e) => setLogoLocation(e.target.value)}
                  >
                    <option value="top-left">Top left</option>
                    <option value="top-center">Top center</option>
                    <option value="top-right">Top right</option>
                    <option value="bottom-left">Bottom left</option>
                    <option value="bottom-center">Bottom center</option>
                    <option value="bottom-right">Bottom right</option>
                  </select>
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleLogoSelect}
                />
                <div className="logo-controls">
                  <button
                    type="button"
                    className="ghost-button"
                    onClick={() => logoInputRef.current?.click()}
                  >
                    {logoDataUrl ? 'Change logo' : '+ Add logo'}
                  </button>
                  {logoDataUrl && (
                    <button
                      type="button"
                      className="ghost-button logo-remove"
                      onClick={() => setConfirmDeleteState({
                        title: t('modal.removeLogo'),
                        message: t('modal.removeLogoConfirm'),
                        confirmLabel: t('modal.yesRemoveLabel'),
                        onConfirm: () => setLogoDataUrl(null),
                      })}
                    >
                      Remove
                    </button>
                  )}
                </div>
                {logoDataUrl && (
                  <div className="logo-preview">
                    <img src={logoDataUrl} alt={t('app.documentSenderLogoAlt')} />
                  </div>
                )}
              </div>
            )}
            {!isRecipientView && canUseVerification && !appConfig.hideVerificationFeature && (
              <div className="verification-section">
                <span className="form-include-label">Verification</span>
                <p className="verification-hint">Require recipients to complete these checks before signing. (Premium)</p>
                <div className="verification-options">
                  <label className="verification-option">
                    <span className="verification-row">
                      <input
                        type="checkbox"
                        checked={verifyPhoto}
                        onChange={(e) => setVerifyPhoto(e.target.checked)}
                      />
                      <span className="verification-label">Photo verification</span>
                    </span>
                    <span className="verification-desc">Require a selfie or photo ID check</span>
                  </label>
                  <label className="verification-option">
                    <span className="verification-row">
                      <input
                        type="checkbox"
                        checked={verifyEmail}
                        onChange={(e) => setVerifyEmail(e.target.checked)}
                      />
                      <span className="verification-label">Email verification</span>
                    </span>
                    <span className="verification-desc">Confirm recipient owns the email address</span>
                  </label>
                  <label className="verification-option">
                    <span className="verification-row">
                      <input
                        type="checkbox"
                        checked={verifyOTP}
                        onChange={(e) => setVerifyOTP(e.target.checked)}
                      />
                      <span className="verification-label">One-time password (OTP)</span>
                    </span>
                    <span className="verification-desc">Send a code to verify identity</span>
                  </label>
                  <label className="verification-option">
                    <span className="verification-row">
                      <input
                        type="checkbox"
                        checked={verifyPhone}
                        onChange={(e) => setVerifyPhone(e.target.checked)}
                      />
                      <span className="verification-label">Phone verification</span>
                    </span>
                    <span className="verification-desc">Verify via SMS or phone call</span>
                  </label>
                </div>
              </div>
            )}
            {!appConfig.hideLogoFeature && (isRecipientView ? logoDataUrl : (canUseLogo && logoDataUrl)) && !logoLocation.startsWith('top-') && (
              <div className={`document-logo document-logo--${isRecipientView ? logoLocation : 'bottom-center'}`}>
                <img src={logoDataUrl} alt={t('app.documentSenderLogoAlt')} />
              </div>
            )}
            {!isRecipientView && (
              <>
            <section id="doc-section-3" className="send-card send-card--branded has-section-header-bar document-section-jump">
                {sendAreaBanner != null && <div className="send-area-banner">{sendAreaBanner}</div>}
                <div className="section-header-bar section-header-bar--first">
                  <div className="send-card-header send-section-header--branded">
                    <span className="section-badge">3</span>
                    <span className="section-icon" aria-hidden>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    </span>
                    <span>{t('app.recipientsSection')}</span>
                  </div>
                </div>
                <div className="send-row send-row--stacked">
                  <div className="send-field field--required">
                    <label htmlFor="sender-name">{t('app.fromNameOrg')}</label>
                    <input
                      id="sender-name"
                      type="text"
                      placeholder={t('app.fromNamePlaceholder')}
                      value={senderName}
                      onChange={(event) => setSenderName(event.target.value)}
                    />
                  </div>
                  <div className="send-field field--required">
                    <label htmlFor="sender-email">{t('app.fromEmail')}</label>
                    <input
                      id="sender-email"
                      type="email"
                      placeholder={t('app.fromEmailPlaceholder')}
                      value={senderEmail}
                      onChange={(event) => setSenderEmail(event.target.value)}
                    />
                  </div>
                  <div className="send-field recipient-field field--required">
                    <div className="recipient-label">
                      <label htmlFor="recipient-email" className="recipient-label__title">{t('app.recipientEmail')}</label>
                      <div className="recipient-label__hint-row">
                        <span className="recipient-label__hint">({t('app.recipientHint', { max: maxRecipients })})</span>
                        <button
                          type="button"
                          className="help-icon"
                          aria-label="Recipient email help"
                          data-tooltip={t('app.recipientTooltip')}
                        >
                          ?
                        </button>
                      </div>
                    </div>
                    <input
                      ref={recipientEmailInputRef}
                      id="recipient-email"
                      type="email"
                      className={highlightRecipientEmailField ? 'recipient-email-input--error' : ''}
                      placeholder={t('app.recipientPlaceholder')}
                      value={recipientEmail}
                      onChange={(event) => setRecipientEmail(event.target.value)}
                      onFocus={() => setHighlightRecipientEmailField(false)}
                    />
                  </div>
                <div className="send-actions">
                  <button
                    type="button"
                    className="ghost-button"
                    onClick={() => setIsClearConfirmOpen(true)}
                    title={t('app.clearEverythingTitle')}
                  >
                    {t('app.clearEverything')}
                  </button>
                  {(canUseTemplates || !user) && (
                    <button
                      type="button"
                      className={`ghost-button copy-link-btn ${isCopyLinkLoading ? 'is-loading' : ''} ${!(userSubscriptionId || userCustomerId) ? 'copy-link-btn--subscribers-only' : ''}`}
                      onClick={handleCopyLink}
                      disabled={isCopyLinkLoading || !(userSubscriptionId || userCustomerId)}
                      aria-busy={isCopyLinkLoading}
                      title={!(userSubscriptionId || userCustomerId) ? t('app.forSubscribersOnly') : undefined}
                    >
                      {isCopyLinkLoading ? (
                        <>
                          <span className="copy-link-spinner" aria-hidden />
                          {t('app.generatingLink')}
                        </>
                      ) : !(userSubscriptionId || userCustomerId) ? (
                        <span className="copy-link-btn__label">{t('app.copyLinkSubscribersOnly')}</span>
                      ) : (
                        t('app.copyLink')
                      )}
                    </button>
                  )}
                </div>
                </div>
              </section>

              <section id="doc-section-4" className="send-card send-card--branded has-section-header-bar send-card--additional-features document-section-jump">
                <div className="section-header-bar section-header-bar--first">
                  <div className="send-card-header send-section-header--branded">
                    <span className="section-badge">4</span>
                    <span className="section-icon" aria-hidden>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                    </span>
                    <span>{t('app.step4AdditionalFeatures')}</span>
                  </div>
                </div>
                <div className="send-row email-reminders-block">
                  <div className="email-reminders-subhead">
                    <span className="email-reminders-subhead-title">{t('app.emailRemindersHeading')}</span>
                    <button
                      type="button"
                      className="help-icon email-reminders-help-icon"
                      aria-label={t('app.emailRemindersHelp')}
                      data-tooltip={t('app.emailRemindersHelp')}
                    >
                      ?
                    </button>
                  </div>
                  <div className={`email-reminders-checkboxes${!canUseEmailReminders ? ' email-reminders-checkboxes--disabled' : ''}`}>
                    <label className={`email-reminders-option${!canUseEmailReminders ? ' email-reminders-option--disabled' : ''}`}>
                      <input
                        type="checkbox"
                        checked={emailReminder1d}
                        disabled={!canUseEmailReminders}
                        onChange={(e) => setEmailReminder1d(e.target.checked)}
                      />
                      <span>{t('app.emailReminder1d')}</span>
                    </label>
                    <label className={`email-reminders-option${!canUseEmailReminders ? ' email-reminders-option--disabled' : ''}`}>
                      <input
                        type="checkbox"
                        checked={emailReminder3d}
                        disabled={!canUseEmailReminders}
                        onChange={(e) => setEmailReminder3d(e.target.checked)}
                      />
                      <span>{t('app.emailReminder3d')}</span>
                    </label>
                    <label className={`email-reminders-option${!canUseEmailReminders ? ' email-reminders-option--disabled' : ''}`}>
                      <input
                        type="checkbox"
                        checked={emailReminder7d}
                        disabled={!canUseEmailReminders}
                        onChange={(e) => setEmailReminder7d(e.target.checked)}
                      />
                      <span>{t('app.emailReminder7d')}</span>
                    </label>
                  </div>
                </div>
              </section>
              <div className="send-after-step4-actions">
                <button
                  type="button"
                  className="primary-button send-after-step4-actions__send"
                  onClick={handleSendEmail}
                  disabled={sendProgress != null && sendProgress < 100}
                  title={sendProgress != null && sendProgress < 100 ? t('app.sending') || 'Sending…' : t('app.sendEmail')}
                >
                  <svg className="app-header__btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  {sendProgress != null && sendProgress < 100 ? t('app.sending') || 'Sending…' : t('app.sendEmail')}
                </button>
              </div>
              </>
            )}
          </div>

          {isRecipientView && isSignedComplete && (
            <div className="completion-row signed-success-row">
              <p className="signed-success-message">This document has been signed.</p>
              {signedAtDisplay && (
                <p className="signed-at-timestamp">
                  Signed on {new Date(signedAtDisplay).toLocaleString(undefined, {
                    year: 'numeric', month: 'long', day: 'numeric',
                    hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
                  })}
                </p>
              )}
              <p className="signed-readonly-note">This document is locked and cannot be modified.</p>
              {completionStatus && completionStatusType === 'error' && (
                <p className="signed-email-error">{completionStatus}</p>
              )}
              {completionStatus && completionStatusType === 'success' && (
                <p className="signed-email-success">{completionStatus}</p>
              )}
              <button
                type="button"
                className="primary-button signed-download-btn"
                onClick={handleDownloadDocument}
                disabled={isDownloadingDocument}
              >
                <svg className="app-header__btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                {isDownloadingDocument ? 'Generating...' : 'Download Document'}
              </button>
            </div>
          )}
          {isRecipientView && !isSignedComplete && (
            <div className="completion-row">
              <button
                type="button"
                className="primary-button"
                onClick={handleDone}
                disabled={isSendingConfirmation}
              >
                {isSendingConfirmation ? 'Sending...' : 'Done'}
              </button>
              {completionStatus && (
                <span className={`status-note ${completionStatusType === 'error' ? 'status-error' : ''} ${completionStatusType === 'success' ? 'status-success' : ''}`}>
                  {completionStatus}
                </span>
              )}
            </div>
          )}
        </section>}

            {/* Sticky bottom bar: Preview PDF + Send Email (doc creation only) */}
            {!isRecipientView && appTab === 'document' && (
              <div ref={stickySendBarRef} className="sticky-send-bar" aria-label="Document actions">
                {sendProgress != null && sendProgress < 100 && (
                  <div className="sticky-send-bar__progress" role="progressbar" aria-valuenow={sendProgress} aria-valuemin={0} aria-valuemax={100} aria-label="Sending email">
                    <div className="sticky-send-bar__progress-track">
                      <div className="sticky-send-bar__progress-fill" style={{ width: `${sendProgress}%` }} />
            </div>
                    {sendStepMessage && <span className="sticky-send-bar__step">{sendStepMessage}</span>}
              </div>
                )}
                <div className="sticky-send-bar__jump" role="toolbar" aria-label={t('app.documentSectionsNav')}>
                  {[
                    { step: 1, labelKey: 'app.jumpToDocumentContent' },
                    { step: 2, labelKey: 'app.jumpToSignaturesSection' },
                    { step: 3, labelKey: 'app.jumpToRecipientsSection' },
                    { step: 4, labelKey: 'app.jumpToExtrasSection' },
                  ].map(({ step, labelKey }) => {
                    const jumpLabel = t(labelKey)
                    return (
                      <button
                        key={step}
                        type="button"
                        className="sticky-send-bar__jump-btn"
                        onClick={() => scrollToDocumentSection(step)}
                        title={jumpLabel}
                        aria-label={jumpLabel}
                      >
                        <StickySectionJumpIcon step={step} />
                      </button>
                    )
                  })}
                </div>
                <div className="sticky-send-bar__jump-actions-divider" aria-hidden="true" />
                <div className="sticky-send-bar__actions">
                  {isSignedInAppUser && (
                    <>
                      <button
                        type="button"
                        className="sticky-send-bar__btn sticky-send-bar__btn--template"
                        onClick={openSaveTemplate}
                      >
                        <svg className="sticky-send-bar__btn-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8 15 3"/></svg>
                        <span className="sticky-send-bar__btn-label">{t('app.saveTemplate')}</span>
                      </button>
                      <div className="sticky-send-bar__divider" aria-hidden="true" />
                    </>
                  )}
                <button
                  type="button"
                    className="sticky-send-bar__btn sticky-send-bar__btn--secondary"
                    onClick={handlePreview}
                    disabled={sendProgress != null && sendProgress < 100}
                    title={t('app.previewPdf')}
                >
                    <svg className="sticky-send-bar__btn-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    <span className="sticky-send-bar__btn-label">{t('app.previewPdf')}</span>
                </button>
                <div className="sticky-send-bar__divider" aria-hidden="true" />
                <button
                  type="button"
                    className="sticky-send-bar__btn sticky-send-bar__btn--primary"
                    onClick={handleSendEmail}
                    disabled={sendProgress != null && sendProgress < 100}
                    title={sendProgress != null && sendProgress < 100 ? t('app.sending') || 'Sending…' : t('app.sendEmail')}
                >
                    <svg className="sticky-send-bar__btn-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                    <span className="sticky-send-bar__btn-label">{sendProgress != null && sendProgress < 100 ? t('app.sending') || 'Sending…' : t('app.sendEmail')}</span>
                </button>
              </div>
            </div>
            )}

            {/* Send/validation status popup (errors and success messages) */}
            {sendStatus && (
              <div
                className="send-status-popup-backdrop"
                role="dialog"
                aria-modal="true"
                aria-labelledby="send-status-popup-title"
                onClick={(e) => { if (e.target === e.currentTarget) { setSendStatus(''); setSendStatusType('') } }}
              >
                <div className="send-status-popup" onClick={(e) => e.stopPropagation()}>
                  <h3 id="send-status-popup-title" className="send-status-popup__title" aria-hidden={true}>
                    {sendStatusType === 'error' ? 'Errors' : sendStatusType === 'success' ? 'Result' : 'Message'}
                  </h3>
                  <p className={`send-status-popup__message send-status-popup__message--${sendStatusType === 'error' ? 'error' : sendStatusType === 'success' ? 'success' : 'info'}`}>
                    {sendStatus}
                  </p>
                <button
                  type="button"
                    className="sticky-send-bar__btn sticky-send-bar__btn--primary send-status-popup__close"
                    onClick={() => { setSendStatus(''); setSendStatusType('') }}
                >
                    {t('modal.close') || 'Close'}
                </button>
              </div>
          </div>
            )}

            </>
            )}
          </>
        )}

        {view === 'email-activity' && (
          <div className="email-activity-layout">
            <div className="email-activity-page">
              <header className="email-activity-intro">
                <h1 className="email-activity-intro__title">{t('app.emailActivityPageTitle')}</h1>
                <p className="email-activity-intro__subtitle">{t('app.emailActivityPageSubtitle')}</p>
              </header>
              <div className="email-activity-body">
                <section className="email-activity-dashboard" aria-label="Usage dashboard">
                  <div className="email-activity-dashboard-header">
                    <h3 className="email-activity-dashboard-title">{t('app.emailActivityUsageSection')}</h3>
                    <div className="email-activity-dashboard-actions" style={{ display: 'none' }}>
                <button
                  type="button"
                        className="primary-button email-activity-topup-btn"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          if (userCustomerId || userSubscriptionId) {
                            setUpgradeModalLimitReason('emails')
                            setIsUpgradeModalOpen(true)
                          } else {
                            setIsTopUpOpen(true)
                          }
                        }}
                      >
                        {(userCustomerId || userSubscriptionId) ? 'Upgrade plan' : 'Top up credits'}
                </button>
              </div>
            </div>
                  <div className="email-activity-dashboard-grid">
                    {(userCustomerId || userSubscriptionId) && (
                      <div className="usage-card usage-card--plan">
                        <span className="usage-card-label">{t('app.emailActivityCurrentPlan')}</span>
                        <span className="usage-card-value usage-card-value--plan">{subscriptionTier.charAt(0).toUpperCase() + subscriptionTier.slice(1)}</span>
          </div>
                    )}
                    <div className="usage-card usage-card--emails">
                      <span className="usage-card-label">{t('app.emailActivityMonthlySends')}</span>
                      <span className="usage-card-value">{emailsSentThisMonth}<span className="usage-card-of"> / {maxEmailsPerMonth}</span></span>
                      {maxEmailsPerMonth > 0 && (
                        <div className="usage-card-progress" role="progressbar" aria-valuenow={emailsSentThisMonth} aria-valuemin={0} aria-valuemax={maxEmailsPerMonth}>
                          <div className="usage-card-progress-bar" style={{ width: `${Math.min(100, (emailsSentThisMonth / maxEmailsPerMonth) * 100)}%` }} />
                        </div>
                      )}
                    </div>
                    <div className="usage-card usage-card--reminders">
                      <span className="usage-card-label">{t('app.emailActivityReminderEmailsThisMonth')}</span>
                      <span className="usage-card-value usage-card-value--reminder">{(userUsage.reminderEmailsThisMonth ?? 0)}</span>
                      <span className="usage-card-hint usage-card-hint--reminder">{t('app.emailActivityReminderEmailsHint')}</span>
                    </div>
                    <div className="usage-card usage-card--templates">
                      <span className="usage-card-label">{t('app.emailActivityTemplatesSaved')}</span>
                      <span className="usage-card-value">{templates.length}<span className="usage-card-of"> / {maxTemplates > 0 ? maxTemplates : '∞'}</span></span>
                      {maxTemplates > 0 && (
                        <div className="usage-card-progress" role="progressbar" aria-valuenow={templates.length} aria-valuemin={0} aria-valuemax={maxTemplates}>
                          <div className="usage-card-progress-bar" style={{ width: `${Math.min(100, (templates.length / maxTemplates) * 100)}%` }} />
                        </div>
                      )}
                    </div>
                    <div className="usage-card usage-card--signed">
                      <span className="usage-card-label">{t('app.emailActivitySignedThisMonth')}</span>
                      <span className="usage-card-value">{currentEmailsSignedPerMonth}<span className="usage-card-of"> / {maxEmailsSignedPerMonth}</span></span>
                      {maxEmailsSignedPerMonth > 0 && (
                        <div className="usage-card-progress" role="progressbar" aria-valuenow={currentEmailsSignedPerMonth} aria-valuemin={0} aria-valuemax={maxEmailsSignedPerMonth}>
                          <div className="usage-card-progress-bar" style={{ width: `${Math.min(100, (currentEmailsSignedPerMonth / maxEmailsSignedPerMonth) * 100)}%` }} />
                        </div>
                      )}
                    </div>
                    {(userCustomerId || userSubscriptionId) && (
                      <>
                        <div className="usage-card usage-card--topup-emails">
                          <span className="usage-card-label">{t('app.emailActivityTopUpSends')}</span>
                          <span className="usage-card-value">{(userUsage.subscribedEmailTopUpCredit ?? 0)} <span className="usage-card-of">{t('app.emailActivityRemaining')}</span></span>
                        </div>
                        <div className="usage-card usage-card--topup-signed">
                          <span className="usage-card-label">{t('app.emailActivityTopUpSigned')}</span>
                          <span className="usage-card-value">{(userUsage.subscribedSignedAgreementTopUpCredit ?? 0)} <span className="usage-card-of">{t('app.emailActivityRemaining')}</span></span>
                        </div>
                      </>
                    )}
                  </div>
                </section>
                <section className="email-activity-table-section" aria-label="Email events">
                  <div className="email-activity-header-row">
                    <h4 className="email-activity-section-title">{t('app.emailActivityEventsTitle')}</h4>
                    <div className="email-activity-expiry-hint">
                      <p>
                        {t('app.emailActivityEventsHintBefore')}{' '}
                        <strong>{t('app.emailActivityEventsHintEmphasis')}</strong>{' '}
                        {t('app.emailActivityEventsHintAfter')}
                      </p>
                    </div>
                  </div>
                  <div className="email-activity-toolbar">
                    <label className="field field--inline">
                      <span>{t('app.emailActivityMonthFilter')}</span>
                      <select
                        value={emailActivityMonthFilter}
                        onChange={(e) => setEmailActivityMonthFilter(e.target.value)}
                        className="email-activity-month-select"
                        aria-label={t('app.emailActivityMonthFilterAria')}
                      >
                        <option value="">{t('app.emailActivityMonthAll')}</option>
                        {[...new Set(emailEvents.map((e) => {
                          const dateRaw = e.sentAt || e.createdAt
                          const d = dateRaw ? new Date(dateRaw) : new Date(e.createdAtMs || 0)
                          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
                        }))].sort().reverse().map((ym) => (
                          <option key={ym} value={ym}>{new Date(ym + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</option>
                        ))}
                      </select>
                    </label>
            </div>
                  {emailActivityLoading ? (
                    <p className="email-activity-loading">{t('app.emailActivityLoading')}</p>
                  ) : (
                    <div className="email-activity-table-wrap">
                      <table className="email-activity-table" role="grid">
                        <thead>
                          <tr>
                            <th scope="col">{t('app.emailActivityColSent')}</th>
                            <th scope="col">{t('app.emailActivityColReminders')}</th>
                            <th scope="col">{t('app.emailActivityColRecipients')}</th>
                            <th scope="col">{t('app.emailActivityColStatus')}</th>
                            <th scope="col">{t('app.emailActivityColSigners')}</th>
                            <th scope="col">{t('app.emailActivityColSigned')}</th>
                            <th scope="col">{t('app.emailActivityColLink')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {emailEvents
                            .filter((e) => {
                              if (!emailActivityMonthFilter) return true
                              const dateRaw = e.sentAt || e.createdAt
                              const d = dateRaw ? new Date(dateRaw) : new Date(e.createdAtMs || 0)
                              const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
                              return ym === emailActivityMonthFilter
                            })
                            .map((ev) => {
                              const sentDateRaw = ev.sentAt || ev.createdAt
                              const sentDateStr = sentDateRaw ? new Date(sentDateRaw).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : (ev.createdAt ? new Date(ev.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—')
                              const recipients = (ev.recipientEmails || []).join(', ') || '—'
                              const status = ev.status === 'signed' ? t('app.emailActivityStatusSigned') : t('app.emailActivityStatusSent')
                              const signersList = ev.signers && Array.isArray(ev.signers) && ev.signers.length > 0
                                ? ev.signers
                                : (ev.signerName || ev.signerEmail) ? [{ signerName: ev.signerName, signerEmail: ev.signerEmail }] : []
                              const signersDisplay = signersList.length > 0
                                ? signersList.map((s) => s.signerName || s.signerEmail || 'Signer').filter(Boolean).join(', ')
                                : '—'
                              const lastSigned = (() => {
                                const fromSigners = ev.signers?.length > 0
                                  ? ev.signers.reduce((latest, s) => {
                                      const d = s.signedDate ? new Date(s.signedDate) : null
                                      return d && (!latest || d > latest) ? d : latest
                                    }, null)
                                  : null
                                if (fromSigners) return fromSigners
                                const fromEvent = ev.linkDetails?.signedAtIso || ev.metadata?.signedDate || ev.signedDate
                                return fromEvent ? new Date(fromEvent) : null
                              })()
                              const signedDateStr = lastSigned ? lastSigned.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—'
                              const reminderRows = Array.isArray(ev.emailRemindersDelivered) && ev.emailRemindersDelivered.length > 0
                                ? [...ev.emailRemindersDelivered]
                                  .filter((r) => r && (r.dayOffset === 1 || r.dayOffset === 3 || r.dayOffset === 7))
                                  .sort((a, b) => (a.dayOffset || 0) - (b.dayOffset || 0))
                                : []
                              return (
                                <tr key={ev.id}>
                                  <td>{sentDateStr}</td>
                                  <td className="email-activity-reminders">
                                    {reminderRows.length === 0
                                      ? t('app.emailActivityReminderNone')
                                      : (
                                        <ul className="email-activity-reminder-list">
                                          {reminderRows.map((r, ri) => {
                                            const when = r.sentAt ? new Date(r.sentAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—'
                                            const labelKey = r.dayOffset === 1 ? 'app.emailActivityReminderDay1' : r.dayOffset === 3 ? 'app.emailActivityReminderDay3' : 'app.emailActivityReminderDay7'
                                            return (
                                              <li key={`${r.dayOffset}-${ri}`}>
                                                {t(labelKey)}
                                                {when && when !== '—' ? ` · ${when}` : ''}
                                              </li>
                                            )
                                          })}
                                        </ul>
                                      )}
                                  </td>
                                  <td className="email-activity-recipients">{recipients}</td>
                                  <td><span className={`email-activity-status email-activity-status--${ev.status}`}>{status}</span></td>
                                  <td className="email-activity-signers">{signersDisplay}</td>
                                  <td>{signedDateStr}</td>
                                  <td>
                                    {(ev.agreementHtml === 'expired' || ev.linkDetails?.agreementHtml === 'expired' || ev.signingLinkUrl === 'expired' || ev.linkDetails?.signingLinkUrl === 'expired') ? (
                                      <span className="email-activity-expired">{t('app.emailActivityLinkExpired')}</span>
                                    ) : (() => {
                                      const linkUrl = getEmailActivityViewLink(ev)
                                      return linkUrl && linkUrl !== 'expired' ? (
                                        <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="email-activity-pdf-link">{t('app.emailActivityViewLink')}</a>
                                      ) : '—'
                                    })()}
                                  </td>
                                </tr>
                              )
                            })}
                        </tbody>
                      </table>
                      {emailEvents.filter((e) => {
                        if (!emailActivityMonthFilter) return true
                        const dateRaw = e.sentAt || e.createdAt
                        const d = dateRaw ? new Date(dateRaw) : new Date(e.createdAtMs || 0)
                        const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
                        return ym === emailActivityMonthFilter
                      }).length === 0 && (
                        <p className="email-activity-empty">{t('app.emailActivityEmpty')}</p>
                      )}
          </div>
                  )}
                </section>
            </div>
          </div>
          </div>
        )}
      </main>

      {(view === 'auth' || view === 'blog' || view === 'blog-post') && (
        <Footer
          onNavigate={setView}
          termsHref={locPath('/terms')}
          privacyHref={locPath('/privacy')}
          onTermsClick={(e) => goToMarketingSeoPath(e, '/terms')}
          onPrivacyClick={(e) => goToMarketingSeoPath(e, '/privacy')}
        />
      )}

      {(view === 'app' || view === 'email-activity') && (
        <Footer
          onNavigate={setView}
          termsHref={locPath('/terms')}
          privacyHref={locPath('/privacy')}
          onTermsClick={(e) => goToMarketingSeoPath(e, '/terms')}
          onPrivacyClick={(e) => goToMarketingSeoPath(e, '/privacy')}
        />
      )}

      {isLinkModalOpen && (
        <div className="modal-backdrop send-confirm-modal-backdrop" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) { setIsLinkModalOpen(false); setLinkTitle(''); setLinkUrl('') } }}>
          <div className="modal send-confirm-modal modal--compact-branded link-modal" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal__header modal__header--compact">
              <h3 className="modal__title">{t('modal.link')}</h3>
              <button
                type="button"
                className="account-modal__close ghost-button"
                onClick={() => {
                  setIsLinkModalOpen(false)
                  setLinkTitle('')
                  setLinkUrl('')
                }}
                aria-label={t('modal.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="modal__body--card-wrap">
              <div className="modal__card">
              <label className="field">
                <span>{t('modal.linkTitle')}</span>
                <input
                  type="text"
                  value={linkTitle}
                  onChange={(event) => setLinkTitle(event.target.value)}
                  placeholder={t('modal.linkTextPlaceholder')}
                  autoFocus
                />
              </label>
              <label className="field">
                <span>{t('modal.linkUrl')}</span>
                <input
                  type="text"
                  value={linkUrl}
                  onChange={(event) => setLinkUrl(event.target.value)}
                  placeholder={t('modal.linkUrlPlaceholder')}
                />
              </label>
              <div className="modal__actions--below" style={{ paddingTop: 12 }}>
                <button
                  type="button"
                  className="primary-button"
                  onClick={applyLink}
                >
                  {t('modal.save')}
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {charLimitReachedOpen && (
        <div className="modal-backdrop send-confirm-modal-backdrop" role="dialog" aria-modal="true" onClick={() => { setCharLimitReachedOpen(false); setLimitReachedReason(null) }}>
          <div className="modal send-confirm-modal modal--compact-branded" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal__header modal__header--compact">
              <h3 className="modal__title">{limitReachedReason === 'size' ? t('modal.agreementSizeLimitReached') : t('modal.characterLimitReached')}</h3>
              <button type="button" className="account-modal__close ghost-button" aria-label={t('modal.close')} onClick={() => { setCharLimitReachedOpen(false); setLimitReachedReason(null) }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="modal__body--card-wrap">
              <div className="modal__card">
                <p className="modal__subtitle" style={{ margin: 0 }}>
                {limitReachedReason === 'size'
                    ? t('modal.agreementSizeLimitMessage', { max: maxAgreementSizeKb.toLocaleString() })
                    : t('modal.characterLimitMessage', { max: charLimit.toLocaleString() })}
                </p>
              </div>
              <div className="modal__actions--below">
                <button type="button" className="primary-button" onClick={() => { setCharLimitReachedOpen(false); setLimitReachedReason(null) }}>{t('modal.ok')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {signingLimitReachedOpen && (
        <div className="modal-backdrop send-confirm-modal-backdrop" role="dialog" aria-modal="true" onClick={() => setSigningLimitReachedOpen(false)}>
          <div className="modal send-confirm-modal modal--compact-branded" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal__header modal__header--compact">
              <h3 className="modal__title">{t('modal.signingSectionLimitReached')}</h3>
              <button type="button" className="account-modal__close ghost-button" aria-label={t('modal.close')} onClick={() => setSigningLimitReachedOpen(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="modal__body--card-wrap">
              <div className="modal__card">
                <p className="modal__subtitle" style={{ margin: 0 }}>
                  {t('modal.signingSectionLimitMessage', { limit: signatureTextLimitKB })}
                </p>
              </div>
              <div className="modal__actions--below">
                <button type="button" className="primary-button" onClick={() => setSigningLimitReachedOpen(false)}>{t('modal.ok')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isSignatureOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) { setSignatureCustomFieldId(null); setIsSignatureOpen(false) } }}>
          <div className="modal signature-modal" onClick={(e) => e.stopPropagation()}>
            <div className="signature-modal__header">
              <div className="signature-modal__header-text">
                <h3>{t('modal.addYourSignature')}</h3>
                <p className="signature-modal__subtitle">{t('modal.signatureHint')}</p>
              </div>
              <button
                type="button"
                className="signature-modal__close ghost-button"
                onClick={() => { setSignatureCustomFieldId(null); setIsSignatureOpen(false) }}
                aria-label={t('modal.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="signature-modal__body">
            <div className="canvas-wrap">
              <canvas
                ref={canvasRef}
                className="signature-canvas"
                onPointerDown={startDrawing}
                onPointerMove={draw}
                onPointerUp={stopDrawing}
                onPointerLeave={stopDrawing}
              />
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="ghost-button"
                onClick={handleClearSignature}
              >
                {t('modal.clear')}
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={handleSaveSignature}
              >
                {t('modal.saveSignature')}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="pdf-preview-source" aria-hidden="true">
        <section className="document pdf-document" ref={previewRef}>
          <div ref={contentBeforeSignersRef}>
          {!appConfig.hideLogoFeature && canUseLogo && logoDataUrl && logoLocation.startsWith('top-') && (
            <div className={`document-logo document-logo--${logoLocation}`}>
              <img src={logoDataUrl} alt={t('app.documentSenderLogoAlt')} />
            </div>
          )}
          {fullPdfPages.length > 0 && (
            <div className="preview-full-pdf-pages">
              {fullPdfPages.map((page) => (
                <div key={page.pageIndex} className="preview-full-pdf-page">
                  <img src={page.imageDataUrl} alt={t('app.pdfAgreementPageAlt', { page: page.pageIndex })} className="preview-full-pdf-img" />
                </div>
              ))}
            </div>
          )}
          {fullPdfPages.length === 0 && (
            <div
              ref={agreementPreviewRef}
              className="agreement-text"
              dangerouslySetInnerHTML={{ __html: previewAgreementHtml }}
            />
          )}
          </div>
          <div ref={signersPreviewRef} className="form-grid pdf-form-grid form-signers-grid pdf-signers">
            {(!isRecipientView && totalSignatures === 2 ? [1, 0] : signers.map((_, idx) => idx)).map((i) => {
              const signer = signers[i]
              if (!signer) return null
              const activeLayout = getActiveLayout(i)
              const col0 = activeLayout.filter((f) => f.column === 0)
              const col1 = activeLayout.filter((f) => f.column === 1)
              let previewBlockTitle = `Signer ${i + 1}`
              if (!isRecipientView && totalSignatures === 2 && signers.length > 1) {
                previewBlockTitle = i === 1 ? t('app.signerBlockRecipient') : t('app.signerBlockYou')
              } else if (!isRecipientView && totalSignatures === 1 && signers.length === 1) {
                previewBlockTitle = t('app.signerBlockRecipient')
              }
              return (
                <div key={i} className={`signer-block${layoutColumns === 2 ? ' signer-block--wide' : ''}`}>
                  {(signers.length > 1 || (!isRecipientView && totalSignatures === 1)) && (
                    <h4 className="signer-block-title">{previewBlockTitle}</h4>
                  )}
                  {layoutColumns === 2 ? (
                    <div className="form-fields-2col">
                      <div className="form-fields-col">
                        {col0.map((f) => renderSignerField(f.id, signer, i, true, f.label))}
                      </div>
                      <div className="form-fields-col">
                        {col1.map((f) => renderSignerField(f.id, signer, i, true, f.label))}
                      </div>
                    </div>
                  ) : (
                    <div className="form-fields-column">
                      {activeLayout.map((f) => renderSignerField(f.id, signer, i, true, f.label))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          {!appConfig.hideLogoFeature && canUseLogo && logoDataUrl && !logoLocation.startsWith('top-') && (
            <div className={`document-logo document-logo--${logoLocation}`}>
              <img src={logoDataUrl} alt={t('app.documentSenderLogoAlt')} />
            </div>
          )}
        </section>
      </div>

      {isPreviewOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal preview-modal">
            <div className="preview-modal__header">
              <div className="preview-modal__header-text">
                <h3>PDF preview</h3>
                <p className="preview-modal__subtitle">Review the exact PDF pages that will be sent and downloaded.</p>
              </div>
              <button
                type="button"
                className="preview-modal__close ghost-button"
                onClick={() => setIsPreviewOpen(false)}
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="preview-modal__body preview-body-scroll">
              <div className={`pdf-preview-frame-wrap ${previewPdfPagesMobile.length > 0 && isMobileView ? 'pdf-preview-frame-wrap--mobile-fallback' : ''}`}>
                {previewPdfUrl && !previewError ? (
                  <iframe
                    title="PDF preview"
                    src={previewPdfUrl}
                    className="pdf-preview-frame"
                    onLoad={() => setPreviewIframeLoaded(true)}
                  />
                ) : null}
                {isMobileView && previewPdfPagesMobile.length > 0 ? (
                  <div className="pdf-preview-pages-mobile">
                    {previewPdfPagesMobile.map((p) => (
                      <div key={p.pageIndex} className="pdf-preview-page-mobile">
                        <img src={p.imageDataUrl} alt={t('app.pdfAgreementPageAlt', { page: p.pageIndex })} />
                      </div>
                    ))}
                  </div>
                ) : null}
                {previewError ? (
                  <div className="pdf-preview-placeholder pdf-preview-placeholder--overlay pdf-preview-placeholder--error">
                    <p className="pdf-preview-placeholder__error-text">{previewError}</p>
                    <button
                      type="button"
                      className="sticky-send-bar__btn sticky-send-bar__btn--primary"
                      onClick={() => { setPreviewError(null); setPreviewPdfUrl(''); setPreviewIframeLoaded(false); setPreviewRetryKey((k) => k + 1) }}
                    >
                      Try again
                    </button>
                  </div>
                ) : (() => {
                  const showIframeLoading = !isMobileView && (!previewPdfUrl || !previewIframeLoaded)
                  const showMobileLoading = isMobileView && previewPdfPagesMobileLoading && previewPdfPagesMobile.length === 0
                  const showGenerating = !previewPdfUrl
                  if (showIframeLoading || showMobileLoading || showGenerating) {
                    return (
                      <div className="pdf-preview-placeholder pdf-preview-placeholder--overlay" aria-busy="true">
                    <div className="pdf-preview-spinner" aria-hidden="true" />
                        <p>{previewPdfUrl ? 'Loading preview…' : 'Generating PDF preview…'}</p>
                  </div>
                    )
                  }
                  return null
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {isTermsOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal terms-modal">
            <div className="modal-header">
              <div>
                <h3>Terms of Service</h3>
                <p>Last updated: March 15, 2026</p>
              </div>
              <button
                type="button"
                className="ghost-button"
                onClick={() => setIsTermsOpen(false)}
              >
                Close
              </button>
            </div>
            <div className="terms-body">
              <TermsOfServiceBody />
            </div>
          </div>
        </div>
      )}

      {isPrivacyOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal terms-modal">
            <div className="modal-header">
              <div>
                <h3>Privacy Policy</h3>
                <p>Last updated: 26 March 2026</p>
              </div>
              <button
                type="button"
                className="ghost-button"
                onClick={() => setIsPrivacyOpen(false)}
              >
                Close
              </button>
            </div>
            <div className="terms-body">
              <PrivacyPolicyBody />
            </div>
          </div>
        </div>
      )}

      {isAccountOpen && (
        <div className="modal-backdrop account-modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal account-modal modal--compact-branded">
            <div className="account-modal__header modal__header--compact">
              <h3 className="modal__title">{t('app.myAccount')}</h3>
              <div className="account-modal__header-actions">
                <button
                  type="button"
                  className="account-modal__close ghost-button"
                  onClick={() => setIsAccountOpen(false)}
                  aria-label={t('modal.close')}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              </div>
            </div>
            <div className="modal__body--card-wrap">
              <div className="modal__card" style={{ marginBottom: 0 }}>
                <p className="modal__subtitle" style={{ marginBottom: 0 }}>Manage your profile, subscription, and activity.</p>
              </div>
            <div className="account-modal__body" style={{ paddingTop: 0 }}>
            {(!user || user.isAnonymous || !user.email) ? (
              <div className="account-guest-content">
                <p className="account-guest-message">You are not currently logged in. Please log in or sign up.</p>
                <div className="account-guest-actions">
                  <button
                    type="button"
                    className="primary-button account-guest-btn"
                    onClick={() => { setIsAccountOpen(false); setAuthMode('signup'); setIsAuthModalOpen(true) }}
                  >
                    {t('auth.createAccountButton')}
                  </button>
                  <button
                    type="button"
                    className="ghost-button account-guest-btn"
                    onClick={() => { setIsAccountOpen(false); setAuthMode('login'); setIsAuthModalOpen(true) }}
                  >
                    {t('auth.logIn')}
                  </button>
                </div>
              </div>
            ) : (
            <nav className="account-menu" aria-label="Account options">
              <button
                type="button"
                className="account-nav-btn"
                onClick={() => {
                  setIsAccountOpen(false)
                  setIsAccountDetailsOpen(true)
                }}
              >
                <span className="account-nav-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </span>
                <span className="account-nav-label">Account details</span>
                <span className="account-nav-chevron" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                </span>
              </button>
                <button
                  type="button"
                  className="account-nav-btn"
                  onClick={() => {
                    setIsAccountOpen(false)
                  setIsSubscriptionOpen(true)
                  }}
                >
                  <span className="account-nav-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l9 10-9 10-9-10 9-10z"/></svg>
                  </span>
                <span className="account-nav-label">My Subscription</span>
                  <span className="account-nav-chevron" aria-hidden>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                  </span>
                </button>
              <button
                type="button"
                className="account-nav-btn"
                onClick={() => {
                  setIsAccountOpen(false)
                  if (user?.email) setContactEmail(user.email)
                  setIsContactSupportOpen(true)
                }}
              >
                <span className="account-nav-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
                </span>
                <span className="account-nav-label">Contact Support</span>
                <span className="account-nav-chevron" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                </span>
              </button>
              <button
                type="button"
                className="account-nav-btn account-nav-btn--logout"
                onClick={() => {
                  setIsAccountOpen(false)
                  setIsLogoutOpen(true)
                }}
              >
                <span className="account-nav-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                </span>
                <span className="account-nav-label">Log out</span>
                <span className="account-nav-chevron" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                </span>
              </button>
            </nav>
            )}
            </div>
            </div>
          </div>
        </div>
      )}

      {isAccountDetailsOpen && (
        <div className="modal-backdrop account-details-modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal account-details-modal modal--compact-branded">
            <div className="account-modal__header modal__header--compact">
              <h3 className="modal__title">Account details</h3>
              <button
                type="button"
                className="account-modal__close ghost-button"
                onClick={() => { setIsAccountDetailsOpen(false); setIsAccountOpen(true) }}
                aria-label={t('modal.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="modal__body--card-wrap">
              <div className="modal__card" style={{ marginBottom: 16 }}>
                <p className="modal__subtitle" style={{ marginBottom: 0 }}>Update your profile and default sender email.</p>
              </div>
            <section className="account-details-modal__body account-details-section">
              <div className="account-details-fields">
                <label>
                  <span>Email</span>
                  <input
                    type="email"
                    readOnly
                    value={user?.email ?? ''}
                    className="readonly-input"
                  />
                </label>
                <label>
                  <span>First name</span>
                  <input
                    type="text"
                    value={accountDetailsFirstName}
                    onChange={(e) => setAccountDetailsFirstName(e.target.value)}
                    placeholder="First name"
                  />
                </label>
                <label>
                  <span>Last name</span>
                  <input
                    type="text"
                    value={accountDetailsLastName}
                    onChange={(e) => setAccountDetailsLastName(e.target.value)}
                    placeholder="Last name"
                  />
                </label>
                <label>
                  <span>Phone</span>
                  <input
                    type="tel"
                    value={accountDetailsPhone}
                    onChange={(e) => setAccountDetailsPhone(e.target.value)}
                    placeholder="Phone"
                  />
                </label>
                <label className="field">
                  <span>Default Email</span>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={defaultFromEmail}
                    onChange={(e) => setDefaultFromEmail(e.target.value)}
                  />
                  <p className="field-help">
                    If this is populated, it will set the default &quot;From&quot; email. When set, this email will always be used to auto-populate the From email field when sending documents.
                  </p>
                </label>
                <label className="account-details-checkbox-row">
                  <input
                    type="checkbox"
                    checked={sendEmailTo}
                    onChange={(e) => setSendEmailTo(e.target.checked)}
                  />
                  <span>
                    <span className="account-details-checkbox-row__label">{t('account.sendCopyToSelf')}</span>
                    <span className="account-details-checkbox-row__help">{t('account.sendCopyToSelfHelp')}</span>
                  </span>
                </label>
              </div>
              {accountDetailsStatus && (
                <p className="account-details-status">{accountDetailsStatus}</p>
              )}
              <button
                type="button"
                className="primary-button"
                disabled={accountDetailsSaving}
                onClick={async () => {
                  if (!user?.uid) return
                  setAccountDetailsSaving(true)
                  setAccountDetailsStatus('')
                  try {
                    await updateDoc(doc(db, 'users', user.uid), {
                      firstName: accountDetailsFirstName.trim(),
                      lastName: accountDetailsLastName.trim(),
                      phone: accountDetailsPhone.trim(),
                      defaultFromEmail: defaultFromEmail.trim(),
                      sendEmailTo: sendEmailTo === true,
                      updatedAt: serverTimestamp(),
                    })
                    setSenderEmail(defaultFromEmail.trim() || senderEmail)
                    setAuthFirstName(accountDetailsFirstName.trim())
                    setAuthLastName(accountDetailsLastName.trim())
                    setAccountDetailsStatus('Saved.')
                    setTimeout(() => setAccountDetailsStatus(''), 3000)
                  } catch (err) {
                    setAccountDetailsStatus(err?.message || 'Failed to save.')
                  } finally {
                    setAccountDetailsSaving(false)
                  }
                }}
              >
                {accountDetailsSaving ? 'Saving...' : 'Save'}
              </button>
            </section>
            </div>
          </div>
        </div>
      )}

      {isHistoryOpen && (
        <div className="modal-backdrop history-modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal history-modal">
            <div className="account-modal__header modal__header--compact">
              <h3 className="modal__title">Template history</h3>
              <button
                type="button"
                className="account-modal__close ghost-button"
                onClick={() => { setIsHistoryOpen(false); setIsAccountOpen(true) }}
                aria-label={t('modal.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="modal__body--card-wrap">
              <div className="modal__card">
                <p className="modal__subtitle">Load a saved template to reuse your document, fields, and settings.</p>
                <div className="history-modal__templates-used">
                  <p className="history-modal__templates-used-title">{t('app.templatesUsed')}</p>
                  {maxTemplates > 0 ? (
                    <>
                      <div className="history-modal__templates-used-value" aria-label={`${templates.length} of ${maxTemplates}`}>
                        <span className="history-modal__templates-used-num">{templates.length}</span>
                        <span className="history-modal__templates-used-slash">/</span>
                        <span className="history-modal__templates-used-num history-modal__templates-used-num--max">{maxTemplates}</span>
                      </div>
                      <div
                        className="history-modal__templates-used-track"
                        role="progressbar"
                        aria-valuenow={templates.length}
                        aria-valuemin={0}
                        aria-valuemax={maxTemplates}
                      >
                        <div
                          className="history-modal__templates-used-fill"
                          style={{ width: `${Math.min(100, (templates.length / maxTemplates) * 100)}%` }}
                        />
                      </div>
                    </>
                  ) : (
                    <p className="history-modal__templates-used-unlimited">{t('app.templatesUsedSaved', { count: templates.length })}</p>
                  )}
                </div>
              </div>
            <div className="history-modal__body">
              {templates.length === 0 ? (
                <div className="history-empty-state">
                  <div className="history-empty-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/></svg>
                  </div>
                  <h4>No templates saved yet</h4>
                  <p>Create a document, add your content and signature fields, then save it as a template from the editor. Your saved templates will appear here for quick reuse.</p>
                </div>
              ) : (
                <div className="history-list">
                  {templates.map((template) => {
                    const signerCount = Array.isArray(template.signers) ? template.signers.length : (template.totalSignatures ?? 1)
                    const customFieldCount = Array.isArray(template.customFields) ? template.customFields.length : 0
                    const updatedAt = template.updatedAt ? new Date(template.updatedAt) : null
                    const createdAt = template.createdAt ? new Date(template.createdAt) : null
                    return (
                    <div key={template.id} className="history-item">
                      <button
                        type="button"
                        className="history-main"
                        onClick={() => loadTemplate(template)}
                      >
                          <div className="history-item-content">
                          <h3>{template.name}</h3>
                            <p className="history-item-subtitle">{template.scopeTitle || 'Untitled agreement'}</p>
                            <div className="history-item-meta">
                              <span className="history-meta-chip">{signerCount} signer{signerCount !== 1 ? 's' : ''}</span>
                              {customFieldCount > 0 && <span className="history-meta-chip">{customFieldCount} custom field{customFieldCount !== 1 ? 's' : ''}</span>}
                        </div>
                          </div>
                          <div className="history-item-dates">
                            {updatedAt && (
                              <span className="history-date" title={updatedAt.toLocaleString()}>
                                Updated {updatedAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                            )}
                            {createdAt && updatedAt && createdAt.getTime() !== updatedAt.getTime() && (
                              <span className="history-date history-date--muted">Created {createdAt.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</span>
                            )}
                          </div>
                      </button>
                      <button
                        type="button"
                          className="history-delete-btn"
                          onClick={() => setConfirmDeleteState({
                            title: t('modal.deleteTemplate'),
                            message: t('modal.deleteTemplateConfirm', { name: template.name }),
                            confirmLabel: t('modal.yesDelete'),
                            onConfirm: () => deleteTemplate(template.id),
                          })}
                          title={t('modal.deleteTemplate')}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                        Delete
                      </button>
                    </div>
                    )
                  })}
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      )}

      {false && isPaywallOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label={user ? 'Choose how to send' : 'Sign in to send'}>
          <div className="modal modal--branded paywall-modal paywall-modal--compact">
            <div className="modal-header paywall-modal__header--compact">
              <div>
                <h3 id="paywall-title">Create an account to send</h3>
              </div>
              <button type="button" className="ghost-button" onClick={() => { setIsPaywallOpen(false); setPaywallPendingAction(null) }} aria-label="Close">Close</button>
            </div>
            <div className="modal-body">
              <div className="paywall-actions">
                {!user ? (
                  <>
                <button
                  type="button"
                  className="primary-button paywall-btn paywall-btn--signup"
                  onClick={() => {
                    setIsPaywallOpen(false)
                    setPaywallPendingAction(null)
                    setAuthMode('signup')
                        setShowOneTimeOptions(true)
                        setSubscriptionTier('premium')
                        setIsOneTimeUse(true)
                    setIsAuthModalOpen(true)
                  }}
                >
                      {t('auth.createAccountButton')}
                </button>
                <button
                  type="button"
                      className="ghost-button paywall-btn"
                  onClick={() => {
                    setIsPaywallOpen(false)
                    setPaywallPendingAction(null)
                        setAuthMode('login')
                        setIsAuthModalOpen(true)
                      }}
                    >
                      {t('auth.logIn')}
                    </button>
                  </>
                ) : (
                  <>
                    {user?.isAnonymous && (
                      <p className="paywall-anon-hint">You're signed in anonymously. After paying, return here, your credit may take a few seconds to appear.</p>
                    )}
                    <button
                      type="button"
                      className="primary-button paywall-btn paywall-btn--signup"
                      onClick={() => {
                        setIsPaywallOpen(false)
                        setPaywallPendingAction(null)
                      setAuthMode('signup')
                      setIsAuthModalOpen(true)
                      }}
                    >
                      {t('auth.signUpForSubscription')}
                </button>
                <p className="paywall-divider">or</p>
                <button
                  type="button"
                  className="ghost-button paywall-btn paywall-btn--oneoff"
                  onClick={() => {
                    setIsPaywallOpen(false)
                    setPaywallPendingAction(null)
                        redirectToOneOffPayment()
                  }}
                >
                      Pay once (No Subscription)
                </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isSubscribeOrPayOpen && (
        <div className="modal-backdrop subscription-modal-backdrop" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) setIsSubscribeOrPayOpen(false) }}>
          <div className="modal subscription-modal subscription-modal--subscribe-or-pay" onClick={(e) => e.stopPropagation()}>
            <div className="subscription-modal__header">
              <button type="button" className="link-button subscription-modal__back" onClick={() => setIsSubscribeOrPayOpen(false)} style={{ alignSelf: 'flex-start', marginBottom: 8 }}>
                ← Back
              </button>
              <div className="subscription-modal__header-text">
                <h2 className="subscription-modal__title">Would you like to subscribe or do one off payment?</h2>
              </div>
              <button type="button" className="subscription-modal__close" onClick={() => setIsSubscribeOrPayOpen(false)} aria-label="Close">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="subscription-modal__body">
              <div className="one-time-use-link-wrap">
                <button type="button" className={`one-time-use-link ${subscribeOrPayShowOneTime ? 'active' : ''}`} onClick={() => { setSubscribeOrPayShowOneTime((prev) => !prev); if (subscribeOrPayShowOneTime) { setIsOneTimeUse(false); } else { setSubscriptionTier('premium'); setIsOneTimeUse(true); } }}>
                  {subscribeOrPayShowOneTime ? <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg> Back to subscriptions</> : <>Looking for one-time use? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg></>}
                </button>
              </div>
              {!subscribeOrPayShowOneTime && (
                <div className="signup-billing-toggle subscription-billing-toggle">
                  <button type="button" className={`toggle-option ${billingCycle === 'yearly' ? 'is-active' : ''}`} onClick={() => setBillingCycle('yearly')}>Bill annually</button>
                  <button type="button" className={`toggle-option ${billingCycle === 'monthly' ? 'is-active' : ''}`} onClick={() => setBillingCycle('monthly')}>Bill monthly</button>
                </div>
              )}
              {!subscribeOrPayShowOneTime && (
                <div className="subscription-tiers signup-tiers">
                  <div className={`tier-card ${subscriptionTier === 'basic' ? 'selected' : ''}`} role="button" tabIndex={0} onClick={() => { setSubscriptionTier('basic'); setIsOneTimeUse(false); }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSubscriptionTier('basic'); setIsOneTimeUse(false); } }}>
                    {billingCycle === 'yearly' && <span className="tier-discount-badge">-{tierPricing.basic.discountPercent}%</span>}
                    <div className="tier-header"><div className="tier-icon-wrap"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg></div><h4>{t('pricing.tierBasic')}</h4><p className="tier-desc">{t('pricing.descBasic')}</p></div>
                    {billingCycle === 'yearly' && <p className="tier-annual-billing">{t('pricing.billedPerYear', { amount: tierPricing.basic.annual })}</p>}
                    <div className="tier-price-row"><span className="tier-price">${billingCycle === 'monthly' ? tierPricing.basic.monthly : (tierPricing.basic.annual / 12) % 1 === 0 ? tierPricing.basic.annual / 12 : (tierPricing.basic.annual / 12).toFixed(2)}</span><span className="tier-period">{t('pricing.perMonth')}</span></div>
                    <ul className="tier-features"><li><CheckIcon /> {t('pricing.featureRecipients5')}</li><li><CheckIcon /> {t('pricing.featureEmails5')}</li><li><CheckIcon /> {t('pricing.featureTemplates3')}</li><li><CheckIcon /> {t('pricing.featureChars3k')}</li><li><CheckIcon /> {t('pricing.featureAttachmentsNone')}</li><li><CheckIcon /> {t('pricing.featurePdfUpload')}</li><li><CheckIcon /> {t('pricing.featureCustomFields')}</li></ul>
                  </div>
                  <div className={`tier-card highlighted ${subscriptionTier === 'standard' ? 'selected' : ''}`} role="button" tabIndex={0} onClick={() => { setSubscriptionTier('standard'); setIsOneTimeUse(false); }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSubscriptionTier('standard'); setIsOneTimeUse(false); } }}>
                    {billingCycle === 'yearly' && <span className="tier-discount-badge">-{tierPricing.standard.discountPercent}%</span>}
                    <div className="tier-header"><div className="tier-icon-wrap"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg></div><h4>{t('pricing.tierStandard')}</h4><p className="tier-desc">{t('pricing.descStandard')}</p></div>
                    {billingCycle === 'yearly' && <p className="tier-annual-billing">{t('pricing.billedPerYear', { amount: tierPricing.standard.annual })}</p>}
                    <div className="tier-price-row"><span className="tier-price">${billingCycle === 'monthly' ? tierPricing.standard.monthly : (tierPricing.standard.annual / 12) % 1 === 0 ? tierPricing.standard.annual / 12 : (tierPricing.standard.annual / 12).toFixed(2)}</span><span className="tier-period">{t('pricing.perMonth')}</span></div>
                    <ul className="tier-features"><li><CheckIcon /> {t('pricing.featureRecipients10')}</li><li><CheckIcon /> {t('pricing.featureEmails50')}</li><li><CheckIcon /> {t('pricing.featureTemplates10')}</li><li><CheckIcon /> {t('pricing.featureChars20k')}</li><li><CheckIcon /> {t('pricing.featureAttachments3')}</li><li><CheckIcon /> {t('pricing.featureAuditTrail')}</li><li><CheckIcon /> {t('pricing.featurePdfUpload')}</li><li><CheckIcon /> {t('pricing.featurePdfExtract')}</li><li><CheckIcon /> {t('pricing.featureCustomFields')}</li><li><CheckIcon /> {t('pricing.featureCopyLink')}</li>{!appConfig.hideLogoFeature && <li><CheckIcon /> {t('pricing.featureLogo')}</li>}</ul>
                  </div>
                  <div className={`tier-card ${subscriptionTier === 'premium' ? 'selected' : ''}`} role="button" tabIndex={0} onClick={() => { setSubscriptionTier('premium'); setIsOneTimeUse(false); }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSubscriptionTier('premium'); setIsOneTimeUse(false); } }}>
                    {billingCycle === 'yearly' && <span className="tier-discount-badge">-{tierPricing.premium.discountPercent}%</span>}
                    <div className="tier-header"><div className="tier-icon-wrap"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg></div><h4>{t('pricing.tierPremium')}</h4><p className="tier-desc">{t('pricing.descPremium')}</p></div>
                    {billingCycle === 'yearly' && <p className="tier-annual-billing">{t('pricing.billedPerYear', { amount: tierPricing.premium.annual })}</p>}
                    <div className="tier-price-row"><span className="tier-price">${billingCycle === 'monthly' ? tierPricing.premium.monthly : (tierPricing.premium.annual / 12) % 1 === 0 ? tierPricing.premium.annual / 12 : (tierPricing.premium.annual / 12).toFixed(2)}</span><span className="tier-period">{t('pricing.perMonth')}</span></div>
                    <ul className="tier-features"><li><CheckIcon /> {t('pricing.featureRecipients10')}</li><li><CheckIcon /> {t('pricing.featureEmails150')}</li><li><CheckIcon /> {t('pricing.featureTemplates25')}</li><li><CheckIcon /> {t('pricing.featureChars30k')}</li><li><CheckIcon /> {t('pricing.featureAttachments10')}</li><li><CheckIcon /> {t('pricing.featureAuditTrail')}</li><li><CheckIcon /> {t('pricing.featurePdfUpload')}</li><li><CheckIcon /> {t('pricing.featurePdfExtract')}</li><li><CheckIcon /> {t('pricing.featureCustomFields')}</li><li><CheckIcon /> {t('pricing.featureCopyLink')}</li>{!appConfig.hideVerificationFeature && <li><CheckIcon /> {t('pricing.featureVerification')}</li>}{!appConfig.hideLogoFeature && <li><CheckIcon /> {t('pricing.featureLogo')}</li>}</ul>
                  </div>
                </div>
              )}
              {subscribeOrPayShowOneTime && (
                <div className="subscription-tiers signup-tiers one-time-tiers one-time-single">
                  <div className="tier-card popular highlighted" role="button" tabIndex={0} onClick={() => { setSubscriptionTier('premium'); setIsOneTimeUse(true); }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSubscriptionTier('premium'); setIsOneTimeUse(true); } }}>
                    <span className="tier-badge">ALL FEATURES</span>
                    <h4>One-time use</h4>
                    <p className="tier-desc">Full access for 1 send. Pay again for another send.</p>
                    <ul className="tier-features">
                      <li><CheckIcon /> {t('pricing.featureTemplates25')}</li><li><CheckIcon /> {t('pricing.featureRecipients10')}</li><li><CheckIcon /> {t('pricing.oneTimeSendIncluded', { price: tierPricing.oneTimePrice })}</li><li><CheckIcon /> {t('pricing.featureChars30k')}</li><li><CheckIcon /> {t('pricing.featureAttachments10')}</li><li><CheckIcon /> {t('pricing.featureAuditTrail')}</li><li><CheckIcon /> {t('pricing.featurePdfUpload')}</li><li><CheckIcon /> {t('pricing.featurePdfExtract')}</li><li><CheckIcon /> {t('pricing.featureCustomFields')}</li><li><CheckIcon /> {t('pricing.featureCopyLink')}</li>
                      {!appConfig.hideLogoFeature && <li><CheckIcon /> {t('pricing.featureLogo')}</li>}
                      {!appConfig.hideVerificationFeature && <li><CheckIcon /> {t('pricing.featureVerification')}</li>}
                    </ul>
                    <p className="tier-price">${tierPricing.oneTimePrice} <span>one time</span></p>
                  </div>
                </div>
              )}
              {isOneTimeUse && <p className="one-time-selected-hint">{`One-time plan: all features, 1 send. Pay $${tierPricing.oneTimePrice} again for each additional send.`}</p>}
              <div className="subscription-modal__footer subscription-modal__footer--subscribe-or-pay">
                {!user ? (
                  <button type="button" className="primary-button full-width" onClick={() => { setIsSubscribeOrPayOpen(false); setAuthMode('signup'); setIsAuthModalOpen(true); }}>
                    Create account or log in to continue
                  </button>
                ) : subscribeOrPayShowOneTime ? (
                  <button type="button" className="primary-button full-width" onClick={async () => { setIsSubscribeOrPayOpen(false); setIsOneTimeUse(true); await redirectToOneOffPayment(); }}>
                    Get Credit Now
                  </button>
                ) : (
                  <button type="button" className="primary-button full-width" onClick={async () => {
                    try {
                      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
                      const billingInterval = billingCycle === 'yearly' ? 'annual' : 'monthly';
                      const savePromise = saveDocumentStateForPaymentReturn()
                      const { data } = await createCheckoutSession({ tierId: subscriptionTier, billingInterval, returnOrigin: typeof window !== 'undefined' ? window.location.origin : '' });
                      if (data?.url) {
                        setIsSubscribeOrPayOpen(false);
                        await savePromise
                        window.location.href = data.url;
                      }
                    } catch (e) { console.error('Checkout failed:', e); }
                  }}>
                    Subscribe
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isQuickSendPaymentOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="quicksend-payment-title" onClick={(e) => { if (e.target === e.currentTarget) setIsQuickSendPaymentOpen(false) }}>
          <div className="modal modal--branded paywall-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 id="quicksend-payment-title">{`Pay $${tierPricing.oneTimePrice} to send your document`}</h3>
                <p>You'll be redirected to payment. After payment you'll return here to your document.</p>
              </div>
              <button type="button" className="ghost-button" onClick={() => setIsQuickSendPaymentOpen(false)} aria-label="Close">Close</button>
            </div>
            <div className="modal-body">
              <div className="paywall-actions">
                <button
                  type="button"
                  className="primary-button paywall-btn"
                  onClick={async () => {
                    if (user?.uid) {
                      try {
                        sessionStorage.setItem('quickSendPending', '1')
                      } catch (_) {}
                      await redirectToOneOffPayment()
                    }
                  }}
                >
                  {`Pay $${tierPricing.oneTimePrice} to send`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isLimitReachedOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="limit-reached-title" onClick={(e) => { if (e.target === e.currentTarget) { setIsLimitReachedOpen(false); setLimitReachedPendingAction(null); setUpgradeModalLimitReason(null) } }}>
          <div className="modal modal--branded paywall-modal limit-reached-modal" onClick={(e) => e.stopPropagation()}>
            <div className="limit-reached-modal__topbar" aria-hidden="true">
              <PublicPictureImg name="primedocumentsignlogowhite" alt={t('seo.brandLogoMarkAlt')} className="limit-reached-modal__brand-logo" width={28} height={28} />
              <span className="limit-reached-modal__brand-name">Prime Document Sign</span>
            </div>
            <div className="limit-reached-modal__header">
              <div className="limit-reached-modal__header-text">
                <h3 id="limit-reached-title">
                  {(isOneTimeUse || !(userSubscriptionId || userCustomerId)) ? 'Send limit reached' : 'Monthly limit reached'}
                </h3>
                <p className="limit-reached-modal__subtitle">
                  {(isOneTimeUse || !(userSubscriptionId || userCustomerId))
                    ? "You've used your sends. Would you like to top up your account?"
                    : 'Your monthly limit has been reached. Would you like to top up your account?'}
                </p>
              </div>
              <button type="button" className="limit-reached-modal__close" onClick={() => { setIsLimitReachedOpen(false); setLimitReachedPendingAction(null); setUpgradeModalLimitReason(null) }} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="modal-body limit-reached-modal__body">
              {(userSubscriptionId || userCustomerId) && ((userUsage.subscribedEmailTopUpCredit ?? 0) <= 0 && (userUsage.subscribedSignedAgreementTopUpCredit ?? 0) <= 0) && (
                <div className="limit-reached-subscribed-description">
                  <p>Top-ups add extra email sends and signed agreements to your account. These credits are used after your regular monthly allowance and do not expire at the end of the month, they last until you use them. You get a one-time batch based on your current plan.</p>
                </div>
              )}
              <div className="limit-reached-actions">
                {(isOneTimeUse || !(userSubscriptionId || userCustomerId)) ? (
                  <button
                    type="button"
                    className="primary-button"
                    onClick={() => {
                      // Non‑subscribed / one‑time and anonymous users:
                      // show the normal “subscription or one‑time” chooser.
                      setIsLimitReachedOpen(false)
                      setLimitReachedPendingAction(null)
                      setSubscribeOrPayShowOneTime(true)
                      setIsOneTimeUse(true)
                      setSubscriptionTier('premium')
                      setBillingCycle('yearly')
                      setIsSubscribeOrPayOpen(true)
                    }}
                  >
                    Top up account
                  </button>
                ) : (
                  <div className="limit-reached-subscribed-actions">
                    {(() => {
                      const emailTopUp = userUsage.subscribedEmailTopUpCredit ?? 0
                      const signedTopUp = userUsage.subscribedSignedAgreementTopUpCredit ?? 0
                      const bothTopUpExhausted = emailTopUp <= 0 && signedTopUp <= 0
                      if (!bothTopUpExhausted) {
                        return (
                          <button type="button" className="primary-button" onClick={() => { setIsLimitReachedOpen(false); setLimitReachedPendingAction(null); setIsUpgradeModalOpen(true) }}>
                            Upgrade subscription
                          </button>
                        )
                      }
                      const tierKey = (subscriptionTier || 'standard').toLowerCase()
                      const topUp = SUBSCRIBER_TOPUP_BY_TIER[tierKey] || SUBSCRIBER_TOPUP_BY_TIER.standard
                      return (
                        <>
                          <div className="limit-reached-topup-card">
                            <strong>{topUp.label} top-up</strong>: {topUp.emails} email sends, {topUp.signed} signed agreements, ${topUp.price}
                          </div>
                  <button
                    type="button"
                    className="primary-button"
                            onClick={async () => {
                              try {
                                const createTopUpSession = httpsCallable(functions, 'createSubscriberTopUpCheckoutSession')
                                const savePromise = saveDocumentStateForPaymentReturn()
                                const { data } = await createTopUpSession({
                                  tierId: tierKey,
                                  returnOrigin: typeof window !== 'undefined' ? window.location.origin : '',
                                })
                                if (data?.url) {
                                  await savePromise
                                  window.location.href = data.url
                                }
                              } catch (e) {
                                if (topUp.link) window.location.href = topUp.link
                                else reportErrorToAdmin('CreateSubscriberTopUpSession', e, { tierKey })
                              }
                            }}
                          >
                            Get credits now
                  </button>
                          <button type="button" className="ghost-button limit-reached-upgrade-btn" onClick={() => { setIsLimitReachedOpen(false); setLimitReachedPendingAction(null); setIsUpgradeModalOpen(true) }}>
                            Upgrade subscription
                          </button>
                        </>
                      )
                    })()}
                  </div>
                )}
                <button
                  type="button"
                  className="ghost-button"
                  onClick={() => { setIsLimitReachedOpen(false); setLimitReachedPendingAction(null); setUpgradeModalLimitReason(null) }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isUpgradeModalOpen && (userCustomerId || userSubscriptionId) && createPortal(
        <div className="modal-backdrop subscription-modal-backdrop" role="dialog" aria-modal="true" style={{ zIndex: 10000 }} onClick={(e) => { if (e.target === e.currentTarget) { setIsUpgradeModalOpen(false); setUpgradeModalLimitReason(null) } }}>
          <div className="modal subscription-modal subscription-modal--upgrade" onClick={(e) => e.stopPropagation()}>
            <div className="subscription-modal__header">
              <div className="subscription-modal__header-text">
                <h2 className="subscription-modal__title">Monthly limit reached</h2>
                <p className="subscription-modal__subtitle">
                  {upgradeModalLimitReason === 'signed'
                    ? "You've reached your monthly signed agreements limit. Please upgrade to get more monthly credits."
                    : "You've reached your monthly email limit. Please upgrade to get more monthly credits."}
                </p>
              </div>
              <button type="button" className="subscription-modal__close" onClick={() => { setIsUpgradeModalOpen(false); setUpgradeModalLimitReason(null) }} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>

            {(userCustomerId || userSubscriptionId) && (
            <div className="subscription-modal__current">
              <span className="subscription-modal__current-label">Current plan</span>
              <span className="subscription-modal__current-pill">
                {(subscriptionTier || subscriptionSavedTier || 'standard').charAt(0).toUpperCase() + (subscriptionTier || subscriptionSavedTier || 'standard').slice(1)} - {subscriptionBilling === 'annual' ? 'Annually' : 'Monthly'}
              </span>
            </div>
            )}

            <div className="subscription-modal__billing">
              <label className="subscription-modal__billing-label">View pricing</label>
              <div className="signup-billing-toggle">
                <button type="button" className={`toggle-option ${subscriptionBilling === 'annual' ? 'is-active' : ''}`} onClick={() => setSubscriptionBilling('annual')}>{t('pricing.billAnnually')}</button>
                <button type="button" className={`toggle-option ${subscriptionBilling === 'monthly' ? 'is-active' : ''}`} onClick={() => setSubscriptionBilling('monthly')}>{t('pricing.billMonthly')}</button>
              </div>
            </div>

            <div className="subscription-modal__tiers subscription-tiers signup-tiers">
              {(['basic', 'standard', 'premium']).map((tierId) => {
                const tierData = tiers[tierId] ?? TIER_SEED_DATA[tierId]
                const stripeArr = tierData?.Stripe ?? tierData?.stripe ?? []
                const monthlyOption = stripeArr.find((s) => (s.type || '').toLowerCase() === 'monthly')
                const annualOption = stripeArr.find((s) => (s.type || '').toLowerCase() === 'annual')
                const monthlyPrice = monthlyOption?.price ?? 0
                const annualPrice = annualOption?.price ?? 0
                const isYearly = subscriptionBilling === 'annual'
                const displayPrice = isYearly ? (annualPrice / 12) : monthlyPrice
                const displayPriceFormatted = (typeof displayPrice === 'number' && Number.isInteger(displayPrice)) ? displayPrice : (typeof displayPrice === 'number' ? displayPrice.toFixed(2) : displayPrice)
                const annualSavingsPercent = monthlyPrice > 0 && annualPrice >= 0 ? Math.round(((monthlyPrice * 12 - annualPrice) / (monthlyPrice * 12)) * 100) : 0
                const showSavingsBadge = isYearly && annualSavingsPercent > 0
                const hasSub = !!(userCustomerId || userSubscriptionId)
                const isCurrent = hasSub ? ((subscriptionSavedTier ?? subscriptionTier) === tierId) : (tierId === 'standard')
                const titles = { basic: t('pricing.tierBasic'), standard: t('pricing.tierStandard'), premium: t('pricing.tierPremium') }
                const descs = { basic: t('pricing.descBasic'), standard: t('pricing.descStandard'), premium: t('pricing.descPremium') }
                const BasicIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>)
                const PremiumIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>)
                const Icon = tierId === 'premium' ? PremiumIcon : BasicIcon
                return (
                  <div key={tierId} className={`tier-card ${isCurrent ? 'highlighted tier-card--current' : ''} ${tierId === 'standard' ? 'popular' : ''}`} role="button" tabIndex={0} onClick={() => handleUpdateSubscriptionPlan(tierId)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleUpdateSubscriptionPlan(tierId); } }}>
                    {hasSub && isCurrent && <span className="tier-badge tier-badge--current">{t('auth.currentPlan')}</span>}
                    {!isCurrent && <span className="tier-badge">{t('pricing.mostPopular')}</span>}
                    {showSavingsBadge && !isCurrent && <span className="tier-discount-badge">-{annualSavingsPercent}%</span>}
                    <div className="tier-header">
                      <div className="tier-icon-wrap"><Icon /></div>
                      <h4>{titles[tierId]}</h4>
                      <p className="tier-desc">{descs[tierId]}</p>
                    </div>
                    {isYearly && annualPrice > 0 && <p className="tier-annual-billing">Billed ${annualPrice}/yr</p>}
                    <div className="tier-price-row">
                      <span className="tier-price">${displayPriceFormatted}</span>
                      <span className="tier-period">{t('pricing.perMonth')}</span>
                    </div>
                    <ul className="tier-features">
                      {tierId === 'basic' && (<><li><CheckIcon /> {t('pricing.featureRecipients5')}</li><li><CheckIcon /> {t('pricing.featureEmails5')}</li><li><CheckIcon /> {t('pricing.featureTemplates3')}</li><li><CheckIcon /> {t('pricing.featureChars3k')}</li><li><CheckIcon /> {t('pricing.featureAttachmentsNone')}</li><li><CheckIcon /> {t('pricing.featurePdfUpload')}</li><li><CheckIcon /> {t('pricing.featureCustomFields')}</li></>)}
                      {tierId === 'standard' && (<><li><CheckIcon /> {t('pricing.featureRecipients10')}</li><li><CheckIcon /> {t('pricing.featureEmails50')}</li><li><CheckIcon /> {t('pricing.featureTemplates10')}</li><li><CheckIcon /> {t('pricing.featureChars20k')}</li><li><CheckIcon /> {t('pricing.featureAttachments3')}</li><li><CheckIcon /> {t('pricing.featureAuditTrail')}</li><li><CheckIcon /> {t('pricing.featurePdfUpload')}</li><li><CheckIcon /> {t('pricing.featurePdfExtract')}</li><li><CheckIcon /> {t('pricing.featureCustomFields')}</li><li><CheckIcon /> {t('pricing.featureCopyLink')}</li>{!appConfig.hideLogoFeature && <li><CheckIcon /> {t('pricing.featureLogo')}</li>}</>)}
                      {tierId === 'premium' && (<><li><CheckIcon /> {t('pricing.featureRecipients10')}</li><li><CheckIcon /> {t('pricing.featureEmails150')}</li><li><CheckIcon /> {t('pricing.featureTemplates25')}</li><li><CheckIcon /> {t('pricing.featureChars30k')}</li><li><CheckIcon /> {t('pricing.featureAttachments10')}</li><li><CheckIcon /> {t('pricing.featureAuditTrail')}</li><li><CheckIcon /> {t('pricing.featurePdfUpload')}</li><li><CheckIcon /> {t('pricing.featurePdfExtract')}</li><li><CheckIcon /> {t('pricing.featureCustomFields')}</li><li><CheckIcon /> {t('pricing.featureCopyLink')}</li>{!appConfig.hideVerificationFeature && <li><CheckIcon /> {t('pricing.featureVerification')}</li>}{!appConfig.hideLogoFeature && <li><CheckIcon /> {t('pricing.featureLogo')}</li>}</>)}
                    </ul>
                    <button type="button" className={`tier-button tier-button--action ${isCurrent ? 'tier-button--current' : 'primary'}`} onClick={(e) => { e.stopPropagation(); handleUpdateSubscriptionPlan(tierId); }}>
                      {(userCustomerId || userSubscriptionId) ? t('auth.manageSubscription') : t('auth.subscribe')}
                    </button>
                  </div>
                )
              })}
            </div>

            <div className="subscription-modal__footer">
              <p className="subscription-modal__hint">{t('auth.updatePlanHint')}</p>
              {(() => {
                const tierKey = (subscriptionTier || subscriptionSavedTier || 'standard').toLowerCase()
                const topUp = SUBSCRIBER_TOPUP_BY_TIER[tierKey] || SUBSCRIBER_TOPUP_BY_TIER.standard
                return (
                  <button
                    type="button"
                    className="ghost-button subscription-modal__topup-btn"
                    onClick={async () => {
                      try {
                        const createTopUpSession = httpsCallable(functions, 'createSubscriberTopUpCheckoutSession')
                        const savePromise = saveDocumentStateForPaymentReturn()
                        const { data } = await createTopUpSession({
                          tierId: tierKey,
                          returnOrigin: typeof window !== 'undefined' ? window.location.origin : '',
                        })
                        if (data?.url) {
                          await savePromise
                          window.location.href = data.url
                        }
                      } catch (e) {
                        if (topUp.link) window.location.href = topUp.link
                        else reportErrorToAdmin('CreateSubscriberTopUpSession', e, { tierKey })
                      }
                    }}
                  >
                    {t('auth.topUpCredits', { label: topUp.label, price: topUp.price })}
                  </button>
                )
              })()}
              {subscriptionUpdateStatus && <span className="subscription-modal__status">{subscriptionUpdateStatus}</span>}
            </div>
          </div>
        </div>,
        document.body
      )}

      {isTopUpOpen && createPortal(
        <div className="modal-backdrop subscription-modal-backdrop" role="dialog" aria-modal="true" style={{ zIndex: 10000 }} onClick={(e) => { if (e.target === e.currentTarget) { setIsTopUpOpen(false) } }}>
          <div className="modal subscription-modal subscription-modal--topup" onClick={(e) => e.stopPropagation()}>
            <div className="subscription-modal__header">
              <div className="subscription-modal__header-text">
                <h2 className="subscription-modal__title">{isOneTimeUse ? 'Add another send' : 'Top up your account'}</h2>
                <p className="subscription-modal__subtitle">
                  {`Pay $${tierPricing.oneTimePrice} for one more send with full Premium features. You'll be redirected to payment and returned here after.`}
                </p>
              </div>
              <button type="button" className="subscription-modal__close" onClick={() => setIsTopUpOpen(false)} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
              <div className="subscription-modal__tiers subscription-tiers signup-tiers topup-tiers topup-one-time">
                <div
                  className="tier-card popular highlighted"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setIsTopUpOpen(false)
                    redirectToOneOffPayment()
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsTopUpOpen(false); redirectToOneOffPayment() } }}
                >
                  <span className="tier-badge">1 SEND</span>
                  <div className="tier-header">
                    <div className="tier-icon-wrap">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
                    </div>
                    <h4>One-Off Premium</h4>
                    <p className="tier-desc">Full access for 1 send. All Premium features included.</p>
                  </div>
                  <div className="tier-price-row">
                    <span className="tier-price">${tierPricing.oneTimePrice}</span>
                    <span className="tier-period">one time</span>
                  </div>
                  <ul className="tier-features">
                    <li><CheckIcon /> {t('pricing.oneTimeOneSendPremium')}</li>
                    <li><CheckIcon /> {t('pricing.featureRecipients10')}</li>
                    <li><CheckIcon /> {t('pricing.featureChars30k')}</li>
                    <li><CheckIcon /> {t('pricing.featureAttachments10')}</li>
                    <li><CheckIcon /> {t('pricing.featureAuditTrail')}</li>
                    <li><CheckIcon /> {t('pricing.featurePdfUpload')}</li>
                    <li><CheckIcon /> {t('pricing.featurePdfExtract')}</li>
                    <li><CheckIcon /> {t('pricing.featureCopyLink')}</li>
                  </ul>
                  <button type="button" className="tier-button tier-button--action primary" onClick={(e) => { e.stopPropagation(); setIsTopUpOpen(false); redirectToOneOffPayment() }}>
                    {`Pay $${tierPricing.oneTimePrice}`}
                  </button>
                </div>
              </div>
          </div>
        </div>,
        document.body
      )}

      {purchaseSuccessData && createPortal(
        <div className="modal-backdrop subscription-modal-backdrop purchase-success-backdrop" role="dialog" aria-modal="true" aria-labelledby="purchase-success-title" onClick={(e) => { if (e.target === e.currentTarget) setPurchaseSuccessData(null) }}>
          <div className="modal subscription-modal purchase-success-modal">
            <div className="purchase-success-icon-wrap">
              <svg className="purchase-success-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h2 id="purchase-success-title" className="purchase-success-title">
              {purchaseSuccessData.type === 'oneoff' ? 'Payment successful!' : 'Thank you for your purchase'}
            </h2>
            <p className="purchase-success-subtitle">
              {purchaseSuccessData.type === 'oneoff' ? (
                <>{purchaseSuccessData.validity}</>
              ) : purchaseSuccessData.type === 'topup' ? (
                <>Your <strong>{purchaseSuccessData.tierName}</strong> top-up has been applied: {purchaseSuccessData.details}. {purchaseSuccessData.validity}.</>
              ) : (
                <>You&apos;re now on the <strong>{purchaseSuccessData.tierName}</strong> plan{purchaseSuccessData.billing ? ` (${purchaseSuccessData.billing})` : ''}. {purchaseSuccessData.validity || 'Your new limits are active.'}</>
              )}
            </p>
            {purchaseSuccessData.type === 'oneoff' && (purchaseSuccessData.credits ?? emailOneOffCredits ?? 0) > 0 && (
              <div className="purchase-success-credits-banner send-card-credits-banner" style={{ marginTop: 16, marginBottom: 8 }}>
                <span className="send-card-credits-label">{t('app.emailCredits')}</span>
                <span className="send-card-credits-value">{purchaseSuccessData.credits ?? emailOneOffCredits}</span>
                <span className="send-card-credits-hint">{t('app.creditHintDeliver')}</span>
              </div>
            )}
            {purchaseSuccessData.price && (
              <p className="purchase-success-price">{purchaseSuccessData.price}</p>
            )}
            <div className="purchase-success-actions">
              {purchaseSuccessData.type === 'oneoff' && (
            <button
              type="button"
                  className="primary-button purchase-success-back"
                  onClick={() => {
                    setView('app')
                    setPurchaseSuccessData(null)
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8 }}>
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                  Back to document
            </button>
              )}
                    <button
                      type="button"
                className={purchaseSuccessData.type === 'oneoff' ? 'ghost-button purchase-success-close' : 'primary-button purchase-success-close'}
                onClick={() => setPurchaseSuccessData(null)}
                    >
                {purchaseSuccessData.type === 'oneoff' ? 'Close' : 'Close'}
                    </button>
                  </div>
                </div>
        </div>,
        document.body
      )}

      {isSubscriptionOpen && (
        <div className="modal-backdrop subscription-modal-backdrop" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) { setIsSubscriptionOpen(false); setIsAccountOpen(true) } }}>
          <div className="modal subscription-modal subscription-modal--account">
            <div className="account-modal__header modal__header--compact subscription-modal__header--compact">
              <h2 className="modal__title subscription-modal__title">My Subscription</h2>
              <button
                type="button"
                className="account-modal__close ghost-button"
                onClick={() => { setIsSubscriptionOpen(false); setIsAccountOpen(true) }}
                aria-label={t('modal.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="subscription-modal__body subscription-modal__body--account">
              <div className="modal__card subscription-modal__intro-card">
                <p className="modal__subtitle" style={{ margin: 0 }}>Manage your plan, payment method, and billing in Stripe.</p>
            </div>

            {(userCustomerId || userSubscriptionId) && (
            <div className="subscription-modal__current">
              <span className="subscription-modal__current-label">Current plan</span>
              <span className="subscription-modal__current-pill">
                {subscriptionTier.charAt(0).toUpperCase() + subscriptionTier.slice(1)} - {subscriptionBilling === 'annual' ? 'Annually' : 'Monthly'}
              </span>
            </div>
            )}

            {(emailOneOffCredits || 0) > 0 && (
              <div className="subscription-modal__credits">
                <span className="subscription-modal__credits-label">Email One Off Credits</span>
                <span className="subscription-modal__credits-value">{emailOneOffCredits}</span>
                <span className="subscription-modal__credits-hint">1 credit = 1 email send</span>
              </div>
            )}

            <div className="subscription-modal__billing">
              <label className="subscription-modal__billing-label">View pricing</label>
              <div className="signup-billing-toggle">
                <button type="button" className={`toggle-option ${subscriptionBilling === 'annual' ? 'is-active' : ''}`} onClick={() => setSubscriptionBilling('annual')}>{t('pricing.billAnnually')}</button>
                <button type="button" className={`toggle-option ${subscriptionBilling === 'monthly' ? 'is-active' : ''}`} onClick={() => setSubscriptionBilling('monthly')}>{t('pricing.billMonthly')}</button>
              </div>
            </div>

            <div className="subscription-modal__tiers subscription-tiers signup-tiers">
              {(['basic', 'standard', 'premium']).map((tierId) => {
                const tierData = tiers[tierId] ?? TIER_SEED_DATA[tierId]
                const stripeArr = tierData?.Stripe ?? tierData?.stripe ?? []
                const monthlyOption = stripeArr.find((s) => (s.type || '').toLowerCase() === 'monthly')
                const annualOption = stripeArr.find((s) => (s.type || '').toLowerCase() === 'annual')
                const monthlyPrice = monthlyOption?.price ?? 0
                const annualPrice = annualOption?.price ?? 0
                const isYearly = subscriptionBilling === 'annual'
                const displayPrice = isYearly ? (annualPrice / 12) : monthlyPrice
                const displayPriceFormatted = (typeof displayPrice === 'number' && Number.isInteger(displayPrice)) ? displayPrice : (typeof displayPrice === 'number' ? displayPrice.toFixed(2) : displayPrice)
                const annualSavingsPercent = monthlyPrice > 0 && annualPrice >= 0 ? Math.round(((monthlyPrice * 12 - annualPrice) / (monthlyPrice * 12)) * 100) : 0
                const showSavingsBadge = isYearly && annualSavingsPercent > 0
                const hasSub = !!(userCustomerId || userSubscriptionId)
                const isCurrent = hasSub ? ((subscriptionSavedTier ?? subscriptionTier) === tierId) : (tierId === 'standard')
                const titles = { basic: t('pricing.tierBasic'), standard: t('pricing.tierStandard'), premium: t('pricing.tierPremium') }
                const descs = { basic: t('pricing.descBasic'), standard: t('pricing.descStandard'), premium: t('pricing.descPremium') }
                const BasicIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>)
                const PremiumIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>)
                const Icon = tierId === 'premium' ? PremiumIcon : BasicIcon
                return (
                  <div key={tierId} className={`tier-card ${isCurrent ? 'highlighted tier-card--current' : ''} ${tierId === 'standard' ? 'popular' : ''}`} role="button" tabIndex={0} onClick={() => handleUpdateSubscriptionPlan(tierId)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleUpdateSubscriptionPlan(tierId); } }}>
                    {hasSub && isCurrent && <span className="tier-badge tier-badge--current">{t('auth.currentPlan')}</span>}
                    {!isCurrent && <span className="tier-badge">{t('pricing.mostPopular')}</span>}
                    {showSavingsBadge && !isCurrent && <span className="tier-discount-badge">-{annualSavingsPercent}%</span>}
                    <div className="tier-header">
                      <div className="tier-icon-wrap"><Icon /></div>
                      <h4>{titles[tierId]}</h4>
                      <p className="tier-desc">{descs[tierId]}</p>
                    </div>
                    {isYearly && annualPrice > 0 && <p className="tier-annual-billing">Billed ${annualPrice}/yr</p>}
                    <div className="tier-price-row">
                      <span className="tier-price">${displayPriceFormatted}</span>
                      <span className="tier-period">/mo</span>
                    </div>
                    <ul className="tier-features">
                      {tierId === 'basic' && (<><li><CheckIcon /> {t('pricing.featureRecipients5')}</li><li><CheckIcon /> {t('pricing.featureEmails5')}</li><li><CheckIcon /> {t('pricing.featureTemplates3')}</li><li><CheckIcon /> {t('pricing.featureChars3k')}</li><li><CheckIcon /> {t('pricing.featureAttachmentsNone')}</li><li><CheckIcon /> {t('pricing.featurePdfUpload')}</li><li><CheckIcon /> {t('pricing.featureCustomFields')}</li></>)}
                      {tierId === 'standard' && (<><li><CheckIcon /> {t('pricing.featureRecipients10')}</li><li><CheckIcon /> {t('pricing.featureEmails50')}</li><li><CheckIcon /> {t('pricing.featureTemplates10')}</li><li><CheckIcon /> {t('pricing.featureChars20k')}</li><li><CheckIcon /> {t('pricing.featureAttachments3')}</li><li><CheckIcon /> {t('pricing.featureAuditTrail')}</li><li><CheckIcon /> {t('pricing.featurePdfUpload')}</li><li><CheckIcon /> {t('pricing.featurePdfExtract')}</li><li><CheckIcon /> {t('pricing.featureCustomFields')}</li><li><CheckIcon /> {t('pricing.featureCopyLink')}</li>{!appConfig.hideLogoFeature && <li><CheckIcon /> {t('pricing.featureLogo')}</li>}</>)}
                      {tierId === 'premium' && (<><li><CheckIcon /> {t('pricing.featureRecipients10')}</li><li><CheckIcon /> {t('pricing.featureEmails150')}</li><li><CheckIcon /> {t('pricing.featureTemplates25')}</li><li><CheckIcon /> {t('pricing.featureChars30k')}</li><li><CheckIcon /> {t('pricing.featureAttachments10')}</li><li><CheckIcon /> {t('pricing.featureAuditTrail')}</li><li><CheckIcon /> {t('pricing.featurePdfUpload')}</li><li><CheckIcon /> {t('pricing.featurePdfExtract')}</li><li><CheckIcon /> {t('pricing.featureCustomFields')}</li><li><CheckIcon /> {t('pricing.featureCopyLink')}</li>{!appConfig.hideVerificationFeature && <li><CheckIcon /> {t('pricing.featureVerification')}</li>}{!appConfig.hideLogoFeature && <li><CheckIcon /> {t('pricing.featureLogo')}</li>}</>)}
                    </ul>
                    <button
                      type="button"
                      className={`tier-button tier-button--action ${isCurrent ? 'tier-button--current' : 'primary'}`}
                      onClick={(e) => { e.stopPropagation(); handleUpdateSubscriptionPlan(tierId); }}
                    >
                      {(userCustomerId || userSubscriptionId) ? t('auth.manageSubscription') : t('auth.subscribe')}
                    </button>
                  </div>
                )
              })}
            </div>

            <div className="subscription-modal__footer">
              <p className="subscription-modal__hint">
                {(userCustomerId || userSubscriptionId)
                  ? t('auth.updatePlanStripe')
                  : t('auth.subscribeToPlan')}
              </p>
              {subscriptionUpdateStatus && (
                <span className="subscription-modal__status">{subscriptionUpdateStatus}</span>
              )}
            </div>
            </div>
          </div>
        </div>
      )}

      {isContactSupportOpen && (
        <div className="modal-backdrop contact-support-modal-backdrop" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) { setIsContactSupportOpen(false); setIsAccountOpen(true) } }}>
          <div className="modal contact-support-modal modal--compact-branded" aria-labelledby="contact-support-modal-title" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal__header modal__header--compact contact-support-modal__header-row">
              <div className="contact-support-modal__title-block">
                <h3 className="modal__title" id="contact-support-modal-title">{t('footer.contactSupport')}</h3>
                <p className="contact-support-modal__tagline">{t('contact.subtitle')}</p>
              </div>
              <button
                type="button"
                className="account-modal__close ghost-button"
                onClick={() => { setIsContactSupportOpen(false); setIsAccountOpen(true) }}
                aria-label={t('modal.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="contact-support-modal__shell">
              <ContactForm
                {...contactFormProps}
                className="contact-support-modal__scroll contact-support-form"
                compact
                onSubmit={() => handleContactSubmit('LOGGED IN USER', 'Contact support enquiry sent successfully!')}
              />
            </div>
          </div>
        </div>
      )}

      {isLayoutEditorOpen && (
        <div className="modal-backdrop layout-editor-backdrop send-confirm-modal-backdrop" role="dialog" aria-modal="true" style={{ zIndex: 10000 }} onClick={(e) => { if (e.target === e.currentTarget) { setIsLayoutEditorOpen(false); setEditFieldTargetId(null) } }}>
          <div className="modal layout-editor-modal send-confirm-modal modal--compact-branded" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <div className="account-modal__header modal__header--compact layout-editor-modal__header">
              <div className="layout-editor-modal__header-top">
                <h3 className="modal__title">{t('modal.editFieldLayout')}</h3>
                <button type="button" className="account-modal__close ghost-button" onClick={() => { setIsLayoutEditorOpen(false); setEditFieldTargetId(null) }} aria-label={t('modal.close')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
              <p className="layout-editor-modal__header-hint">{t('modal.editFieldLayoutHint')}</p>
            </div>
            <div className="send-confirm-modal__body layout-editor-modal__body">
            <div className="layout-columns-selector">
              {/* Desktop: one row = column buttons + divider + add buttons (no Columns label) */}
              <div className="layout-toolbar-desktop">
              <div className="layout-columns-btns">
                {[1, 2].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`layout-col-btn ${editColumns === n ? 'active' : ''}`}
                    onClick={() => {
                      setEditColumns(n)
                      if (n === 1) setEditLayout((prev) => prev.map((f) => ({ ...f, column: 0 })))
                    }}
                  >
                      {n === 1 ? t('modal.colOne') : t('modal.colTwo')}
                  </button>
                ))}
              </div>
              <div className="layout-toolbar-divider" />
                <div className="layout-add-btns-row">
              <button
                type="button"
                className="layout-add-spacer-btn"
                onClick={() => {
                  const id = `spacer_${Date.now()}`
                      setEditLayout((prev) => [...prev, { id, label: t('modal.space'), fieldType: 'spacer', column: 0, isSpacer: true }])
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    {t('modal.addSpace')}
                  </button>
                  <button
                    type="button"
                    className="layout-add-spacer-btn layout-add-field-btn"
                    onClick={() => {
                      setAddFieldLabel('')
                      setAddFieldType('text')
                      setAddFieldError('')
                      setAddFieldSig1(true)
                      setAddFieldSig2(true)
                      setAddFieldContent('')
                      setAddFieldOptions([])
                      setAddFieldOptionInput('')
                      setIsAddFieldOpen(true)
                    }}
                    disabled={customFields.length >= (appConfig.maxCustomFields ?? 20) || signingSectionSize.atLimit}
                    title={signingSectionSize.atLimit ? `Signing section limit (${signatureTextLimitKB} KB) reached. Remove some content first.` : customFields.length >= (appConfig.maxCustomFields ?? 20) ? `Maximum ${appConfig.maxCustomFields ?? 20} custom fields.` : undefined}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    {t('app.addField')}
              </button>
                </div>
              </div>
              {/* Mobile only: one row = Column size picklist + Add space + Add field */}
              <div className="layout-toolbar-mobile">
                <label className="layout-column-size-label" htmlFor="layout-column-size-picklist">{t('modal.columnSize')}</label>
                <select
                  id="layout-column-size-picklist"
                  className="layout-column-size-picklist"
                  value={editColumns}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10)
                    setEditColumns(n)
                    if (n === 1) setEditLayout((prev) => prev.map((f) => ({ ...f, column: 0 })))
                  }}
                  aria-label={t('modal.columnSize')}
                >
                  <option value={1}>{t('modal.colOne')}</option>
                  <option value={2}>{t('modal.colTwo')}</option>
                </select>
                <button
                  type="button"
                  className="layout-add-spacer-btn"
                  onClick={() => {
                    const id = `spacer_${Date.now()}`
                    setEditLayout((prev) => [...prev, { id, label: t('modal.space'), fieldType: 'spacer', column: 0, isSpacer: true }])
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                  {t('modal.addSpace')}
                </button>
                <button
                  type="button"
                  className="layout-add-spacer-btn layout-add-field-btn"
                  onClick={() => {
                    setAddFieldLabel('')
                    setAddFieldType('text')
                    setAddFieldError('')
                    setAddFieldSig1(true)
                    setAddFieldSig2(true)
                    setAddFieldContent('')
                    setAddFieldOptions([])
                    setAddFieldOptionInput('')
                    setIsAddFieldOpen(true)
                  }}
                  disabled={customFields.length >= (appConfig.maxCustomFields ?? 20) || signingSectionSize.atLimit}
                  title={signingSectionSize.atLimit ? `Signing section limit (${signatureTextLimitKB} KB) reached.` : undefined}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                  {t('app.addField')}
                </button>
              </div>
            </div>
            <div className={`layout-editor-grid layout-editor-grid--${editColumns}col`}>
              {Array.from({ length: editColumns }, (_, colIdx) => {
                const colFields = editLayout.filter((f) => f.column === colIdx)
                return (
                  <div
                    key={colIdx}
                    className={`layout-editor-col ${dragOverCol === colIdx && !dragOverId ? 'drag-over-col' : ''}`}
                    onDragOver={(e) => { e.preventDefault(); setDragOverCol(colIdx); setDragOverId(null) }}
                    onDrop={(e) => { e.preventDefault(); handleLayoutDrop(colIdx, null) }}
                  >
                    <div className="layout-col-header">
                      {editColumns === 1 ? t('modal.column1') : colIdx === 0 ? t('modal.column1') : t('modal.column2')}
                    </div>
                    <div className="layout-col-fields">
                      {colFields.map((field) => (
                        <div key={field.id}>
                          {removingFieldId === field.id ? (
                            <div className="layout-field-confirm-remove">
                              <span>{t('modal.removeFromLayout', { name: field.isSpacer ? t('modal.space') : (field.labelOverride || field.label) })}</span>
                              <div className="layout-field-confirm-actions">
                                <button type="button" className="layout-confirm-yes" onClick={() => {
                                  setEditLayout((prev) => field.isSpacer
                                    ? prev.filter((f) => f.id !== field.id)
                                    : prev.map((f) => f.id === field.id ? { ...f, excluded: true, column: -1 } : f))
                                  setRemovingFieldId(null)
                                }}>{t('modal.yesRemove')}</button>
                                <button type="button" className="layout-confirm-no" onClick={() => setRemovingFieldId(null)}>{t('modal.keepIt')}</button>
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`layout-field-card${field.isSpacer ? ' layout-field-card--spacer' : ''}${field.excluded ? ' layout-field-card--excluded' : ''}${dragFieldId === field.id ? ' is-dragging' : ''}${dragOverId === field.id ? ' is-drag-target' : ''}`}
                              draggable={!field.excluded}
                              onDragStart={(e) => { if (field.excluded) return; e.dataTransfer.effectAllowed = 'move'; setDragFieldId(field.id) }}
                              onDragEnd={() => { setDragFieldId(null); setDragOverId(null); setDragOverCol(null) }}
                              onDragOver={(e) => { if (field.excluded) return; e.preventDefault(); e.stopPropagation(); setDragOverId(field.id); setDragOverCol(colIdx) }}
                              onDrop={(e) => { if (field.excluded) return; e.preventDefault(); e.stopPropagation(); handleLayoutDrop(colIdx, field.id) }}
                            >
                              {!field.excluded && (
                                <svg className="drag-handle-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/></svg>
                              )}
                              {field.isSpacer ? (
                                <>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
                                  <span className="layout-field-label layout-field-label--spacer">{t('modal.emptySpace')}</span>
                                </>
                              ) : editingLabelId === field.id ? (
                                <>
                                  <input
                                    className="layout-label-edit-input"
                                    autoFocus
                                    value={editingLabelValue}
                                    onChange={(e) => setEditingLabelValue(e.target.value)}
                                    onBlur={() => {
                                      setEditLayout((prev) => prev.map((f) => f.id === field.id ? { ...f, labelOverride: editingLabelValue || f.label } : f))
                                      setEditingLabelId(null)
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') e.target.blur()
                                      if (e.key === 'Escape') { setEditingLabelId(null) }
                                    }}
                                  />
                                </>
                              ) : (
                                <>
                                  <div className="layout-field-card__text">
                                  <span className="layout-field-label">{field.labelOverride || field.label}</span>
                                    <span className="layout-field-type-badge">{field.fieldType === 'textDisplay' ? 'Text Display' : field.fieldType === 'picklist' ? 'Picklist' : field.fieldType === 'number' ? 'Number' : field.fieldType === 'time' ? 'Time' : field.fieldType}</span>
                                  </div>
                                  {!field.excluded && (
                                    <button
                                      type="button"
                                      className="layout-field-edit-label-btn"
                                      title={field.id.startsWith('cf_') ? 'Edit field' : 'Edit label'}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        if (field.id.startsWith('cf_')) {
                                          const cf = customFields.find((c) => c.id === field.id)
                                          if (cf) {
                                            setEditFieldLabel(cf.label)
                                            setEditFieldType(cf.type)
                                            setEditFieldContent(cf.content || '')
                                            setEditFieldOptions(cf.options || [])
                                            setEditFieldOptionInput('')
                                            const addedTo = cf.addedToSigners ?? [0, 1]
                                            setEditFieldSig1(addedTo.includes(0))
                                            setEditFieldSig2(addedTo.includes(1))
                                            setEditFieldError('')
                                            setEditFieldTargetId(field.id)
                                          }
                                        } else {
                                          setEditingLabelValue(field.labelOverride || field.label)
                                          setEditingLabelId(field.id)
                                        }
                                      }}
                                    >
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                    </button>
                                  )}
                                </>
                              )}
                              <button
                                type="button"
                                className="layout-spacer-remove"
                                title={field.excluded ? t('modal.restoreToLayout') : t('modal.removeFromLayoutTitle')}
                                onClick={() => {
                                  if (field.excluded) {
                                    setEditLayout((prev) => prev.map((f) => f.id === field.id ? { ...f, excluded: false, column: 0 } : f))
                                  } else {
                                    setRemovingFieldId(field.id)
                                  }
                                }}
                              >
                                {field.excluded ? (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                                ) : (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      {colFields.length === 0 && (
                        <div className="layout-col-empty-drop">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 5v14M5 12l7 7 7-7"/></svg>
                          {t('modal.dropFieldsHere')}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
            {editLayout.some((f) => f.excluded) && (
              <div className="layout-excluded-section">
                <div className="layout-excluded-label">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21H7a2 2 0 01-2-2V5a2 2 0 012-2h10a2 2 0 012 2v14a2 2 0 01-2 2z"/><path d="M9 9h6M9 13h6M9 17h4"/></svg>
                  {t('modal.removedFromLayout')}
                </div>
                <div className="layout-excluded-chips">
                  {editLayout.filter((f) => f.excluded).map((field) => (
                    <span key={field.id} className="layout-excluded-chip">
                      {field.labelOverride || field.label}
                      <button
                        type="button"
                        className="layout-excluded-restore"
                        title={t('modal.restoreToLayout')}
                        onClick={() => setEditLayout((prev) => prev.map((f) => f.id === field.id ? { ...f, excluded: false, column: 0 } : f))}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="modal-actions send-confirm-modal__actions layout-editor-modal__actions">
              <button type="button" className="ghost-button" onClick={() => { setIsLayoutEditorOpen(false); setEditFieldTargetId(null) }}>{t('app.cancel')}</button>
              <button type="button" className="primary-button" onClick={handleSaveLayout}>{t('modal.saveLayout')}</button>
            </div>
            </div>
          </div>
        </div>
      )}

      {editFieldTargetId && (() => {
        const cf = customFields.find((c) => c.id === editFieldTargetId)
        const hasData = cf && signers.some((s) => {
          const v = (s.customFields || {})[cf.id]
          return v !== undefined && v !== '' && v !== false
        })
        return (
          <div className="modal-backdrop send-confirm-modal-backdrop" role="dialog" aria-modal="true" style={{ zIndex: 10001 }} onClick={(e) => { if (e.target === e.currentTarget) { setEditFieldTargetId(null); setEditFieldError('') } }}>
            <div className="modal send-confirm-modal modal--compact-branded" onClick={(e) => e.stopPropagation()}>
              <div className="account-modal__header modal__header--compact">
                <h3 className="modal__title">{t('modal.editCustomField')}</h3>
                <button
                  type="button"
                  className="account-modal__close ghost-button"
                  onClick={() => { setEditFieldTargetId(null); setEditFieldError('') }}
                  aria-label={t('modal.close')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              </div>
              <div className="modal__body--card-wrap">
                <div className="modal__card">
                  <p className="modal__subtitle" style={{ margin: 0 }}>{t('modal.editCustomFieldHint')}</p>
              </div>
              <div className="modal-body">
              {editFieldType !== 'textDisplay' && (
                <label className={`field${editFieldError ? ' field--error' : ''}`}>
                  <span>{t('modal.fieldLabel')}</span>
                  <input
                    type="text"
                    placeholder={t('modal.fieldLabelPlaceholder')}
                    value={editFieldLabel}
                    onChange={(e) => {
                      setEditFieldLabel(e.target.value)
                      if (editFieldError) setEditFieldError('')
                    }}
                    aria-invalid={!!editFieldError}
                    aria-describedby={editFieldError ? 'edit-field-error' : undefined}
                  />
                  {editFieldError && (
                    <span id="edit-field-error" className="field-error" role="alert">
                      {editFieldError}
                    </span>
                  )}
                </label>
              )}
                <div className="field">
                  <span>{t('modal.fieldType')}</span>
                  {hasData && (
                    <p className="field-hint" style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                      {t('modal.typeHasDataHint')}
                    </p>
                  )}
                  <div className="field-type-picker">
                    {FIELD_TYPES.map(({ value, icon }) => (
                      <button
                        key={value}
                        type="button"
                        className={`field-type-btn${editFieldType === value ? ' active' : ''}${hasData ? ' field-type-btn--disabled' : ''}`}
                        onClick={() => { if (!hasData) setEditFieldType(value) }}
                        disabled={hasData}
                        title={hasData ? t('modal.typeHasDataTitle') : undefined}
                      >
                        {icon}
                        {t(`modal.${value}`)}
                      </button>
                    ))}
                  </div>
                </div>
                {editFieldType === 'textDisplay' && (
                  <label className="field field--text-display-editor">
                    <span>{t('modal.displayContent')}</span>
                    <p className="field-hint">Shown as informational text only. Recipients cannot edit this.</p>
                    <textarea
                      placeholder={t('modal.displayContentPlaceholder')}
                      value={editFieldContent}
                      maxLength={100}
                      rows={4}
                      onChange={(e) => {
                        setEditFieldContent(e.target.value.slice(0, 100))
                        if (editFieldError) setEditFieldError('')
                      }}
                    />
                    <span className="field-char-count">{editFieldContent.length}/100</span>
                    {editFieldError && (
                      <span className="field-error" role="alert">
                        {editFieldError}
                      </span>
                    )}
                  </label>
                )}
                {editFieldType === 'picklist' && (
                  <div className="field picklist-options-field">
                    <span>{t('modal.picklistOptions')}</span>
                    <div className="picklist-options-list">
                      {editFieldOptions.map((opt, idx) => (
                        <div key={idx} className="picklist-option-row">
                          <span className="picklist-option-value">{opt}</span>
                          <button type="button" className="ghost-button picklist-option-remove" onClick={() => setEditFieldOptions((prev) => prev.filter((_, i) => i !== idx))} aria-label={t('modal.removeFromLayoutTitle')}>×</button>
                        </div>
                      ))}
                      {editFieldOptions.length < 10 && (
                        <div className="picklist-option-add">
                          <input
                            type="text"
                            placeholder={t('modal.typeAndPressAdd')}
                            value={editFieldOptionInput}
                            onChange={(e) => setEditFieldOptionInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const v = editFieldOptionInput.trim(); if (v && editFieldOptions.length < 10) { setEditFieldOptions((prev) => [...prev, v]); setEditFieldOptionInput('') } } }}
                          />
                          <button type="button" className="ghost-button" onClick={() => { const v = editFieldOptionInput.trim(); if (v && editFieldOptions.length < 10) { setEditFieldOptions((prev) => [...prev, v]); setEditFieldOptionInput('') } }}>{t('modal.add')}</button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="field">
                  <p className="field-hint" style={{ marginBottom: '0.5rem', color: '#64748b', fontSize: '0.9rem' }}>{t('modal.addedToSignatureHint')}</p>
                  <span>{t('modal.addedToSignature')}</span>
                  <div className="field-checkbox-group" style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={editFieldSig1}
                        onChange={(e) => setEditFieldSig1(e.target.checked)}
                      />
                      {t('modal.signature1')}
                    </label>
                    {totalSignatures === 2 && (
                      <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={editFieldSig2}
                          onChange={(e) => setEditFieldSig2(e.target.checked)}
                        />
                        {t('modal.signature2')}
                      </label>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal__actions--below modal-actions">
                <button
                  type="button"
                  className="ghost-button"
                  onClick={() => { setEditFieldTargetId(null); setEditFieldError('') }}
                >
                  {t('modal.close')}
                </button>
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => {
                    const textDisplayLabel = getTextDisplayFieldLabel(editFieldContent)
                    const label = editFieldType === 'textDisplay' ? textDisplayLabel : editFieldLabel.trim()
                    if (editFieldType !== 'textDisplay' && !label) {
                      setEditFieldError(t('modal.fieldLabelRequired'))
                      return
                    }
                    if (editFieldType === 'textDisplay' && !editFieldContent.trim()) {
                      setEditFieldError(t('modal.displayContentRequired'))
                      return
                    }
                    if (editFieldType === 'picklist' && editFieldOptions.length === 0) {
                      setEditFieldError(t('modal.picklistOptionRequired'))
                      return
                    }
                    const addedToSigners = []
                    if (editFieldSig1) addedToSigners.push(0)
                    if (editFieldSig2 && totalSignatures === 2) addedToSigners.push(1)
                    if (addedToSigners.length === 0) {
                      setEditFieldError(t('modal.selectOneSignature'))
                      return
                    }
                    const updateData = { label, type: editFieldType, addedToSigners }
                    if (editFieldType === 'textDisplay') updateData.content = editFieldContent.trim().slice(0, 100)
                    if (editFieldType === 'picklist') updateData.options = [...editFieldOptions]
                    setCustomFields((prev) => prev.map((cfItem) =>
                      cfItem.id === editFieldTargetId
                        ? { ...cfItem, ...updateData }
                        : cfItem
                    ))
                    setLabelOverrides((prev) => {
                      const next = { ...prev }
                      delete next[editFieldTargetId]
                      return next
                    })
                    const defaultVal = editFieldType === 'checkbox' ? false : ''
                    setSigners((prev) => prev.map((s, idx) => {
                      const hadField = (cf?.addedToSigners ?? [0, 1]).includes(idx)
                      const hasField = addedToSigners.includes(idx)
                      if (hadField && !hasField) {
                        const { [editFieldTargetId]: _, ...rest } = s.customFields || {}
                        return { ...s, customFields: rest }
                      }
                      if (!hadField && hasField) {
                        return { ...s, customFields: { ...(s.customFields || {}), [editFieldTargetId]: defaultVal } }
                      }
                      if (hadField && hasField) {
                        const typeChanged = cf?.type !== editFieldType
                        return { ...s, customFields: { ...(s.customFields || {}), [editFieldTargetId]: typeChanged ? defaultVal : (s.customFields || {})[editFieldTargetId] } }
                      }
                      return s
                    }))
                    setEditLayout((prev) => prev.map((f) =>
                      f.id === editFieldTargetId ? { ...f, label, labelOverride: '' } : f
                    ))
                    setEditFieldTargetId(null)
                    setEditFieldError('')
                  }}
                >
                  {t('modal.save')}
                </button>
              </div>
              </div>
            </div>
          </div>
        )
      })()}

      {isAddFieldOpen && (
        <div className="modal-backdrop send-confirm-modal-backdrop" role="dialog" aria-modal="true" style={{ zIndex: 10002 }} onClick={(e) => { if (e.target === e.currentTarget) { setIsAddFieldOpen(false); setAddFieldError(''); setAddFieldSig1(true); setAddFieldSig2(true); setAddFieldContent(''); setAddFieldOptions([]); setAddFieldOptionInput('') } }}>
          <div className="modal send-confirm-modal modal--compact-branded add-field-modal" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal__header modal__header--compact">
              <h3 className="modal__title">{t('modal.addCustomField')}</h3>
              <button
                type="button"
                className="account-modal__close ghost-button"
                onClick={() => { setIsAddFieldOpen(false); setAddFieldError(''); setAddFieldSig1(true); setAddFieldSig2(true); setAddFieldContent(''); setAddFieldOptions([]); setAddFieldOptionInput('') }}
                aria-label={t('modal.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="modal__body--card-wrap">
              <div className="modal__card">
                <p className="modal__subtitle" style={{ margin: 0 }}>{t('modal.addCustomFieldHint')}</p>
            </div>
            <div className="modal-body">
              {addFieldType !== 'textDisplay' && (
              <label className={`field${addFieldError ? ' field--error' : ''}`}>
                  <span>{t('modal.fieldLabel')}</span>
                <input
                  type="text"
                    placeholder={t('modal.fieldLabelPlaceholder')}
                  value={addFieldLabel}
                  onChange={(e) => {
                    setAddFieldLabel(e.target.value)
                    if (addFieldError) setAddFieldError('')
                  }}
                  aria-invalid={!!addFieldError}
                  aria-describedby={addFieldError ? 'add-field-error' : undefined}
                />
                {addFieldError && (
                  <span id="add-field-error" className="field-error" role="alert">
                    {addFieldError}
                  </span>
                )}
              </label>
              )}
              <div className="field">
                <span>{t('modal.fieldType')}</span>
                <div className="field-type-picker">
                  {FIELD_TYPES.map(({ value, icon }) => (
                    <button
                      key={value}
                      type="button"
                      className={`field-type-btn${addFieldType === value ? ' active' : ''}`}
                      onClick={() => setAddFieldType(value)}
                    >
                      {icon}
                      {t(`modal.${value}`)}
                    </button>
                  ))}
                </div>
              </div>
              {addFieldType === 'textDisplay' && (
                <label className="field field--text-display-editor">
                  <span>{t('modal.displayContent')}</span>
                  <p className="field-hint">Shown as informational text only. Recipients cannot edit this.</p>
                  <textarea
                    placeholder={t('modal.displayContentPlaceholder')}
                    value={addFieldContent}
                    maxLength={100}
                    rows={4}
                    onChange={(e) => {
                      setAddFieldContent(e.target.value.slice(0, 100))
                      if (addFieldError) setAddFieldError('')
                    }}
                  />
                  <span className="field-char-count">{addFieldContent.length}/100</span>
                  {addFieldError && (
                    <span className="field-error" role="alert">
                      {addFieldError}
                    </span>
                  )}
                </label>
              )}
              {addFieldType === 'picklist' && (
                <div className="field picklist-options-field">
                  <span>{t('modal.picklistOptions')}</span>
                  <div className="picklist-options-list">
                    {addFieldOptions.map((opt, idx) => (
                      <div key={idx} className="picklist-option-row">
                        <span className="picklist-option-value">{opt}</span>
                        <button type="button" className="ghost-button picklist-option-remove" onClick={() => setAddFieldOptions((prev) => prev.filter((_, i) => i !== idx))} aria-label={t('modal.removeFromLayoutTitle')}>×</button>
            </div>
                    ))}
                    {addFieldOptions.length < 10 && (
                      <div className="picklist-option-add">
                        <input
                          type="text"
                          placeholder={t('modal.typeAndPressAdd')}
                          value={addFieldOptionInput}
                          onChange={(e) => setAddFieldOptionInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const v = addFieldOptionInput.trim(); if (v && addFieldOptions.length < 10) { setAddFieldOptions((prev) => [...prev, v]); setAddFieldOptionInput('') } } }}
                        />
                        <button type="button" className="ghost-button" onClick={() => { const v = addFieldOptionInput.trim(); if (v && addFieldOptions.length < 10) { setAddFieldOptions((prev) => [...prev, v]); setAddFieldOptionInput('') } }}>{t('modal.add')}</button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="field">
                <p className="field-hint" style={{ marginBottom: '0.5rem', color: '#64748b', fontSize: '0.9rem' }}>{t('modal.addedToSignatureHint')}</p>
                <span>{t('modal.addedToSignature')}</span>
                <div className="field-checkbox-group" style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={addFieldSig1}
                      onChange={(e) => setAddFieldSig1(e.target.checked)}
                    />
                    {t('modal.signature1')}
                  </label>
                  {totalSignatures === 2 && (
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={addFieldSig2}
                        onChange={(e) => setAddFieldSig2(e.target.checked)}
                      />
                      {t('modal.signature2')}
                    </label>
                  )}
                </div>
              </div>
            </div>
            <div className="modal__actions--below modal-actions">
              <button
                type="button"
                className="ghost-button"
                onClick={() => { setIsAddFieldOpen(false); setAddFieldError(''); setAddFieldSig1(true); setAddFieldSig2(true); setAddFieldContent(''); setAddFieldOptions([]); setAddFieldOptionInput('') }}
              >
                {t('modal.close')}
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={() => {
                  const textDisplayLabel = getTextDisplayFieldLabel(addFieldContent)
                  const label = addFieldType === 'textDisplay' ? textDisplayLabel : addFieldLabel.trim()
                  if (addFieldType !== 'textDisplay' && !label) {
                    setAddFieldError(t('modal.fieldLabelRequired'))
                    return
                  }
                  if (addFieldType === 'textDisplay' && !addFieldContent.trim()) {
                    setAddFieldError(t('modal.displayContentRequired'))
                    return
                  }
                  if (addFieldType === 'picklist' && addFieldOptions.length === 0) {
                    setAddFieldError(t('modal.picklistOptionRequired'))
                    return
                  }
                  const addedToSigners = []
                  if (addFieldSig1) addedToSigners.push(0)
                  if (addFieldSig2 && totalSignatures === 2) addedToSigners.push(1)
                  if (addedToSigners.length === 0) {
                    setAddFieldError(t('modal.selectOneSignature'))
                    return
                  }
                  const id = `cf_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
                  const cfData = { id, label, type: addFieldType, addedToSigners }
                  if (addFieldType === 'textDisplay') cfData.content = addFieldContent.trim().slice(0, 100)
                  if (addFieldType === 'picklist') cfData.options = [...addFieldOptions]
                  const defaultVal = addFieldType === 'checkbox' ? false : ''
                  const wouldBeSigners = signers.map((s, idx) => {
                    if (!addedToSigners.includes(idx)) return s
                    return { ...s, customFields: { ...(s.customFields || {}), [id]: defaultVal } }
                  })
                  const wouldBeBytes = new TextEncoder().encode(JSON.stringify(wouldBeSigners)).length
                  if (wouldBeBytes > signatureTextLimitKB * 1024) {
                    setAddFieldError(t('modal.signingSectionExceed', { limit: signatureTextLimitKB }))
                    return
                  }
                  setCustomFields((prev) => [...prev, cfData])
                  setSigners(wouldBeSigners)
                  if (isLayoutEditorOpen) {
                    setEditLayout((prev) => [...prev, { id, label, fieldType: addFieldType, column: 0 }])
                  }
                  setAddFieldLabel('')
                  setAddFieldType('text')
                  setAddFieldContent('')
                  setAddFieldOptions([])
                  setAddFieldOptionInput('')
                  setAddFieldSig1(true)
                  setAddFieldSig2(true)
                  setAddFieldError('')
                  setIsAddFieldOpen(false)
                }}
              >
                Save
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {isClearConfirmOpen && createPortal(
        <div className="modal-backdrop send-confirm-modal-backdrop" role="dialog" aria-modal="true" style={{ zIndex: 10000 }} onClick={(e) => { if (e.target === e.currentTarget) setIsClearConfirmOpen(false) }}>
          <div className="modal send-confirm-modal modal--compact-branded" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal__header modal__header--compact">
              <h3 className="modal__title">{t('app.clearConfirmTitle')}</h3>
              <button
                type="button"
                className="account-modal__close ghost-button"
                onClick={() => setIsClearConfirmOpen(false)}
                aria-label={t('modal.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="modal__body--card-wrap">
              <div className="modal__card">
                <p className="modal__subtitle">{t('app.clearConfirmMessage')}</p>
              </div>
              <div className="send-confirm-modal__actions modal__actions--below">
              <button
                type="button"
                className="ghost-button"
                onClick={() => setIsClearConfirmOpen(false)}
              >
                  {t('app.cancel')}
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={() => {
                  handleClearDraft()
                  setIsClearConfirmOpen(false)
                }}
              >
                  {t('app.clearConfirmButton')}
              </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {confirmDeleteState && createPortal(
        <div className="modal-backdrop confirm-delete-backdrop send-confirm-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="confirm-delete-title" style={{ zIndex: 10001 }} onClick={(e) => { if (e.target === e.currentTarget) setConfirmDeleteState(null) }}>
          <div className="modal send-confirm-modal modal--compact-branded confirm-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal__header modal__header--compact">
              <h3 id="confirm-delete-title" className="modal__title">{confirmDeleteState.title}</h3>
              <button
                type="button"
                className="account-modal__close ghost-button"
                onClick={() => setConfirmDeleteState(null)}
                aria-label={t('modal.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
              </div>
            <div className="modal__body--card-wrap">
              <div className="modal__card">
                <div className="confirm-delete-icon" aria-hidden style={{ marginBottom: 12 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                </div>
                <p className="modal__subtitle" style={{ margin: 0 }}>{confirmDeleteState.message}</p>
              </div>
              <div className="modal__actions--below confirm-delete-actions">
              <button
                type="button"
                  className="ghost-button"
                  onClick={() => setConfirmDeleteState(null)}
                >
                  {t('modal.noKeepIt')}
                </button>
                <button
                  type="button"
                  className="primary-button confirm-delete-btn"
                  onClick={() => {
                    confirmDeleteState.onConfirm?.()
                    setConfirmDeleteState(null)
                  }}
                >
                  {confirmDeleteState.confirmLabel || t('modal.yesDelete')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {isSendConfirmOpen && createPortal(
        <div className="modal-backdrop send-confirm-modal-backdrop" role="dialog" aria-modal="true" style={{ zIndex: 10000 }} onClick={(e) => { if (e.target === e.currentTarget) setIsSendConfirmOpen(false) }}>
          <div className="modal send-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal__header send-confirm-modal__header">
              <h3 className="send-confirm-modal__title">{t('modal.readyToSend')}</h3>
              <button
                type="button"
                className="account-modal__close ghost-button"
                onClick={() => setIsSendConfirmOpen(false)}
                aria-label={t('modal.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="send-confirm-modal__body">
              <div className="send-confirm-modal__card">
                <p className="send-confirm-modal__subtitle">{t('modal.readyToSendSubtitle')}</p>
                <p className="send-confirm-modal__note">
                  <span className="send-confirm-modal__note-label">NOTE:</span> {t('modal.readyToSendNote')}
                </p>
              </div>
              <div className="send-confirm-modal__recipients-wrap">
                <span className="send-confirm-modal__recipients-label">{t('modal.recipientsLabel')}</span>
                <ul className="send-confirm-modal__recipients">
                  {recipientEmail.split(',').map((e) => e.trim()).filter(Boolean).map((email, i) => (
                    <li key={i}>{email}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="send-confirm-modal__actions modal-actions">
              <button
                type="button"
                className="ghost-button"
                onClick={() => setIsSendConfirmOpen(false)}
              >
                {t('modal.no')}
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={async () => {
                  setIsSendConfirmOpen(false)
                  await performSendEmail()
                }}
              >
                {t('modal.yesSend')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {isEmailExistsModalOpen && (
        <div className="modal-backdrop send-confirm-modal-backdrop" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) setIsEmailExistsModalOpen(false) }}>
          <div className="modal send-confirm-modal modal--compact-branded" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal__header modal__header--compact">
              <h3 className="modal__title">{t('modal.accountAlreadyExists')}</h3>
              <button type="button" className="account-modal__close ghost-button" onClick={() => setIsEmailExistsModalOpen(false)} aria-label={t('modal.close')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="modal__body--card-wrap">
              <div className="modal__card">
                <p className="modal__subtitle">{t('modal.accountAlreadyExistsMessage')}</p>
              </div>
              <div className="modal__actions--below">
                <button type="button" className="ghost-button" onClick={() => { setIsEmailExistsModalOpen(false); setAuthEmail(''); setAuthError(''); }}>
                  {t('modal.useDifferentEmail')}
                </button>
                <button type="button" className="primary-button" onClick={() => { setIsEmailExistsModalOpen(false); setAuthMode('login'); setSignupStep(1); }}>
                  {t('modal.logIn')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isLogoutOpen && (
        <div className="modal-backdrop account-modal-backdrop" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) { setIsLogoutOpen(false); setIsAccountOpen(true) } }}>
          <div className="modal account-modal logout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal__header modal__header--compact">
              <h3 className="modal__title">{t('modal.logOut')}</h3>
              <button
                type="button"
                className="account-modal__close ghost-button"
                onClick={() => { setIsLogoutOpen(false); setIsAccountOpen(true) }}
                aria-label={t('modal.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="modal__body--card-wrap">
              <div className="modal__card">
                <p className="modal__subtitle">{t('modal.logOutSubtitle')}</p>
              </div>
              <div className="modal__actions--below logout-modal__actions">
              <button
                type="button"
                className="ghost-button"
                onClick={() => { setIsLogoutOpen(false); setIsAccountOpen(true) }}
              >
                  {t('modal.no')}
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={async () => {
                  setIsLogoutOpen(false)
                  await authSignOut()
                  setView('auth')
                }}
              >
                  {t('modal.yesLogOut')}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isStartFromTemplateOpen && (
        <div className="modal-backdrop start-from-template-backdrop" role="dialog" aria-modal="true" aria-labelledby="start-from-template-title" onClick={(e) => { if (e.target === e.currentTarget) setIsStartFromTemplateOpen(false) }}>
          <div className="modal send-confirm-modal modal--compact-branded start-from-template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal__header modal__header--compact">
              <h3 id="start-from-template-title" className="modal__title">{t('app.startFromTemplate')}</h3>
              <button
                type="button"
                className="account-modal__close ghost-button"
                onClick={() => setIsStartFromTemplateOpen(false)}
                aria-label={t('modal.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
              </div>
            <div className="modal__body--card-wrap">
              <div className="modal__card">
                <p className="modal__subtitle" style={{ margin: 0 }}>{t('app.startFromTemplateHint')}</p>
              </div>
              <ul className="start-from-template-list" role="list">
                {STARTER_TEMPLATE_IDS.map((templateId) => (
                  <li key={templateId}>
              <button
                type="button"
                      className="start-from-template-item"
                      onClick={() => handleStarterTemplateSelect(templateId)}
                    >
                      <span className="start-from-template-item__name">{t(`templates.starter.${templateId}.name`)}</span>
                      <span className="start-from-template-item__title">{t(`templates.starter.${templateId}.scopeTitle`)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {templateReplaceConfirmOpen && pendingStarterTemplate != null && (
        <div className="modal-backdrop start-from-template-backdrop" role="dialog" aria-modal="true" aria-labelledby="template-replace-confirm-title" onClick={(e) => { if (e.target === e.currentTarget) { setTemplateReplaceConfirmOpen(false); setPendingStarterTemplate(null) } }}>
          <div className="modal send-confirm-modal modal--compact-branded start-from-template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal__header modal__header--compact">
              <h3 id="template-replace-confirm-title" className="modal__title">{t('app.templateReplaceConfirmTitle')}</h3>
              <button
                type="button"
                className="account-modal__close ghost-button"
                onClick={() => { setTemplateReplaceConfirmOpen(false); setPendingStarterTemplate(null) }}
                aria-label={t('modal.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="modal__body--card-wrap">
              <p className="modal__subtitle" style={{ margin: '0 0 16px' }}>{t('app.templateReplaceConfirmMessage')}</p>
            <div className="modal__body start-from-template-modal__body" style={{ paddingTop: 0 }}>
              <div className="start-from-template-confirm-actions">
                <button
                  type="button"
                  className="sticky-send-bar__btn sticky-send-bar__btn--secondary"
                  onClick={() => { setTemplateReplaceConfirmOpen(false); setPendingStarterTemplate(null) }}
                >
                  {t('app.templateReplaceConfirmCancel')}
                </button>
                <button
                  type="button"
                  className="sticky-send-bar__btn sticky-send-bar__btn--primary"
                  onClick={() => applyStarterTemplate(pendingStarterTemplate)}
                >
                  {t('app.templateReplaceConfirmUseTemplate')}
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}

      {isSaveTemplateOpen && (
        <div className="modal-backdrop account-modal-backdrop" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) setIsSaveTemplateOpen(false) }}>
          <div className="modal save-template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal__header modal__header--compact">
              <h3 className="modal__title">{t('app.saveTemplate')}</h3>
              <button
                type="button"
                className="account-modal__close ghost-button"
                onClick={() => setIsSaveTemplateOpen(false)}
                aria-label={t('modal.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="modal__body--card-wrap">
              <div className="modal__card">
                <p className="modal__subtitle">
                  {activeTemplateId
                    ? 'Update your template or save the current document as a new one.'
                    : 'Name your template to save it for later.'}
                </p>
                {activeTemplateId && (
                  <p className="modal__subtitle" style={{ marginTop: 8 }}>
                    Current template: <strong>{templates.find((t) => t.id === activeTemplateId)?.name || '—'}</strong>
                  </p>
                )}
                <label className="field" style={{ marginTop: 12, marginBottom: 0 }}>
                <span>Template name</span>
                <input
                  type="text"
                  placeholder="e.g. Q2 Service Agreement"
                  value={templateNameInput}
                  onChange={(event) => setTemplateNameInput(event.target.value)}
                />
              </label>
              {templateError && (
                  <p className="status-note" style={{ marginTop: 8 }}>{templateError}</p>
              )}
            </div>
              <div className="modal__actions--below">
              <button
                type="button"
                className="ghost-button"
                onClick={() => setIsSaveTemplateOpen(false)}
              >
                {t('app.cancel')}
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={() => persistTemplate('new')}
              >
                Save as new
              </button>
              <button
                type="button"
                className="ghost-button"
                disabled={!activeTemplateId}
                onClick={() => persistTemplate('overwrite')}
              >
                Update template
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {templateSaveSuccess && (
        <div className="modal-backdrop template-success-backdrop" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) setTemplateSaveSuccess(null) }}>
          <div className="modal template-success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal__header modal__header--compact">
              <h3 className="modal__title">Template saved successfully</h3>
              <button
                type="button"
                className="account-modal__close ghost-button"
                onClick={() => setTemplateSaveSuccess(null)}
                aria-label={t('modal.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="modal__body--card-wrap">
              <div className="modal__card">
                <p className="modal__subtitle">
                  {templateSaveSuccess.isUpdate
                    ? `"${templateSaveSuccess.templateName}" has been updated with your current document.`
                    : `"${templateSaveSuccess.templateName}" has been saved. You can use it from the template dropdown.`}
                </p>
              </div>
              <div className="modal__actions--below">
              <button
                type="button"
                className="primary-button"
                onClick={() => setTemplateSaveSuccess(null)}
              >
                  {t('modal.ok')}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {isAdminOpen && (
        <Suspense fallback={null}>
        <AdminCRM
          adminCredentials={adminCredentials}
          onLogin={setAdminCredentials}
          onLogout={() => setAdminCredentials(null)}
          onClose={() => { setIsAdminOpen(false); setAdminCredentials(null); }}
        />
        </Suspense>
      )}
      <EmailVerificationGate active={blockUnverifiedEmailPassword} />
    </div>
  )
}

export default App
